import {
  FormEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { cloudConfigured, supabase } from "./supabase";
import { WarehousePage } from "./WarehousePage";
import { normaliseWarehouseItems, normaliseWarehouseMovements } from "./warehouse";
import { BicyclePriceList } from "./BicyclePriceList";
import { mayManageBicyclePrices, normaliseBicyclePrices } from "./bicyclePrices";
import {
  currentMonth,
  investorFinances,
  investorTotals,
  monthKey,
  money,
  shortDate,
  totals,
  uid,
  vehicleFinances,
} from "./utils";
import {
  emptyData,
  type AccidentAttachment,
  type AccidentAttachmentKind,
  type AccidentFaultStatus,
  type AccidentRecord,
  type AccidentRegistrationMethod,
  type Bicycle,
  type BicycleEvent,
  type BicycleEventType,
  type BicycleGeneralExpense,
  type BicycleGeneralExpenseCategory,
  type BicycleInspection,
  type BicycleInspectionDamage,
  type BicycleInspectionMaintenanceItem,
  type BicycleInspectionStatus,
  type BicycleRentalPlan,
  type BicycleRentalPaymentMethod,
  type BicycleRepairOrder,
  type BicycleRepairPayer,
  type BicycleRepairService,
  type BicycleRepairStatus,
  type BicycleRepairUrgency,
  type BicycleStatus,
  type BicycleWaitlistEntry,
  type BicycleWaitlistModel,
  type DowntimeZeroDay,
  type EventType,
  type Investor,
  type InvestorPayment,
  type NikitaMoneyCategory,
  type NikitaMoneyEntry,
  type NikitaMoneyEntryKind,
  type PaymentMethod,
  type PlannedPayment,
  type PlannedPaymentOverride,
  type ReferenceItem,
  type RepairPartStatus,
  type RepairRecommendation,
  type RepairRecommendationPart,
  type RepairRecommendationPriority,
  type RentalBooking,
  type RentalBookingStatus,
  type RentalCar,
  type RentalCarStatus,
  type RentalEvent,
  type RentalEventType,
  type RentalRateTier,
  type TireSeason,
  type Vehicle,
  type VehicleInspection,
  type VehicleInspectionStatus,
  type VehicleStatus,
  type VoyagerData,
  type VoyagerEvent,
} from "./types";

const PAYMENT_METHODS: PaymentMethod[] = [
  "СБЕР",
  "СБЕР Бизнес",
  "Т-Бизнес",
  "Т-Физ",
  "1С",
  "НАЛИЧНЫЕ",
  "МСС",
  "ВТБ(ПДС)",
];
const BICYCLE_RENTAL_PAYMENT_METHODS: BicycleRentalPaymentMethod[] = [
  "Наличные",
  "МНС",
  "МСС",
  "ПДС",
];
const BICYCLE_RENTAL_PLANS: BicycleRentalPlan[] = [
  "Не уточнено",
  "Будет продлевать",
  "Вернёт велосипед",
];
const BICYCLE_RENTAL_PLAN_LABELS: Record<BicycleRentalPlan, string> = {
  "Не уточнено": "Планы не уточнены",
  "Будет продлевать": "Будет продлевать",
  "Вернёт велосипед": "Вернёт велосипед",
};
const BICYCLE_REPAIR_SERVICES: BicycleRepairService[] = [
  "Наш сервис",
  "ЭлВел",
  "Плюс Минус",
  "Другой",
];
const BICYCLE_GENERAL_EXPENSE_CATEGORIES: BicycleGeneralExpenseCategory[] = [
  "Запчасти на склад",
  "Расходники",
  "Инструмент",
  "Общий ремонт",
  "Доставка",
  "Прочее",
];
const BICYCLE_WAITLIST_MODELS: BicycleWaitlistModel[] = [
  "Titan 2.0",
  "UNO",
  "Monster Pro",
];

type Page =
  | "dashboard"
  | "attention"
  | "fleet"
  | "expenses"
  | "income"
  | "company-income"
  | "nikita-money"
  | "warehouse"
  | "downtime-table"
  | "repair-recommendations"
  | "personal-profit"
  | "payments"
  | "tasks"
  | "bicycles"
  | "rentals"
  | "accidents"
  | "inspections"
  | "bicycle-inspections"
  | "investors"
  | "investor-reports"
  | "documents"
  | "reference"
  | "settings";

type NavGroupId = "main" | "vehicles" | "bicycles" | "finance";

const NAV_GROUPS: { id: NavGroupId; icon: string; label: string }[] = [
  { id: "main", icon: "⌂", label: "Главное" },
  { id: "vehicles", icon: "◆", label: "Автомобили" },
  { id: "bicycles", icon: "◈", label: "Электровелосипеды" },
  { id: "finance", icon: "₽", label: "Финансы и управление" },
];

const NAV_ITEMS: {
  id: Page;
  icon: string;
  label: string;
  keywords: string[];
  group: NavGroupId;
  managementOnly?: boolean;
}[] = [
  { id: "dashboard", icon: "⌂", label: "Обзор", keywords: ["главная", "сводка"], group: "main" },
  { id: "tasks", icon: "✓", label: "Задачи", keywords: ["дела", "срок"], group: "main" },
  { id: "downtime-table", icon: "▦", label: "Таблица простоев", keywords: ["простой", "списания"], group: "main" },
  { id: "payments", icon: "▦", label: "Календарь платежей", keywords: ["платеж", "оплата", "календарь"], group: "main" },
  { id: "documents", icon: "▤", label: "Документы", keywords: ["файлы", "полис"], group: "main" },
  { id: "reference", icon: "⌘", label: "Справочник", keywords: ["ссылки", "заметки", "таблицы"], group: "main" },
  { id: "warehouse", icon: "▥", label: "Склад компании", keywords: ["склад", "офис", "гараж", "запчасти", "материалы", "расходники", "остатки"], group: "main" },
  { id: "settings", icon: "⚙", label: "Настройки", keywords: ["сотрудники", "резервная копия", "диагностика"], group: "main" },
  { id: "fleet", icon: "◆", label: "Таксопарк", keywords: ["авто", "машины", "пробег", "то"], group: "vehicles" },
  { id: "rentals", icon: "◇", label: "Прокат автомобилей", keywords: ["прокат", "аренда", "бронь"], group: "vehicles" },
  { id: "repair-recommendations", icon: "⚒", label: "Рекомендации по ремонтам", keywords: ["ремонт", "запчасти"], group: "vehicles" },
  { id: "inspections", icon: "◉", label: "Осмотры авто", keywords: ["осмотр", "проверка", "водитель", "повреждение"], group: "vehicles" },
  { id: "accidents", icon: "⚠", label: "Реестр ДТП", keywords: ["дтп", "авария", "повреждение", "постановление"], group: "vehicles" },
  { id: "bicycles", icon: "◈", label: "Электровелосипеды", keywords: ["вело", "велосипед"], group: "bicycles" },
  { id: "bicycle-inspections", icon: "◎", label: "Осмотры велосипедов", keywords: ["осмотр", "велосипед", "арендатор", "повреждение", "обслуживание"], group: "bicycles" },
  { id: "expenses", icon: "↘", label: "Расходы", keywords: ["траты", "затраты"], group: "finance" },
  { id: "income", icon: "↗", label: "Доходы", keywords: ["выручка", "поступления"], group: "finance" },
  { id: "company-income", icon: "₽", label: "Доход компании", keywords: ["прибыль", "заработок", "доля", "аренда", "прокат", "велосипеды"], group: "finance", managementOnly: true },
  { id: "personal-profit", icon: "◒", label: "Личная прибыль", keywords: ["личные финансы"], group: "finance" },
  { id: "nikita-money", icon: "₽", label: "Учёт денег Никиты", keywords: ["никита", "счёт", "счет", "приход", "расход", "остаток"], group: "finance" },
  { id: "investors", icon: "◌", label: "Инвесторы", keywords: ["инвестор", "выплаты"], group: "finance" },
  { id: "investor-reports", icon: "▧", label: "Отчёты инвесторам", keywords: ["отчет", "отчёт"], group: "finance" },
];

const FAVORITABLE_PAGE_IDS = new Set<Page>(
  NAV_ITEMS.filter((item) => item.id !== "dashboard").map((item) => item.id),
);

const NIKITA_MONEY_CATEGORIES: NikitaMoneyCategory[] = [
  "Короб",
  "Аренда",
  "Прокат",
  "Велосипед",
  "Авито",
  "Пополнение 1С",
  "Ремонты",
  "Прочее",
];

function normaliseFavoritePages(value: unknown): Page[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is Page =>
      typeof item === "string" && FAVORITABLE_PAGE_IDS.has(item as Page),
  );
}

const NAV_PAGE_IDS = NAV_ITEMS.map((item) => item.id);
const NAV_PAGE_ID_SET = new Set<Page>(NAV_PAGE_IDS);

function normaliseNavigationOrder(value: unknown): Page[] {
  const saved = Array.isArray(value)
    ? value.filter(
        (item): item is Page =>
          typeof item === "string" && NAV_PAGE_ID_SET.has(item as Page),
      )
    : [];
  return [...new Set([...saved, ...NAV_PAGE_IDS])];
}
type VehicleTab =
  | "overview"
  | "finance"
  | "repair"
  | "maintenance"
  | "documents"
  | "photos"
  | "history";
type BicycleTab =
  | "overview"
  | "finance"
  | "rentals"
  | "repair"
  | "maintenance"
  | "documents"
  | "history";
type BicycleSection = "fleet" | "repairs" | "prices" | "general-expenses" | "waitlist" | "archive";
type RentalCarTab =
  | "overview"
  | "bookings"
  | "finance"
  | "repair"
  | "documents"
  | "damages"
  | "history";
type SyncState = "loading" | "saved" | "saving" | "offline" | "error";
type AttentionCategory = "fleet" | "bicycles" | "rentals" | "tasks";
type AttentionItem = {
  key: string;
  category: AttentionCategory;
  vehicleId?: string;
  bicycleId?: string;
  rentalCarId?: string;
  taskId?: string;
  fleetMileage?: boolean;
  icon: string;
  tone: string;
  title: string;
  text: string;
};

const ATTENTION_CATEGORIES: {
  id: AttentionCategory;
  label: string;
  icon: string;
}[] = [
  { id: "fleet", label: "Таксопарк", icon: "●" },
  { id: "bicycles", label: "Электровелосипеды", icon: "⚡" },
  { id: "rentals", label: "Прокат автомобилей", icon: "◇" },
  { id: "tasks", label: "Задачи", icon: "✓" },
];
type LocalSnapshotOutbox = {
  version: 1;
  companyId: string;
  userId: string;
  baseRevision: number | null;
  basePayload: VoyagerData;
  payload: VoyagerData;
  updatedAt: string;
};
type LocalSnapshotCache = {
  version: 1;
  company: CompanyContext;
  userId: string;
  revision: number;
  payload: VoyagerData;
  savedAt: string;
};
type SnapshotBackup = {
  id: string;
  company_id: string;
  revision: number;
  reason: "daily" | "manual" | "before_restore";
  created_at: string;
  created_by: string | null;
};
type CompanyRole = "owner" | "admin" | "editor" | "viewer";
type AccessState = "loading" | "ready" | "waiting";
type CompanyContext = {
  id: string;
  name: string;
  role: CompanyRole;
};
type CompanyMember = {
  user_id: string;
  email: string;
  display_name: string | null;
  role: CompanyRole;
  status: "active" | "disabled";
  created_at: string;
};
type InvestorPortalAccount = {
  investor_id: string;
  user_id: string;
  email: string;
  status: "active" | "disabled";
  created_at: string;
  updated_at: string;
};
type InvestorPortalContext = {
  companyId: string;
  companyName: string;
  investorId: string;
  userId: string;
  revision: number;
  updatedAt: string;
  data: VoyagerData;
};
type TaskPriority = "normal" | "important" | "urgent";
type CompanyTask = {
  id: string;
  company_id: string;
  title: string;
  description: string | null;
  due_date: string | null;
  due_time: string | null;
  assignee_user_id: string | null;
  priority: TaskPriority;
  status: "open" | "completed";
  created_by: string;
  completed_by: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};
type PersonalFinanceKind = "income" | "expense";
type PersonalFinanceOverride = {
  title?: string;
  amount?: number | null;
  note?: string;
  cancelled?: boolean;
};
type PersonalFinanceItem = {
  id: string;
  company_id: string;
  kind: PersonalFinanceKind;
  title: string;
  amount: number | null;
  note: string | null;
  start_month: string;
  repeat_monthly: boolean;
  overrides: Record<string, PersonalFinanceOverride> | null;
  created_by: string;
  created_at: string;
  updated_at: string;
};
type InvestorReportStatus = "draft" | "ready";
type InvestorReport = {
  id: string;
  company_id: string;
  investor_id: string;
  investor_name: string;
  period_month: string;
  vehicle_count: number;
  revenue: number;
  operating_expenses: number;
  personal_expenses: number;
  accrued: number;
  payments: number;
  payout: number;
  rental_days: number;
  idle_days: number;
  lost_income: number;
  comment: string | null;
  status: InvestorReportStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
};

const ROLE_LABELS: Record<CompanyRole, string> = {
  owner: "Владелец",
  admin: "Администратор",
  editor: "Редактор",
  viewer: "Только просмотр",
};

const EVENT_LABELS: Record<EventType, string> = {
  income: "Доход",
  expense: "Расход",
  repair: "Ремонт",
  maintenance: "ТО",
  document: "Документ",
  photo: "Фото",
  mileage: "Пробег",
  note: "Заметка",
};
const EVENT_ICONS: Record<EventType, string> = {
  income: "↗",
  expense: "↘",
  repair: "🔧",
  maintenance: "🛠",
  document: "▤",
  photo: "▧",
  mileage: "◉",
  note: "✎",
};
const STATUS_COLORS: Record<VehicleStatus, string> = {
  Работает: "green",
  "На ТО": "yellow",
  Ремонт: "orange",
  Простой: "red",
  Архив: "gray",
};
const BICYCLE_STATUS_COLORS: Record<BicycleStatus, string> = {
  Свободен: "green",
  "В аренде": "blue",
  "На ТО": "yellow",
  Ремонт: "orange",
  Архив: "gray",
};
const BICYCLE_EVENT_LABELS: Record<BicycleEventType, string> = {
  rental: "Аренда",
  income: "Доход",
  expense: "Расход",
  repair: "Ремонт",
  maintenance: "ТО",
  document: "Документ",
  mileage: "Пробег",
  note: "Заметка",
};
const BICYCLE_EVENT_ICONS: Record<BicycleEventType, string> = {
  rental: "⌁",
  income: "↗",
  expense: "↘",
  repair: "🔧",
  maintenance: "🛠",
  document: "▤",
  mileage: "◉",
  note: "✎",
};
const RENTAL_STATUS_COLORS: Record<RentalCarStatus, string> = {
  Доступен: "green",
  Забронирован: "yellow",
  "В аренде": "blue",
  Обслуживание: "orange",
  Архив: "gray",
};
const RENTAL_EVENT_LABELS: Record<RentalEventType, string> = {
  income: "Доход",
  expense: "Расход",
  repair: "Ремонт",
  maintenance: "ТО",
  document: "Документ",
  damage: "Повреждение",
  mileage: "Пробег",
  note: "Заметка",
};
const RENTAL_EVENT_ICONS: Record<RentalEventType, string> = {
  income: "↗",
  expense: "↘",
  repair: "🔧",
  maintenance: "🛠",
  document: "▤",
  damage: "⚠",
  mileage: "◉",
  note: "✎",
};

function normaliseData(payload: unknown): VoyagerData {
  const raw = (
    payload && typeof payload === "object" ? payload : {}
  ) as Partial<VoyagerData>;
  return {
    schemaVersion: 15,
    vehicles: Array.isArray(raw.vehicles)
      ? raw.vehicles.map((vehicle) => ({
          ...vehicle,
          driverName:
            typeof vehicle.driverName === "string"
              ? vehicle.driverName
              : undefined,
          driverPhone:
            typeof vehicle.driverPhone === "string"
              ? vehicle.driverPhone
              : undefined,
        }))
      : [],
    investors: Array.isArray(raw.investors) ? raw.investors : [],
    events: Array.isArray(raw.events) ? raw.events : [],
    references: Array.isArray(raw.references) ? raw.references : [],
    bicycles: Array.isArray(raw.bicycles) ? raw.bicycles : [],
    bicycleEvents: Array.isArray(raw.bicycleEvents) ? raw.bicycleEvents : [],
    bicyclePriceItems: normaliseBicyclePrices(raw.bicyclePriceItems),
    bicycleGeneralExpenses: Array.isArray(raw.bicycleGeneralExpenses)
      ? raw.bicycleGeneralExpenses.filter(
          (entry): entry is BicycleGeneralExpense =>
            Boolean(
              entry?.id &&
                typeof entry.date === "string" &&
                Number.isFinite(Number(entry.amount)) &&
                Number(entry.amount) > 0 &&
                BICYCLE_GENERAL_EXPENSE_CATEGORIES.includes(entry.category),
            ),
        )
      : [],
    bicycleRepairOrders: Array.isArray(raw.bicycleRepairOrders)
      ? raw.bicycleRepairOrders.map((item) => ({
          ...item,
          repairs: Array.isArray(item.repairs) ? item.repairs : [],
        }))
      : [],
    bicycleWaitlist: Array.isArray(raw.bicycleWaitlist)
      ? raw.bicycleWaitlist
      : [],
    bicycleInspections: Array.isArray(raw.bicycleInspections)
      ? raw.bicycleInspections.map((inspection) => ({
          ...inspection,
          damages: Array.isArray(inspection.damages) ? inspection.damages : [],
          maintenanceItems: Array.isArray(inspection.maintenanceItems)
            ? inspection.maintenanceItems
            : [],
          photos: Array.isArray(inspection.photos) ? inspection.photos : [],
        }))
      : [],
    rentalCars: Array.isArray(raw.rentalCars) ? raw.rentalCars : [],
    rentalBookings: Array.isArray(raw.rentalBookings) ? raw.rentalBookings : [],
    rentalEvents: Array.isArray(raw.rentalEvents) ? raw.rentalEvents : [],
    accidentRecords: Array.isArray(raw.accidentRecords)
      ? raw.accidentRecords.map((record) => ({
          ...record,
          vehiclePlate:
            typeof record.vehiclePlate === "string" ? record.vehiclePlate : "",
          vehicleName:
            typeof record.vehicleName === "string" ? record.vehicleName : "",
          attachments: Array.isArray(record.attachments)
            ? record.attachments
            : [],
        }))
      : [],
    vehicleInspections: Array.isArray(raw.vehicleInspections)
      ? raw.vehicleInspections.map((inspection) => ({
          ...inspection,
          photos: Array.isArray(inspection.photos) ? inspection.photos : [],
        }))
      : [],
    nikitaMoneyEntries: Array.isArray(raw.nikitaMoneyEntries)
      ? raw.nikitaMoneyEntries.filter(
          (entry): entry is NikitaMoneyEntry =>
            Boolean(
              entry?.id &&
                (entry.kind === "income" || entry.kind === "expense") &&
                typeof entry.date === "string" &&
                Number.isFinite(Number(entry.amount)) &&
                Number(entry.amount) > 0 &&
                NIKITA_MONEY_CATEGORIES.includes(entry.category),
            ),
        )
      : [],
    warehouseItems: normaliseWarehouseItems(raw.warehouseItems),
    warehouseMovements: normaliseWarehouseMovements(raw.warehouseMovements),
    investorPayments: Array.isArray(raw.investorPayments)
      ? raw.investorPayments.map((payment) => ({
          ...payment,
          periodMonth:
            typeof payment.periodMonth === "string" &&
            /^\d{4}-\d{2}$/.test(payment.periodMonth)
              ? payment.periodMonth
              : undefined,
        }))
      : [],
    plannedPayments: Array.isArray(raw.plannedPayments)
      ? raw.plannedPayments.map((payment) => ({
          ...payment,
          paidDates: Array.isArray(payment.paidDates) ? payment.paidDates : [],
          overrides:
            payment.overrides && typeof payment.overrides === "object"
              ? payment.overrides
              : {},
        }))
      : [],
    downtimeZeroDays: Array.isArray(raw.downtimeZeroDays)
      ? raw.downtimeZeroDays.filter(
          (item): item is DowntimeZeroDay =>
            Boolean(item?.vehicleId && /^\d{4}-\d{2}-\d{2}$/.test(item.date)),
        )
      : [],
    downtimeHiddenVehicleIds: Array.isArray(raw.downtimeHiddenVehicleIds)
      ? raw.downtimeHiddenVehicleIds.filter(
          (id): id is string => typeof id === "string",
        )
      : [],
    repairRecommendations: Array.isArray(raw.repairRecommendations)
      ? raw.repairRecommendations.map((item) => ({
          ...item,
          parts: Array.isArray(item.parts) ? item.parts : [],
        }))
      : [],
    repairStorageLocations: Array.from(
      new Set([
        "Офис",
        "Вальтер",
        ...(Array.isArray(raw.repairStorageLocations)
          ? raw.repairStorageLocations.filter(
              (item): item is string =>
                typeof item === "string" && Boolean(item.trim()),
            )
          : []),
      ]),
    ),
    settings: {
      monthlyVehicleExpenseLimit:
        Number(raw.settings?.monthlyVehicleExpenseLimit) || 17000,
      insuranceAlertDays: Number(raw.settings?.insuranceAlertDays) || 30,
    },
    updatedAt:
      typeof raw.updatedAt === "string"
        ? raw.updatedAt
        : new Date().toISOString(),
  };
}

function sameSnapshotValue(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function eventSnapshotKey(item: VoyagerEvent) {
  return item.source === "downtime_table" && item.vehicleId
    ? `downtime:${item.vehicleId}:${item.date.slice(0, 10)}`
    : item.id;
}

const CLOUD_REQUEST_TIMEOUT_MS = 8000;

function withCloudTimeout<T>(
  request: PromiseLike<T>,
  label: string,
  timeoutMs = CLOUD_REQUEST_TIMEOUT_MS,
) {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error(`${label}: облако не ответило за 8 секунд`)),
      timeoutMs,
    );
    Promise.resolve(request).then(
      (result) => {
        window.clearTimeout(timer);
        resolve(result);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function listContainsOnlyAdditions<T>(
  before: T[],
  after: T[],
  getKey: (item: T) => string,
) {
  if (after.length < before.length) return false;
  const afterByKey = new Map(after.map((item) => [getKey(item), item]));
  if (afterByKey.size !== after.length) return false;
  return before.every((item) =>
    sameSnapshotValue(item, afterByKey.get(getKey(item))),
  );
}

function isAppendOnlySnapshotChange(before: VoyagerData, after: VoyagerData) {
  return (
    before.schemaVersion === after.schemaVersion &&
    sameSnapshotValue(before.settings, after.settings) &&
    listContainsOnlyAdditions(before.vehicles, after.vehicles, (item) => item.id) &&
    listContainsOnlyAdditions(before.investors, after.investors, (item) => item.id) &&
    listContainsOnlyAdditions(before.events, after.events, eventSnapshotKey) &&
    listContainsOnlyAdditions(before.references, after.references, (item) => item.id) &&
    listContainsOnlyAdditions(before.bicycles, after.bicycles, (item) => item.id) &&
    listContainsOnlyAdditions(before.bicyclePriceItems, after.bicyclePriceItems, (item) => item.id) &&
    listContainsOnlyAdditions(
      before.bicycleEvents,
      after.bicycleEvents,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(
      before.bicycleGeneralExpenses,
      after.bicycleGeneralExpenses,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(
      before.bicycleRepairOrders,
      after.bicycleRepairOrders,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(
      before.bicycleWaitlist,
      after.bicycleWaitlist,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(
      before.bicycleInspections,
      after.bicycleInspections,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(before.rentalCars, after.rentalCars, (item) => item.id) &&
    listContainsOnlyAdditions(
      before.rentalBookings,
      after.rentalBookings,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(
      before.rentalEvents,
      after.rentalEvents,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(
      before.accidentRecords,
      after.accidentRecords,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(
      before.vehicleInspections,
      after.vehicleInspections,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(
      before.nikitaMoneyEntries,
      after.nikitaMoneyEntries,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(
      before.investorPayments,
      after.investorPayments,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(before.warehouseItems, after.warehouseItems, (item) => item.id) &&
    listContainsOnlyAdditions(before.warehouseMovements, after.warehouseMovements, (item) => item.id) &&
    listContainsOnlyAdditions(
      before.plannedPayments,
      after.plannedPayments,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(
      before.downtimeZeroDays,
      after.downtimeZeroDays,
      (item) => `${item.vehicleId}:${item.date}`,
    ) &&
    listContainsOnlyAdditions(
      before.downtimeHiddenVehicleIds,
      after.downtimeHiddenVehicleIds,
      (item) => item,
    ) &&
    listContainsOnlyAdditions(
      before.repairRecommendations,
      after.repairRecommendations,
      (item) => item.id,
    ) &&
    listContainsOnlyAdditions(
      before.repairStorageLocations,
      after.repairStorageLocations,
      (item) => item,
    )
  );
}

function snapshotListHasConflict<T>(
  base: T[],
  local: T[],
  remote: T[],
  getKey: (item: T) => string,
) {
  const baseByKey = new Map(base.map((item) => [getKey(item), item]));
  const localByKey = new Map(local.map((item) => [getKey(item), item]));
  const remoteByKey = new Map(remote.map((item) => [getKey(item), item]));
  const keys = new Set([...baseByKey.keys(), ...localByKey.keys(), ...remoteByKey.keys()]);
  return [...keys].some((key) => {
    const baseItem = baseByKey.get(key);
    const localItem = localByKey.get(key);
    const remoteItem = remoteByKey.get(key);
    return (
      !sameSnapshotValue(localItem, baseItem) &&
      !sameSnapshotValue(remoteItem, baseItem) &&
      !sameSnapshotValue(localItem, remoteItem)
    );
  });
}

function hasSnapshotConflict(
  base: VoyagerData,
  local: VoyagerData,
  remote: VoyagerData,
) {
  const settingConflict =
    (!sameSnapshotValue(local.settings.monthlyVehicleExpenseLimit, base.settings.monthlyVehicleExpenseLimit) &&
      !sameSnapshotValue(remote.settings.monthlyVehicleExpenseLimit, base.settings.monthlyVehicleExpenseLimit) &&
      !sameSnapshotValue(local.settings.monthlyVehicleExpenseLimit, remote.settings.monthlyVehicleExpenseLimit)) ||
    (!sameSnapshotValue(local.settings.insuranceAlertDays, base.settings.insuranceAlertDays) &&
      !sameSnapshotValue(remote.settings.insuranceAlertDays, base.settings.insuranceAlertDays) &&
      !sameSnapshotValue(local.settings.insuranceAlertDays, remote.settings.insuranceAlertDays));
  return (
    settingConflict ||
    snapshotListHasConflict(base.vehicles, local.vehicles, remote.vehicles, (item) => item.id) ||
    snapshotListHasConflict(base.investors, local.investors, remote.investors, (item) => item.id) ||
    snapshotListHasConflict(base.events, local.events, remote.events, eventSnapshotKey) ||
    snapshotListHasConflict(base.references, local.references, remote.references, (item) => item.id) ||
    snapshotListHasConflict(base.bicycles, local.bicycles, remote.bicycles, (item) => item.id) ||
    snapshotListHasConflict(base.bicyclePriceItems, local.bicyclePriceItems, remote.bicyclePriceItems, (item) => item.id) ||
    snapshotListHasConflict(base.bicycleEvents, local.bicycleEvents, remote.bicycleEvents, (item) => item.id) ||
    snapshotListHasConflict(base.bicycleGeneralExpenses, local.bicycleGeneralExpenses, remote.bicycleGeneralExpenses, (item) => item.id) ||
    snapshotListHasConflict(base.bicycleRepairOrders, local.bicycleRepairOrders, remote.bicycleRepairOrders, (item) => item.id) ||
    snapshotListHasConflict(base.bicycleWaitlist, local.bicycleWaitlist, remote.bicycleWaitlist, (item) => item.id) ||
    snapshotListHasConflict(base.bicycleInspections, local.bicycleInspections, remote.bicycleInspections, (item) => item.id) ||
    snapshotListHasConflict(base.rentalCars, local.rentalCars, remote.rentalCars, (item) => item.id) ||
    snapshotListHasConflict(base.rentalBookings, local.rentalBookings, remote.rentalBookings, (item) => item.id) ||
    snapshotListHasConflict(base.rentalEvents, local.rentalEvents, remote.rentalEvents, (item) => item.id) ||
    snapshotListHasConflict(base.accidentRecords, local.accidentRecords, remote.accidentRecords, (item) => item.id) ||
    snapshotListHasConflict(base.vehicleInspections, local.vehicleInspections, remote.vehicleInspections, (item) => item.id) ||
    snapshotListHasConflict(base.nikitaMoneyEntries, local.nikitaMoneyEntries, remote.nikitaMoneyEntries, (item) => item.id) ||
    snapshotListHasConflict(base.warehouseItems, local.warehouseItems, remote.warehouseItems, (item) => item.id) ||
    snapshotListHasConflict(base.warehouseMovements, local.warehouseMovements, remote.warehouseMovements, (item) => item.id) ||
    snapshotListHasConflict(base.investorPayments, local.investorPayments, remote.investorPayments, (item) => item.id) ||
    snapshotListHasConflict(base.plannedPayments, local.plannedPayments, remote.plannedPayments, (item) => item.id) ||
    snapshotListHasConflict(base.downtimeZeroDays, local.downtimeZeroDays, remote.downtimeZeroDays, (item) => `${item.vehicleId}:${item.date}`) ||
    snapshotListHasConflict(base.downtimeHiddenVehicleIds, local.downtimeHiddenVehicleIds, remote.downtimeHiddenVehicleIds, (item) => item) ||
    snapshotListHasConflict(base.repairRecommendations, local.repairRecommendations, remote.repairRecommendations, (item) => item.id) ||
    snapshotListHasConflict(base.repairStorageLocations, local.repairStorageLocations, remote.repairStorageLocations, (item) => item)
  );
}

function mergeSnapshotList<T>(
  base: T[],
  local: T[],
  remote: T[],
  getKey: (item: T) => string,
) {
  const baseByKey = new Map(base.map((item) => [getKey(item), item]));
  const localByKey = new Map(local.map((item) => [getKey(item), item]));
  const remoteByKey = new Map(remote.map((item) => [getKey(item), item]));
  const orderedKeys = Array.from(
    new Set([
      ...local.map(getKey),
      ...remote.map(getKey).filter((key) => !localByKey.has(key)),
    ]),
  );

  return orderedKeys.flatMap((key) => {
    const baseItem = baseByKey.get(key);
    const localItem = localByKey.get(key);
    const remoteItem = remoteByKey.get(key);
    let selected: T | undefined;

    if (sameSnapshotValue(localItem, baseItem)) selected = remoteItem;
    else if (sameSnapshotValue(remoteItem, baseItem)) selected = localItem;
    else if (sameSnapshotValue(localItem, remoteItem)) selected = localItem;
    else selected = localItem;

    return selected === undefined ? [] : [selected];
  });
}

function mergeSnapshotData(
  baseValue: VoyagerData,
  localValue: VoyagerData,
  remoteValue: VoyagerData,
): VoyagerData {
  const choose = <T,>(base: T, local: T, remote: T) => {
    if (sameSnapshotValue(local, base)) return remote;
    if (sameSnapshotValue(remote, base)) return local;
    if (sameSnapshotValue(local, remote)) return local;
    return local;
  };

  return {
    schemaVersion: Math.max(
      baseValue.schemaVersion,
      localValue.schemaVersion,
      remoteValue.schemaVersion,
    ),
    vehicles: mergeSnapshotList(
      baseValue.vehicles,
      localValue.vehicles,
      remoteValue.vehicles,
      (item) => item.id,
    ),
    investors: mergeSnapshotList(
      baseValue.investors,
      localValue.investors,
      remoteValue.investors,
      (item) => item.id,
    ),
    events: mergeSnapshotList(
      baseValue.events,
      localValue.events,
      remoteValue.events,
      eventSnapshotKey,
    ),
    references: mergeSnapshotList(
      baseValue.references,
      localValue.references,
      remoteValue.references,
      (item) => item.id,
    ),
    bicycles: mergeSnapshotList(
      baseValue.bicycles,
      localValue.bicycles,
      remoteValue.bicycles,
      (item) => item.id,
    ),
    bicycleEvents: mergeSnapshotList(
      baseValue.bicycleEvents,
      localValue.bicycleEvents,
      remoteValue.bicycleEvents,
      (item) => item.id,
    ),
    bicyclePriceItems: mergeSnapshotList(
      baseValue.bicyclePriceItems, localValue.bicyclePriceItems, remoteValue.bicyclePriceItems, (item) => item.id,
    ),
    bicycleGeneralExpenses: mergeSnapshotList(
      baseValue.bicycleGeneralExpenses,
      localValue.bicycleGeneralExpenses,
      remoteValue.bicycleGeneralExpenses,
      (item) => item.id,
    ),
    bicycleRepairOrders: mergeSnapshotList(
      baseValue.bicycleRepairOrders,
      localValue.bicycleRepairOrders,
      remoteValue.bicycleRepairOrders,
      (item) => item.id,
    ),
    bicycleWaitlist: mergeSnapshotList(
      baseValue.bicycleWaitlist,
      localValue.bicycleWaitlist,
      remoteValue.bicycleWaitlist,
      (item) => item.id,
    ),
    bicycleInspections: mergeSnapshotList(
      baseValue.bicycleInspections,
      localValue.bicycleInspections,
      remoteValue.bicycleInspections,
      (item) => item.id,
    ),
    rentalCars: mergeSnapshotList(
      baseValue.rentalCars,
      localValue.rentalCars,
      remoteValue.rentalCars,
      (item) => item.id,
    ),
    rentalBookings: mergeSnapshotList(
      baseValue.rentalBookings,
      localValue.rentalBookings,
      remoteValue.rentalBookings,
      (item) => item.id,
    ),
    rentalEvents: mergeSnapshotList(
      baseValue.rentalEvents,
      localValue.rentalEvents,
      remoteValue.rentalEvents,
      (item) => item.id,
    ),
    accidentRecords: mergeSnapshotList(
      baseValue.accidentRecords,
      localValue.accidentRecords,
      remoteValue.accidentRecords,
      (item) => item.id,
    ),
    vehicleInspections: mergeSnapshotList(
      baseValue.vehicleInspections,
      localValue.vehicleInspections,
      remoteValue.vehicleInspections,
      (item) => item.id,
    ),
    nikitaMoneyEntries: mergeSnapshotList(
      baseValue.nikitaMoneyEntries,
      localValue.nikitaMoneyEntries,
      remoteValue.nikitaMoneyEntries,
      (item) => item.id,
    ),
    warehouseItems: mergeSnapshotList(
      baseValue.warehouseItems, localValue.warehouseItems, remoteValue.warehouseItems, (item) => item.id,
    ),
    warehouseMovements: mergeSnapshotList(
      baseValue.warehouseMovements, localValue.warehouseMovements, remoteValue.warehouseMovements, (item) => item.id,
    ),
    investorPayments: mergeSnapshotList(
      baseValue.investorPayments,
      localValue.investorPayments,
      remoteValue.investorPayments,
      (item) => item.id,
    ),
    plannedPayments: mergeSnapshotList(
      baseValue.plannedPayments,
      localValue.plannedPayments,
      remoteValue.plannedPayments,
      (item) => item.id,
    ),
    downtimeZeroDays: mergeSnapshotList(
      baseValue.downtimeZeroDays,
      localValue.downtimeZeroDays,
      remoteValue.downtimeZeroDays,
      (item) => `${item.vehicleId}:${item.date}`,
    ),
    downtimeHiddenVehicleIds: mergeSnapshotList(
      baseValue.downtimeHiddenVehicleIds,
      localValue.downtimeHiddenVehicleIds,
      remoteValue.downtimeHiddenVehicleIds,
      (item) => item,
    ),
    repairRecommendations: mergeSnapshotList(
      baseValue.repairRecommendations,
      localValue.repairRecommendations,
      remoteValue.repairRecommendations,
      (item) => item.id,
    ),
    repairStorageLocations: mergeSnapshotList(
      baseValue.repairStorageLocations,
      localValue.repairStorageLocations,
      remoteValue.repairStorageLocations,
      (item) => item,
    ),
    settings: {
      monthlyVehicleExpenseLimit: choose(
        baseValue.settings.monthlyVehicleExpenseLimit,
        localValue.settings.monthlyVehicleExpenseLimit,
        remoteValue.settings.monthlyVehicleExpenseLimit,
      ),
      insuranceAlertDays: choose(
        baseValue.settings.insuranceAlertDays,
        localValue.settings.insuranceAlertDays,
        remoteValue.settings.insuranceAlertDays,
      ),
    },
    updatedAt: new Date().toISOString(),
  };
}

function hasMeaningfulData(payload: unknown) {
  const value = normaliseData(payload);
  return [
    value.vehicles,
    value.investors,
    value.events,
    value.references,
    value.bicycles,
    value.bicycleEvents,
    value.bicyclePriceItems,
    value.bicycleGeneralExpenses,
    value.bicycleRepairOrders,
    value.bicycleWaitlist,
    value.bicycleInspections,
    value.rentalCars,
    value.rentalBookings,
    value.rentalEvents,
    value.accidentRecords,
    value.vehicleInspections,
    value.nikitaMoneyEntries,
    value.warehouseItems,
    value.warehouseMovements,
    value.investorPayments,
    value.plannedPayments,
    value.repairRecommendations,
  ].some((items) => items.length > 0);
}

const SNAPSHOT_OUTBOX_PREFIX = "voyager-snapshot-outbox-v1";
const SNAPSHOT_CACHE_PREFIX = "voyager-snapshot-cache-v1";

function snapshotOutboxKey(companyId: string, userId: string) {
  return `${SNAPSHOT_OUTBOX_PREFIX}:${companyId}:${userId}`;
}

function readSnapshotOutbox(companyId: string, userId: string) {
  try {
    const raw = localStorage.getItem(snapshotOutboxKey(companyId, userId));
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<LocalSnapshotOutbox>;
    if (
      stored.version !== 1 ||
      stored.companyId !== companyId ||
      stored.userId !== userId ||
      !stored.basePayload ||
      !stored.payload
    ) {
      return null;
    }
    return {
      version: 1,
      companyId,
      userId,
      baseRevision:
        typeof stored.baseRevision === "number" ? stored.baseRevision : null,
      basePayload: normaliseData(stored.basePayload),
      payload: normaliseData(stored.payload),
      updatedAt:
        typeof stored.updatedAt === "string"
          ? stored.updatedAt
          : new Date().toISOString(),
    } satisfies LocalSnapshotOutbox;
  } catch (error) {
    console.error("Voyager local recovery read failed:", error);
    return null;
  }
}

function writeSnapshotOutbox(outbox: LocalSnapshotOutbox) {
  localStorage.setItem(
    snapshotOutboxKey(outbox.companyId, outbox.userId),
    JSON.stringify(outbox),
  );
}

function clearSnapshotOutbox(companyId: string, userId: string) {
  localStorage.removeItem(snapshotOutboxKey(companyId, userId));
}

function snapshotCacheKey(companyId: string, userId: string) {
  return `${SNAPSHOT_CACHE_PREFIX}:${companyId}:${userId}`;
}

function readLatestSnapshotCache(userId: string) {
  try {
    let latest: LocalSnapshotCache | null = null;
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(`${SNAPSHOT_CACHE_PREFIX}:`)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const stored = JSON.parse(raw) as Partial<LocalSnapshotCache>;
      if (
        stored.version !== 1 ||
        stored.userId !== userId ||
        !stored.company ||
        !stored.payload ||
        typeof stored.revision !== "number" ||
        typeof stored.savedAt !== "string"
      )
        continue;
      const candidate: LocalSnapshotCache = {
        version: 1,
        company: stored.company,
        userId,
        revision: stored.revision,
        payload: normaliseData(stored.payload),
        savedAt: stored.savedAt,
      };
      if (!latest || candidate.savedAt > latest.savedAt) latest = candidate;
    }
    return latest;
  } catch (error) {
    console.error("Voyager confirmed cache read failed:", error);
    return null;
  }
}

function writeSnapshotCache(cache: LocalSnapshotCache) {
  localStorage.setItem(
    snapshotCacheKey(cache.company.id, cache.userId),
    JSON.stringify(cache),
  );
}

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [data, setData] = useState<VoyagerData | null>(null);
  const [company, setCompany] = useState<CompanyContext | null>(null);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [companyTasks, setCompanyTasks] = useState<CompanyTask[]>([]);
  const [personalFinanceItems, setPersonalFinanceItems] = useState<
    PersonalFinanceItem[]
  >([]);
  const [investorReports, setInvestorReports] = useState<InvestorReport[]>([]);
  const [investorPortalAccounts, setInvestorPortalAccounts] = useState<
    InvestorPortalAccount[]
  >([]);
  const [investorPortal, setInvestorPortal] =
    useState<InvestorPortalContext | null>(null);
  const [accessState, setAccessState] = useState<AccessState>("loading");
  const [sync, setSync] = useState<SyncState>("loading");
  const [syncError, setSyncError] = useState("");
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [lastCloudSyncAt, setLastCloudSyncAt] = useState<string | null>(null);
  const [cloudLoadMs, setCloudLoadMs] = useState<number | null>(null);
  const [cacheShownMs, setCacheShownMs] = useState<number | null>(null);
  const [cloudConfirmed, setCloudConfirmed] = useState(false);
  const [page, setPage] = useState<Page>("dashboard");
  const [favoritePages, setFavoritePages] = useState<Page[]>([]);
  const [navigationOrder, setNavigationOrder] = useState<Page[]>(NAV_PAGE_IDS);
  const [hasCustomNavigationOrder, setHasCustomNavigationOrder] =
    useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(
    null,
  );
  const [selectedBicycleId, setSelectedBicycleId] = useState<string | null>(
    null,
  );
  const [selectedRentalCarId, setSelectedRentalCarId] = useState<string | null>(
    null,
  );
  const [selectedInvestorId, setSelectedInvestorId] = useState<string | null>(
    null,
  );
  const [investorPeriod, setInvestorPeriod] = useState(currentMonth);
  const skipSave = useRef(false);
  const loadedWorkspace = useRef<string | null>(null);
  const snapshotRevision = useRef<number | null>(null);
  const snapshotBase = useRef<VoyagerData | null>(null);
  const pendingSnapshot = useRef<VoyagerData | null>(null);
  const pendingSnapshotBase = useRef<VoyagerData | null>(null);
  const snapshotSaveRunning = useRef(false);
  const snapshotRetryTimer = useRef<number | null>(null);
  const snapshotRetryAttempt = useRef(0);

  const persistPendingSnapshot = (
    companyId: string,
    userId: string,
    payload: VoyagerData,
  ) => {
    const basePayload =
      pendingSnapshotBase.current || snapshotBase.current || payload;
    pendingSnapshotBase.current = basePayload;
    try {
      writeSnapshotOutbox({
        version: 1,
        companyId,
        userId,
        baseRevision: snapshotRevision.current,
        basePayload,
        payload,
        updatedAt: new Date().toISOString(),
      });
      setHasPendingChanges(true);
      return true;
    } catch (error) {
      const message =
        "Не удалось создать локальную резервную копию. Не закрывайте страницу.";
      console.error("Voyager local recovery write failed:", error);
      setSyncError(message);
      setSync("error");
      return false;
    }
  };

  const clearPendingSnapshot = (companyId: string, userId: string) => {
    pendingSnapshot.current = null;
    pendingSnapshotBase.current = null;
    try {
      clearSnapshotOutbox(companyId, userId);
    } catch (error) {
      console.error("Voyager local recovery cleanup failed:", error);
    }
    setHasPendingChanges(false);
  };

  const saveCompanySnapshot = async (
    client: NonNullable<typeof supabase>,
    companyId: string,
    payload: VoyagerData,
  ): Promise<{ saved: boolean; payload: VoyagerData; conflict?: boolean }> => {
    let nextPayload = payload;

    for (let attempt = 0; attempt < 4; attempt += 1) {
      if (snapshotRevision.current === null || !snapshotBase.current) {
        let latestResult;
        try {
          latestResult = await withCloudTimeout(
            client
              .from("voyager_company_snapshots")
              .select("payload, revision")
              .eq("company_id", companyId)
              .maybeSingle(),
            "Проверка актуальной версии",
          );
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          setSyncError(message);
          setCloudConfirmed(false);
          return { saved: false, payload: nextPayload };
        }
        const { data: latest, error: latestError } = latestResult;
        if (latestError || !latest) {
          const message =
            latestError?.message || "Запись компании не найдена или недоступна";
          console.error("Voyager snapshot sync failed:", message);
          setSyncError(message);
          setCloudConfirmed(false);
          return { saved: false, payload: nextPayload };
        }
        snapshotRevision.current = Number(latest.revision);
        snapshotBase.current = normaliseData(latest.payload);
      }

      const expectedRevision = snapshotRevision.current;
      const basePayload = snapshotBase.current;
      let saveResult;
      try {
        saveResult = await withCloudTimeout(
          client
            .from("voyager_company_snapshots")
            .update({ payload: nextPayload })
            .eq("company_id", companyId)
            .eq("revision", expectedRevision)
            .select("revision, updated_at")
            .maybeSingle(),
          "Сохранение изменений",
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        setSyncError(message);
        setCloudConfirmed(false);
        return { saved: false, payload: nextPayload };
      }
      const { data: savedRow, error } = saveResult;

      if (error) {
        console.error("Voyager snapshot sync failed:", error.message);
        setSyncError(error.message);
        setCloudConfirmed(false);
        return { saved: false, payload: nextPayload };
      }
      if (savedRow) {
        snapshotRevision.current = Number(savedRow.revision);
        snapshotBase.current = nextPayload;
        setLastCloudSyncAt(savedRow.updated_at || new Date().toISOString());
        setSyncError("");
        setCloudConfirmed(true);
        return { saved: true, payload: nextPayload };
      }

      let conflictRefreshResult;
      try {
        conflictRefreshResult = await withCloudTimeout(
          client
            .from("voyager_company_snapshots")
            .select("payload, revision")
            .eq("company_id", companyId)
            .maybeSingle(),
          "Сверка одновременных изменений",
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        setSyncError(message);
        setCloudConfirmed(false);
        return { saved: false, payload: nextPayload };
      }
      const { data: latest, error: latestError } = conflictRefreshResult;
      if (latestError || !latest) {
        const message =
          latestError?.message || "Не удалось получить актуальную версию данных";
        console.error("Voyager snapshot conflict refresh failed:", message);
        setSyncError(message);
        setCloudConfirmed(false);
        return { saved: false, payload: nextPayload };
      }

      const remotePayload = normaliseData(latest.payload);
      if (hasSnapshotConflict(basePayload, nextPayload, remotePayload)) {
        const message =
          "Одна и та же запись изменена на двух устройствах. Облачная версия не перезаписана, локальная копия сохранена на этом устройстве.";
        console.error("Voyager snapshot conflict blocked:", message);
        setSyncError(message);
        setCloudConfirmed(true);
        return { saved: false, payload: nextPayload, conflict: true };
      }
      nextPayload = mergeSnapshotData(
        basePayload,
        nextPayload,
        remotePayload,
      );
      snapshotRevision.current = Number(latest.revision);
      snapshotBase.current = remotePayload;
    }

    const message = "Данные одновременно изменялись в другой вкладке. Повторите сохранение.";
    console.error("Voyager snapshot sync failed:", message);
    setSyncError(message);
    return { saved: false, payload: nextPayload };
  };

  function scheduleSnapshotRetry(
    client: NonNullable<typeof supabase>,
    companyId: string,
    userId: string,
  ) {
    if (snapshotRetryTimer.current !== null) return;
    const delay = Math.min(
      30000,
      3000 * 2 ** Math.min(snapshotRetryAttempt.current, 3),
    );
    snapshotRetryAttempt.current += 1;
    snapshotRetryTimer.current = window.setTimeout(() => {
      snapshotRetryTimer.current = null;
      if (!pendingSnapshot.current || !navigator.onLine) return;
      setSync("saving");
      void flushSnapshotQueue(client, companyId, userId);
    }, delay);
  }

  async function flushSnapshotQueue(
    client: NonNullable<typeof supabase>,
    companyId: string,
    userId: string,
  ) {
    if (snapshotSaveRunning.current) return;
    snapshotSaveRunning.current = true;
    let failed = false;
    let blockedByConflict = false;

    while (pendingSnapshot.current) {
      const requestedPayload = pendingSnapshot.current;
      pendingSnapshot.current = null;
      const result = await saveCompanySnapshot(
        client,
        companyId,
        requestedPayload,
      );
      if (!result.saved) {
        failed = true;
        blockedByConflict = Boolean(result.conflict);
        pendingSnapshot.current = pendingSnapshot.current
          ? mergeSnapshotData(
              requestedPayload,
              pendingSnapshot.current,
              result.payload,
            )
          : result.payload;
        persistPendingSnapshot(companyId, userId, pendingSnapshot.current);
        break;
      }

      if (!sameSnapshotValue(result.payload, requestedPayload)) {
        if (pendingSnapshot.current) {
          pendingSnapshot.current = mergeSnapshotData(
            requestedPayload,
            pendingSnapshot.current,
            result.payload,
          );
        }
        setData((current) => {
          if (!current) return current;
          if (sameSnapshotValue(current, requestedPayload)) {
            skipSave.current = true;
            return result.payload;
          }
          return mergeSnapshotData(requestedPayload, current, result.payload);
        });
      }
    }

    snapshotSaveRunning.current = false;
    if (!failed && !pendingSnapshot.current) {
      if (snapshotRetryTimer.current !== null) {
        window.clearTimeout(snapshotRetryTimer.current);
        snapshotRetryTimer.current = null;
      }
      snapshotRetryAttempt.current = 0;
      clearPendingSnapshot(companyId, userId);
    } else if (failed && navigator.onLine && !blockedByConflict) {
      scheduleSnapshotRetry(client, companyId, userId);
    }
    setSync(
      failed
        ? navigator.onLine
          ? "error"
          : "offline"
        : pendingSnapshot.current
          ? "saving"
          : "saved",
    );
  }

  const refreshMembers = async (companyId: string) => {
    if (!supabase) return;
    const { data: result, error } = await supabase.rpc(
      "voyager_list_company_members",
      { target_company_id: companyId },
    );
    if (!error) setMembers((result || []) as CompanyMember[]);
  };
  const refreshTasks = async (companyId: string) => {
    if (!supabase) return;
    const { data: result, error } = await supabase
      .from("voyager_tasks")
      .select("*")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });
    if (!error) setCompanyTasks((result || []) as CompanyTask[]);
  };
  const refreshPersonalFinance = async (companyId: string) => {
    if (!supabase) return;
    const { data: result, error } = await supabase
      .from("voyager_personal_finance_items")
      .select("*")
      .eq("company_id", companyId)
      .order("created_at", { ascending: true });
    if (!error)
      setPersonalFinanceItems((result || []) as PersonalFinanceItem[]);
  };
  const refreshInvestorReports = async (companyId: string) => {
    if (!supabase) return;
    const { data: result, error } = await supabase
      .from("voyager_investor_reports")
      .select("*")
      .eq("company_id", companyId)
      .order("period_month", { ascending: false });
    if (!error) setInvestorReports((result || []) as InvestorReport[]);
  };
  const refreshInvestorPortalAccounts = async (companyId: string) => {
    if (!supabase) return;
    const { data: result, error } = await supabase.rpc(
      "voyager_list_investor_portal_accounts",
      { target_company_id: companyId },
    );
    if (!error)
      setInvestorPortalAccounts((result || []) as InvestorPortalAccount[]);
  };
  const refreshUserPreferences = async (userId: string) => {
    if (!supabase) return;
    const { data: result, error } = await supabase
      .from("voyager_user_preferences")
      .select("favorite_pages,navigation_order")
      .eq("user_id", userId)
      .maybeSingle();
    if (!error) {
      setFavoritePages(normaliseFavoritePages(result?.favorite_pages));
      const savedOrder = result?.navigation_order;
      setHasCustomNavigationOrder(Array.isArray(savedOrder) && savedOrder.length > 0);
      setNavigationOrder(normaliseNavigationOrder(savedOrder));
    }
  };

  const toggleFavoritePage = async (targetPage: Page) => {
    if (!supabase || !session || targetPage === "dashboard") return;
    const previous = favoritePages;
    const next = previous.includes(targetPage)
      ? previous.filter((item) => item !== targetPage)
      : [...previous, targetPage];
    setFavoritePages(next);
    const { error } = await supabase.from("voyager_user_preferences").upsert({
      user_id: session.user.id,
      favorite_pages: next,
      updated_at: new Date().toISOString(),
    });
    if (error) {
      setFavoritePages(previous);
      alert(`Не удалось синхронизировать избранное: ${error.message}`);
    }
  };

  const saveNavigationOrder = async (nextOrder: Page[]) => {
    if (!supabase || !session) return;
    const previous = navigationOrder;
    const previousCustomState = hasCustomNavigationOrder;
    const normalised = normaliseNavigationOrder(nextOrder);
    setNavigationOrder(normalised);
    setHasCustomNavigationOrder(true);
    const { error } = await supabase.from("voyager_user_preferences").upsert({
      user_id: session.user.id,
      navigation_order: normalised,
      updated_at: new Date().toISOString(),
    });
    if (error) {
      setNavigationOrder(previous);
      setHasCustomNavigationOrder(previousCustomState);
      alert(`Не удалось сохранить порядок разделов: ${error.message}`);
    }
  };

  useEffect(() => {
    if (!supabase) {
      setAuthReady(true);
      return;
    }
    supabase.auth.getSession().then(({ data: result }) => {
      setSession(result.session);
      setAuthReady(true);
    });
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, next) => {
        setSession(next);
        if (!next) {
          setData(null);
          setCompany(null);
          setMembers([]);
          setCompanyTasks([]);
          setPersonalFinanceItems([]);
          setInvestorReports([]);
          setInvestorPortalAccounts([]);
          setInvestorPortal(null);
          setFavoritePages([]);
          setAccessState("loading");
          loadedWorkspace.current = null;
        }
      },
    );
    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!supabase || !session || loadedWorkspace.current) return;
    const client = supabase;
    let active = true;
    let workspaceRetryTimer: number | null = null;
    const loadStartedAt = performance.now();
    const cached = readLatestSnapshotCache(session.user.id);
    setSync("loading");
    setAccessState("loading");
    setCloudLoadMs(null);
    setCacheShownMs(null);
    void refreshUserPreferences(session.user.id);
    if (cached) {
      loadedWorkspace.current = cached.company.id;
      snapshotRevision.current = cached.revision;
      snapshotBase.current = cached.payload;
      setCompany(cached.company);
      setData(cached.payload);
      setLastCloudSyncAt(cached.savedAt);
      setCacheShownMs(Math.round(performance.now() - loadStartedAt));
      setAccessState("ready");
      setCloudConfirmed(false);
      setSync(navigator.onLine ? "loading" : "offline");
    }
    const scheduleWorkspaceRetry = () => {
      if (workspaceRetryTimer !== null || !navigator.onLine) return;
      workspaceRetryTimer = window.setTimeout(() => {
        workspaceRetryTimer = null;
        if (!active) return;
        void loadWorkspace();
      }, 15000);
    };
    const loadWorkspace = async () => {
      let membershipResult;
      try {
        membershipResult = await withCloudTimeout(
          client
            .from("voyager_company_members")
            .select("company_id, role, status, created_at")
            .eq("user_id", session.user.id)
            .eq("status", "active")
            .order("created_at", { ascending: true }),
          "Проверка доступа",
        );
      } catch (error) {
        if (!active) return;
        const message = error instanceof Error ? error.message : String(error);
        setSyncError(
          cached
            ? `${message}. Можно создавать новые записи; изменение и удаление существующих временно защищено.`
            : message,
        );
        setCloudConfirmed(false);
        setSync(navigator.onLine ? "error" : "offline");
        scheduleWorkspaceRetry();
        return;
      }
      const { data: memberships, error: membershipError } = membershipResult;
      if (!active) return;
      if (membershipError) {
        setSyncError(
          cached
            ? `Облако временно недоступно: ${membershipError.message}. Показана последняя подтверждённая копия.`
            : membershipError.message,
        );
        setCloudConfirmed(false);
        setSync("error");
        scheduleWorkspaceRetry();
        return;
      }

      let membership = memberships?.find((item) => item.role !== "owner");
      if (!membership) membership = memberships?.[0];

      if (!membership) {
        const { data: portalRows, error: portalError } = await client
          .from("voyager_investor_portal_snapshots")
          .select(
            "company_id, company_name, investor_id, user_id, payload, revision, updated_at",
          )
          .eq("user_id", session.user.id)
          .order("updated_at", { ascending: false })
          .limit(1);
        if (!active) return;
        if (portalError) {
          setSyncError(portalError.message);
          setSync("error");
          return;
        }
        const portalRow = portalRows?.[0];
        if (portalRow) {
          loadedWorkspace.current = `investor:${portalRow.company_id}:${portalRow.investor_id}`;
          setInvestorPortal({
            companyId: portalRow.company_id,
            companyName: portalRow.company_name,
            investorId: portalRow.investor_id,
            userId: portalRow.user_id,
            revision: Number(portalRow.revision || 0),
            updatedAt: portalRow.updated_at,
            data: normaliseData(portalRow.payload),
          });
          setCompany(null);
          setData(null);
          setLastCloudSyncAt(portalRow.updated_at);
          setAccessState("ready");
          setSync("saved");
          setSyncError("");
          return;
        }

        const { data: legacy, error: legacyError } = await client
          .from("voyager_snapshots")
          .select("payload")
          .eq("user_id", session.user.id)
          .maybeSingle();
        if (!active) return;
        if (legacyError) {
          setSyncError(legacyError.message);
          setSync("error");
          return;
        }
        if (!legacy || !hasMeaningfulData(legacy.payload)) {
          setAccessState("waiting");
          setSync("saved");
          setCompany(null);
          setData(null);
          return;
        }
        const { data: companyId, error: bootstrapError } = await client.rpc(
          "bootstrap_voyager_company",
          { company_name: "Voyager" },
        );
        if (bootstrapError || !companyId) {
          setSyncError(bootstrapError?.message || "Не удалось создать компанию");
          setSync("error");
          return;
        }
        membership = {
          company_id: companyId as string,
          role: "owner",
          status: "active",
          created_at: new Date().toISOString(),
        };
      }

      if (cached && cached.company.id === membership.company_id) {
        setCompany(cached.company);
        setData(cached.payload);
        setLastCloudSyncAt(cached.savedAt);
        setCacheShownMs(Math.round(performance.now() - loadStartedAt));
        setAccessState("ready");
      }

      let companyLoadResult;
      try {
        companyLoadResult = await withCloudTimeout(
          Promise.all([
            Promise.resolve(
              client
                .from("voyager_companies")
                .select("id, name")
                .eq("id", membership.company_id)
                .single(),
            ),
            Promise.resolve(
              client
                .from("voyager_company_snapshots")
                .select("payload, revision, updated_at")
                .eq("company_id", membership.company_id)
                .maybeSingle(),
            ),
          ]),
          "Загрузка данных компании",
        );
      } catch (error) {
        if (!active) return;
        const message = error instanceof Error ? error.message : String(error);
        setSyncError(
          cached
            ? `${message}. Показана последняя подтверждённая копия.`
            : message,
        );
        setCloudConfirmed(false);
        setSync(navigator.onLine ? "error" : "offline");
        scheduleWorkspaceRetry();
        return;
      }
      const [{ data: companyRow, error: companyError }, snapshotResult] =
        companyLoadResult;
      if (!active) return;
      if (companyError || snapshotResult.error || !companyRow) {
        setSyncError(
          companyError?.message ||
            snapshotResult.error?.message ||
            "Не удалось загрузить данные компании",
        );
        setCloudConfirmed(false);
        setSync("error");
        scheduleWorkspaceRetry();
        return;
      }
      const remoteInitial = snapshotResult.data
        ? normaliseData(snapshotResult.data.payload)
        : emptyData();
      const remoteRevision = snapshotResult.data
        ? Number(snapshotResult.data.revision)
        : null;
      snapshotRevision.current = remoteRevision;
      snapshotBase.current = remoteInitial;
      setLastCloudSyncAt(
        snapshotResult.data?.updated_at || new Date().toISOString(),
      );
      setCloudLoadMs(Math.round(performance.now() - loadStartedAt));
      setCloudConfirmed(true);
      if (workspaceRetryTimer !== null) {
        window.clearTimeout(workspaceRetryTimer);
        workspaceRetryTimer = null;
      }
      let initial = remoteInitial;
      let recoveredPending = false;
      const recovered =
        membership.role === "viewer"
          ? null
          : readSnapshotOutbox(membership.company_id, session.user.id);
      if (recovered) {
        initial = mergeSnapshotData(
          recovered.basePayload,
          recovered.payload,
          remoteInitial,
        );
        pendingSnapshot.current = initial;
        pendingSnapshotBase.current = remoteInitial;
        recoveredPending = true;
        setHasPendingChanges(true);
        try {
          writeSnapshotOutbox({
            version: 1,
            companyId: membership.company_id,
            userId: session.user.id,
            baseRevision: remoteRevision,
            basePayload: remoteInitial,
            payload: initial,
            updatedAt: new Date().toISOString(),
          });
        } catch (error) {
          console.error("Voyager local recovery refresh failed:", error);
        }
      } else {
        setHasPendingChanges(false);
      }
      skipSave.current = !recoveredPending;
      loadedWorkspace.current = membership.company_id;
      const nextCompany: CompanyContext = {
        id: membership.company_id,
        name: companyRow.name,
        role: membership.role as CompanyRole,
      };
      setCompany(nextCompany);
      setInvestorPortal(null);
      setData(initial);
      setAccessState("ready");
      setSyncError("");
      setSync(
        recoveredPending
          ? navigator.onLine
            ? "saving"
            : "offline"
          : navigator.onLine
            ? "saved"
            : "offline",
      );
      void refreshMembers(nextCompany.id);
      void refreshTasks(nextCompany.id);
      void refreshPersonalFinance(nextCompany.id);
      void refreshInvestorReports(nextCompany.id);
      if (nextCompany.role === "owner")
        void refreshInvestorPortalAccounts(nextCompany.id);
    };
    void loadWorkspace();
    return () => {
      active = false;
      if (workspaceRetryTimer !== null)
        window.clearTimeout(workspaceRetryTimer);
    };
  }, [session]);

  useEffect(() => {
    if (!supabase || !session) return;
    const client = supabase;
    const channel = client
      .channel(`voyager-user-preferences-${session.user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "voyager_user_preferences",
          filter: `user_id=eq.${session.user.id}`,
        },
        (payload) => {
          if (payload.eventType === "DELETE") {
            setFavoritePages([]);
            setNavigationOrder(NAV_PAGE_IDS);
            setHasCustomNavigationOrder(false);
            return;
          }
          const row = payload.new as {
            favorite_pages?: unknown;
            navigation_order?: unknown;
          };
          setFavoritePages(normaliseFavoritePages(row.favorite_pages));
          setHasCustomNavigationOrder(
            Array.isArray(row.navigation_order) && row.navigation_order.length > 0,
          );
          setNavigationOrder(normaliseNavigationOrder(row.navigation_order));
        },
      )
      .subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [session?.user.id]);

  useEffect(() => {
    if (!supabase || !company || loadedWorkspace.current !== company.id) return;
    const client = supabase;
    const channel = client
      .channel(`voyager-company-${company.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "voyager_company_snapshots",
          filter: `company_id=eq.${company.id}`,
        },
        (payload) => {
          const row = payload.new as {
            payload?: unknown;
            revision?: number;
            updated_at?: string;
          };
          const nextRevision = Number(row.revision || 0);
          if (
            snapshotRevision.current !== null &&
            nextRevision <= snapshotRevision.current
          )
            return;
          const next = normaliseData(row.payload);
          setLastCloudSyncAt(row.updated_at || new Date().toISOString());
          setData((current) => {
            const previousBase = snapshotBase.current;
            snapshotRevision.current = nextRevision;
            snapshotBase.current = next;
            if (!current || !previousBase) {
              skipSave.current = true;
              return next;
            }
            if (sameSnapshotValue(current, next)) return current;
            if (sameSnapshotValue(current, previousBase)) {
              skipSave.current = true;
              return next;
            }
            return mergeSnapshotData(previousBase, current, next);
          });
        },
      )
      .subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [company]);

  useEffect(() => {
    if (!supabase || !session || !investorPortal) return;
    const client = supabase;
    const channel = client
      .channel(
        `voyager-investor-portal-${investorPortal.companyId}-${investorPortal.investorId}`,
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "voyager_investor_portal_snapshots",
          filter: `user_id=eq.${session.user.id}`,
        },
        (payload) => {
          if (payload.eventType === "DELETE") {
            setInvestorPortal(null);
            setAccessState("waiting");
            return;
          }
          const row = payload.new as {
            company_id?: string;
            company_name?: string;
            investor_id?: string;
            user_id?: string;
            payload?: unknown;
            revision?: number;
            updated_at?: string;
          };
          if (
            row.company_id !== investorPortal.companyId ||
            row.investor_id !== investorPortal.investorId
          )
            return;
          setInvestorPortal((current) =>
            current
              ? {
                  ...current,
                  companyName: row.company_name || current.companyName,
                  revision: Number(row.revision || current.revision),
                  updatedAt: row.updated_at || new Date().toISOString(),
                  data: normaliseData(row.payload),
                }
              : current,
          );
          setLastCloudSyncAt(row.updated_at || new Date().toISOString());
          setSync("saved");
        },
      )
      .subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [
    investorPortal?.companyId,
    investorPortal?.investorId,
    session?.user.id,
  ]);

  useEffect(() => {
    if (!supabase || !session || !investorPortal) return;
    const client = supabase;
    const verifyAccess = async () => {
      const { data: row, error } = await client
        .from("voyager_investor_portal_snapshots")
        .select(
          "company_id, company_name, investor_id, user_id, payload, revision, updated_at",
        )
        .eq("company_id", investorPortal.companyId)
        .eq("investor_id", investorPortal.investorId)
        .eq("user_id", session.user.id)
        .maybeSingle();
      if (error) return;
      if (!row) {
        setInvestorPortal(null);
        setAccessState("waiting");
        return;
      }
      if (Number(row.revision || 0) <= investorPortal.revision) return;
      setInvestorPortal({
        companyId: row.company_id,
        companyName: row.company_name,
        investorId: row.investor_id,
        userId: row.user_id,
        revision: Number(row.revision || 0),
        updatedAt: row.updated_at,
        data: normaliseData(row.payload),
      });
      setLastCloudSyncAt(row.updated_at);
    };
    const timer = window.setInterval(() => void verifyAccess(), 45000);
    return () => window.clearInterval(timer);
  }, [
    investorPortal?.companyId,
    investorPortal?.investorId,
    investorPortal?.revision,
    session?.user.id,
  ]);

  useEffect(() => {
    if (!supabase || !company) return;
    const client = supabase;
    const channel = client
      .channel(`voyager-investor-reports-${company.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "voyager_investor_reports",
          filter: `company_id=eq.${company.id}`,
        },
        () => void refreshInvestorReports(company.id),
      )
      .subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [company]);

  useEffect(() => {
    if (!supabase || !company) return;
    const client = supabase;
    const channel = client
      .channel(`voyager-personal-finance-${company.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "voyager_personal_finance_items",
          filter: `company_id=eq.${company.id}`,
        },
        () => void refreshPersonalFinance(company.id),
      )
      .subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [company]);

  useEffect(() => {
    if (!supabase || !company) return;
    const client = supabase;
    const channel = client
      .channel(`voyager-tasks-${company.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "voyager_tasks",
          filter: `company_id=eq.${company.id}`,
        },
        () => void refreshTasks(company.id),
      )
      .subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [company]);

  useEffect(() => {
    if (
      !data ||
      !company ||
      !supabase ||
      !session ||
      loadedWorkspace.current !== company.id ||
      company.role === "viewer"
    )
      return;
    const client = supabase;
    if (skipSave.current) {
      skipSave.current = false;
      return;
    }
    if (
      snapshotBase.current &&
      sameSnapshotValue(data, snapshotBase.current) &&
      !snapshotSaveRunning.current
    ) {
      clearPendingSnapshot(company.id, session.user.id);
      setSync("saved");
      return;
    }
    pendingSnapshot.current = data;
    pendingSnapshotBase.current ||= snapshotBase.current;
    persistPendingSnapshot(company.id, session.user.id, data);
    setSync(navigator.onLine ? "saving" : "offline");
    if (!navigator.onLine) return;
    const timer = window.setTimeout(() => {
      void flushSnapshotQueue(client, company.id, session.user.id);
    }, 650);
    return () => window.clearTimeout(timer);
  }, [data, company, session?.user.id]);

  useEffect(() => {
    if (
      !data ||
      !company ||
      !session ||
      loadedWorkspace.current !== company.id ||
      snapshotRevision.current === null ||
      !snapshotBase.current ||
      hasPendingChanges ||
      !sameSnapshotValue(data, snapshotBase.current)
    )
      return;
    try {
      writeSnapshotCache({
        version: 1,
        company,
        userId: session.user.id,
        revision: snapshotRevision.current,
        payload: data,
        savedAt: lastCloudSyncAt || new Date().toISOString(),
      });
    } catch (error) {
      console.error("Voyager confirmed cache write failed:", error);
    }
  }, [data, company, session?.user.id, hasPendingChanges, lastCloudSyncAt]);

  useEffect(() => {
    const online = async () => {
      if (
        !data ||
        !company ||
        !supabase ||
        !session ||
        loadedWorkspace.current !== company.id
      ) {
        setSync("loading");
        return;
      }
      if (company.role === "viewer") {
        setSync("saved");
        return;
      }
      setSync("saving");
      pendingSnapshot.current = data;
      pendingSnapshotBase.current ||= snapshotBase.current;
      persistPendingSnapshot(company.id, session.user.id, data);
      await flushSnapshotQueue(supabase, company.id, session.user.id);
    };
    const offline = () => {
      setCloudConfirmed(false);
      setSync("offline");
    };
    addEventListener("online", online);
    addEventListener("offline", offline);
    return () => {
      removeEventListener("online", online);
      removeEventListener("offline", offline);
    };
  }, [data, company, session?.user.id]);

  useEffect(() => {
    const warnBeforeClose = (event: BeforeUnloadEvent) => {
      if (!hasPendingChanges) return;
      event.preventDefault();
      event.returnValue = "";
    };
    addEventListener("beforeunload", warnBeforeClose);
    return () => removeEventListener("beforeunload", warnBeforeClose);
  }, [hasPendingChanges]);

  useEffect(
    () => () => {
      if (snapshotRetryTimer.current !== null) {
        window.clearTimeout(snapshotRetryTimer.current);
      }
    },
    [],
  );

  const updateData = (updater: (current: VoyagerData) => VoyagerData) => {
    if (company?.role === "viewer") {
      alert("У вас доступ только для просмотра");
      return;
    }
    if (!company || loadedWorkspace.current !== company.id) {
      alert("Дождитесь подтверждения подключения к облаку перед изменением данных");
      return;
    }
    setData((current) => {
      if (!current) return current;
      const next = { ...updater(current), updatedAt: new Date().toISOString() };
      if (!cloudConfirmed && !isAppendOnlySnapshotChange(current, next)) {
        alert(
          "Облако сейчас не подтвердило соединение. Чтобы изменения сотрудников не перезаписали друг друга, пока можно только создавать новые записи. Изменение и удаление существующих данных станет доступно после восстановления связи.",
        );
        return current;
      }
      return next;
    });
  };

  if (!cloudConfigured) return <SetupScreen />;
  if (!authReady) return <FullLoader label="Проверяем подключение…" />;
  if (!session) return <AuthScreen />;
  if (investorPortal)
    return (
      <InvestorPortalApp
        portal={investorPortal}
        email={session.user.email || ""}
        onLogout={() => supabase!.auth.signOut()}
      />
    );
  if (accessState === "waiting")
    return (
      <WaitingAccess
        email={session.user.email || ""}
        onLogout={() => supabase!.auth.signOut()}
      />
    );
  if (!data || !company)
    return <FullLoader label="Загружаем пространство компании…" />;

  const selectedVehicle = data.vehicles.find((v) => v.id === selectedVehicleId);
  const selectedBicycle = data.bicycles.find((b) => b.id === selectedBicycleId);
  const selectedRentalCar = data.rentalCars.find(
    (c) => c.id === selectedRentalCarId,
  );
  const selectedInvestor = data.investors.find(
    (i) => i.id === selectedInvestorId,
  );
  const currentMember = members.find(
    (member) => member.user_id === session.user.id,
  );
  const currentUserName =
    currentMember?.display_name?.trim() || session.user.email || "Сотрудник";
  return (
    <div className={`app-shell ${company.role === "viewer" ? "read-only" : ""}`}>
      <Sidebar
        key={session.user.id}
        page={page}
        role={company.role}
        favoritePages={favoritePages}
        navigationOrder={navigationOrder}
        hasCustomNavigationOrder={hasCustomNavigationOrder}
        onToggleFavorite={(targetPage) => void toggleFavoritePage(targetPage)}
        onSaveNavigationOrder={(nextOrder) => void saveNavigationOrder(nextOrder)}
        onPage={(next) => {
          setPage(next);
          setSelectedVehicleId(null);
          setSelectedBicycleId(null);
          setSelectedRentalCarId(null);
          setSelectedInvestorId(null);
        }}
      />
      <main className="main-area">
        <Topbar
          sync={sync}
          syncError={syncError}
          hasPendingChanges={hasPendingChanges}
          cloudConfirmed={cloudConfirmed}
          email={session.user.email || ""}
          displayName={currentUserName}
          companyName={company.name}
          role={company.role}
          onLogout={() => {
            if (
              hasPendingChanges &&
              !confirm(
                "Есть изменения, которые ещё не отправлены в облако. Выйти всё равно? Локальная резервная копия останется на этом устройстве.",
              )
            )
              return;
            void supabase!.auth.signOut();
          }}
        />
        {selectedVehicle ? (
          <VehicleDetails
            vehicle={selectedVehicle}
            data={data}
            updateData={updateData}
            onBack={() => setSelectedVehicleId(null)}
            userId={company.id}
            onOpenRecommendations={() => {
              setSelectedVehicleId(null);
              setPage("repair-recommendations");
            }}
          />
        ) : selectedBicycle ? (
          <BicycleDetails
            bicycle={selectedBicycle}
            data={data}
            updateData={updateData}
            onBack={() => setSelectedBicycleId(null)}
            userId={company.id}
          />
        ) : selectedRentalCar ? (
          <RentalCarDetails
            car={selectedRentalCar}
            data={data}
            updateData={updateData}
            onBack={() => setSelectedRentalCarId(null)}
            userId={company.id}
          />
        ) : selectedInvestor ? (
          <InvestorDetails
            investor={selectedInvestor}
            data={data}
            period={investorPeriod}
            onPeriodChange={setInvestorPeriod}
            updateData={updateData}
            onBack={() => setSelectedInvestorId(null)}
            onOpenVehicle={setSelectedVehicleId}
            company={company}
            portalAccount={investorPortalAccounts.find(
              (account) => account.investor_id === selectedInvestor.id,
            )}
            onPortalAccessChange={() =>
              refreshInvestorPortalAccounts(company.id)
            }
          />
        ) : page === "dashboard" ? (
          <Dashboard
            data={data}
            role={company.role}
            tasks={companyTasks}
            onOpenVehicle={setSelectedVehicleId}
            onOpenBicycle={setSelectedBicycleId}
            onOpenRentalCar={setSelectedRentalCarId}
            onOpenAttention={() => setPage("attention")}
            onOpenFleet={() => setPage("fleet")}
            onOpenTasks={() => setPage("tasks")}
            onNavigate={setPage}
            updateData={updateData}
          />
        ) : page === "attention" ? (
          <AttentionPage
            data={data}
            tasks={companyTasks}
            onBack={() => setPage("dashboard")}
            onOpenVehicle={setSelectedVehicleId}
            onOpenBicycle={setSelectedBicycleId}
            onOpenRentalCar={setSelectedRentalCarId}
            onOpenFleet={() => setPage("fleet")}
            onOpenTasks={() => setPage("tasks")}
          />
        ) : page === "fleet" ? (
          <Fleet
            data={data}
            onOpenVehicle={setSelectedVehicleId}
            updateData={updateData}
          />
        ) : page === "expenses" ? (
          <FinanceLedger
            mode="expenses"
            data={data}
            updateData={updateData}
            onOpenVehicle={setSelectedVehicleId}
          />
        ) : page === "income" ? (
          <FinanceLedger
            mode="income"
            data={data}
            updateData={updateData}
            onOpenVehicle={setSelectedVehicleId}
          />
        ) : page === "company-income" ? (
          <CompanyIncomePage
            data={data}
            onOpenVehicle={setSelectedVehicleId}
            onOpenBicycle={setSelectedBicycleId}
            onOpenRentalCar={setSelectedRentalCarId}
          />
        ) : page === "nikita-money" ? (
          <NikitaMoneyPage
            data={data}
            updateData={updateData}
            canEdit={company.role !== "viewer"}
          />
        ) : page === "warehouse" ? (
          <WarehousePage
            data={data}
            updateData={updateData}
            canEdit={company.role !== "viewer"}
            cloudConfirmed={cloudConfirmed}
            actor={{ id: session.user.id, name: currentUserName }}
          />
        ) : page === "downtime-table" ? (
          <DowntimeTablePage
            data={data}
            updateData={updateData}
            canEdit={company.role !== "viewer"}
          />
        ) : page === "repair-recommendations" ? (
          <RepairRecommendationsPage
            data={data}
            updateData={updateData}
            canEdit={company.role !== "viewer"}
            onOpenVehicle={setSelectedVehicleId}
          />
        ) : page === "accidents" ? (
          <AccidentRegistry
            data={data}
            updateData={updateData}
            canEdit={company.role !== "viewer"}
            userId={company.id}
          />
        ) : page === "inspections" ? (
          <VehicleInspectionsPage
            data={data}
            updateData={updateData}
            canEdit={company.role !== "viewer"}
            userId={company.id}
          />
        ) : page === "bicycle-inspections" ? (
          <BicycleInspectionsPage
            data={data}
            updateData={updateData}
            canEdit={company.role !== "viewer"}
            userId={company.id}
            inspectorName={currentUserName}
          />
        ) : page === "personal-profit" ? (
          <PersonalProfitPage
            company={company}
            items={personalFinanceItems}
            currentUserId={session.user.id}
            onRefresh={() => refreshPersonalFinance(company.id)}
          />
        ) : page === "payments" ? (
          <PaymentCalendar
            data={data}
            updateData={updateData}
            canEdit={company.role !== "viewer"}
          />
        ) : page === "tasks" ? (
          <TasksPage
            company={company}
            tasks={companyTasks}
            members={members}
            currentUserId={session.user.id}
            onRefresh={() => refreshTasks(company.id)}
          />
        ) : page === "bicycles" ? (
          <Bicycles
            data={data}
            canManagePrices={mayManageBicyclePrices(company.role)}
            cloudConfirmed={cloudConfirmed}
            onOpenBicycle={setSelectedBicycleId}
            updateData={updateData}
          />
        ) : page === "rentals" ? (
          <RentalCars
            data={data}
            onOpenCar={setSelectedRentalCarId}
            updateData={updateData}
          />
        ) : page === "investors" ? (
          <Investors
            data={data}
            period={investorPeriod}
            onPeriodChange={setInvestorPeriod}
            updateData={updateData}
            onOpenInvestor={setSelectedInvestorId}
          />
        ) : page === "investor-reports" ? (
          <InvestorReportsPage
            company={company}
            data={data}
            reports={investorReports}
            currentUserId={session.user.id}
            onRefresh={() => refreshInvestorReports(company.id)}
          />
        ) : page === "documents" ? (
          <Documents
            data={data}
            onOpenVehicle={setSelectedVehicleId}
            userId={company.id}
            updateData={updateData}
          />
        ) : page === "reference" ? (
          <ReferenceDirectory data={data} updateData={updateData} />
        ) : (
          <Settings
            data={data}
            updateData={updateData}
            email={session.user.email || ""}
            currentUserName={currentUserName}
            company={company}
            members={members}
            sync={sync}
            syncError={syncError}
            hasPendingChanges={hasPendingChanges}
            lastCloudSyncAt={lastCloudSyncAt}
            cloudLoadMs={cloudLoadMs}
            cacheShownMs={cacheShownMs}
            snapshotRevision={snapshotRevision.current}
            onRefreshMembers={() => refreshMembers(company.id)}
          />
        )}
      </main>
    </div>
  );
}

function Sidebar({
  page,
  onPage,
  role,
  favoritePages,
  navigationOrder,
  hasCustomNavigationOrder,
  onToggleFavorite,
  onSaveNavigationOrder,
}: {
  page: Page;
  onPage: (page: Page) => void;
  role: CompanyRole;
  favoritePages: Page[];
  navigationOrder: Page[];
  hasCustomNavigationOrder: boolean;
  onToggleFavorite: (page: Page) => void;
  onSaveNavigationOrder: (order: Page[]) => void;
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [editingNavigation, setEditingNavigation] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<NavGroupId[]>(() =>
    NAV_GROUPS.map((group) => group.id),
  );
  const availableNav = NAV_ITEMS.filter(
    (item) => !item.managementOnly || role === "owner" || role === "admin",
  );
  const favoriteSet = new Set(favoritePages);
  const defaultOrderIndex = new Map(NAV_PAGE_IDS.map((item, index) => [item, index]));
  const orderIndex = new Map(
    normaliseNavigationOrder(navigationOrder).map((item, index) => [item, index]),
  );
  const nav = [...availableNav].sort(
    (left, right) =>
      (hasCustomNavigationOrder ? orderIndex : defaultOrderIndex).get(left.id)! -
      (hasCustomNavigationOrder ? orderIndex : defaultOrderIndex).get(right.id)!,
  );
  const groups = NAV_GROUPS
    .filter((group) => nav.some((item) => item.group === group.id))
    .sort((left, right) => {
      if (!hasCustomNavigationOrder) {
        return NAV_GROUPS.findIndex((group) => group.id === left.id) -
          NAV_GROUPS.findIndex((group) => group.id === right.id);
      }
      const firstPosition = (groupId: NavGroupId) => Math.min(
        ...nav.filter((item) => item.group === groupId).map((item) => orderIndex.get(item.id) ?? 999),
      );
      return firstPosition(left.id) - firstPosition(right.id);
    });
  const groupedNav = groups.map((group) => ({
    ...group,
    items: nav.filter((item) => item.group === group.id),
  }));
  const favoriteNav = nav.filter((item) => favoriteSet.has(item.id));
  const activeItem = nav.find((item) => item.id === page) || nav[0];

  const fullNavigationOrder = () => hasCustomNavigationOrder
    ? normaliseNavigationOrder(navigationOrder)
    : [...NAV_PAGE_IDS];

  const moveNavigationItem = (
    targetPage: Page,
    direction: -1 | 1,
    groupItems: typeof NAV_ITEMS,
  ) => {
    const visibleIndex = groupItems.findIndex((item) => item.id === targetPage);
    const neighbour = groupItems[visibleIndex + direction];
    if (!neighbour) return;
    const fullOrder = fullNavigationOrder();
    const targetIndex = fullOrder.indexOf(targetPage);
    const neighbourIndex = fullOrder.indexOf(neighbour.id);
    [fullOrder[targetIndex], fullOrder[neighbourIndex]] = [
      fullOrder[neighbourIndex],
      fullOrder[targetIndex],
    ];
    onSaveNavigationOrder(fullOrder);
  };

  const moveNavigationGroup = (targetGroup: NavGroupId, direction: -1 | 1) => {
    const visibleIndex = groupedNav.findIndex((group) => group.id === targetGroup);
    const neighbour = groupedNav[visibleIndex + direction];
    if (!neighbour) return;
    const fullOrder = fullNavigationOrder();
    const allGroups = [...NAV_GROUPS].sort((left, right) => {
      const firstPosition = (groupId: NavGroupId) => Math.min(
        ...NAV_ITEMS.filter((item) => item.group === groupId).map((item) => fullOrder.indexOf(item.id)),
      );
      return firstPosition(left.id) - firstPosition(right.id);
    });
    const targetIndex = allGroups.findIndex((group) => group.id === targetGroup);
    const neighbourIndex = allGroups.findIndex((group) => group.id === neighbour.id);
    [allGroups[targetIndex], allGroups[neighbourIndex]] = [
      allGroups[neighbourIndex],
      allGroups[targetIndex],
    ];
    onSaveNavigationOrder(allGroups.flatMap((group) =>
      fullOrder.filter((item) => NAV_ITEMS.find((navItem) => navItem.id === item)?.group === group.id),
    ));
  };

  const toggleGroup = (groupId: NavGroupId) => {
    if (editingNavigation) return;
    setCollapsedGroups((current) => current.includes(groupId)
      ? current.filter((item) => item !== groupId)
      : [...current, groupId]);
  };

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileMenuOpen]);

  const selectPage = (nextPage: Page) => {
    onPage(nextPage);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">V</div>
          <div className="brand-copy">
            <b>VOYAGER</b>
            <span>OPERATING SYSTEM</span>
          </div>
          <button
            type="button"
            className={`sidebar-edit-toggle ${editingNavigation ? "active" : ""}`}
            aria-label={editingNavigation ? "Завершить настройку меню" : "Изменить порядок разделов"}
            title={editingNavigation ? "Готово" : "Изменить порядок разделов"}
            onClick={() => setEditingNavigation((value) => !value)}
          >
            {editingNavigation ? "✓" : "✎"}
          </button>
        </div>
        <nav className="desktop-sidebar-nav" aria-label="Разделы Voyager">
          {!editingNavigation && favoriteNav.length > 0 && (
            <section className="sidebar-favorites">
              <div className="sidebar-section-caption"><span>★</span><b>Избранное</b></div>
              {favoriteNav.map((item) => (
                <div className={`sidebar-nav-row compact ${page === item.id ? "active" : ""}`} key={`favorite-${item.id}`}>
                  <button type="button" className="sidebar-nav-link" onClick={() => selectPage(item.id)}>
                    <span className="sidebar-nav-icon">{item.icon}</span>
                    <span className="sidebar-nav-label">{item.label}</span>
                  </button>
                  <button
                    type="button"
                    className="sidebar-favorite-toggle selected"
                    aria-label={`Убрать «${item.label}» из избранного`}
                    title="Убрать из избранного"
                    onClick={() => onToggleFavorite(item.id)}
                  >★</button>
                </div>
              ))}
            </section>
          )}
          <div className="sidebar-groups">
            {groupedNav.map((group, groupIndex) => {
              const collapsed = collapsedGroups.includes(group.id) && !editingNavigation;
              const groupActive = group.items.some((item) => item.id === page);
              return (
                <section className={`sidebar-group ${groupActive ? "active" : ""} ${editingNavigation ? "editing" : ""}`} key={group.id}>
                  <div className="sidebar-group-heading">
                    <button
                      type="button"
                      className="sidebar-group-toggle"
                      aria-expanded={!collapsed}
                      onClick={() => toggleGroup(group.id)}
                    >
                      <span className="sidebar-group-icon">{group.icon}</span>
                      <b>{group.label}</b>
                      <small>{group.items.length}</small>
                      <span className="sidebar-group-arrow">{collapsed ? "⌄" : "⌃"}</span>
                    </button>
                    {editingNavigation && (
                      <span className="sidebar-group-reorder">
                        <button type="button" disabled={groupIndex === 0} onClick={() => moveNavigationGroup(group.id, -1)} aria-label={`Переместить папку «${group.label}» выше`}>↑</button>
                        <button type="button" disabled={groupIndex === groupedNav.length - 1} onClick={() => moveNavigationGroup(group.id, 1)} aria-label={`Переместить папку «${group.label}» ниже`}>↓</button>
                      </span>
                    )}
                  </div>
                  {!collapsed && <div className="sidebar-group-items">
                    {group.items.map((item, itemIndex) => {
                      const favorite = favoriteSet.has(item.id);
                      return <div className={`sidebar-nav-row ${page === item.id ? "active" : ""} ${editingNavigation ? "editing" : ""}`} key={item.id}>
                        <button type="button" className="sidebar-nav-link" onClick={() => selectPage(item.id)} disabled={editingNavigation}>
                          <span className="sidebar-nav-icon">{item.icon}</span>
                          <span className="sidebar-nav-label">{item.label}</span>
                        </button>
                        {editingNavigation ? <span className="sidebar-reorder-controls">
                          <button type="button" disabled={itemIndex === 0} onClick={() => moveNavigationItem(item.id, -1, group.items)} aria-label={`Переместить «${item.label}» выше`}>↑</button>
                          <button type="button" disabled={itemIndex === group.items.length - 1} onClick={() => moveNavigationItem(item.id, 1, group.items)} aria-label={`Переместить «${item.label}» ниже`}>↓</button>
                        </span> : item.id !== "dashboard" ? <button
                          type="button"
                          className={`sidebar-favorite-toggle ${favorite ? "selected" : ""}`}
                          aria-label={favorite ? `Убрать «${item.label}» из избранного` : `Добавить «${item.label}» в избранное`}
                          title={favorite ? "Убрать из избранного" : "Добавить в избранное"}
                          onClick={() => onToggleFavorite(item.id)}
                        >{favorite ? "★" : "☆"}</button> : null}
                      </div>;
                    })}
                  </div>}
                </section>
              );
            })}
          </div>
        </nav>
        <button
          type="button"
          className="mobile-menu-trigger"
          aria-expanded={mobileMenuOpen}
          aria-controls="voyager-mobile-menu"
          onClick={() => setMobileMenuOpen(true)}
        >
          <span className="mobile-menu-trigger-icon">☰</span>
          <span className="mobile-menu-trigger-copy">
            <b>Все разделы</b>
            <small>{activeItem.label}</small>
          </span>
          <span className="mobile-menu-trigger-arrow">⌃</span>
        </button>
        <div className="sidebar-footer">
          <span className="live-dot" /> CLOUD NEXT <small>v4.32.1</small>
        </div>
      </aside>

      {mobileMenuOpen && (
        <div
          className="mobile-menu-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setMobileMenuOpen(false);
          }}
        >
          <section
            id="voyager-mobile-menu"
            className="mobile-menu-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Все разделы Voyager"
          >
            <div className="mobile-menu-head">
              <div>
                <b>Все разделы</b>
                <small>{editingNavigation ? "Настройте порядок разделов" : "Выберите нужный раздел"}</small>
              </div>
              <span className="mobile-menu-head-actions">
                <button
                  type="button"
                  className={editingNavigation ? "active" : ""}
                  aria-label={editingNavigation ? "Завершить настройку меню" : "Изменить порядок разделов"}
                  onClick={() => setEditingNavigation((value) => !value)}
                >
                  {editingNavigation ? "✓" : "✎"}
                </button>
                <button
                  type="button"
                  aria-label="Закрыть меню"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  ×
                </button>
              </span>
            </div>
            <nav className="mobile-menu-list" aria-label="Мобильная навигация">
              {!editingNavigation && favoriteNav.length > 0 && <section className="mobile-menu-group favorites">
                <div className="mobile-menu-group-title"><span>★</span><b>Избранное</b><small>{favoriteNav.length}</small></div>
                <div className="mobile-menu-group-items">
                  {favoriteNav.map((item) => <div className={`mobile-menu-row ${page === item.id ? "active" : ""}`} key={`mobile-favorite-${item.id}`}>
                    <button type="button" className="mobile-menu-link" onClick={() => selectPage(item.id)}><span>{item.icon}</span><b>{item.label}</b></button>
                    <button type="button" className="mobile-menu-favorite selected" onClick={() => onToggleFavorite(item.id)} aria-label={`Убрать «${item.label}» из избранного`}>★</button>
                  </div>)}
                </div>
              </section>}
              {groupedNav.map((group, groupIndex) => {
                const collapsed = collapsedGroups.includes(group.id) && !editingNavigation;
                const groupActive = group.items.some((item) => item.id === page);
                return <section className={`mobile-menu-group ${groupActive ? "active" : ""} ${editingNavigation ? "editing" : ""}`} key={group.id}>
                  <div className="mobile-menu-group-heading">
                    <button type="button" className="mobile-menu-group-toggle" aria-expanded={!collapsed} onClick={() => toggleGroup(group.id)}>
                      <span>{group.icon}</span><b>{group.label}</b><small>{group.items.length}</small><em>{collapsed ? "⌄" : "⌃"}</em>
                    </button>
                    {editingNavigation && <span className="mobile-menu-group-reorder">
                      <button type="button" disabled={groupIndex === 0} onClick={() => moveNavigationGroup(group.id, -1)} aria-label={`Переместить папку «${group.label}» выше`}>↑</button>
                      <button type="button" disabled={groupIndex === groupedNav.length - 1} onClick={() => moveNavigationGroup(group.id, 1)} aria-label={`Переместить папку «${group.label}» ниже`}>↓</button>
                    </span>}
                  </div>
                  {!collapsed && <div className="mobile-menu-group-items">
                    {group.items.map((item, itemIndex) => {
                      const favorite = favoriteSet.has(item.id);
                      return <div className={`mobile-menu-row ${page === item.id ? "active" : ""} ${editingNavigation ? "editing" : ""}`} key={item.id}>
                        <button type="button" className="mobile-menu-link" onClick={() => selectPage(item.id)} disabled={editingNavigation}><span>{item.icon}</span><b>{item.label}</b></button>
                        {editingNavigation ? <span className="mobile-menu-reorder">
                          <button type="button" disabled={itemIndex === 0} onClick={() => moveNavigationItem(item.id, -1, group.items)} aria-label={`Переместить «${item.label}» выше`}>↑</button>
                          <button type="button" disabled={itemIndex === group.items.length - 1} onClick={() => moveNavigationItem(item.id, 1, group.items)} aria-label={`Переместить «${item.label}» ниже`}>↓</button>
                        </span> : item.id === "dashboard" ? <span className="mobile-menu-current" aria-hidden="true">{page === item.id ? "✓" : ""}</span> : <button
                          type="button"
                          className={`mobile-menu-favorite ${favorite ? "selected" : ""}`}
                          aria-label={favorite ? `Убрать «${item.label}» из избранного` : `Добавить «${item.label}» в избранное`}
                          onClick={() => onToggleFavorite(item.id)}
                        >{favorite ? "★" : "☆"}</button>}
                      </div>;
                    })}
                  </div>}
                </section>;
              })}
            </nav>
          </section>
        </div>
      )}
    </>
  );
}

function Topbar({
  sync,
  syncError,
  hasPendingChanges,
  cloudConfirmed,
  email,
  displayName,
  companyName,
  role,
  onLogout,
}: {
  sync: SyncState;
  syncError: string;
  hasPendingChanges: boolean;
  cloudConfirmed: boolean;
  email: string;
  displayName: string;
  companyName: string;
  role: CompanyRole;
  onLogout: () => void;
}) {
  const labels: Record<SyncState, string> = {
    loading: cloudConfirmed
      ? "Проверяем облако"
      : "Работаем из сохранённой копии · проверяем облако",
    saving: hasPendingChanges ? "Сохраняем в облако" : "Проверяем сохранение",
    saved: "Все изменения сохранены",
    offline: hasPendingChanges
      ? "Нет сети · сохранено на устройстве"
      : "Нет сети",
    error: hasPendingChanges
      ? "Не сохранено в облаке"
      : "Ошибка синхронизации",
  };
  return (
    <header className="topbar">
      <div
        className={`sync sync-${sync}`}
        title={
          sync === "error"
            ? syncError || "Не удалось сохранить данные в облаке"
            : hasPendingChanges
              ? "Изменения защищены локальной копией и будут отправлены в Supabase после восстановления связи"
              : undefined
        }
      >
        <i />
        {labels[sync]}
      </div>
      <div className="account">
        <span>{displayName[0]?.toUpperCase()}</span>
        <div>
          <b>{displayName}</b>
          <small>
            {companyName} · {ROLE_LABELS[role]}
          </small>
          <button onClick={onLogout}>Выйти</button>
        </div>
      </div>
    </header>
  );
}

function WaitingAccess({
  email,
  onLogout,
}: {
  email: string;
  onLogout: () => void;
}) {
  return (
    <div className="access-screen">
      <section className="access-card">
        <div className="brand-mark">V</div>
        <span className="eyebrow">VOYAGER OS · CLOUD</span>
        <h1>Ожидается доступ к компании</h1>
        <p>
          Аккаунт <b>{email}</b> зарегистрирован. Передайте этот email владельцу
          Voyager — он добавит вас в разделе «Настройки».
        </p>
        <p className="muted">
          После добавления выйдите и войдите снова. Самостоятельно создавать
          отдельную базу не нужно.
        </p>
        <button className="secondary" onClick={onLogout}>
          Выйти
        </button>
      </section>
    </div>
  );
}

function Dashboard({
  data,
  role,
  tasks,
  onOpenVehicle,
  onOpenBicycle,
  onOpenRentalCar,
  onOpenAttention,
  onOpenFleet,
  onOpenTasks,
  onNavigate,
  updateData,
}: {
  data: VoyagerData;
  role: CompanyRole;
  tasks: CompanyTask[];
  onOpenVehicle: (id: string) => void;
  onOpenBicycle: (id: string) => void;
  onOpenRentalCar: (id: string) => void;
  onOpenAttention: () => void;
  onOpenFleet: () => void;
  onOpenTasks: () => void;
  onNavigate: (page: Page) => void;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
}) {
  const [showVehicle, setShowVehicle] = useState(false);
  const sum = totals(data);
  const attention = useMemo(() => getAttention(data, tasks), [data, tasks]);
  const attentionGroups = useMemo(
    () => groupAttention(attention).filter((group) => group.items.length > 0),
    [attention],
  );
  const recent = [...data.events]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 6);
  const working = data.vehicles.filter((v) => v.status === "Работает").length;
  const investorSummary = investorTotals(data, currentMonth());
  const unassignedCompanyProfit = data.vehicles
    .filter((vehicle) => !vehicle.investorId)
    .reduce(
      (total, vehicle) =>
        total + vehicleFinances(data.events, vehicle.id).profit,
      0,
    );
  const companyEarnings =
    investorSummary.companyShare + unassignedCompanyProfit;
  return (
    <PageWrap
      className="dashboard-page"
      title="Добрый день"
      subtitle="Вот что происходит с вашим автопарком сегодня."
      actions={
        <button className="primary" onClick={() => setShowVehicle(true)}>
          ＋ Добавить автомобиль
        </button>
      }
    >
      <SectionSearch onNavigate={onNavigate} role={role} />
      <div className="hero-stats">
        <Stat
          label="Автомобилей"
          value={String(data.vehicles.length)}
          note={`${working} работают`}
          tone="mint"
        />
        <Stat
          label="Доход за месяц"
          value={money(sum.income)}
          note={currentMonth()}
        />
        <Stat
          label="Расходы за месяц"
          value={money(sum.expense)}
          note="Все операции"
          tone="warm"
        />
        <Stat
          label="Прибыль за месяц"
          value={money(sum.profit)}
          note="До выплат инвесторам"
          tone={sum.profit >= 0 ? "mint" : "danger"}
        />
      </div>
      <div className="hero-stats investor-summary">
        <Stat
          label="Начислено инвесторам"
          value={money(investorSummary.accrued)}
          note="По условиям инвесторов"
        />
        <Stat
          label="Личные расходы инвесторов"
          value={money(investorSummary.personalExpenses)}
          note="Вычитаются из выплат"
          tone="warm"
        />
        <Stat
          label="К выплате инвесторам"
          value={money(investorSummary.payable)}
          note={
            investorSummary.payable < 0
              ? "Есть задолженность инвесторов"
              : "С учётом выплат и личных расходов"
          }
          tone={investorSummary.payable >= 0 ? "mint" : "danger"}
        />
        <Stat
          label="Заработок компании"
          value={money(companyEarnings)}
          note="Доля компании за месяц"
          tone={companyEarnings >= 0 ? "mint" : "danger"}
        />
      </div>
      <div className="dashboard-grid">
        <section className="panel attention-panel">
          <div className="panel-heading-action">
            <PanelTitle
              title="Требует внимания"
              meta={attentionCountLabel(attention.length)}
            />
            {attention.length > 0 && (
              <button className="secondary tiny" onClick={onOpenAttention}>
                Открыть весь список
              </button>
            )}
          </div>
          {attention.length ? (
            <div className="attention-compact-groups">
              {attentionGroups.map((group) => (
                <section className="attention-compact-group" key={group.id}>
                  <div className="attention-category-heading">
                    <span>{group.icon}</span>
                    <div>
                      <b>{group.label}</b>
                      <small>{attentionCountLabel(group.items.length)}</small>
                    </div>
                  </div>
                  <AttentionRow
                    item={group.items[0]}
                    compact
                    onOpenVehicle={onOpenVehicle}
                    onOpenBicycle={onOpenBicycle}
                    onOpenRentalCar={onOpenRentalCar}
                    onOpenFleet={onOpenFleet}
                    onOpenTasks={onOpenTasks}
                  />
                  {group.items.length > 1 && (
                    <button
                      className="attention-group-more"
                      onClick={onOpenAttention}
                    >
                      Ещё {group.items.length - 1} →
                    </button>
                  )}
                </section>
              ))}
            </div>
          ) : (
            <Empty
              icon="✓"
              title="Всё спокойно"
              text="Нет просроченных ТО, документов и других предупреждений."
            />
          )}
        </section>
        <section className="panel">
          <PanelTitle title="Автопарк" meta="Текущий статус" />
          <div className="status-chart">
            {(
              [
                "Работает",
                "На ТО",
                "Ремонт",
                "Простой",
                "Архив",
              ] as VehicleStatus[]
            ).map((status) => {
              const count = data.vehicles.filter(
                (v) => v.status === status,
              ).length;
              const width = data.vehicles.length
                ? Math.max(3, (count / data.vehicles.length) * 100)
                : 0;
              return (
                <div className="chart-row" key={status}>
                  <span>
                    <i className={STATUS_COLORS[status]} />
                    {status}
                  </span>
                  <div>
                    <b
                      style={{ width: `${width}%` }}
                      className={STATUS_COLORS[status]}
                    />
                  </div>
                  <strong>{count}</strong>
                </div>
              );
            })}
          </div>
        </section>
        <section className="panel wide">
          <PanelTitle title="Последние события" meta="Живая лента" />
          {recent.length ? (
            recent.map((event) => (
              <EventRow
                key={event.id}
                event={event}
                vehicle={data.vehicles.find((v) => v.id === event.vehicleId)}
                onClick={() => onOpenVehicle(event.vehicleId)}
              />
            ))
          ) : (
            <Empty
              icon="↗"
              title="Лента пока пустая"
              text="Добавьте автомобиль и первое событие."
            />
          )}
        </section>
      </div>
      {showVehicle && (
        <VehicleModal
          investors={data.investors}
          onClose={() => setShowVehicle(false)}
          onSave={(vehicle) => {
            updateData((d) => ({ ...d, vehicles: [...d.vehicles, vehicle] }));
            setShowVehicle(false);
          }}
        />
      )}
    </PageWrap>
  );
}

type CompanyIncomeDirection = "fleet" | "rentals" | "bicycles";
type CompanyIncomeSort = "profit" | "income" | "expense" | "name";
type CompanyIncomeOperationFilter = "all" | "income" | "expense";

type CompanyIncomeAsset = {
  id: string;
  title: string;
  subtitle: string;
  status: string;
  income: number;
  expense: number;
  operatingProfit: number;
  companyProfit: number;
  investorAccrued: number;
  operationCount: number;
};

type CompanyIncomeOperation = {
  id: string;
  title: string;
  date: string;
  amount: number;
  kind: "income" | "expense";
  notes?: string;
};

const BICYCLE_GENERAL_EXPENSE_ASSET_ID = "bicycle-general-expenses";

const COMPANY_INCOME_DIRECTIONS: {
  id: CompanyIncomeDirection;
  label: string;
  shortLabel: string;
  icon: string;
  description: string;
}[] = [
  {
    id: "fleet",
    label: "Аренда (таксопарк)",
    shortLabel: "Аренда",
    icon: "◆",
    description: "Доля компании после расходов и начислений инвесторам",
  },
  {
    id: "rentals",
    label: "Прокат автомобилей",
    shortLabel: "Прокат",
    icon: "◇",
    description: "Фактические доходы и расходы прокатных автомобилей",
  },
  {
    id: "bicycles",
    label: "Электровелосипеды",
    shortLabel: "Электровелосипеды",
    icon: "◈",
    description: "Арендные поступления, обслуживание и другие расходы",
  },
];

function fleetCompanyIncomeAssets(
  data: VoyagerData,
  period: string,
): CompanyIncomeAsset[] {
  const investorAccruedByVehicle = new Map<string, number>();

  data.investors.forEach((investor) => {
    const vehicles = data.vehicles.filter(
      (vehicle) => vehicle.investorId === investor.id,
    );
    const finance = investorFinances(data, investor, period);
    const weights = vehicles.map((vehicle) => {
      const vehicleFinance = vehicleFinances(data.events, vehicle.id, period);
      return {
        id: vehicle.id,
        weight: Math.max(
          0,
          investor.commissionBase === "revenue"
            ? vehicleFinance.income
            : vehicleFinance.profit,
        ),
      };
    });
    const totalWeight = weights.reduce((sum, item) => sum + item.weight, 0);
    weights.forEach((item) => {
      investorAccruedByVehicle.set(
        item.id,
        totalWeight > 0 ? (finance.accrued * item.weight) / totalWeight : 0,
      );
    });
  });

  return data.vehicles.map((vehicle) => {
    const finance = vehicleFinances(data.events, vehicle.id, period);
    const investor = data.investors.find(
      (item) => item.id === vehicle.investorId,
    );
    const investorAccrued = investor
      ? investorAccruedByVehicle.get(vehicle.id) || 0
      : 0;
    const operationCount = data.events.filter(
      (event) =>
        event.vehicleId === vehicle.id &&
        monthKey(event.date) === period &&
        (event.type === "income" ||
          (["expense", "repair", "maintenance"].includes(event.type) &&
            event.expenseScope !== "investor_personal")),
    ).length;
    return {
      id: vehicle.id,
      title: vehicle.plate,
      subtitle: `${vehicle.make} ${vehicle.model}${investor ? ` · ${investor.name}` : " · Автомобиль компании"}`,
      status: vehicle.status,
      income: finance.income,
      expense: finance.expense,
      operatingProfit: finance.profit,
      companyProfit: finance.profit - investorAccrued,
      investorAccrued,
      operationCount,
    };
  });
}

function rentalCompanyIncomeAssets(
  data: VoyagerData,
  period: string,
): CompanyIncomeAsset[] {
  return data.rentalCars.map((car) => {
    const finance = rentalFinances(data.rentalEvents, car.id, period);
    const operationCount = data.rentalEvents.filter(
      (event) =>
        event.rentalCarId === car.id &&
        monthKey(event.date) === period &&
        ["income", "expense", "repair", "maintenance"].includes(event.type),
    ).length;
    return {
      id: car.id,
      title: car.plate,
      subtitle: `${car.make} ${car.model}`,
      status: car.status,
      income: finance.income,
      expense: finance.expense,
      operatingProfit: finance.profit,
      companyProfit: finance.profit,
      investorAccrued: 0,
      operationCount,
    };
  });
}

function bicycleCompanyIncomeAssets(
  data: VoyagerData,
  period: string,
): CompanyIncomeAsset[] {
  const bicycleAssets = data.bicycles.map((bicycle) => {
    const finance = bicycleFinances(data.bicycleEvents, bicycle.id, period);
    const operationCount = data.bicycleEvents.filter(
      (event) =>
        event.bicycleId === bicycle.id &&
        monthKey(event.date) === period &&
        ["rental", "income", "expense", "repair", "maintenance"].includes(
          event.type,
        ),
    ).length;
    return {
      id: bicycle.id,
      title: bicycle.inventoryNumber,
      subtitle: `${bicycle.brand} ${bicycle.model}`,
      status: bicycle.status,
      income: finance.income,
      expense: finance.expense,
      operatingProfit: finance.profit,
      companyProfit: finance.profit,
      investorAccrued: 0,
      operationCount,
    };
  });
  const generalExpenses = data.bicycleGeneralExpenses.filter(
    (expense) => monthKey(expense.date) === period,
  );
  const generalExpenseTotal = generalExpenses.reduce(
    (sum, expense) => sum + expense.amount,
    0,
  );
  if (!generalExpenses.length) return bicycleAssets;
  return [
    ...bicycleAssets,
    {
      id: BICYCLE_GENERAL_EXPENSE_ASSET_ID,
      title: "Общие расходы",
      subtitle: "Расходы всего велопарка без привязки к велосипеду",
      status: "Общий учёт",
      income: 0,
      expense: generalExpenseTotal,
      operatingProfit: -generalExpenseTotal,
      companyProfit: -generalExpenseTotal,
      investorAccrued: 0,
      operationCount: generalExpenses.length,
    },
  ];
}

function companyIncomeOperations(
  data: VoyagerData,
  direction: CompanyIncomeDirection,
  assetId: string,
  period: string,
): CompanyIncomeOperation[] {
  if (direction === "fleet") {
    return data.events
      .filter(
        (event) =>
          event.vehicleId === assetId &&
          monthKey(event.date) === period &&
          (event.type === "income" ||
            (["expense", "repair", "maintenance"].includes(event.type) &&
              event.expenseScope !== "investor_personal")),
      )
      .map((event) => ({
        id: event.id,
        title: event.title,
        date: event.date,
        amount: event.amount || 0,
        kind: event.type === "income" ? "income" as const : "expense" as const,
        notes: [event.notes, event.paymentMethod].filter(Boolean).join(" · "),
      }));
  }
  if (direction === "rentals") {
    return data.rentalEvents
      .filter(
        (event) =>
          event.rentalCarId === assetId &&
          monthKey(event.date) === period &&
          ["income", "expense", "repair", "maintenance"].includes(event.type),
      )
      .map((event) => ({
        id: event.id,
        title: event.title,
        date: event.date,
        amount: event.amount || 0,
        kind: event.type === "income" ? "income" as const : "expense" as const,
        notes: [event.notes, event.paymentMethod].filter(Boolean).join(" · "),
      }));
  }
  if (assetId === BICYCLE_GENERAL_EXPENSE_ASSET_ID) {
    return data.bicycleGeneralExpenses
      .filter((expense) => monthKey(expense.date) === period)
      .map((expense) => ({
        id: expense.id,
        title: expense.category,
        date: expense.date,
        amount: expense.amount,
        kind: "expense" as const,
        notes: [
          expense.notes,
          expense.service === "Другой"
            ? expense.customService
            : expense.service,
          expense.paymentMethod,
        ].filter(Boolean).join(" · "),
      }));
  }
  return data.bicycleEvents
    .filter(
      (event) =>
        event.bicycleId === assetId &&
        monthKey(event.date) === period &&
        ["rental", "income", "expense", "repair", "maintenance"].includes(
          event.type,
        ),
    )
    .map((event) => ({
      id: event.id,
      title: event.title,
      date: event.date,
      amount: event.amount || 0,
      kind:
        event.type === "income" || event.type === "rental"
          ? "income" as const
          : "expense" as const,
      notes: [event.notes, event.paymentMethod].filter(Boolean).join(" · "),
    }));
}

function companyIncomeAssetTotals(assets: CompanyIncomeAsset[]) {
  return assets.reduce(
    (total, asset) => ({
      income: total.income + asset.income,
      expense: total.expense + asset.expense,
      operatingProfit: total.operatingProfit + asset.operatingProfit,
      companyProfit: total.companyProfit + asset.companyProfit,
      investorAccrued: total.investorAccrued + asset.investorAccrued,
      operationCount: total.operationCount + asset.operationCount,
    }),
    {
      income: 0,
      expense: 0,
      operatingProfit: 0,
      companyProfit: 0,
      investorAccrued: 0,
      operationCount: 0,
    },
  );
}

function CompanyIncomePage({
  data,
  onOpenVehicle,
  onOpenBicycle,
  onOpenRentalCar,
}: {
  data: VoyagerData;
  onOpenVehicle: (id: string) => void;
  onOpenBicycle: (id: string) => void;
  onOpenRentalCar: (id: string) => void;
}) {
  const [period, setPeriod] = useState(currentMonth());
  const [direction, setDirection] = useState<CompanyIncomeDirection>("fleet");
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<CompanyIncomeSort>("profit");
  const [operationFilter, setOperationFilter] =
    useState<CompanyIncomeOperationFilter>("all");

  const assetsByDirection = useMemo(
    () => ({
      fleet: fleetCompanyIncomeAssets(data, period),
      rentals: rentalCompanyIncomeAssets(data, period),
      bicycles: bicycleCompanyIncomeAssets(data, period),
    }),
    [data, period],
  );
  const directionTotals = {
    fleet: companyIncomeAssetTotals(assetsByDirection.fleet),
    rentals: companyIncomeAssetTotals(assetsByDirection.rentals),
    bicycles: companyIncomeAssetTotals(assetsByDirection.bicycles),
  };
  const totalCompanyProfit =
    directionTotals.fleet.companyProfit +
    directionTotals.rentals.companyProfit +
    directionTotals.bicycles.companyProfit;
  const currentDirection = COMPANY_INCOME_DIRECTIONS.find(
    (item) => item.id === direction,
  )!;
  const currentAssets = assetsByDirection[direction];
  const totalsForDirection = directionTotals[direction];
  const selectedAsset = currentAssets.find(
    (asset) => asset.id === selectedAssetId,
  );
  const visibleAssets = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("ru");
    return [...currentAssets]
      .filter((asset) =>
        `${asset.title} ${asset.subtitle}`
          .toLocaleLowerCase("ru")
          .includes(needle),
      )
      .sort((left, right) => {
        if (sort === "name") return left.title.localeCompare(right.title, "ru");
        if (sort === "income") return right.income - left.income;
        if (sort === "expense") return right.expense - left.expense;
        return right.companyProfit - left.companyProfit;
      });
  }, [currentAssets, query, sort]);
  const operations = selectedAsset
    ? companyIncomeOperations(data, direction, selectedAsset.id, period)
        .filter(
          (operation) =>
            operationFilter === "all" || operation.kind === operationFilter,
        )
        .sort((left, right) => right.date.localeCompare(left.date))
    : [];

  const openSourceCard = () => {
    if (!selectedAsset) return;
    if (direction === "fleet") onOpenVehicle(selectedAsset.id);
    else if (direction === "rentals") onOpenRentalCar(selectedAsset.id);
    else onOpenBicycle(selectedAsset.id);
  };
  const selectedAssetHasSourceCard =
    selectedAsset?.id !== BICYCLE_GENERAL_EXPENSE_ASSET_ID;

  return (
    <PageWrap
      className="company-income-page"
      title="Доход компании"
      subtitle="Заработок по направлениям на основе уже внесённых доходов и расходов."
      actions={
        <div className="company-income-period">
          <button
            type="button"
            aria-label="Предыдущий месяц"
            onClick={() => setPeriod(shiftMonthKey(period, -1))}
          >
            ←
          </button>
          <input
            type="month"
            value={period}
            aria-label="Расчётный месяц"
            onChange={(event) => setPeriod(event.target.value || currentMonth())}
          />
          <button
            type="button"
            aria-label="Следующий месяц"
            onClick={() => setPeriod(shiftMonthKey(period, 1))}
          >
            →
          </button>
        </div>
      }
    >
      <div className="hero-stats company-income-overview">
        <Stat
          label="Общий заработок компании"
          value={money(totalCompanyProfit)}
          note={investorReportPeriodLabel(period)}
          tone={totalCompanyProfit < 0 ? "danger" : "mint"}
        />
        <Stat
          label="Аренда · доля компании"
          value={money(directionTotals.fleet.companyProfit)}
          note={`${assetsByDirection.fleet.length} автомобилей`}
          tone={directionTotals.fleet.companyProfit < 0 ? "danger" : ""}
        />
        <Stat
          label="Прокат автомобилей"
          value={money(directionTotals.rentals.companyProfit)}
          note={`${assetsByDirection.rentals.length} автомобилей`}
          tone={directionTotals.rentals.companyProfit < 0 ? "danger" : ""}
        />
        <Stat
          label="Электровелосипеды"
          value={money(directionTotals.bicycles.companyProfit)}
          note={`${data.bicycles.length} единиц`}
          tone={directionTotals.bicycles.companyProfit < 0 ? "danger" : ""}
        />
      </div>

      <div className="company-income-direction-tabs" role="tablist" aria-label="Направления дохода">
        {COMPANY_INCOME_DIRECTIONS.map((item) => (
          <button
            type="button"
            role="tab"
            aria-selected={direction === item.id}
            className={direction === item.id ? "active" : ""}
            key={item.id}
            onClick={() => {
              setDirection(item.id);
              setSelectedAssetId(null);
              setOperationFilter("all");
              setQuery("");
            }}
          >
            <span>{item.icon}</span>
            <div><b>{item.label}</b><small>{item.description}</small></div>
            <strong>{money(directionTotals[item.id].companyProfit)}</strong>
          </button>
        ))}
      </div>

      {selectedAsset ? (
        <section className="company-income-detail">
          <div className="company-income-detail-head">
            <button
              type="button"
              className="back-link"
              onClick={() => {
                setSelectedAssetId(null);
                setOperationFilter("all");
              }}
            >
              ← Назад к списку
            </button>
            {selectedAssetHasSourceCard && (
              <button type="button" className="secondary" onClick={openSourceCard}>
                Открыть основную карточку →
              </button>
            )}
          </div>
          <div className="company-income-asset-heading">
            <span>{currentDirection.icon}</span>
            <div>
              <p className="eyebrow">{currentDirection.label.toUpperCase()}</p>
              <h2>{selectedAsset.title}</h2>
              <p>{selectedAsset.subtitle} · {selectedAsset.status}</p>
            </div>
          </div>
          <div className="hero-stats company-income-detail-stats">
            <Stat label="Доход" value={money(selectedAsset.income)} note={investorReportPeriodLabel(period)} tone="mint" />
            <Stat label="Расходы" value={money(selectedAsset.expense)} note="Все фактические расходы" tone="warm" />
            <Stat label="Прибыль до распределения" value={money(selectedAsset.operatingProfit)} note="Доход минус расходы" tone={selectedAsset.operatingProfit < 0 ? "danger" : ""} />
            <Stat label={direction === "fleet" ? "Доля компании" : "Прибыль компании"} value={money(selectedAsset.companyProfit)} note={direction === "fleet" && selectedAsset.investorAccrued > 0 ? `Инвестору: ${money(selectedAsset.investorAccrued)}` : "Итог направления"} tone={selectedAsset.companyProfit < 0 ? "danger" : "mint"} />
          </div>
          <section className="panel company-income-operations">
            <div className="company-income-operations-head">
              <PanelTitle title="Доходы и расходы" meta={`${operations.length} операций`} />
              <div className="company-income-operation-filter">
                {(["all", "income", "expense"] as CompanyIncomeOperationFilter[]).map((filter) => (
                  <button type="button" className={operationFilter === filter ? "active" : ""} key={filter} onClick={() => setOperationFilter(filter)}>
                    {filter === "all" ? "Все" : filter === "income" ? "Доходы" : "Расходы"}
                  </button>
                ))}
              </div>
            </div>
            {operations.length ? (
              <div className="company-income-operation-list">
                {operations.map((operation) => (
                  <div className="company-income-operation-row" key={operation.id}>
                    <span className={`event-icon event-${operation.kind}`}>{operation.kind === "income" ? "↗" : "↘"}</span>
                    <div><b>{operation.title}</b><p>{shortDate(operation.date)}{operation.notes ? ` · ${operation.notes}` : ""}</p></div>
                    <strong className={operation.kind === "income" ? "positive" : "negative"}>{operation.kind === "income" ? "+" : "−"}{money(operation.amount)}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <Empty icon="₽" title="Операций нет" text="За выбранный месяц и фильтр записи отсутствуют." />
            )}
          </section>
        </section>
      ) : (
        <>
          <div className="hero-stats company-income-direction-summary">
            <Stat label="Доход направления" value={money(totalsForDirection.income)} note={investorReportPeriodLabel(period)} tone="mint" />
            <Stat label="Расходы направления" value={money(totalsForDirection.expense)} note={`${totalsForDirection.operationCount} операций`} tone="warm" />
            <Stat label="Прибыль до распределения" value={money(totalsForDirection.operatingProfit)} note="Доход минус расходы" tone={totalsForDirection.operatingProfit < 0 ? "danger" : ""} />
            <Stat label={direction === "fleet" ? "Доля компании" : "Прибыль компании"} value={money(totalsForDirection.companyProfit)} note={direction === "fleet" ? `Инвесторам: ${money(totalsForDirection.investorAccrued)}` : currentDirection.shortLabel} tone={totalsForDirection.companyProfit < 0 ? "danger" : "mint"} />
          </div>
          <section className="panel company-income-assets-panel">
            <div className="company-income-assets-head">
              <div>
                <h2>{currentDirection.label}</h2>
                <p>{currentDirection.description}</p>
              </div>
              <div className="company-income-assets-controls">
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Поиск по номеру или модели" aria-label="Поиск объектов" />
                <select value={sort} onChange={(event) => setSort(event.target.value as CompanyIncomeSort)} aria-label="Сортировка объектов">
                  <option value="profit">Сначала прибыльные</option>
                  <option value="income">По доходу</option>
                  <option value="expense">По расходам</option>
                  <option value="name">По названию</option>
                </select>
              </div>
            </div>
            {visibleAssets.length ? (
              <div className="company-income-asset-grid">
                {visibleAssets.map((asset) => (
                  <button type="button" className="company-income-asset-card" key={asset.id} onClick={() => setSelectedAssetId(asset.id)}>
                    <header><span>{currentDirection.icon}</span><div><b>{asset.title}</b><small>{asset.subtitle}</small></div><em>{asset.status}</em></header>
                    <div className="company-income-asset-metrics">
                      <span>Доход<b>{money(asset.income)}</b></span>
                      <span>Расходы<b>{money(asset.expense)}</b></span>
                      <span>{direction === "fleet" ? "Доля компании" : "Прибыль"}<b className={asset.companyProfit < 0 ? "negative" : "positive"}>{money(asset.companyProfit)}</b></span>
                    </div>
                    <footer><span>{asset.operationCount} операций</span><b>Подробнее →</b></footer>
                  </button>
                ))}
              </div>
            ) : (
              <Empty icon={currentDirection.icon} title="Ничего не найдено" text={query ? "Попробуйте изменить поисковый запрос." : "В этом направлении пока нет техники."} />
            )}
          </section>
        </>
      )}
    </PageWrap>
  );
}

function AttentionPage({
  data,
  tasks,
  onBack,
  onOpenVehicle,
  onOpenBicycle,
  onOpenRentalCar,
  onOpenFleet,
  onOpenTasks,
}: {
  data: VoyagerData;
  tasks: CompanyTask[];
  onBack: () => void;
  onOpenVehicle: (id: string) => void;
  onOpenBicycle: (id: string) => void;
  onOpenRentalCar: (id: string) => void;
  onOpenFleet: () => void;
  onOpenTasks: () => void;
}) {
  const attention = useMemo(() => getAttention(data, tasks), [data, tasks]);
  const attentionGroups = useMemo(() => groupAttention(attention), [attention]);
  return (
    <PageWrap
      title="Требует внимания"
      subtitle="Полный список предупреждений по таксопарку, велосипедам, прокату и задачам."
      actions={
        <button className="secondary" onClick={onBack}>
          ← Вернуться в обзор
        </button>
      }
    >
      <div className="attention-category-list">
        {attentionGroups.map((group) => (
          <section className="panel attention-list" key={group.id}>
            <div className="attention-category-title">
              <span>{group.icon}</span>
              <PanelTitle
                title={group.label}
                meta={attentionCountLabel(group.items.length)}
              />
            </div>
            {group.items.length ? (
              group.items.map((item) => (
                <AttentionRow
                  item={item}
                  key={item.key}
                  onOpenVehicle={onOpenVehicle}
                  onOpenBicycle={onOpenBicycle}
                  onOpenRentalCar={onOpenRentalCar}
                  onOpenFleet={onOpenFleet}
                  onOpenTasks={onOpenTasks}
                />
              ))
            ) : (
              <p className="attention-category-empty">Нет уведомлений</p>
            )}
          </section>
        ))}
      </div>
    </PageWrap>
  );
}

function SectionSearch({
  onNavigate,
  role,
}: {
  onNavigate: (page: Page) => void;
  role: CompanyRole;
}) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const results = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("ru");
    if (!needle) return [];
    return NAV_ITEMS.filter((item) => item.id !== "dashboard")
      .filter((item) => !item.managementOnly || role === "owner" || role === "admin")
      .filter((item) =>
      [item.label, ...item.keywords]
        .join(" ")
        .toLocaleLowerCase("ru")
        .includes(needle),
    );
  }, [query, role]);

  useEffect(() => setActiveIndex(0), [query]);

  const openResult = (item: (typeof NAV_ITEMS)[number]) => {
    onNavigate(item.id);
    setQuery("");
  };

  return (
    <div className="section-finder">
      <div className="section-search-box">
        <span aria-hidden="true">⌕</span>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (!results.length) return;
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActiveIndex((index) => (index + 1) % results.length);
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex(
                (index) => (index - 1 + results.length) % results.length,
              );
            } else if (event.key === "Enter") {
              event.preventDefault();
              openResult(results[activeIndex]);
            } else if (event.key === "Escape") {
              setQuery("");
            }
          }}
          placeholder="Найти раздел: ДТП, расходы, задачи…"
          aria-label="Найти раздел"
          aria-expanded={Boolean(query)}
          aria-controls="section-search-results"
          role="combobox"
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            aria-label="Очистить поиск"
            onClick={() => setQuery("")}
          >
            ×
          </button>
        )}
      </div>
      {query && (
        <div className="section-search-results" id="section-search-results">
          {results.length ? (
            results.map((item, index) => (
              <button
                type="button"
                className={index === activeIndex ? "active" : ""}
                key={item.id}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => openResult(item)}
              >
                <span>{item.icon}</span>
                <b>{item.label}</b>
                <em>Перейти →</em>
              </button>
            ))
          ) : (
            <p>Раздел не найден. Попробуйте другое слово.</p>
          )}
        </div>
      )}
    </div>
  );
}

function AttentionRow({
  item,
  compact = false,
  onOpenVehicle,
  onOpenBicycle,
  onOpenRentalCar,
  onOpenFleet,
  onOpenTasks,
}: {
  item: AttentionItem;
  compact?: boolean;
  onOpenVehicle: (id: string) => void;
  onOpenBicycle: (id: string) => void;
  onOpenRentalCar: (id: string) => void;
  onOpenFleet: () => void;
  onOpenTasks: () => void;
}) {
  const openItem = () => {
    if (item.fleetMileage) onOpenFleet();
    else if (item.rentalCarId) onOpenRentalCar(item.rentalCarId);
    else if (item.taskId) onOpenTasks();
    else if (item.bicycleId) onOpenBicycle(item.bicycleId);
    else if (item.vehicleId) onOpenVehicle(item.vehicleId);
  };
  return (
    <button
      className={`attention-row${compact ? " compact" : ""}`}
      onClick={openItem}
    >
      <span className={`attention-icon ${item.tone}`}>{item.icon}</span>
      <div>
        <b>{item.title}</b>
        <p>{item.text}</p>
      </div>
      <em>→</em>
    </button>
  );
}

type AccidentSort =
  | "date-desc"
  | "date-asc"
  | "amount-desc"
  | "amount-asc"
  | "plate-asc";

function AccidentRegistry({
  data,
  updateData,
  canEdit,
  userId,
}: {
  data: VoyagerData;
  updateData: (fn: (data: VoyagerData) => VoyagerData) => void;
  canEdit: boolean;
  userId: string;
}) {
  const [query, setQuery] = useState("");
  const [vehicleId, setVehicleId] = useState("all");
  const [fault, setFault] = useState<"all" | AccidentFaultStatus>("all");
  const [registration, setRegistration] = useState<
    "all" | AccidentRegistrationMethod
  >("all");
  const [payment, setPayment] = useState<"all" | "paid" | "unpaid">("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState<AccidentSort>("date-desc");
  const [editing, setEditing] = useState<AccidentRecord | null>(null);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<AccidentRecord | null>(null);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("ru");
    return data.accidentRecords
      .filter((record) => {
        const vehicle = data.vehicles.find((item) => item.id === record.vehicleId);
        const searchable = [
          record.vehiclePlate,
          record.vehicleName,
          vehicle?.plate,
          vehicle?.make,
          vehicle?.model,
          record.driverName,
          record.comment,
          record.registrationMethod,
          record.faultStatus,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("ru");
        return (
          (!needle || searchable.includes(needle)) &&
          (vehicleId === "all" || record.vehicleId === vehicleId) &&
          (fault === "all" || record.faultStatus === fault) &&
          (registration === "all" ||
            record.registrationMethod === registration) &&
          (payment === "all" || record.paid === (payment === "paid")) &&
          (!dateFrom || record.date.slice(0, 10) >= dateFrom) &&
          (!dateTo || record.date.slice(0, 10) <= dateTo)
        );
      })
      .sort((left, right) => {
        if (sort === "date-asc") return left.date.localeCompare(right.date);
        if (sort === "amount-desc")
          return right.damageAmount - left.damageAmount;
        if (sort === "amount-asc") return left.damageAmount - right.damageAmount;
        if (sort === "plate-asc")
          return left.vehiclePlate.localeCompare(right.vehiclePlate, "ru");
        return right.date.localeCompare(left.date);
      });
  }, [data.accidentRecords, data.vehicles, dateFrom, dateTo, fault, payment, query, registration, sort, vehicleId]);

  const resetFilters = () => {
    setQuery("");
    setVehicleId("all");
    setFault("all");
    setRegistration("all");
    setPayment("all");
    setDateFrom("");
    setDateTo("");
    setSort("date-desc");
  };

  const deleteRecord = async (record: AccidentRecord) => {
    if (!confirm(`Удалить запись ДТП по автомобилю ${record.vehiclePlate}?`))
      return;
    updateData((current) => ({
      ...current,
      accidentRecords: current.accidentRecords.filter(
        (item) => item.id !== record.id,
      ),
    }));
    await Promise.all(
      record.attachments.map((attachment) =>
        removePrivateFile(attachment.filePath),
      ),
    );
    if (selected?.id === record.id) setSelected(null);
  };

  const saveRecord = async (
    record: AccidentRecord,
    removedPaths: string[],
  ) => {
    updateData((current) => ({
      ...current,
      accidentRecords: current.accidentRecords.some(
        (item) => item.id === record.id,
      )
        ? current.accidentRecords.map((item) =>
            item.id === record.id ? record : item,
          )
        : [record, ...current.accidentRecords],
    }));
    await Promise.all(removedPaths.map((path) => removePrivateFile(path)));
    setEditing(null);
    setCreating(false);
  };

  const totalDamage = data.accidentRecords.reduce(
    (total, record) => total + record.damageAmount,
    0,
  );
  const activeFilters =
    Boolean(query || dateFrom || dateTo) ||
    [vehicleId, fault, registration, payment].some((value) => value !== "all");

  return (
    <PageWrap
      className="accident-page"
      title="Реестр ДТП"
      subtitle="Информационный журнал происшествий — без влияния на финансы таксопарка."
      actions={
        canEdit ? (
          <button className="primary" onClick={() => setCreating(true)}>
            ＋ Добавить ДТП
          </button>
        ) : (
          <span className="read-only-badge">Только просмотр</span>
        )
      }
    >
      <div className="hero-stats accident-summary">
        <Stat label="Всего ДТП" value={String(data.accidentRecords.length)} note="За всё время" />
        <Stat
          label="Виновность водителя"
          value={String(data.accidentRecords.filter((item) => item.faultStatus === "Виновен").length)}
          note="Записей со статусом «Виновен»"
        />
        <Stat
          label="Не оплачено"
          value={String(data.accidentRecords.filter((item) => !item.paid).length)}
          note="Требуют контроля"
          tone="amber"
        />
        <Stat label="Сумма повреждений" value={money(totalDamage)} note="Справочно, не в финансах" />
      </div>

      <section className="panel accident-filters">
        <div className="accident-filter-grid">
          <Field label="Поиск">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Номер, водитель, автомобиль…"
            />
          </Field>
          <Field label="Автомобиль">
            <select value={vehicleId} onChange={(event) => setVehicleId(event.target.value)}>
              <option value="all">Все автомобили</option>
              {data.vehicles.map((vehicle) => (
                <option value={vehicle.id} key={vehicle.id}>
                  {vehicle.plate} · {vehicle.make} {vehicle.model}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Виновность">
            <select value={fault} onChange={(event) => setFault(event.target.value as typeof fault)}>
              <option value="all">Любой статус</option>
              <option>Виновен</option>
              <option>Не виновен</option>
            </select>
          </Field>
          <Field label="Кем оформлялось">
            <select value={registration} onChange={(event) => setRegistration(event.target.value as typeof registration)}>
              <option value="all">Любой способ</option>
              <option>ГАИ</option>
              <option>Аварийный комиссар</option>
              <option>Решили на месте</option>
            </select>
          </Field>
          <Field label="Оплата">
            <select value={payment} onChange={(event) => setPayment(event.target.value as typeof payment)}>
              <option value="all">Любой статус</option>
              <option value="paid">Оплачено</option>
              <option value="unpaid">Не оплачено</option>
            </select>
          </Field>
          <Field label="Дата с">
            <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </Field>
          <Field label="Дата по">
            <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </Field>
          <Field label="Сортировка">
            <select value={sort} onChange={(event) => setSort(event.target.value as AccidentSort)}>
              <option value="date-desc">Сначала новые</option>
              <option value="date-asc">Сначала старые</option>
              <option value="amount-desc">Сумма: по убыванию</option>
              <option value="amount-asc">Сумма: по возрастанию</option>
              <option value="plate-asc">По госномеру</option>
            </select>
          </Field>
        </div>
        <div className="accident-filter-footer">
          <span>Найдено: {filtered.length}</span>
          {activeFilters && (
            <button className="secondary small" type="button" onClick={resetFilters}>
              Сбросить фильтры
            </button>
          )}
        </div>
      </section>

      <div className="accident-list">
        {filtered.length ? (
          filtered.map((record) => (
            <AccidentCard
              key={record.id}
              record={record}
              vehicle={data.vehicles.find((item) => item.id === record.vehicleId)}
              canEdit={canEdit}
              onOpen={() => setSelected(record)}
              onEdit={() => setEditing(record)}
              onDelete={() => void deleteRecord(record)}
            />
          ))
        ) : (
          <section className="panel">
            <Empty
              icon="⚠"
              title={data.accidentRecords.length ? "Ничего не найдено" : "Реестр пока пуст"}
              text={data.accidentRecords.length ? "Измените условия поиска или сбросьте фильтры." : "Добавьте первое ДТП, чтобы сохранить сведения и документы."}
            />
          </section>
        )}
      </div>

      {(creating || editing) && (
        <AccidentModal
          record={editing || undefined}
          vehicles={data.vehicles}
          userId={userId}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSave={saveRecord}
        />
      )}
      {selected && (
        <AccidentDetailsModal
          record={selected}
          vehicle={data.vehicles.find((item) => item.id === selected.vehicleId)}
          canEdit={canEdit}
          onClose={() => setSelected(null)}
          onEdit={() => {
            setEditing(selected);
            setSelected(null);
          }}
        />
      )}
    </PageWrap>
  );
}

function AccidentCard({
  record,
  vehicle,
  canEdit,
  onOpen,
  onEdit,
  onDelete,
}: {
  record: AccidentRecord;
  vehicle?: Vehicle;
  canEdit: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <article className="panel accident-card">
      <div className="accident-card-date">
        <span>Дата ДТП</span>
        <strong>{shortDate(record.date)}</strong>
      </div>
      <div className="accident-card-main">
        <div className="accident-card-title">
          <h2>{record.vehiclePlate || vehicle?.plate || "Без номера"}</h2>
          <span>{record.vehicleName || [vehicle?.make, vehicle?.model].filter(Boolean).join(" ")}</span>
        </div>
        <p><b>{record.driverName}</b> · {record.registrationMethod}</p>
        <div className="accident-badges">
          <span className={record.faultStatus === "Виновен" ? "danger" : "success"}>{record.faultStatus}</span>
          <span className={record.paid ? "success" : "warning"}>{record.paid ? "Оплачено" : "Не оплачено"}</span>
          <span>{record.attachments.length} файл.</span>
        </div>
      </div>
      <div className="accident-card-amount">
        <span>Повреждения</span>
        <strong>{money(record.damageAmount)}</strong>
      </div>
      <div className="accident-card-actions">
        <button className="secondary" onClick={onOpen}>Открыть</button>
        {canEdit && <button className="secondary" onClick={onEdit}>Изменить</button>}
        {canEdit && <button className="danger-button" onClick={onDelete}>Удалить</button>}
      </div>
    </article>
  );
}

function AccidentDetailsModal({
  record,
  vehicle,
  canEdit,
  onClose,
  onEdit,
}: {
  record: AccidentRecord;
  vehicle?: Vehicle;
  canEdit: boolean;
  onClose: () => void;
  onEdit: () => void;
}) {
  const photos = record.attachments.filter((item) => item.kind === "photo");
  const documents = record.attachments.filter((item) => item.kind === "document");
  return (
    <Modal title={`ДТП · ${record.vehiclePlate || vehicle?.plate || "Автомобиль"}`} onClose={onClose} wide>
      <div className="accident-details-grid">
        <InfoLine label="Дата ДТП" value={shortDate(record.date)} />
        <InfoLine label="Автомобиль" value={`${record.vehiclePlate || vehicle?.plate || "—"} · ${record.vehicleName || [vehicle?.make, vehicle?.model].filter(Boolean).join(" ") || "—"}`} />
        <InfoLine label="Водитель" value={record.driverName} />
        <InfoLine label="Виновность" value={record.faultStatus} />
        <InfoLine label="Оформление" value={record.registrationMethod} />
        <InfoLine label="Сумма повреждений" value={money(record.damageAmount)} />
        <InfoLine label="Статус оплаты" value={record.paid ? "Оплачено" : "Не оплачено"} />
      </div>
      {record.comment && <div className="accident-comment"><span>Комментарий</span><p>{record.comment}</p></div>}
      <AccidentAttachmentList title="Фотографии повреждений" items={photos} emptyText="Фотографии не загружены" />
      <AccidentAttachmentList title="Постановления и документы" items={documents} emptyText="Документы не загружены" />
      <div className="modal-actions accident-detail-actions">
        <span />
        <button className="secondary" onClick={onClose}>Закрыть</button>
        {canEdit && <button className="primary" onClick={onEdit}>Изменить</button>}
      </div>
    </Modal>
  );
}

function AccidentAttachmentList({
  title,
  items,
  emptyText,
}: {
  title: string;
  items: AccidentAttachment[];
  emptyText: string;
}) {
  return (
    <section className="accident-attachment-section">
      <h3>{title}</h3>
      {items.length ? (
        <div className="accident-attachment-list">
          {items.map((item) => (
            <button className="secondary" key={item.id} onClick={() => void openPrivateFile(item.filePath)}>
              <span>{item.kind === "photo" ? "▧" : "▤"}</span>
              <b>{item.fileName}</b>
              <em>Открыть →</em>
            </button>
          ))}
        </div>
      ) : <p className="muted">{emptyText}</p>}
    </section>
  );
}

function AccidentModal({
  record,
  vehicles,
  userId,
  onClose,
  onSave,
}: {
  record?: AccidentRecord;
  vehicles: Vehicle[];
  userId: string;
  onClose: () => void;
  onSave: (record: AccidentRecord, removedPaths: string[]) => void | Promise<void>;
}) {
  const [vehicleId, setVehicleId] = useState(record?.vehicleId || vehicles[0]?.id || "");
  const [date, setDate] = useState(record?.date.slice(0, 10) || new Date().toISOString().slice(0, 10));
  const [driverName, setDriverName] = useState(record?.driverName || "");
  const [faultStatus, setFaultStatus] = useState<AccidentFaultStatus>(record?.faultStatus || "Виновен");
  const [registrationMethod, setRegistrationMethod] = useState<AccidentRegistrationMethod>(record?.registrationMethod || "ГАИ");
  const [damageAmount, setDamageAmount] = useState(String(record?.damageAmount ?? ""));
  const [paid, setPaid] = useState(record?.paid || false);
  const [comment, setComment] = useState(record?.comment || "");
  const [attachments, setAttachments] = useState(record?.attachments || []);
  const [removedPaths, setRemovedPaths] = useState<string[]>([]);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const removeAttachment = (attachment: AccidentAttachment) => {
    setAttachments((items) => items.filter((item) => item.id !== attachment.id));
    setRemovedPaths((paths) => [...paths, attachment.filePath]);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const vehicle = vehicles.find((item) => item.id === vehicleId);
    if (!vehicle) {
      alert("Выберите автомобиль из таксопарка.");
      return;
    }
    if (!driverName.trim()) {
      alert("Укажите ФИО водителя.");
      return;
    }
    if ((photoFiles.length || documentFiles.length) && !supabase) {
      alert("Хранилище файлов сейчас недоступно. Попробуйте позже.");
      return;
    }
    setUploading(true);
    const recordId = record?.id || uid();
    const uploadedPaths: string[] = [];
    const nextAttachments = [...attachments];
    const pending: { file: File; kind: AccidentAttachmentKind }[] = [
      ...photoFiles.map((file) => ({ file, kind: "photo" as const })),
      ...documentFiles.map((file) => ({ file, kind: "document" as const })),
    ];
    for (const { file, kind } of pending) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `${userId}/accidents/${recordId}/${uid()}-${safeName}`;
      const { error } = await supabase!.storage
        .from("voyager-files")
        .upload(filePath, file, { contentType: file.type, upsert: false });
      if (error) {
        await Promise.all(uploadedPaths.map((path) => removePrivateFile(path)));
        alert(`Не удалось загрузить файл «${file.name}»: ${error.message}`);
        setUploading(false);
        return;
      }
      uploadedPaths.push(filePath);
      nextAttachments.push({
        id: uid(),
        kind,
        fileName: file.name,
        filePath,
        createdAt: new Date().toISOString(),
      });
    }
    const now = new Date().toISOString();
    await onSave(
      {
        id: recordId,
        vehicleId,
        vehiclePlate: vehicle.plate,
        vehicleName: [vehicle.make, vehicle.model].filter(Boolean).join(" "),
        date: new Date(`${date}T12:00:00`).toISOString(),
        driverName: driverName.trim(),
        faultStatus,
        registrationMethod,
        damageAmount: Number(damageAmount) || 0,
        paid,
        comment: comment.trim() || undefined,
        attachments: nextAttachments,
        createdAt: record?.createdAt || now,
        updatedAt: now,
      },
      removedPaths,
    );
    setUploading(false);
  };

  return (
    <Modal title={record ? "Изменить запись ДТП" : "Новое ДТП"} onClose={onClose} wide>
      <form className="form-grid accident-form" onSubmit={submit}>
        <Field label="Автомобиль">
          <select required value={vehicleId} onChange={(event) => setVehicleId(event.target.value)}>
            <option value="" disabled>Выберите автомобиль</option>
            {vehicles.map((vehicle) => (
              <option value={vehicle.id} key={vehicle.id}>{vehicle.plate} · {vehicle.make} {vehicle.model}</option>
            ))}
          </select>
        </Field>
        <Field label="Дата ДТП">
          <input required type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </Field>
        <Field label="ФИО водителя" wide>
          <input required value={driverName} onChange={(event) => setDriverName(event.target.value)} placeholder="Иванов Иван Иванович" />
        </Field>
        <Field label="Виновность водителя">
          <select value={faultStatus} onChange={(event) => setFaultStatus(event.target.value as AccidentFaultStatus)}>
            <option>Виновен</option><option>Не виновен</option>
          </select>
        </Field>
        <Field label="Кем оформлялось">
          <select value={registrationMethod} onChange={(event) => setRegistrationMethod(event.target.value as AccidentRegistrationMethod)}>
            <option>ГАИ</option><option>Аварийный комиссар</option><option>Решили на месте</option>
          </select>
        </Field>
        <Field label="Сумма повреждений, ₽">
          <input type="number" min="0" step="0.01" value={damageAmount} onChange={(event) => setDamageAmount(event.target.value)} placeholder="0" />
        </Field>
        <Field label="Статус оплаты">
          <select value={paid ? "paid" : "unpaid"} onChange={(event) => setPaid(event.target.value === "paid")}>
            <option value="unpaid">Не оплачено</option><option value="paid">Оплачено</option>
          </select>
        </Field>
        <Field label="Комментарий" wide>
          <textarea rows={3} value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Обстоятельства ДТП, повреждения и другие сведения" />
        </Field>
        <Field label="Фотографии повреждений" wide>
          <input type="file" accept="image/*" multiple onChange={(event) => setPhotoFiles(Array.from(event.target.files || []))} />
          {photoFiles.length > 0 && <small className="file-selection-note">Выбрано фотографий: {photoFiles.length}</small>}
        </Field>
        <Field label="Постановления и документы" wide>
          <input type="file" accept="image/*,.pdf,.doc,.docx" multiple onChange={(event) => setDocumentFiles(Array.from(event.target.files || []))} />
          {documentFiles.length > 0 && <small className="file-selection-note">Выбрано документов: {documentFiles.length}</small>}
        </Field>
        {attachments.length > 0 && (
          <div className="accident-existing-files wide">
            <span>Сохранённые вложения</span>
            {attachments.map((attachment) => (
              <div key={attachment.id}>
                <b>{attachment.fileName}</b>
                <button type="button" className="danger-button" onClick={() => removeAttachment(attachment)}>Убрать</button>
              </div>
            ))}
          </div>
        )}
        <div className="modal-actions wide">
          <span />
          <button type="button" className="secondary" onClick={onClose} disabled={uploading}>Отмена</button>
          <button className="primary" disabled={uploading}>{uploading ? "Загрузка…" : "Сохранить"}</button>
        </div>
      </form>
    </Modal>
  );
}

type InspectionTab = VehicleInspectionStatus;
type InspectionDamageFilter = "all" | "yes" | "no";
type InspectionSort = "date-desc" | "date-asc" | "mileage-desc" | "mileage-asc";

function inspectionDateTime(value: string) {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function inspectionDateInput(value: Date) {
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
  ].join("-");
}

function lastCompletedInspection(
  inspections: VehicleInspection[],
  vehicleId: string,
) {
  return [...inspections]
    .filter(
      (inspection) =>
        inspection.vehicleId === vehicleId && inspection.status !== "pending",
    )
    .sort((left, right) => right.scheduledAt.localeCompare(left.scheduledAt))[0];
}

function VehicleInspectionsPage({
  data,
  updateData,
  canEdit,
  userId,
}: {
  data: VoyagerData;
  updateData: (fn: (data: VoyagerData) => VoyagerData) => void;
  canEdit: boolean;
  userId: string;
}) {
  const [tab, setTab] = useState<InspectionTab>("pending");
  const [query, setQuery] = useState("");
  const [vehicleId, setVehicleId] = useState("all");
  const [damage, setDamage] = useState<InspectionDamageFilter>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState<InspectionSort>("date-desc");
  const [creating, setCreating] = useState(false);
  const [editingSchedule, setEditingSchedule] =
    useState<VehicleInspection | null>(null);
  const [conducting, setConducting] = useState<VehicleInspection | null>(null);
  const [selected, setSelected] = useState<VehicleInspection | null>(null);

  const counts = useMemo(
    () => ({
      pending: data.vehicleInspections.filter((item) => item.status === "pending").length,
      archive: data.vehicleInspections.filter((item) => item.status === "archive").length,
      damaged: data.vehicleInspections.filter((item) => item.status === "damaged").length,
    }),
    [data.vehicleInspections],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("ru");
    return data.vehicleInspections
      .filter((inspection) => {
        const vehicle = data.vehicles.find((item) => item.id === inspection.vehicleId);
        const searchable = [
          inspection.vehiclePlate,
          inspection.vehicleName,
          vehicle?.plate,
          vehicle?.make,
          vehicle?.model,
          inspection.driverName,
          inspection.damageDescription,
          inspection.comment,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("ru");
        return (
          inspection.status === tab &&
          (!needle || searchable.includes(needle)) &&
          (vehicleId === "all" || inspection.vehicleId === vehicleId) &&
          (tab === "pending" ||
            damage === "all" ||
            inspection.hasDamage === (damage === "yes")) &&
          (!dateFrom || inspection.scheduledAt.slice(0, 10) >= dateFrom) &&
          (!dateTo || inspection.scheduledAt.slice(0, 10) <= dateTo)
        );
      })
      .sort((left, right) => {
        if (sort === "date-asc") return left.scheduledAt.localeCompare(right.scheduledAt);
        if (sort === "mileage-desc") return (right.mileage || 0) - (left.mileage || 0);
        if (sort === "mileage-asc") return (left.mileage || 0) - (right.mileage || 0);
        return right.scheduledAt.localeCompare(left.scheduledAt);
      });
  }, [data.vehicleInspections, data.vehicles, damage, dateFrom, dateTo, query, sort, tab, vehicleId]);

  const saveSchedule = (inspection: VehicleInspection) => {
    updateData((current) => ({
      ...current,
      vehicleInspections: current.vehicleInspections.some((item) => item.id === inspection.id)
        ? current.vehicleInspections.map((item) => item.id === inspection.id ? inspection : item)
        : [inspection, ...current.vehicleInspections],
    }));
    setCreating(false);
    setEditingSchedule(null);
  };

  const saveCompleted = async (
    inspection: VehicleInspection,
    removedPaths: string[],
  ) => {
    updateData((current) => {
      const previous = current.vehicleInspections.find((item) => item.id === inspection.id);
      const mileageEvents = current.events.filter(
        (event) => event.inspectionId !== inspection.id,
      );
      const nextEvents = [...mileageEvents];
      let savedInspection = inspection;
      if (typeof inspection.mileage === "number") {
        const mileageEventId = previous?.mileageEventId || inspection.mileageEventId || uid();
        nextEvents.push({
          id: mileageEventId,
          vehicleId: inspection.vehicleId,
          type: "mileage",
          title: "Пробег при осмотре автомобиля",
          mileage: inspection.mileage,
          date: inspection.scheduledAt,
          notes: `Осмотр водителя: ${inspection.driverName}`,
          paid: true,
          source: "vehicle_inspection",
          inspectionId: inspection.id,
          createdAt:
            current.events.find((event) => event.id === mileageEventId)?.createdAt ||
            new Date().toISOString(),
        });
        savedInspection = { ...inspection, mileageEventId };
      } else {
        savedInspection = { ...inspection, mileageEventId: undefined };
      }
      return {
        ...current,
        events: nextEvents,
        vehicles: current.vehicles.map((vehicle) =>
          vehicle.id === inspection.vehicleId && typeof inspection.mileage === "number"
            ? {
                ...vehicle,
                mileage: inspection.mileage,
                mileageUpdatedWeek: currentMileageWeek(),
              }
            : vehicle,
        ),
        vehicleInspections: current.vehicleInspections.map((item) =>
          item.id === inspection.id ? savedInspection : item,
        ),
      };
    });
    await Promise.all(removedPaths.map((path) => removePrivateFile(path)));
    setConducting(null);
  };

  const deleteInspection = async (inspection: VehicleInspection) => {
    if (!confirm(`Удалить осмотр автомобиля ${inspection.vehiclePlate}?`)) return;
    updateData((current) => ({
      ...current,
      vehicleInspections: current.vehicleInspections.filter((item) => item.id !== inspection.id),
      events: current.events.filter((event) => event.inspectionId !== inspection.id),
    }));
    await Promise.all(inspection.photos.map((photo) => removePrivateFile(photo.filePath)));
    if (selected?.id === inspection.id) setSelected(null);
  };

  const resetFilters = () => {
    setQuery("");
    setVehicleId("all");
    setDamage("all");
    setDateFrom("");
    setDateTo("");
    setSort("date-desc");
  };

  return (
    <PageWrap
      className="inspection-page"
      title="Осмотры авто"
      subtitle="Запись и результаты осмотров автомобилей таксопарка."
      actions={canEdit ? (
        <button className="primary" onClick={() => setCreating(true)}>＋ Добавить осмотр</button>
      ) : <span className="read-only-badge">Только просмотр</span>}
    >
      <div className="inspection-tabs" role="tablist">
        {([
          ["pending", "Актуальные"],
          ["archive", "Архив"],
          ["damaged", "С повреждениями"],
        ] as [InspectionTab, string][]).map(([id, label]) => (
          <button key={id} className={tab === id ? "active" : ""} onClick={() => setTab(id)}>
            {label}<span>{counts[id]}</span>
          </button>
        ))}
      </div>

      <section className="panel inspection-filters">
        <div className="inspection-filter-grid">
          <Field label="Поиск">
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Госномер, автомобиль или водитель…" />
          </Field>
          <Field label="Автомобиль">
            <select value={vehicleId} onChange={(event) => setVehicleId(event.target.value)}>
              <option value="all">Все автомобили</option>
              {data.vehicles.map((vehicle) => <option value={vehicle.id} key={vehicle.id}>{vehicle.plate} · {vehicle.make} {vehicle.model}</option>)}
            </select>
          </Field>
          <Field label="Повреждения">
            <select value={damage} onChange={(event) => setDamage(event.target.value as InspectionDamageFilter)} disabled={tab === "pending"}>
              <option value="all">Любой результат</option><option value="yes">Есть</option><option value="no">Отсутствуют</option>
            </select>
          </Field>
          <Field label="Дата с"><input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></Field>
          <Field label="Дата по"><input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></Field>
          <Field label="Сортировка">
            <select value={sort} onChange={(event) => setSort(event.target.value as InspectionSort)}>
              <option value="date-desc">Сначала новые</option><option value="date-asc">Сначала старые</option><option value="mileage-desc">Пробег: по убыванию</option><option value="mileage-asc">Пробег: по возрастанию</option>
            </select>
          </Field>
        </div>
        <div className="accident-filter-footer">
          <span>Найдено: {filtered.length}</span>
          {(query || vehicleId !== "all" || damage !== "all" || dateFrom || dateTo || sort !== "date-desc") && <button className="secondary small" onClick={resetFilters}>Сбросить фильтры</button>}
        </div>
      </section>

      <div className="inspection-list">
        {filtered.length ? filtered.map((inspection) => (
          <InspectionCard
            key={inspection.id}
            inspection={inspection}
            vehicle={data.vehicles.find((vehicle) => vehicle.id === inspection.vehicleId)}
            canEdit={canEdit}
            onOpen={() =>
              inspection.status === "pending" && canEdit
                ? setConducting(inspection)
                : setSelected(inspection)
            }
            onEdit={() => inspection.status === "pending" ? setEditingSchedule(inspection) : setConducting(inspection)}
            onDelete={() => void deleteInspection(inspection)}
          />
        )) : (
          <section className="panel"><Empty icon="◉" title="Осмотры не найдены" text={data.vehicleInspections.length ? "Измените условия поиска или выберите другую вкладку." : "Добавьте первую запись на осмотр автомобиля."} /></section>
        )}
      </div>

      {(creating || editingSchedule) && (
        <InspectionScheduleModal
          inspection={editingSchedule || undefined}
          vehicles={data.vehicles}
          onClose={() => { setCreating(false); setEditingSchedule(null); }}
          onSave={saveSchedule}
        />
      )}
      {conducting && (
        <InspectionConductModal
          inspection={conducting}
          vehicles={data.vehicles}
          userId={userId}
          onClose={() => setConducting(null)}
          onSave={saveCompleted}
        />
      )}
      {selected && (
        <InspectionDetailsModal
          inspection={selected}
          vehicle={data.vehicles.find((vehicle) => vehicle.id === selected.vehicleId)}
          canEdit={canEdit}
          onClose={() => setSelected(null)}
          onEdit={() => { setConducting(selected); setSelected(null); }}
        />
      )}
    </PageWrap>
  );
}

function InspectionCard({ inspection, vehicle, canEdit, onOpen, onEdit, onDelete }: {
  inspection: VehicleInspection;
  vehicle?: Vehicle;
  canEdit: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <article className="panel inspection-card">
      <div className="inspection-card-date"><span>Дата и время</span><strong>{inspectionDateTime(inspection.scheduledAt)}</strong></div>
      <div className="inspection-card-main">
        <div><h2>{inspection.vehiclePlate || vehicle?.plate || "Без номера"}</h2><span>{inspection.vehicleName || [vehicle?.make, vehicle?.model].filter(Boolean).join(" ")}</span></div>
        <p>{inspection.driverName}</p>
      </div>
      {inspection.status !== "pending" && (
        <div className="inspection-card-result">
          <span className={inspection.status === "damaged" ? "damaged" : "clear"}>{inspection.status === "damaged" ? "С повреждениями" : "Без повреждений"}</span>
          {typeof inspection.mileage === "number" && <b>{inspection.mileage.toLocaleString("ru-RU")} км</b>}
        </div>
      )}
      <div className="inspection-card-actions">
        <button
          className={inspection.status === "pending" && canEdit ? "primary" : "secondary"}
          onClick={onOpen}
        >
          {inspection.status === "pending" && canEdit ? "Осмотреть" : "Открыть"}
        </button>
        {canEdit && <button className="secondary" onClick={onEdit}>Изменить</button>}
        {canEdit && <button className="danger-button" onClick={onDelete}>Удалить</button>}
      </div>
    </article>
  );
}

function InspectionScheduleModal({ inspection, vehicles, onClose, onSave }: {
  inspection?: VehicleInspection;
  vehicles: Vehicle[];
  onClose: () => void;
  onSave: (inspection: VehicleInspection) => void;
}) {
  const [vehicleId, setVehicleId] = useState(inspection?.vehicleId || vehicles[0]?.id || "");
  const initialDate = inspection?.scheduledAt ? new Date(inspection.scheduledAt) : new Date();
  const [date, setDate] = useState(inspectionDateInput(initialDate));
  const [time, setTime] = useState(inspection?.scheduledAt ? `${String(initialDate.getHours()).padStart(2, "0")}:${String(initialDate.getMinutes()).padStart(2, "0")}` : "12:00");
  const initialVehicle = vehicles.find((vehicle) => vehicle.id === vehicleId);
  const [driverName, setDriverName] = useState(inspection?.driverName || initialVehicle?.driverName || "");
  const selectVehicle = (id: string) => {
    setVehicleId(id);
    if (!inspection) setDriverName(vehicles.find((vehicle) => vehicle.id === id)?.driverName || "");
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const vehicle = vehicles.find((item) => item.id === vehicleId);
    if (!vehicle) return;
    const now = new Date().toISOString();
    onSave({
      id: inspection?.id || uid(),
      vehicleId,
      vehiclePlate: vehicle.plate,
      vehicleName: [vehicle.make, vehicle.model].filter(Boolean).join(" "),
      scheduledAt: new Date(`${date}T${time}:00`).toISOString(),
      driverName: driverName.trim(),
      status: inspection?.status || "pending",
      mileage: inspection?.mileage,
      mileageEventId: inspection?.mileageEventId,
      hasDamage: inspection?.hasDamage,
      damageDescription: inspection?.damageDescription,
      damageAmount: inspection?.damageAmount,
      lateFine: inspection?.lateFine,
      washFine: inspection?.washFine,
      comment: inspection?.comment,
      photos: inspection?.photos || [],
      completedAt: inspection?.completedAt,
      createdAt: inspection?.createdAt || now,
      updatedAt: now,
    });
  };
  return (
    <Modal title={inspection ? "Изменить запись на осмотр" : "Добавить осмотр"} onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <Field label="Автомобиль" wide><select required value={vehicleId} onChange={(event) => selectVehicle(event.target.value)}><option value="" disabled>Выберите автомобиль</option>{vehicles.map((vehicle) => <option value={vehicle.id} key={vehicle.id}>{vehicle.plate} · {vehicle.make} {vehicle.model}</option>)}</select></Field>
        <Field label="Дата"><input required type="date" value={date} onChange={(event) => setDate(event.target.value)} /></Field>
        <Field label="Время"><input required type="time" value={time} onChange={(event) => setTime(event.target.value)} /></Field>
        <Field label="ФИО водителя" wide><input required value={driverName} onChange={(event) => setDriverName(event.target.value)} placeholder="Иванов Иван Иванович" /></Field>
        <div className="modal-actions wide"><span /><button type="button" className="secondary" onClick={onClose}>Отмена</button><button className="primary">Сохранить</button></div>
      </form>
    </Modal>
  );
}

function InspectionConductModal({ inspection, vehicles, userId, onClose, onSave }: {
  inspection: VehicleInspection;
  vehicles: Vehicle[];
  userId: string;
  onClose: () => void;
  onSave: (inspection: VehicleInspection, removedPaths: string[]) => void | Promise<void>;
}) {
  const [vehicleId, setVehicleId] = useState(inspection.vehicleId);
  const initialDate = new Date(inspection.scheduledAt);
  const [date, setDate] = useState(inspectionDateInput(initialDate));
  const [time, setTime] = useState(`${String(initialDate.getHours()).padStart(2, "0")}:${String(initialDate.getMinutes()).padStart(2, "0")}`);
  const [driverName, setDriverName] = useState(inspection.driverName);
  const vehicle = vehicles.find((item) => item.id === vehicleId);
  const [mileage, setMileage] = useState(inspection.mileage === undefined ? String(vehicle?.mileage || "") : String(inspection.mileage));
  const [damageState, setDamageState] = useState<"" | "yes" | "no">(inspection.hasDamage === undefined ? "" : inspection.hasDamage ? "yes" : "no");
  const [result, setResult] = useState<Exclude<VehicleInspectionStatus, "pending">>(inspection.status === "pending" ? "archive" : inspection.status);
  const [damageDescription, setDamageDescription] = useState(inspection.damageDescription || "");
  const [damageAmount, setDamageAmount] = useState(String(inspection.damageAmount ?? ""));
  const [lateFine, setLateFine] = useState(String(inspection.lateFine ?? ""));
  const [washFine, setWashFine] = useState(String(inspection.washFine ?? ""));
  const [comment, setComment] = useState(inspection.comment || "");
  const [photos, setPhotos] = useState(inspection.photos || []);
  const [removedPaths, setRemovedPaths] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const setDamage = (value: "yes" | "no") => {
    setDamageState(value);
    setResult(value === "yes" ? "damaged" : "archive");
  };
  const setInspectionResult = (value: Exclude<VehicleInspectionStatus, "pending">) => {
    setResult(value);
    setDamageState(value === "damaged" ? "yes" : "no");
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!damageState) { alert("Укажите наличие или отсутствие повреждений."); return; }
    const selectedVehicle = vehicles.find((item) => item.id === vehicleId);
    if (!selectedVehicle) return;
    if (newFiles.length && !supabase) { alert("Хранилище файлов сейчас недоступно."); return; }
    setUploading(true);
    const uploadedPaths: string[] = [];
    const nextPhotos = [...photos];
    for (const file of newFiles) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `${userId}/inspections/${inspection.id}/${uid()}-${safeName}`;
      const { error } = await supabase!.storage.from("voyager-files").upload(filePath, file, { contentType: file.type, upsert: false });
      if (error) {
        await Promise.all(uploadedPaths.map((path) => removePrivateFile(path)));
        alert(`Не удалось загрузить файл «${file.name}»: ${error.message}`);
        setUploading(false);
        return;
      }
      uploadedPaths.push(filePath);
      nextPhotos.push({ id: uid(), fileName: file.name, filePath, createdAt: new Date().toISOString() });
    }
    const now = new Date().toISOString();
    await onSave({
      ...inspection,
      vehicleId,
      vehiclePlate: selectedVehicle.plate,
      vehicleName: [selectedVehicle.make, selectedVehicle.model].filter(Boolean).join(" "),
      scheduledAt: new Date(`${date}T${time}:00`).toISOString(),
      driverName: driverName.trim(),
      status: result,
      mileage: mileage.trim() ? Math.max(0, Number(mileage) || 0) : undefined,
      hasDamage: damageState === "yes",
      damageDescription: damageDescription.trim() || undefined,
      damageAmount: damageAmount.trim() ? Number(damageAmount) || 0 : undefined,
      lateFine: lateFine.trim() ? Number(lateFine) || 0 : undefined,
      washFine: washFine.trim() ? Number(washFine) || 0 : undefined,
      comment: comment.trim() || undefined,
      photos: nextPhotos,
      completedAt: inspection.completedAt || now,
      updatedAt: now,
    }, removedPaths);
    setUploading(false);
  };
  return (
    <Modal title={inspection.status === "pending" ? "Осмотреть автомобиль" : "Изменить осмотр"} onClose={onClose} wide>
      <form className="form-grid inspection-conduct-form" onSubmit={submit}>
        <Field label="Автомобиль"><select required disabled value={vehicleId} onChange={(event) => setVehicleId(event.target.value)}>{vehicles.map((item) => <option value={item.id} key={item.id}>{item.plate} · {item.make} {item.model}</option>)}</select></Field>
        <Field label="ФИО водителя"><input required value={driverName} onChange={(event) => setDriverName(event.target.value)} /></Field>
        <Field label="Дата"><input required type="date" value={date} onChange={(event) => setDate(event.target.value)} /></Field>
        <Field label="Время"><input required type="time" value={time} onChange={(event) => setTime(event.target.value)} /></Field>
        <Field label="Пробег, км"><input type="number" min="0" value={mileage} onChange={(event) => setMileage(event.target.value)} placeholder="Не указан" /></Field>
        <Field label="Наличие повреждений"><select required value={damageState} onChange={(event) => setDamage(event.target.value as "yes" | "no")}><option value="" disabled>Выберите результат</option><option value="no">Повреждения отсутствуют</option><option value="yes">Повреждения есть</option></select></Field>
        {damageState === "yes" && <><Field label="Описание повреждений" wide><textarea rows={3} value={damageDescription} onChange={(event) => setDamageDescription(event.target.value)} placeholder="Опишите обнаруженные повреждения" /></Field><Field label="Сумма повреждений, ₽"><input type="number" min="0" step="0.01" value={damageAmount} onChange={(event) => setDamageAmount(event.target.value)} /></Field></>}
        <Field label="Штраф за опоздание, ₽"><input type="number" min="0" step="0.01" value={lateFine} onChange={(event) => setLateFine(event.target.value)} /></Field>
        <Field label="Штраф за мойку, ₽"><input type="number" min="0" step="0.01" value={washFine} onChange={(event) => setWashFine(event.target.value)} /></Field>
        <Field label="Результат осмотра"><select value={result} onChange={(event) => setInspectionResult(event.target.value as Exclude<VehicleInspectionStatus, "pending">)}><option value="archive">Архив — без повреждений</option><option value="damaged">С повреждениями</option></select></Field>
        <Field label="Комментарий" wide><textarea rows={3} value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Дополнительная информация по осмотру" /></Field>
        <Field label="Фотографии" wide><input type="file" accept="image/*" multiple onChange={(event) => setNewFiles(Array.from(event.target.files || []))} />{newFiles.length > 0 && <small className="file-selection-note">Выбрано фотографий: {newFiles.length}</small>}</Field>
        {photos.length > 0 && <div className="inspection-existing-photos wide"><span>Загруженные фотографии</span>{photos.map((photo) => <div key={photo.id}><b>{photo.fileName}</b><button type="button" className="danger-button" onClick={() => { setPhotos((items) => items.filter((item) => item.id !== photo.id)); setRemovedPaths((paths) => [...paths, photo.filePath]); }}>Убрать</button></div>)}</div>}
        <p className="inspection-finance-note wide">Суммы повреждений и штрафов сохраняются только для просмотра и не участвуют в финансах.</p>
        <div className="modal-actions wide"><span /><button type="button" className="secondary" onClick={onClose} disabled={uploading}>Отмена</button><button className="primary" disabled={uploading}>{uploading ? "Загрузка…" : "Сохранить осмотр"}</button></div>
      </form>
    </Modal>
  );
}

function InspectionDetailsModal({ inspection, vehicle, canEdit, onClose, onEdit }: {
  inspection: VehicleInspection;
  vehicle?: Vehicle;
  canEdit: boolean;
  onClose: () => void;
  onEdit: () => void;
}) {
  return (
    <Modal title={`Осмотр · ${inspection.vehiclePlate || vehicle?.plate || "Автомобиль"}`} onClose={onClose} wide>
      <div className="accident-details-grid">
        <InfoLine label="Дата и время" value={inspectionDateTime(inspection.scheduledAt)} />
        <InfoLine label="Автомобиль" value={`${inspection.vehiclePlate || vehicle?.plate || "—"} · ${inspection.vehicleName || [vehicle?.make, vehicle?.model].filter(Boolean).join(" ") || "—"}`} />
        <InfoLine label="Водитель" value={inspection.driverName} />
        <InfoLine label="Пробег" value={typeof inspection.mileage === "number" ? `${inspection.mileage.toLocaleString("ru-RU")} км` : "Не указан"} />
        <InfoLine label="Повреждения" value={inspection.status === "pending" ? "Осмотр не проведён" : inspection.hasDamage ? "Есть" : "Отсутствуют"} />
        <InfoLine label="Результат" value={inspection.status === "pending" ? "Актуальный — ожидает осмотра" : inspection.status === "damaged" ? "С повреждениями" : "Архив — без повреждений"} />
        <InfoLine label="Сумма повреждений" value={inspection.damageAmount === undefined ? "Не указана" : money(inspection.damageAmount)} />
        <InfoLine label="Штраф за опоздание" value={inspection.lateFine === undefined ? "Не указан" : money(inspection.lateFine)} />
        <InfoLine label="Штраф за мойку" value={inspection.washFine === undefined ? "Не указан" : money(inspection.washFine)} />
      </div>
      {inspection.damageDescription && <div className="accident-comment"><span>Описание повреждений</span><p>{inspection.damageDescription}</p></div>}
      {inspection.comment && <div className="accident-comment"><span>Комментарий</span><p>{inspection.comment}</p></div>}
      <section className="accident-attachment-section"><h3>Фотографии осмотра</h3>{inspection.photos.length ? <div className="accident-attachment-list">{inspection.photos.map((photo) => <button className="secondary" key={photo.id} onClick={() => void openPrivateFile(photo.filePath)}><span>▧</span><b>{photo.fileName}</b><em>Открыть →</em></button>)}</div> : <p className="muted">Фотографии не загружены</p>}</section>
      <div className="modal-actions accident-detail-actions"><span /><button className="secondary" onClick={onClose}>Закрыть</button>{canEdit && <button className="primary" onClick={onEdit}>Изменить</button>}</div>
    </Modal>
  );
}

type BicycleInspectionTab = BicycleInspectionStatus;
type BicycleInspectionBooleanFilter = "all" | "yes" | "no";
type BicycleInspectionSort = "date-desc" | "date-asc" | "mileage-desc" | "mileage-asc";

function bicycleInspectionTotalDamage(inspection: BicycleInspection) {
  return inspection.damages.reduce((sum, item) => sum + (item.amount || 0), 0);
}

function BicycleInspectionsPage({
  data,
  updateData,
  canEdit,
  userId,
  inspectorName,
}: {
  data: VoyagerData;
  updateData: (fn: (data: VoyagerData) => VoyagerData) => void;
  canEdit: boolean;
  userId: string;
  inspectorName: string;
}) {
  const [tab, setTab] = useState<BicycleInspectionTab>("pending");
  const [period, setPeriod] = useState(currentMonth());
  const [query, setQuery] = useState("");
  const [bicycleId, setBicycleId] = useState("all");
  const [damage, setDamage] = useState<BicycleInspectionBooleanFilter>("all");
  const [maintenance, setMaintenance] = useState<BicycleInspectionBooleanFilter>("all");
  const [sort, setSort] = useState<BicycleInspectionSort>("date-desc");
  const [creating, setCreating] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<BicycleInspection | null>(null);
  const [conducting, setConducting] = useState<BicycleInspection | null>(null);
  const [selected, setSelected] = useState<BicycleInspection | null>(null);

  const periodItems = data.bicycleInspections.filter(
    (item) => monthKey(item.scheduledAt) === period,
  );
  const counts: Record<BicycleInspectionTab, number> = {
    pending: periodItems.filter((item) => item.status === "pending").length,
    archive: periodItems.filter((item) => item.status === "archive").length,
    damaged: periodItems.filter((item) => item.status === "damaged").length,
  };
  const needle = query.trim().toLocaleLowerCase("ru");
  const filtered = periodItems
    .filter((inspection) => {
      const bicycle = data.bicycles.find((item) => item.id === inspection.bicycleId);
      const searchable = [
        inspection.bicycleInventoryNumber,
        inspection.bicycleName,
        bicycle?.serialNumber,
        bicycle?.batteryNumber,
        bicycle?.chargerNumber,
        inspection.renterName,
        inspection.inspectorName,
        inspection.comment,
        ...inspection.damages.flatMap((item) => [item.part, item.description]),
        ...inspection.maintenanceItems.map((item) => item.work),
      ].filter(Boolean).join(" ").toLocaleLowerCase("ru");
      return (
        inspection.status === tab &&
        (!needle || searchable.includes(needle)) &&
        (bicycleId === "all" || inspection.bicycleId === bicycleId) &&
        (tab === "pending" || damage === "all" || inspection.hasDamage === (damage === "yes")) &&
        (tab === "pending" || maintenance === "all" || inspection.requiresMaintenance === (maintenance === "yes"))
      );
    })
    .sort((left, right) => {
      if (sort === "date-asc") return left.scheduledAt.localeCompare(right.scheduledAt);
      if (sort === "mileage-desc") return (right.mileage || 0) - (left.mileage || 0);
      if (sort === "mileage-asc") return (left.mileage || 0) - (right.mileage || 0);
      return right.scheduledAt.localeCompare(left.scheduledAt);
    });

  const saveSchedule = (inspection: BicycleInspection) => {
    updateData((current) => ({
      ...current,
      bicycleInspections: current.bicycleInspections.some((item) => item.id === inspection.id)
        ? current.bicycleInspections.map((item) => item.id === inspection.id ? inspection : item)
        : [inspection, ...current.bicycleInspections],
    }));
    setCreating(false);
    setEditingSchedule(null);
  };

  const saveCompleted = async (
    inspection: BicycleInspection,
    removedPaths: string[],
  ) => {
    updateData((current) => {
      const previous = current.bicycleInspections.find((item) => item.id === inspection.id);
      const nextEvents = current.bicycleEvents.filter(
        (event) => event.inspectionId !== inspection.id,
      );
      if (typeof inspection.mileage === "number") {
        const previousMileage = current.bicycleEvents.find(
          (event) => event.inspectionId === inspection.id && event.type === "mileage",
        );
        nextEvents.push({
          id: previousMileage?.id || uid(),
          bicycleId: inspection.bicycleId,
          type: "mileage",
          title: "Пробег при осмотре велосипеда",
          mileage: inspection.mileage,
          date: inspection.scheduledAt,
          notes: `Осмотр арендатора: ${inspection.renterName || "не указан"}`,
          source: "bicycle_inspection",
          inspectionId: inspection.id,
          createdAt: previousMileage?.createdAt || new Date().toISOString(),
        });
      }

      const repairItems = [
        ...inspection.damages.map((item) =>
          [item.part, item.description].filter(Boolean).join(" — "),
        ),
        ...inspection.maintenanceItems.map((item) => item.work),
      ].filter(Boolean);
      const needsRepair = Boolean(
        (inspection.hasDamage && inspection.damages.length) ||
        (inspection.requiresMaintenance && inspection.maintenanceItems.length),
      );
      let linkedRepairOrderId = previous?.linkedRepairOrderId;
      let repairOrders = [...current.bicycleRepairOrders];
      const existingIndex = repairOrders.findIndex(
        (item) => item.sourceInspectionId === inspection.id || item.id === linkedRepairOrderId,
      );
      if (needsRepair) {
        const existing = existingIndex >= 0 ? repairOrders[existingIndex] : undefined;
        const damageTotal = bicycleInspectionTotalDamage(inspection);
        const generatedNotes = [
          `Создано автоматически из осмотра от ${shortDate(inspection.scheduledAt)}.`,
          damageTotal > 0 ? `Оценка повреждений: ${money(damageTotal)}.` : "",
          inspection.comment || "",
        ].filter(Boolean).join(" ");
        const repairOrder: BicycleRepairOrder = {
          id: existing?.id || uid(),
          bicycleId: inspection.bicycleId,
          urgency: existing?.urgency || (
            inspection.maintenanceItems.some((item) => item.urgency === "Срочный") || inspection.hasDamage
              ? "Срочный"
              : "Не срочный"
          ),
          repairs: repairItems,
          service: existing?.service || "Не выбран",
          customService: existing?.customService,
          laborCost: existing?.laborCost,
          payer: existing?.payer || "Не выбран",
          status: existing?.status || "Не выполнено",
          notes: existing && !existing.needsCompletion ? existing.notes : generatedNotes,
          sourceInspectionId: inspection.id,
          needsCompletion: existing
            ? existing.needsCompletion
            : true,
          createdAt: existing?.createdAt || new Date().toISOString(),
          completedAt: existing?.completedAt,
        };
        linkedRepairOrderId = repairOrder.id;
        if (existingIndex >= 0) repairOrders[existingIndex] = repairOrder;
        else repairOrders = [repairOrder, ...repairOrders];
      } else if (existingIndex >= 0 && repairOrders[existingIndex].needsCompletion) {
        repairOrders.splice(existingIndex, 1);
        linkedRepairOrderId = undefined;
      }

      const savedInspection = { ...inspection, linkedRepairOrderId };
      return {
        ...current,
        bicycles: current.bicycles.map((bicycle) =>
          bicycle.id === inspection.bicycleId && typeof inspection.mileage === "number"
            ? { ...bicycle, mileage: inspection.mileage }
            : bicycle,
        ),
        bicycleEvents: nextEvents,
        bicycleRepairOrders: repairOrders,
        bicycleInspections: current.bicycleInspections.map((item) =>
          item.id === inspection.id ? savedInspection : item,
        ),
      };
    });
    await Promise.all(removedPaths.map((path) => removePrivateFile(path)));
    setConducting(null);
  };

  const deleteInspection = async (inspection: BicycleInspection) => {
    if (!confirm(`Удалить осмотр велосипеда ${inspection.bicycleInventoryNumber}?`)) return;
    updateData((current) => ({
      ...current,
      bicycleInspections: current.bicycleInspections.filter((item) => item.id !== inspection.id),
      bicycleEvents: current.bicycleEvents.filter((event) => event.inspectionId !== inspection.id),
      bicycleRepairOrders: current.bicycleRepairOrders.filter(
        (item) => item.sourceInspectionId !== inspection.id || !item.needsCompletion,
      ),
    }));
    await Promise.all(inspection.photos.map((photo) => removePrivateFile(photo.filePath)));
    if (selected?.id === inspection.id) setSelected(null);
  };

  const resetFilters = () => {
    setQuery("");
    setBicycleId("all");
    setDamage("all");
    setMaintenance("all");
    setSort("date-desc");
  };

  return (
    <PageWrap
      className="inspection-page bicycle-inspection-page"
      title="Осмотры велосипедов"
      subtitle="Планирование осмотров, фиксация повреждений и автоматическая постановка ремонта."
      actions={canEdit
        ? <button className="primary" onClick={() => setCreating(true)}>＋ Добавить осмотр</button>
        : <span className="read-only-badge">Только просмотр</span>}
    >
      <div className="inspection-tabs" role="tablist">
        {([
          ["pending", "Актуальные"],
          ["archive", "Архив"],
          ["damaged", "С повреждениями"],
        ] as [BicycleInspectionTab, string][]).map(([id, label]) => (
          <button key={id} className={tab === id ? "active" : ""} onClick={() => setTab(id)}>
            {label}<span>{counts[id]}</span>
          </button>
        ))}
      </div>
      <section className="panel inspection-filters">
        <div className="inspection-filter-grid bicycle-inspection-filter-grid">
          <Field label="Месяц"><input type="month" value={period} onChange={(event) => setPeriod(event.target.value)} /></Field>
          <Field label="Поиск"><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Номер, модель или арендатор…" /></Field>
          <Field label="Велосипед"><select value={bicycleId} onChange={(event) => setBicycleId(event.target.value)}><option value="all">Все велосипеды</option>{data.bicycles.map((bicycle) => <option key={bicycle.id} value={bicycle.id}>{bicycle.inventoryNumber} · {bicycle.brand} {bicycle.model}</option>)}</select></Field>
          <Field label="Повреждения"><select value={tab === "pending" ? "all" : damage} disabled={tab === "pending"} onChange={(event) => setDamage(event.target.value as BicycleInspectionBooleanFilter)}><option value="all">Любой результат</option><option value="yes">Есть</option><option value="no">Нет</option></select></Field>
          <Field label="Обслуживание"><select value={tab === "pending" ? "all" : maintenance} disabled={tab === "pending"} onChange={(event) => setMaintenance(event.target.value as BicycleInspectionBooleanFilter)}><option value="all">Любой результат</option><option value="yes">Требуется</option><option value="no">Не требуется</option></select></Field>
          <Field label="Сортировка"><select value={sort} onChange={(event) => setSort(event.target.value as BicycleInspectionSort)}><option value="date-desc">Сначала новые</option><option value="date-asc">Сначала старые</option><option value="mileage-desc">Пробег: по убыванию</option><option value="mileage-asc">Пробег: по возрастанию</option></select></Field>
        </div>
        <div className="accident-filter-footer"><span>Найдено: {filtered.length}</span>{(query || bicycleId !== "all" || damage !== "all" || maintenance !== "all" || sort !== "date-desc") && <button className="secondary small" onClick={resetFilters}>Сбросить фильтры</button>}</div>
      </section>
      <div className="inspection-list">
        {filtered.length ? filtered.map((inspection) => (
          <BicycleInspectionCard
            key={inspection.id}
            inspection={inspection}
            bicycle={data.bicycles.find((item) => item.id === inspection.bicycleId)}
            canEdit={canEdit}
            onOpen={() => inspection.status === "pending" && canEdit ? setConducting(inspection) : setSelected(inspection)}
            onEdit={() => inspection.status === "pending" ? setEditingSchedule(inspection) : setConducting(inspection)}
            onDelete={() => void deleteInspection(inspection)}
          />
        )) : <section className="panel"><Empty icon="◎" title="Осмотры не найдены" text={data.bicycleInspections.length ? "Измените месяц или условия фильтрации." : "Добавьте первый осмотр электровелосипеда."} /></section>}
      </div>
      {(creating || editingSchedule) && <BicycleInspectionScheduleModal inspection={editingSchedule || undefined} bicycles={data.bicycles} onClose={() => { setCreating(false); setEditingSchedule(null); }} onSave={saveSchedule} />}
      {conducting && <BicycleInspectionConductModal inspection={conducting} bicycles={data.bicycles} userId={userId} inspectorName={inspectorName} onClose={() => setConducting(null)} onSave={saveCompleted} />}
      {selected && <BicycleInspectionDetailsModal inspection={selected} bicycle={data.bicycles.find((item) => item.id === selected.bicycleId)} repair={data.bicycleRepairOrders.find((item) => item.id === selected.linkedRepairOrderId)} canEdit={canEdit} onClose={() => setSelected(null)} onEdit={() => { setConducting(selected); setSelected(null); }} />}
    </PageWrap>
  );
}

function BicycleInspectionCard({ inspection, bicycle, canEdit, onOpen, onEdit, onDelete }: {
  inspection: BicycleInspection;
  bicycle?: Bicycle;
  canEdit: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const attention = inspection.status === "damaged";
  return <article className="panel inspection-card bicycle-inspection-card">
    <div className="inspection-card-date"><span>Дата и время</span><strong>{inspectionDateTime(inspection.scheduledAt)}</strong></div>
    <div className="inspection-card-main"><div><h2>{inspection.bicycleInventoryNumber || bicycle?.inventoryNumber || "Без номера"}</h2><span>{inspection.bicycleName || [bicycle?.brand, bicycle?.model].filter(Boolean).join(" ")}</span></div><p>{inspection.renterName || "Арендатор не назначен"}</p></div>
    {inspection.status !== "pending" && <div className="inspection-card-result"><span className={attention ? "damaged" : "clear"}>{attention ? "Требует внимания" : "Без замечаний"}</span>{typeof inspection.mileage === "number" && <b>{inspection.mileage.toLocaleString("ru-RU")} км</b>}</div>}
    <div className="inspection-card-actions"><button className={inspection.status === "pending" && canEdit ? "primary" : "secondary"} onClick={onOpen}>{inspection.status === "pending" && canEdit ? "Провести осмотр" : "Открыть"}</button>{canEdit && <button className="secondary" onClick={onEdit}>Изменить</button>}{canEdit && <button className="danger-button" onClick={onDelete}>Удалить</button>}</div>
  </article>;
}

function BicycleInspectionScheduleModal({ inspection, bicycles, onClose, onSave }: {
  inspection?: BicycleInspection;
  bicycles: Bicycle[];
  onClose: () => void;
  onSave: (inspection: BicycleInspection) => void;
}) {
  const availableBicycles = bicycles.filter((item) => item.status !== "Архив");
  const [bicycleId, setBicycleId] = useState(inspection?.bicycleId || availableBicycles[0]?.id || "");
  const initialDate = inspection?.scheduledAt ? new Date(inspection.scheduledAt) : new Date();
  const [date, setDate] = useState(inspectionDateInput(initialDate));
  const [time, setTime] = useState(inspection?.scheduledAt ? `${String(initialDate.getHours()).padStart(2, "0")}:${String(initialDate.getMinutes()).padStart(2, "0")}` : "12:00");
  const selected = bicycles.find((item) => item.id === bicycleId);
  const [comment, setComment] = useState(inspection?.comment || "");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    const now = new Date().toISOString();
    onSave({
      id: inspection?.id || uid(), bicycleId, bicycleInventoryNumber: selected.inventoryNumber,
      bicycleName: `${selected.brand} ${selected.model}`.trim(), renterName: selected.currentRenter || "Не назначен",
      scheduledAt: new Date(`${date}T${time}:00`).toISOString(), status: inspection?.status || "pending",
      mileage: inspection?.mileage, hasDamage: inspection?.hasDamage, damages: inspection?.damages || [],
      requiresMaintenance: inspection?.requiresMaintenance, maintenanceItems: inspection?.maintenanceItems || [],
      comment: comment.trim() || undefined, photos: inspection?.photos || [], inspectorName: inspection?.inspectorName,
      linkedRepairOrderId: inspection?.linkedRepairOrderId, completedAt: inspection?.completedAt,
      createdAt: inspection?.createdAt || now, updatedAt: now,
    });
  };
  return <Modal title={inspection ? "Изменить запись на осмотр" : "Добавить осмотр велосипеда"} onClose={onClose}>
    <form className="form-grid" onSubmit={submit}>
      <Field label="Электровелосипед" wide><select required value={bicycleId} onChange={(event) => setBicycleId(event.target.value)}><option value="" disabled>Выберите велосипед</option>{availableBicycles.map((item) => <option key={item.id} value={item.id}>{item.inventoryNumber} · {item.brand} {item.model}</option>)}</select></Field>
      <Field label="ФИО арендатора" wide><input readOnly value={selected?.currentRenter || "Не назначен"} /></Field>
      <Field label="Дата"><input required type="date" value={date} onChange={(event) => setDate(event.target.value)} /></Field>
      <Field label="Время"><input required type="time" value={time} onChange={(event) => setTime(event.target.value)} /></Field>
      <Field label="Комментарий" wide><textarea rows={3} value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Дополнительная информация к записи" /></Field>
      <div className="modal-actions wide"><span /><button type="button" className="secondary" onClick={onClose}>Отмена</button><button className="primary">Сохранить</button></div>
    </form>
  </Modal>;
}

function BicycleInspectionConductModal({ inspection, bicycles, userId, inspectorName, onClose, onSave }: {
  inspection: BicycleInspection;
  bicycles: Bicycle[];
  userId: string;
  inspectorName: string;
  onClose: () => void;
  onSave: (inspection: BicycleInspection, removedPaths: string[]) => void | Promise<void>;
}) {
  const bicycle = bicycles.find((item) => item.id === inspection.bicycleId);
  const initialDate = new Date(inspection.scheduledAt);
  const [date, setDate] = useState(inspectionDateInput(initialDate));
  const [time, setTime] = useState(`${String(initialDate.getHours()).padStart(2, "0")}:${String(initialDate.getMinutes()).padStart(2, "0")}`);
  const [mileage, setMileage] = useState(String(inspection.mileage ?? bicycle?.mileage ?? ""));
  const [hasDamage, setHasDamage] = useState<"" | "yes" | "no">(inspection.hasDamage === undefined ? "" : inspection.hasDamage ? "yes" : "no");
  const [damages, setDamages] = useState<BicycleInspectionDamage[]>(inspection.damages.length ? inspection.damages : [{ id: uid(), part: "" }]);
  const [requiresMaintenance, setRequiresMaintenance] = useState<"" | "yes" | "no">(inspection.requiresMaintenance === undefined ? "" : inspection.requiresMaintenance ? "yes" : "no");
  const [maintenanceItems, setMaintenanceItems] = useState<BicycleInspectionMaintenanceItem[]>(inspection.maintenanceItems.length ? inspection.maintenanceItems : [{ id: uid(), work: "", urgency: "Не срочный" }]);
  const [comment, setComment] = useState(inspection.comment || "");
  const [photos, setPhotos] = useState(inspection.photos || []);
  const [removedPaths, setRemovedPaths] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!hasDamage || !requiresMaintenance) { alert("Укажите наличие повреждений и необходимость обслуживания."); return; }
    const cleanDamages = hasDamage === "yes" ? damages.filter((item) => item.part.trim()).map((item) => ({ ...item, part: item.part.trim(), description: item.description?.trim() || undefined, amount: item.amount === undefined ? undefined : Number(item.amount) || 0 })) : [];
    const cleanMaintenance = requiresMaintenance === "yes" ? maintenanceItems.filter((item) => item.work.trim()).map((item) => ({ ...item, work: item.work.trim() })) : [];
    if (hasDamage === "yes" && !cleanDamages.length) { alert("Добавьте хотя бы одну повреждённую запчасть."); return; }
    if (requiresMaintenance === "yes" && !cleanMaintenance.length) { alert("Добавьте хотя бы одну необходимую работу."); return; }
    if (newFiles.length && !supabase) { alert("Хранилище файлов сейчас недоступно."); return; }
    setUploading(true);
    const uploadedPaths: string[] = [];
    const nextPhotos = [...photos];
    for (const file of newFiles) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `${userId}/bicycle-inspections/${inspection.id}/${uid()}-${safeName}`;
      const { error } = await supabase!.storage.from("voyager-files").upload(filePath, file, { contentType: file.type, upsert: false });
      if (error) { await Promise.all(uploadedPaths.map((path) => removePrivateFile(path))); alert(`Не удалось загрузить файл «${file.name}»: ${error.message}`); setUploading(false); return; }
      uploadedPaths.push(filePath);
      nextPhotos.push({ id: uid(), fileName: file.name, filePath, createdAt: new Date().toISOString() });
    }
    const now = new Date().toISOString();
    const needsAttention = cleanDamages.length > 0 || cleanMaintenance.length > 0;
    await onSave({
      ...inspection, scheduledAt: new Date(`${date}T${time}:00`).toISOString(),
      renterName: inspection.renterName || bicycle?.currentRenter || "Не назначен",
      status: needsAttention ? "damaged" : "archive", mileage: mileage.trim() ? Math.max(0, Number(mileage) || 0) : undefined,
      hasDamage: hasDamage === "yes", damages: cleanDamages,
      requiresMaintenance: requiresMaintenance === "yes", maintenanceItems: cleanMaintenance,
      comment: comment.trim() || undefined, photos: nextPhotos, inspectorName,
      completedAt: inspection.completedAt || now, updatedAt: now,
    }, removedPaths);
    setUploading(false);
  };
  return <Modal title={inspection.status === "pending" ? "Провести осмотр велосипеда" : "Изменить осмотр велосипеда"} onClose={onClose} wide>
    <form className="form-grid inspection-conduct-form bicycle-inspection-conduct" onSubmit={submit}>
      <Field label="Электровелосипед"><input readOnly value={`${inspection.bicycleInventoryNumber} · ${inspection.bicycleName}`} /></Field>
      <Field label="ФИО арендатора"><input readOnly value={inspection.renterName || "Не назначен"} /></Field>
      <Field label="Дата"><input required type="date" value={date} onChange={(event) => setDate(event.target.value)} /></Field>
      <Field label="Время"><input required type="time" value={time} onChange={(event) => setTime(event.target.value)} /></Field>
      <Field label="Пробег, км"><input type="number" min="0" value={mileage} onChange={(event) => setMileage(event.target.value)} /></Field>
      <Field label="Повреждения"><select required value={hasDamage} onChange={(event) => setHasDamage(event.target.value as "" | "yes" | "no")}><option value="" disabled>Выберите</option><option value="no">Повреждений нет</option><option value="yes">Повреждения есть</option></select></Field>
      {hasDamage === "yes" && <div className="wide inspection-items-editor"><div className="repair-items-head"><b>Повреждённые запчасти</b><button type="button" className="secondary small" onClick={() => setDamages((items) => [...items, { id: uid(), part: "" }])}>＋ Добавить</button></div>{damages.map((item, index) => <div className="inspection-damage-row" key={item.id}><span>{index + 1}</span><input required value={item.part} onChange={(event) => setDamages((items) => items.map((value) => value.id === item.id ? { ...value, part: event.target.value } : value))} placeholder="Повреждённая запчасть" /><input value={item.description || ""} onChange={(event) => setDamages((items) => items.map((value) => value.id === item.id ? { ...value, description: event.target.value } : value))} placeholder="Описание" /><input type="number" min="0" step="0.01" value={item.amount ?? ""} onChange={(event) => setDamages((items) => items.map((value) => value.id === item.id ? { ...value, amount: event.target.value ? Number(event.target.value) : undefined } : value))} placeholder="Сумма, ₽" /><button type="button" className="icon-delete" disabled={damages.length === 1} onClick={() => setDamages((items) => items.filter((value) => value.id !== item.id))}>×</button></div>)}</div>}
      <Field label="Требуется обслуживание"><select required value={requiresMaintenance} onChange={(event) => setRequiresMaintenance(event.target.value as "" | "yes" | "no")}><option value="" disabled>Выберите</option><option value="no">Не требуется</option><option value="yes">Требуется</option></select></Field>
      {requiresMaintenance === "yes" && <div className="wide inspection-items-editor"><div className="repair-items-head"><b>Необходимые работы</b><button type="button" className="secondary small" onClick={() => setMaintenanceItems((items) => [...items, { id: uid(), work: "", urgency: "Не срочный" }])}>＋ Добавить</button></div>{maintenanceItems.map((item, index) => <div className="inspection-maintenance-row" key={item.id}><span>{index + 1}</span><input required value={item.work} onChange={(event) => setMaintenanceItems((items) => items.map((value) => value.id === item.id ? { ...value, work: event.target.value } : value))} placeholder="Что требуется сделать" /><select value={item.urgency} onChange={(event) => setMaintenanceItems((items) => items.map((value) => value.id === item.id ? { ...value, urgency: event.target.value as BicycleRepairUrgency } : value))}><option>Срочный</option><option>Не срочный</option></select><button type="button" className="icon-delete" disabled={maintenanceItems.length === 1} onClick={() => setMaintenanceItems((items) => items.filter((value) => value.id !== item.id))}>×</button></div>)}</div>}
      <Field label="Комментарий" wide><textarea rows={3} value={comment} onChange={(event) => setComment(event.target.value)} /></Field>
      <Field label="Фотографии" wide><input type="file" accept="image/*" multiple onChange={(event) => setNewFiles(Array.from(event.target.files || []))} />{newFiles.length > 0 && <small className="file-selection-note">Выбрано фотографий: {newFiles.length}</small>}</Field>
      {photos.length > 0 && <div className="inspection-existing-photos wide"><span>Загруженные фотографии</span>{photos.map((photo) => <div key={photo.id}><b>{photo.fileName}</b><button type="button" className="danger-button" onClick={() => { setPhotos((items) => items.filter((item) => item.id !== photo.id)); setRemovedPaths((paths) => [...paths, photo.filePath]); }}>Убрать</button></div>)}</div>}
      <p className="inspection-finance-note wide">Суммы повреждений справочные и не входят в финансы. При замечаниях будет создан черновик ремонта.</p>
      <div className="modal-actions wide"><span /><button type="button" className="secondary" onClick={onClose} disabled={uploading}>Отмена</button><button className="primary" disabled={uploading}>{uploading ? "Загрузка…" : "Сохранить осмотр"}</button></div>
    </form>
  </Modal>;
}

function BicycleInspectionDetailsModal({ inspection, bicycle, repair, canEdit, onClose, onEdit }: {
  inspection: BicycleInspection;
  bicycle?: Bicycle;
  repair?: BicycleRepairOrder;
  canEdit: boolean;
  onClose: () => void;
  onEdit: () => void;
}) {
  return <Modal title={`Осмотр · ${inspection.bicycleInventoryNumber || bicycle?.inventoryNumber || "Велосипед"}`} onClose={onClose} wide>
    <div className="accident-details-grid">
      <InfoLine label="Дата и время" value={inspectionDateTime(inspection.scheduledAt)} />
      <InfoLine label="Велосипед" value={`${inspection.bicycleInventoryNumber} · ${inspection.bicycleName}`} />
      <InfoLine label="Арендатор" value={inspection.renterName || "Не назначен"} />
      <InfoLine label="Пробег" value={inspection.mileage === undefined ? "Не указан" : `${inspection.mileage.toLocaleString("ru-RU")} км`} />
      <InfoLine label="Повреждения" value={inspection.hasDamage ? "Есть" : "Нет"} />
      <InfoLine label="Сумма повреждений" value={money(bicycleInspectionTotalDamage(inspection))} />
      <InfoLine label="Обслуживание" value={inspection.requiresMaintenance ? "Требуется" : "Не требуется"} />
      <InfoLine label="Провёл осмотр" value={inspection.inspectorName || "Не указан"} />
      <InfoLine label="Связанный ремонт" value={repair ? `${repair.status}${repair.needsCompletion ? " · требует заполнения" : ""}` : "Не создавался"} />
    </div>
    {inspection.damages.length > 0 && <section className="inspection-detail-section"><h3>Повреждения</h3><ol className="repair-task-list">{inspection.damages.map((item) => <li key={item.id}><b>{item.part}</b>{item.description ? ` — ${item.description}` : ""}{item.amount !== undefined ? ` · ${money(item.amount)}` : ""}</li>)}</ol></section>}
    {inspection.maintenanceItems.length > 0 && <section className="inspection-detail-section"><h3>Требуется обслуживание</h3><ol className="repair-task-list">{inspection.maintenanceItems.map((item) => <li key={item.id}>{item.work} · {item.urgency}</li>)}</ol></section>}
    {inspection.comment && <div className="accident-comment"><span>Комментарий</span><p>{inspection.comment}</p></div>}
    <section className="accident-attachment-section"><h3>Фотографии осмотра</h3>{inspection.photos.length ? <div className="accident-attachment-list">{inspection.photos.map((photo) => <button className="secondary" key={photo.id} onClick={() => void openPrivateFile(photo.filePath)}><span>▧</span><b>{photo.fileName}</b><em>Открыть →</em></button>)}</div> : <p className="muted">Фотографии не загружены</p>}</section>
    <div className="modal-actions accident-detail-actions"><span /><button className="secondary" onClick={onClose}>Закрыть</button>{canEdit && <button className="primary" onClick={onEdit}>Изменить</button>}</div>
  </Modal>;
}

function NikitaMoneyPage({
  data,
  updateData,
  canEdit,
}: {
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
  canEdit: boolean;
}) {
  const [month, setMonth] = useState(currentMonth());
  const [kindFilter, setKindFilter] = useState<"all" | NikitaMoneyEntryKind>("all");
  const [categoryFilter, setCategoryFilter] = useState<"all" | NikitaMoneyCategory>("all");
  const [query, setQuery] = useState("");
  const [addingKind, setAddingKind] = useState<NikitaMoneyEntryKind | null>(null);
  const [editingEntry, setEditingEntry] = useState<NikitaMoneyEntry | null>(null);

  const entries = [...data.nikitaMoneyEntries].sort((a, b) =>
    b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt),
  );
  const months = Array.from(
    new Set([currentMonth(), ...entries.map((entry) => entry.date.slice(0, 7))]),
  ).sort((a, b) => b.localeCompare(a));
  const periodEntries = month === "all"
    ? entries
    : entries.filter((entry) => entry.date.slice(0, 7) === month);
  const income = periodEntries
    .filter((entry) => entry.kind === "income")
    .reduce((sum, entry) => sum + entry.amount, 0);
  const expenses = periodEntries
    .filter((entry) => entry.kind === "expense")
    .reduce((sum, entry) => sum + entry.amount, 0);
  const balanceEntries = month === "all"
    ? entries
    : entries.filter((entry) => entry.date.slice(0, 7) <= month);
  const balance = balanceEntries.reduce(
    (sum, entry) => sum + (entry.kind === "income" ? entry.amount : -entry.amount),
    0,
  );
  const runningBalances = new Map<string, number>();
  let runningBalance = 0;
  [...entries]
    .sort((a, b) => a.date.localeCompare(b.date) || a.createdAt.localeCompare(b.createdAt))
    .forEach((entry) => {
      runningBalance += entry.kind === "income" ? entry.amount : -entry.amount;
      runningBalances.set(entry.id, runningBalance);
    });
  const normalizedQuery = query.trim().toLowerCase();
  const filteredEntries = periodEntries.filter((entry) => {
    const matchesKind = kindFilter === "all" || entry.kind === kindFilter;
    const matchesCategory = categoryFilter === "all" || entry.category === categoryFilter;
    const haystack = [entry.category, entry.comment, shortDate(entry.date), entry.amount]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return matchesKind && matchesCategory && (!normalizedQuery || haystack.includes(normalizedQuery));
  });
  const saveEntry = (entry: NikitaMoneyEntry) => {
    updateData((current) => ({
      ...current,
      nikitaMoneyEntries: current.nikitaMoneyEntries.some((item) => item.id === entry.id)
        ? current.nikitaMoneyEntries.map((item) => item.id === entry.id ? entry : item)
        : [entry, ...current.nikitaMoneyEntries],
    }));
    setAddingKind(null);
    setEditingEntry(null);
  };
  const deleteEntry = (entry: NikitaMoneyEntry) => {
    if (!confirm(`Удалить ${entry.kind === "income" ? "приход" : "расход"} на сумму ${money(entry.amount)}?`)) return;
    updateData((current) => ({
      ...current,
      nikitaMoneyEntries: current.nikitaMoneyEntries.filter((item) => item.id !== entry.id),
    }));
  };

  return (
    <PageWrap
      title="Учёт денег Никиты"
      subtitle="Отдельный справочный счёт для учёта поступлений, расходов и остатка."
      actions={canEdit ? (
        <div className="nikita-money-actions">
          <button className="secondary" onClick={() => setAddingKind("expense")}>− Добавить расход</button>
          <button className="primary" onClick={() => setAddingKind("income")}>＋ Добавить приход</button>
        </div>
      ) : undefined}
    >
      <div className="nikita-money-note">
        <span>i</span>
        <div><b>Справочный раздел</b><p>Данные доступны всем сотрудникам и не участвуют в доходах, расходах, прибыли компании или расчётах инвесторов.</p></div>
      </div>
      <div className="hero-stats compact nikita-money-summary">
        <Stat label="Поступило" value={money(income)} note={month === "all" ? "За всё время" : month} tone="mint" />
        <Stat label="Потрачено" value={money(expenses)} note={month === "all" ? "За всё время" : month} tone="warm" />
        <Stat label="Остаток на счёте" value={money(balance)} note={month === "all" ? "Текущий остаток" : "На конец выбранного месяца"} />
      </div>
      <section className="panel nikita-money-panel">
        <div className="section-heading">
          <div><h2>Операции по счёту</h2><p>{filteredEntries.length} записей по выбранным условиям</p></div>
        </div>
        <div className="nikita-money-toolbar">
          <label><span>Месяц</span><select value={month} onChange={(event) => setMonth(event.target.value)}><option value="all">Всё время</option>{months.map((item) => <option value={item} key={item}>{item}</option>)}</select></label>
          <label><span>Операция</span><select value={kindFilter} onChange={(event) => setKindFilter(event.target.value as "all" | NikitaMoneyEntryKind)}><option value="all">Все операции</option><option value="income">Приходы</option><option value="expense">Расходы</option></select></label>
          <label><span>Категория</span><select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value as "all" | NikitaMoneyCategory)}><option value="all">Все категории</option>{NIKITA_MONEY_CATEGORIES.map((item) => <option value={item} key={item}>{item}</option>)}</select></label>
          <label className="nikita-money-search"><span>Поиск</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Категория, комментарий, сумма…" /></label>
        </div>
        {filteredEntries.length ? (
          <div className="nikita-money-list">
            {filteredEntries.map((entry) => (
              <article className={`nikita-money-row ${entry.kind}`} key={entry.id}>
                <div className="nikita-money-kind" aria-hidden="true">{entry.kind === "income" ? "↗" : "↘"}</div>
                <div className="nikita-money-main"><div><b>{entry.category}</b><span>{entry.kind === "income" ? "Приход" : "Расход"} · {shortDate(entry.date)}</span></div>{entry.comment && <p>{entry.comment}</p>}</div>
                <div className="nikita-money-values"><strong>{entry.kind === "income" ? "+" : "−"}{money(entry.amount)}</strong><span>Остаток после: {money(runningBalances.get(entry.id) || 0)}</span></div>
                {canEdit && <div className="nikita-money-row-actions"><button className="secondary" onClick={() => setEditingEntry(entry)}>Изменить</button><button className="danger-button" onClick={() => deleteEntry(entry)}>Удалить</button></div>}
              </article>
            ))}
          </div>
        ) : <div className="empty-card"><b>Операций пока нет</b><p>Измените фильтры или добавьте первый приход либо расход.</p></div>}
      </section>
      {(addingKind || editingEntry) && (
        <NikitaMoneyModal
          entry={editingEntry || undefined}
          initialKind={addingKind || editingEntry?.kind || "income"}
          onSave={saveEntry}
          onClose={() => { setAddingKind(null); setEditingEntry(null); }}
        />
      )}
    </PageWrap>
  );
}

function NikitaMoneyModal({ entry, initialKind, onSave, onClose }: {
  entry?: NikitaMoneyEntry;
  initialKind: NikitaMoneyEntryKind;
  onSave: (entry: NikitaMoneyEntry) => void;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<NikitaMoneyEntryKind>(entry?.kind || initialKind);
  const [date, setDate] = useState(entry?.date || new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(entry ? String(entry.amount) : "");
  const [category, setCategory] = useState<NikitaMoneyCategory>(entry?.category || NIKITA_MONEY_CATEGORIES[0]);
  const [comment, setComment] = useState(entry?.comment || "");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const numericAmount = Number(amount.replace(",", "."));
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      alert("Укажите сумму больше нуля.");
      return;
    }
    const now = new Date().toISOString();
    onSave({
      id: entry?.id || uid(),
      kind,
      date,
      amount: numericAmount,
      category,
      comment: comment.trim() || undefined,
      createdAt: entry?.createdAt || now,
      updatedAt: now,
    });
  };
  return (
    <Modal title={entry ? "Изменить операцию" : kind === "income" ? "Добавить приход" : "Добавить расход"} onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <Field label="Тип операции"><select value={kind} onChange={(event) => setKind(event.target.value as NikitaMoneyEntryKind)}><option value="income">Приход</option><option value="expense">Расход</option></select></Field>
        <Field label="Дата"><input required type="date" value={date} onChange={(event) => setDate(event.target.value)} /></Field>
        <Field label="Сумма, ₽"><input required autoFocus type="number" min="0.01" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0" /></Field>
        <Field label="Категория"><select required value={category} onChange={(event) => setCategory(event.target.value as NikitaMoneyCategory)}>{NIKITA_MONEY_CATEGORIES.map((item) => <option value={item} key={item}>{item}</option>)}</select></Field>
        <Field label="Комментарий" wide><textarea rows={4} value={comment} onChange={(event) => setComment(event.target.value)} placeholder="На что поступили или были потрачены деньги" /></Field>
        <p className="nikita-money-form-note wide">Эта операция сохранится только в разделе «Учёт денег Никиты».</p>
        <div className="modal-actions wide"><span /><button type="button" className="secondary" onClick={onClose}>Отмена</button><button className="primary">Сохранить</button></div>
      </form>
    </Modal>
  );
}

function FinanceLedger({
  mode,
  data,
  updateData,
  onOpenVehicle,
}: {
  mode: "expenses" | "income";
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
  onOpenVehicle: (id: string) => void;
}) {
  const [show, setShow] = useState(false);
  const [editingEntry, setEditingEntry] = useState<VoyagerEvent | null>(null);
  const [month, setMonth] = useState("all");
  const [query, setQuery] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [investorFilter, setInvestorFilter] = useState("all");
  const expenseTypes: EventType[] = ["expense", "repair", "maintenance"];
  const allEvents = data.events
    .filter((event) =>
      mode === "income"
        ? event.type === "income"
        : expenseTypes.includes(event.type),
    )
    .sort((a, b) => b.date.localeCompare(a.date));
  const months = Array.from(
    new Set(allEvents.map((event) => event.date.slice(0, 7))),
  ).sort((a, b) => b.localeCompare(a));
  const monthEvents =
    month === "all"
      ? allEvents
      : allEvents.filter((event) => event.date.slice(0, 7) === month);
  const normalizedQuery = query.trim().toLowerCase();
  const events = monthEvents.filter((event) => {
    const vehicle = data.vehicles.find((item) => item.id === event.vehicleId);
    const investorId = event.investorId || vehicle?.investorId || "";
    const investor = data.investors.find((item) => item.id === investorId);
    const matchesVehicle =
      vehicleFilter === "all" || event.vehicleId === vehicleFilter;
    const matchesInvestor =
      mode === "income" ||
      investorFilter === "all" ||
      investorId === investorFilter;
    const haystack = [
      event.title,
      event.notes,
      event.paymentMethod,
      event.amount,
      vehicle?.make,
      vehicle?.model,
      vehicle?.plate,
      investor?.name,
      shortDate(event.date),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return (
      matchesVehicle &&
      matchesInvestor &&
      (!normalizedQuery || haystack.includes(normalizedQuery))
    );
  });
  const amount = events.reduce((sum, event) => sum + (event.amount || 0), 0);
  const personal = events
    .filter((event) => event.expenseScope === "investor_personal")
    .reduce((sum, event) => sum + (event.amount || 0), 0);
  const companyAmount = mode === "expenses" ? amount - personal : amount;
  const title = mode === "expenses" ? "Расходы" : "Доходы";
  const deleteEntry = (event: VoyagerEvent) => {
    const entryName = mode === "expenses" ? "расход" : "доход";
    if (!confirm(`Отменить ${entryName} «${event.title}»?`)) return;
    void removePrivateFile(event.filePath);
    updateData((d) => ({
      ...d,
      events: d.events.filter((item) => item.id !== event.id),
    }));
  };
  return (
    <PageWrap
      title={title}
      subtitle={
        mode === "expenses"
          ? "Расходы автомобилей и личные расходы инвесторов."
          : "Все статьи доходов таксопарка и общая выручка компании."
      }
      actions={
        <button className="primary" onClick={() => setShow(true)}>
          ＋ Добавить {mode === "expenses" ? "расход" : "доход"}
        </button>
      }
    >
      <div className="hero-stats compact ledger-summary">
        <Stat
          label={mode === "expenses" ? "Расходы компании" : "Общий доход"}
          value={money(companyAmount)}
          note={month === "all" ? "За всё время" : month}
          tone={mode === "expenses" ? "warm" : "mint"}
        />
        {mode === "expenses" ? (
          <>
            <Stat
              label="Расходы инвесторов"
              value={money(personal)}
              note="Без привязки к автомобилю"
            />
            <Stat
              label="Всего записей"
              value={String(events.length)}
              note="Машины и инвесторы"
            />
          </>
        ) : (
          <>
            <Stat
              label="Автомобилей с доходом"
              value={String(
                new Set(events.map((event) => event.vehicleId).filter(Boolean))
                  .size,
              )}
              note="В выбранном периоде"
            />
            <Stat
              label="Статей дохода"
              value={String(events.length)}
              note="Все поступления"
            />
          </>
        )}
      </div>
      <div className="ledger-toolbar">
        <label className="ledger-search">
          Поиск
          <span className="search">
            ⌕
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                mode === "expenses"
                  ? "Статья, сумма, госномер или инвестор"
                  : "Статья, сумма или госномер"
              }
            />
          </span>
        </label>
        <label>
          Автомобиль
          <select
            value={vehicleFilter}
            onChange={(e) => setVehicleFilter(e.target.value)}
          >
            <option value="all">Все госномера</option>
            {data.vehicles
              .slice()
              .sort((a, b) => a.plate.localeCompare(b.plate, "ru"))
              .map((vehicle) => (
                <option value={vehicle.id} key={vehicle.id}>
                  {vehicle.plate} · {vehicle.make} {vehicle.model}
                </option>
              ))}
          </select>
        </label>
        {mode === "expenses" && (
          <label>
            Инвестор
            <select
              value={investorFilter}
              onChange={(e) => setInvestorFilter(e.target.value)}
            >
              <option value="all">Все инвесторы</option>
              {data.investors
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name, "ru"))
                .map((investor) => (
                  <option value={investor.id} key={investor.id}>
                    {investor.name}
                  </option>
                ))}
            </select>
          </label>
        )}
        <label>
          Период
          <select value={month} onChange={(e) => setMonth(e.target.value)}>
            <option value="all">За всё время</option>
            {months.map((item) => (
              <option value={item} key={item}>
                {item}
              </option>
            ))}
          </select>
        </label>
      </div>
      <section className="panel table-panel">
        <div className="ledger-table">
          <div className="ledger-row ledger-head">
            <span>Статья</span>
            <span>Кому относится</span>
            <span>Оплата</span>
            <span>Дата</span>
            <span>Сумма</span>
            <span />
          </div>
          {events.map((event) => {
            const vehicle = data.vehicles.find(
              (item) => item.id === event.vehicleId,
            );
            const investor = data.investors.find(
              (item) => item.id === event.investorId,
            );
            return (
              <div className="ledger-row" key={event.id}>
                <span>
                  <i
                    className={`event-icon event-${event.type}`}
                    aria-hidden="true"
                  >
                    {EVENT_ICONS[event.type]}
                  </i>
                  <span>
                    <b>{event.title}</b>
                    <small>{event.notes || EVENT_LABELS[event.type]}</small>
                  </span>
                </span>
                {vehicle ? (
                  <button onClick={() => onOpenVehicle(vehicle.id)}>
                    {vehicle.make} {vehicle.model} · {vehicle.plate}
                  </button>
                ) : (
                  <span>{investor ? `Инвестор · ${investor.name}` : "—"}</span>
                )}
                <span>{event.paymentMethod || "Не указано"}</span>
                <span>{shortDate(event.date)}</span>
                <strong
                  className={mode === "expenses" ? "negative" : "positive"}
                >
                  {mode === "expenses" ? "−" : "+"}
                  {money(event.amount)}
                </strong>
                <span className="record-actions">
                  <button
                    className="event-edit"
                    title="Изменить запись"
                    onClick={() => setEditingEntry(event)}
                  >
                    Изменить
                  </button>
                  <button
                    className="event-delete"
                    title={mode === "expenses" ? "Отменить расход" : "Отменить доход"}
                    onClick={() => deleteEntry(event)}
                  >
                    ×
                  </button>
                </span>
              </div>
            );
          })}
        </div>
        {!events.length && (
          <Empty
            icon={mode === "expenses" ? "↘" : "↗"}
            title={`${title} пока не добавлены`}
            text={`Нажмите «Добавить ${mode === "expenses" ? "расход" : "доход"}», чтобы создать первую запись.`}
          />
        )}
      </section>
      {(show || editingEntry) && (
        <LedgerEntryModal
          mode={mode}
          entry={editingEntry || undefined}
          vehicles={data.vehicles}
          investors={data.investors}
          onClose={() => {
            setShow(false);
            setEditingEntry(null);
          }}
          onSave={(event) => {
            updateData((d) => ({
              ...d,
              events: d.events.some((item) => item.id === event.id)
                ? d.events.map((item) => (item.id === event.id ? event : item))
                : [event, ...d.events],
            }));
            setShow(false);
            setEditingEntry(null);
          }}
        />
      )}
    </PageWrap>
  );
}

const REPAIR_PRIORITIES: RepairRecommendationPriority[] = [
  "Низкий",
  "Средний",
  "Высокий",
];
const REPAIR_PART_STATUSES: RepairPartStatus[] = [
  "Нет в наличии",
  "Заказано",
  "В наличии",
];

function recommendationEventNotes(item: RepairRecommendation) {
  const parts = item.parts
    .filter((part) => part.name.trim())
    .map(
      (part) =>
        `${part.name} — ${part.status}${part.storageLocation ? ` (${part.storageLocation})` : ""}`,
    );
  return [
    parts.length ? `Запчасти: ${parts.join("; ")}` : "",
    item.comment ? `Комментарий: ${item.comment}` : "",
    "Создано из раздела «Рекомендации по ремонтам»",
  ]
    .filter(Boolean)
    .join(" · ");
}

function RepairRecommendationsPage({
  data,
  updateData,
  canEdit,
  onOpenVehicle,
}: {
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
  canEdit: boolean;
  onOpenVehicle: (id: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [vehicleId, setVehicleId] = useState("all");
  const [priority, setPriority] = useState("all");
  const [partStatus, setPartStatus] = useState("all");
  const [status, setStatus] = useState("active");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<RepairRecommendation | null>(null);

  const vehicleById = new Map(data.vehicles.map((vehicle) => [vehicle.id, vehicle]));
  const list = [...data.repairRecommendations]
    .filter((item) => {
      const vehicle = vehicleById.get(item.vehicleId);
      const haystack = `${vehicle?.make || ""} ${vehicle?.model || ""} ${vehicle?.plate || ""} ${item.recommendation} ${item.parts.map((part) => part.name).join(" ")}`.toLowerCase();
      return (
        haystack.includes(query.trim().toLowerCase()) &&
        (vehicleId === "all" || item.vehicleId === vehicleId) &&
        (priority === "all" || item.priority === priority) &&
        (partStatus === "all" ||
          item.parts.some((part) => part.status === partStatus)) &&
        (status === "all" ||
          (status === "active"
            ? item.status === "Не выполнено"
            : item.status === "Выполнено"))
      );
    })
    .sort((left, right) => {
      if (left.status !== right.status)
        return left.status === "Не выполнено" ? -1 : 1;
      const rank = { Высокий: 0, Средний: 1, Низкий: 2 };
      return rank[left.priority] - rank[right.priority] || right.date.localeCompare(left.date);
    });
  const active = data.repairRecommendations.filter(
    (item) => item.status === "Не выполнено",
  );
  const highPriority = active.filter((item) => item.priority === "Высокий");
  const waitingParts = active.filter((item) =>
    item.parts.some((part) => part.status !== "В наличии"),
  );
  const completedMonth = data.repairRecommendations.filter(
    (item) =>
      item.status === "Выполнено" &&
      item.actualRepairDate?.slice(0, 7) === currentMonth(),
  );

  const saveRecommendation = (next: RepairRecommendation) => {
    updateData((current) => {
      const previous = current.repairRecommendations.find(
        (item) => item.id === next.id,
      );
      let events = current.events;
      let saved = next;
      if (next.status === "Выполнено") {
        const eventId = previous?.repairEventId || next.repairEventId || uid();
        const event: VoyagerEvent = {
          id: eventId,
          vehicleId: next.vehicleId,
          type: "repair",
          title: `Выполнено: ${next.recommendation}`,
          date: new Date(
            `${next.actualRepairDate || new Date().toISOString().slice(0, 10)}T12:00:00`,
          ).toISOString(),
          notes: recommendationEventNotes(next),
          createdAt:
            events.find((item) => item.id === eventId)?.createdAt ||
            new Date().toISOString(),
        };
        events = events.some((item) => item.id === eventId)
          ? events.map((item) => (item.id === eventId ? event : item))
          : [event, ...events];
        saved = { ...next, repairEventId: eventId };
      } else if (previous?.repairEventId || next.repairEventId) {
        const eventId = previous?.repairEventId || next.repairEventId;
        events = events.filter((item) => item.id !== eventId);
        saved = {
          ...next,
          repairEventId: undefined,
          actualRepairDate: undefined,
        };
      }
      return {
        ...current,
        events,
        repairRecommendations: current.repairRecommendations.some(
          (item) => item.id === saved.id,
        )
          ? current.repairRecommendations.map((item) =>
              item.id === saved.id ? saved : item,
            )
          : [saved, ...current.repairRecommendations],
      };
    });
    setEditing(null);
    setModalOpen(false);
  };

  const deleteRecommendation = (item: RepairRecommendation) => {
    if (!confirm(`Удалить рекомендацию «${item.recommendation}»?`)) return;
    updateData((current) => ({
      ...current,
      repairRecommendations: current.repairRecommendations.filter(
        (recommendation) => recommendation.id !== item.id,
      ),
      events: item.repairEventId
        ? current.events.filter((event) => event.id !== item.repairEventId)
        : current.events,
    }));
  };

  return (
    <PageWrap
      className="repair-recommendations-page"
      title="Рекомендации по ремонтам"
      subtitle="Планирование работ, запчастей и мест хранения без влияния на финансовые расчёты."
      actions={
        <button
          className="primary"
          disabled={!canEdit}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          ＋ Добавить рекомендацию
        </button>
      }
    >
      <div className="hero-stats compact repair-summary">
        <Stat label="Не выполнено" value={String(active.length)} note="Активные рекомендации" />
        <Stat label="Высокий приоритет" value={String(highPriority.length)} note="Нужно запланировать" tone="danger" />
        <Stat label="Ожидают запчасти" value={String(waitingParts.length)} note="Нет в наличии или заказано" tone="warm" />
        <Stat label="Выполнено за месяц" value={String(completedMonth.length)} note="Есть в журнале ремонтов" tone="mint" />
      </div>
      <div className="toolbar repair-toolbar">
        <label className="search">
          ⌕
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Поиск по автомобилю, рекомендации или запчасти" />
        </label>
        <select value={vehicleId} onChange={(event) => setVehicleId(event.target.value)}>
          <option value="all">Все автомобили</option>
          {data.vehicles.map((vehicle) => (
            <option key={vehicle.id} value={vehicle.id}>
              {vehicle.plate} · {vehicle.make} {vehicle.model}
            </option>
          ))}
        </select>
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="active">Не выполнено</option>
          <option value="completed">Выполнено</option>
          <option value="all">Все статусы</option>
        </select>
        <select value={priority} onChange={(event) => setPriority(event.target.value)}>
          <option value="all">Любой приоритет</option>
          {REPAIR_PRIORITIES.map((item) => <option key={item}>{item}</option>)}
        </select>
        <select value={partStatus} onChange={(event) => setPartStatus(event.target.value)}>
          <option value="all">Любые запчасти</option>
          {REPAIR_PART_STATUSES.map((item) => <option key={item}>{item}</option>)}
        </select>
      </div>
      {list.length ? (
        <div className="repair-recommendation-list">
          {list.map((item) => {
            const vehicle = vehicleById.get(item.vehicleId);
            return (
              <article className={`repair-recommendation-card ${item.status === "Выполнено" ? "completed" : ""}`} key={item.id}>
                <button className="repair-vehicle-link" onClick={() => onOpenVehicle(item.vehicleId)}>
                  <b>{vehicle?.plate || "Автомобиль удалён"}</b>
                  <span>{vehicle ? `${vehicle.make} ${vehicle.model}` : "История сохранена"}</span>
                </button>
                <div className="repair-main">
                  <div className="repair-main-heading">
                    <b>{item.recommendation}</b>
                    <span className={`repair-priority priority-${item.priority.toLowerCase()}`}>{item.priority}</span>
                  </div>
                  <small>Добавлено {shortDate(item.date)}</small>
                  {item.comment && <p>{item.comment}</p>}
                  <div className="repair-parts">
                    {item.parts.length ? item.parts.map((part) => (
                      <span key={part.id} className={`part-status part-${part.status.replaceAll(" ", "-").toLowerCase()}`}>
                        <b>{part.name}</b>
                        {part.status}{part.storageLocation ? ` · ${part.storageLocation}` : ""}
                      </span>
                    )) : <span className="part-status">Запчасти не указаны</span>}
                  </div>
                </div>
                <div className="repair-dates">
                  <span>План ремонта<b>{item.plannedRepairDate ? shortDate(item.plannedRepairDate) : "Не задан"}</b></span>
                  <span>{item.status === "Выполнено" ? "Выполнено" : "Статус"}<b>{item.status === "Выполнено" && item.actualRepairDate ? shortDate(item.actualRepairDate) : item.status}</b></span>
                </div>
                {canEdit && (
                  <div className="repair-actions">
                    {item.status === "Не выполнено" && (
                      <button className="secondary small" onClick={() => saveRecommendation({ ...item, status: "Выполнено", actualRepairDate: new Date().toISOString().slice(0, 10), updatedAt: new Date().toISOString() })}>Выполнено</button>
                    )}
                    <button className="secondary small" onClick={() => { setEditing(item); setModalOpen(true); }}>Изменить</button>
                    <button className="danger-button small" onClick={() => deleteRecommendation(item)}>Удалить</button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <Empty icon="⚒" title="Рекомендаций не найдено" text="Измените фильтры или добавьте первую рекомендацию по ремонту." />
      )}
      {modalOpen && (
        <RepairRecommendationModal
          item={editing || undefined}
          vehicles={data.vehicles.filter((vehicle) => vehicle.status !== "Архив")}
          storageLocations={data.repairStorageLocations}
          onAddStorage={(location) =>
            updateData((current) => ({
              ...current,
              repairStorageLocations: Array.from(
                new Set([...current.repairStorageLocations, location]),
              ),
            }))
          }
          onClose={() => { setEditing(null); setModalOpen(false); }}
          onSave={saveRecommendation}
        />
      )}
    </PageWrap>
  );
}

function RepairRecommendationModal({
  item,
  vehicles,
  storageLocations,
  onAddStorage,
  onClose,
  onSave,
}: {
  item?: RepairRecommendation;
  vehicles: Vehicle[];
  storageLocations: string[];
  onAddStorage: (location: string) => void;
  onClose: () => void;
  onSave: (item: RepairRecommendation) => void;
}) {
  const now = new Date().toISOString();
  const [vehicleId, setVehicleId] = useState(item?.vehicleId || vehicles[0]?.id || "");
  const [date, setDate] = useState(item?.date.slice(0, 10) || now.slice(0, 10));
  const [recommendation, setRecommendation] = useState(item?.recommendation || "");
  const [priority, setPriority] = useState<RepairRecommendationPriority>(item?.priority || "Средний");
  const [plannedRepairDate, setPlannedRepairDate] = useState(item?.plannedRepairDate || "");
  const [status, setStatus] = useState(item?.status || "Не выполнено");
  const [actualRepairDate, setActualRepairDate] = useState(item?.actualRepairDate || "");
  const [comment, setComment] = useState(item?.comment || "");
  const [parts, setParts] = useState<RepairRecommendationPart[]>(
    item?.parts.length
      ? item.parts
      : [{ id: uid(), name: "", status: "Нет в наличии" }],
  );
  const [newLocation, setNewLocation] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!vehicleId || !recommendation.trim()) {
      alert("Выберите автомобиль и заполните рекомендацию");
      return;
    }
    if (status === "Выполнено" && !actualRepairDate) {
      alert("Укажите фактическую дату ремонта");
      return;
    }
    onSave({
      id: item?.id || uid(),
      vehicleId,
      date: new Date(`${date}T12:00:00`).toISOString(),
      recommendation: recommendation.trim(),
      priority,
      plannedRepairDate: plannedRepairDate || undefined,
      status,
      actualRepairDate: status === "Выполнено" ? actualRepairDate : undefined,
      comment: comment.trim() || undefined,
      parts: parts
        .filter((part) => part.name.trim())
        .map((part) => ({ ...part, name: part.name.trim() })),
      repairEventId: item?.repairEventId,
      createdAt: item?.createdAt || now,
      updatedAt: now,
    });
  };
  const updatePart = (id: string, patch: Partial<RepairRecommendationPart>) =>
    setParts((current) =>
      current.map((part) => (part.id === id ? { ...part, ...patch } : part)),
    );
  return (
    <Modal title={item ? "Изменить рекомендацию" : "Новая рекомендация"} onClose={onClose} wide>
      <form className="form-grid repair-recommendation-form" onSubmit={submit}>
        <Field label="Автомобиль">
          <select value={vehicleId} onChange={(event) => setVehicleId(event.target.value)} required>
            <option value="">Выберите автомобиль</option>
            {vehicles.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>{vehicle.plate} · {vehicle.make} {vehicle.model}</option>
            ))}
          </select>
        </Field>
        <Field label="Дата рекомендации"><input type="date" value={date} onChange={(event) => setDate(event.target.value)} required /></Field>
        <Field label="Рекомендация" wide><textarea rows={3} value={recommendation} onChange={(event) => setRecommendation(event.target.value)} required /></Field>
        <Field label="Приоритет"><select value={priority} onChange={(event) => setPriority(event.target.value as RepairRecommendationPriority)}>{REPAIR_PRIORITIES.map((value) => <option key={value}>{value}</option>)}</select></Field>
        <Field label="Плановая дата ремонта"><input type="date" value={plannedRepairDate} onChange={(event) => setPlannedRepairDate(event.target.value)} /></Field>
        <Field label="Статус"><select value={status} onChange={(event) => { const value = event.target.value as RepairRecommendation["status"]; setStatus(value); if (value === "Выполнено" && !actualRepairDate) setActualRepairDate(now.slice(0, 10)); }}><option>Не выполнено</option><option>Выполнено</option></select></Field>
        <Field label="Фактическая дата ремонта"><input type="date" value={actualRepairDate} onChange={(event) => setActualRepairDate(event.target.value)} disabled={status !== "Выполнено"} /></Field>
        <Field label="Комментарий" wide><textarea rows={2} value={comment} onChange={(event) => setComment(event.target.value)} /></Field>
        <div className="repair-parts-editor">
          <div className="repair-parts-editor-head"><div><b>Необходимые запчасти</b><p>Для каждой запчасти можно выбрать свой статус и место хранения.</p></div><button type="button" className="secondary small" onClick={() => setParts((current) => [...current, { id: uid(), name: "", status: "Нет в наличии" }])}>＋ Запчасть</button></div>
          {parts.map((part, index) => (
            <div className="repair-part-row" key={part.id}>
              <input value={part.name} onChange={(event) => updatePart(part.id, { name: event.target.value })} placeholder={`Запчасть ${index + 1}`} />
              <select value={part.status} onChange={(event) => updatePart(part.id, { status: event.target.value as RepairPartStatus })}>{REPAIR_PART_STATUSES.map((value) => <option key={value}>{value}</option>)}</select>
              <select value={part.storageLocation || ""} onChange={(event) => updatePart(part.id, { storageLocation: event.target.value || undefined })}><option value="">Место не указано</option>{storageLocations.map((value) => <option key={value}>{value}</option>)}</select>
              <button type="button" className="event-delete" title="Удалить запчасть" onClick={() => setParts((current) => current.filter((value) => value.id !== part.id))}>×</button>
            </div>
          ))}
          <div className="repair-storage-add"><input value={newLocation} onChange={(event) => setNewLocation(event.target.value)} placeholder="Новое место хранения" /><button type="button" className="secondary small" onClick={() => { const value = newLocation.trim(); if (!value) return; onAddStorage(value); setNewLocation(""); }}>Добавить место</button></div>
        </div>
        <div className="modal-actions wide"><span /><button type="button" className="secondary" onClick={onClose}>Отмена</button><button className="primary" type="submit">Сохранить</button></div>
      </form>
    </Modal>
  );
}

function DowntimeTablePage({
  data,
  updateData,
  canEdit,
}: {
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
  canEdit: boolean;
}) {
  const [month, setMonth] = useState(currentMonth());
  const [showVehiclePicker, setShowVehiclePicker] = useState(false);
  const [mobileVehicleId, setMobileVehicleId] = useState("");
  const [editing, setEditing] = useState<{
    id: string;
    vehicleId: string;
    date: string;
    value: string;
  } | null>(null);

  const visibleVehicles = data.vehicles
    .filter(
      (vehicle) =>
        vehicle.status !== "Архив" &&
        !data.downtimeHiddenVehicleIds.includes(vehicle.id),
    )
    .sort((a, b) => a.plate.localeCompare(b.plate, "ru"));
  const hiddenVehicles = data.vehicles
    .filter(
      (vehicle) =>
        vehicle.status !== "Архив" &&
        data.downtimeHiddenVehicleIds.includes(vehicle.id),
    )
    .sort((a, b) => a.plate.localeCompare(b.plate, "ru"));
  const visibleVehicleIds = new Set(visibleVehicles.map((vehicle) => vehicle.id));
  const [year, monthIndex] = month.split("-").map(Number);
  const dayCount = new Date(year, monthIndex, 0).getDate();
  const days = Array.from({ length: dayCount }, (_, index) => {
    const day = index + 1;
    const date = `${month}-${String(day).padStart(2, "0")}`;
    return {
      day,
      date,
      weekday: new Intl.DateTimeFormat("ru-RU", { weekday: "short" })
        .format(new Date(`${date}T12:00:00`))
        .replace(".", ""),
    };
  });
  const monthLabel = new Intl.DateTimeFormat("ru-RU", {
    month: "long",
    year: "numeric",
  }).format(new Date(`${month}-01T12:00:00`));
  const tableEvents = data.events.filter(
    (event) =>
      event.type === "income" &&
      event.source === "downtime_table" &&
      visibleVehicleIds.has(event.vehicleId) &&
      event.date.slice(0, 7) === month,
  );
  const eventByCell = new Map(
    tableEvents.map((event) => [
      `${event.vehicleId}:${event.date.slice(0, 10)}`,
      event,
    ]),
  );
  const zeroCells = new Set(
    data.downtimeZeroDays
      .filter(
        (item) =>
          visibleVehicleIds.has(item.vehicleId) && item.date.startsWith(month),
      )
      .map((item) => `${item.vehicleId}:${item.date}`),
  );
  const completedCells = new Set([
    ...eventByCell.keys(),
    ...zeroCells.values(),
  ]).size;
  const monthIncome = tableEvents.reduce(
    (sum, event) => sum + (event.amount || 0),
    0,
  );
  const monthlyVehicleIds = new Set(tableEvents.map((event) => event.vehicleId));

  useEffect(() => {
    if (
      visibleVehicles.length &&
      !visibleVehicles.some((vehicle) => vehicle.id === mobileVehicleId)
    ) {
      setMobileVehicleId(visibleVehicles[0].id);
    }
    if (!visibleVehicles.length && mobileVehicleId) setMobileVehicleId("");
  }, [visibleVehicles, mobileVehicleId]);

  const moveMonth = (offset: number) => {
    const next = new Date(year, monthIndex - 1 + offset, 1, 12);
    setMonth(
      `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`,
    );
    setEditing(null);
  };
  const cellKey = (vehicleId: string, date: string) => `${vehicleId}:${date}`;
  const cellValue = (vehicleId: string, date: string) => {
    const key = cellKey(vehicleId, date);
    const event = eventByCell.get(key);
    if (event) return String(event.amount || 0);
    return zeroCells.has(key) ? "0" : "";
  };
  const startEdit = (
    surface: "desktop" | "mobile",
    vehicleId: string,
    date: string,
  ) => {
    if (!canEdit) return;
    setEditing({
      id: `${surface}:${cellKey(vehicleId, date)}`,
      vehicleId,
      date,
      value: cellValue(vehicleId, date),
    });
  };
  const commitCell = (vehicleId: string, date: string, rawValue: string) => {
    const normalized = rawValue.trim().replace(",", ".");
    if (normalized && (!Number.isFinite(Number(normalized)) || Number(normalized) < 0)) {
      alert("Введите сумму от 0 и выше");
      setEditing(null);
      return;
    }
    const amount = normalized === "" ? null : Number(normalized);
    const key = cellKey(vehicleId, date);
    const existing = eventByCell.get(key);
    const vehicle = data.vehicles.find((item) => item.id === vehicleId);
    updateData((current) => {
      const withoutCellEvent = current.events.filter(
        (event) =>
          !(
            event.source === "downtime_table" &&
            event.vehicleId === vehicleId &&
            event.date.slice(0, 10) === date
          ),
      );
      const withoutZero = current.downtimeZeroDays.filter(
        (item) => !(item.vehicleId === vehicleId && item.date === date),
      );
      if (amount === null) {
        return {
          ...current,
          events: withoutCellEvent,
          downtimeZeroDays: withoutZero,
        };
      }
      if (amount === 0) {
        return {
          ...current,
          events: withoutCellEvent,
          downtimeZeroDays: [...withoutZero, { vehicleId, date }],
        };
      }
      const displayDate = new Intl.DateTimeFormat("ru-RU").format(
        new Date(`${date}T12:00:00`),
      );
      const nextEvent: VoyagerEvent = {
        id: existing?.id || uid(),
        vehicleId,
        investorId: vehicle?.investorId,
        type: "income",
        title: `Списание по аренде за ${displayDate}`,
        amount,
        date: new Date(`${date}T12:00:00`).toISOString(),
        notes: "Добавлено через раздел «Таблица простоев»",
        paid: true,
        source: "downtime_table",
        createdAt: existing?.createdAt || new Date().toISOString(),
      };
      return {
        ...current,
        events: [nextEvent, ...withoutCellEvent],
        downtimeZeroDays: withoutZero,
      };
    });
    setEditing(null);
  };
  const renderCell = (
    surface: "desktop" | "mobile",
    vehicleId: string,
    date: string,
  ) => {
    const key = cellKey(vehicleId, date);
    const editId = `${surface}:${key}`;
    const value = cellValue(vehicleId, date);
    if (editing?.id === editId) {
      return (
        <input
          autoFocus
          className="downtime-cell-input"
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          value={editing.value}
          onChange={(event) =>
            setEditing((current) =>
              current ? { ...current, value: event.target.value } : current,
            )
          }
          onBlur={(event) => commitCell(vehicleId, date, event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitCell(vehicleId, date, event.currentTarget.value);
            }
            if (event.key === "Escape") setEditing(null);
          }}
          aria-label={`Сумма за ${date}`}
        />
      );
    }
    return (
      <button
        type="button"
        className={`downtime-cell-button${value === "0" ? " zero" : ""}${value === "" ? " empty" : ""}`}
        onClick={() => startEdit(surface, vehicleId, date)}
        disabled={!canEdit}
        title={
          value === ""
            ? "Добавить списание"
            : value === "0"
              ? "Списания не было"
              : `${Number(value).toLocaleString("ru-RU")} ₽`
        }
      >
        {value === "" ? "—" : Number(value).toLocaleString("ru-RU")}
      </button>
    );
  };
  const rowTotal = (vehicleId: string) =>
    tableEvents
      .filter((event) => event.vehicleId === vehicleId)
      .reduce((sum, event) => sum + (event.amount || 0), 0);
  const dayTotal = (date: string) =>
    tableEvents
      .filter((event) => event.date.slice(0, 10) === date)
      .reduce((sum, event) => sum + (event.amount || 0), 0);
  const mobileVehicle = visibleVehicles.find(
    (vehicle) => vehicle.id === mobileVehicleId,
  );

  return (
    <PageWrap
      className="downtime-page"
      title="Таблица простоев"
      subtitle="Ежедневные списания по аренде с автоматическим добавлением доходов автомобилям и инвесторам."
      actions={
        <button
          className="primary"
          onClick={() => setShowVehiclePicker(true)}
          disabled={!canEdit || !hiddenVehicles.length}
          title={!hiddenVehicles.length ? "Весь действующий парк уже в таблице" : undefined}
        >
          ＋ Добавить автомобиль
        </button>
      }
    >
      <div className="hero-stats compact downtime-summary">
        <Stat
          label="Списано за месяц"
          value={money(monthIncome)}
          note={`${tableEvents.length} доходов`}
          tone="mint"
        />
        <Stat
          label="Заполнено дней"
          value={String(completedCells)}
          note="Включая нулевые дни"
        />
        <Stat
          label="Автомобилей с доходом"
          value={String(monthlyVehicleIds.size)}
          note="В выбранном месяце"
        />
        <Stat
          label="Автомобилей в таблице"
          value={String(visibleVehicles.length)}
          note={`${hiddenVehicles.length} скрыто`}
        />
      </div>

      <section className="panel downtime-panel">
        <div className="downtime-toolbar">
          <div className="downtime-month-nav">
            <button type="button" onClick={() => moveMonth(-1)} aria-label="Предыдущий месяц">
              ←
            </button>
            <h2>{monthLabel}</h2>
            <button type="button" onClick={() => moveMonth(1)} aria-label="Следующий месяц">
              →
            </button>
          </div>
          <div className="downtime-legend">
            <span><i className="income" /> Доход</span>
            <span><i className="zero" /> Нулевой день</span>
            <span><i /> Не заполнено</span>
          </div>
        </div>

        {!visibleVehicles.length ? (
          <Empty
            icon="▦"
            title="В таблице нет автомобилей"
            text="Добавьте скрытый автомобиль или активируйте автомобиль в таксопарке."
          />
        ) : (
          <>
            <div className="downtime-desktop-table" role="region" aria-label="Таблица ежедневных списаний">
              <table>
                <thead>
                  <tr>
                    <th className="downtime-sticky downtime-plate">Госномер</th>
                    <th className="downtime-sticky downtime-vehicle">Автомобиль</th>
                    {days.map((item) => (
                      <th key={item.date}>
                        <b>{String(item.day).padStart(2, "0")}</b>
                        <small>{item.weekday}</small>
                      </th>
                    ))}
                    <th className="downtime-total">Итого</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleVehicles.map((vehicle) => {
                    const investor = data.investors.find(
                      (item) => item.id === vehicle.investorId,
                    );
                    return (
                      <tr key={vehicle.id}>
                        <th className="downtime-sticky downtime-plate">{vehicle.plate}</th>
                        <th className="downtime-sticky downtime-vehicle">
                          <span>
                            <b>{vehicle.make} {vehicle.model}</b>
                            <small>{investor?.name || "Без инвестора"}</small>
                          </span>
                          {canEdit && (
                            <button
                              type="button"
                              className="downtime-hide"
                              onClick={() =>
                                updateData((current) => ({
                                  ...current,
                                  downtimeHiddenVehicleIds: Array.from(
                                    new Set([
                                      ...current.downtimeHiddenVehicleIds,
                                      vehicle.id,
                                    ]),
                                  ),
                                }))
                              }
                              title="Убрать автомобиль из таблицы"
                              aria-label={`Убрать ${vehicle.plate} из таблицы`}
                            >
                              ×
                            </button>
                          )}
                        </th>
                        {days.map((item) => (
                          <td key={item.date}>{renderCell("desktop", vehicle.id, item.date)}</td>
                        ))}
                        <td className="downtime-total"><b>{money(rowTotal(vehicle.id))}</b></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th className="downtime-sticky downtime-plate">ИТОГО</th>
                    <th className="downtime-sticky downtime-vehicle">За день</th>
                    {days.map((item) => (
                      <td key={item.date}>{dayTotal(item.date).toLocaleString("ru-RU")}</td>
                    ))}
                    <td className="downtime-total"><b>{money(monthIncome)}</b></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="downtime-mobile-list">
              <label>
                Автомобиль
                <select
                  value={mobileVehicleId}
                  onChange={(event) => setMobileVehicleId(event.target.value)}
                >
                  {visibleVehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.plate} · {vehicle.make} {vehicle.model}
                    </option>
                  ))}
                </select>
              </label>
              {mobileVehicle && (
                <>
                  <div className="downtime-mobile-head">
                    <span>
                      <b>{mobileVehicle.plate}</b>
                      <small>{mobileVehicle.make} {mobileVehicle.model}</small>
                    </span>
                    <strong>{money(rowTotal(mobileVehicle.id))}</strong>
                  </div>
                  <div className="downtime-mobile-days">
                    {days.map((item) => (
                      <div key={item.date}>
                        <time dateTime={item.date}>
                          <b>{item.day}</b>
                          <small>{item.weekday}</small>
                        </time>
                        <span>{renderCell("mobile", mobileVehicle.id, item.date)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </section>

      {showVehiclePicker && (
        <DowntimeVehiclePicker
          vehicles={hiddenVehicles}
          onClose={() => setShowVehiclePicker(false)}
          onAdd={(vehicleId) => {
            updateData((current) => ({
              ...current,
              downtimeHiddenVehicleIds: current.downtimeHiddenVehicleIds.filter(
                (id) => id !== vehicleId,
              ),
            }));
            setShowVehiclePicker(false);
          }}
        />
      )}
    </PageWrap>
  );
}

function DowntimeVehiclePicker({
  vehicles,
  onClose,
  onAdd,
}: {
  vehicles: Vehicle[];
  onClose: () => void;
  onAdd: (vehicleId: string) => void;
}) {
  const [vehicleId, setVehicleId] = useState(vehicles[0]?.id || "");
  return (
    <Modal title="Добавить автомобиль в таблицу" onClose={onClose}>
      <form
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          if (vehicleId) onAdd(vehicleId);
        }}
      >
        <Field label="Автомобиль" wide>
          <select
            required
            value={vehicleId}
            onChange={(event) => setVehicleId(event.target.value)}
          >
            {vehicles.map((vehicle) => (
              <option value={vehicle.id} key={vehicle.id}>
                {vehicle.plate} · {vehicle.make} {vehicle.model}
              </option>
            ))}
          </select>
        </Field>
        <div className="modal-actions wide">
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary" disabled={!vehicleId}>
            Добавить
          </button>
        </div>
      </form>
    </Modal>
  );
}

type PersonalFinanceOccurrence = {
  item: PersonalFinanceItem;
  monthKey: string;
  kind: PersonalFinanceKind;
  title: string;
  amount?: number;
  note: string;
};

function personalFinanceOccurrence(
  item: PersonalFinanceItem,
  monthKey: string,
): PersonalFinanceOccurrence | null {
  const startMonth = item.start_month.slice(0, 7);
  if (
    (!item.repeat_monthly && startMonth !== monthKey) ||
    (item.repeat_monthly && monthKey < startMonth)
  )
    return null;
  const override = item.overrides?.[monthKey];
  if (override?.cancelled) return null;
  const overrideHasAmount = Boolean(
    override && Object.prototype.hasOwnProperty.call(override, "amount"),
  );
  const amount = overrideHasAmount
    ? override?.amount ?? undefined
    : monthKey === startMonth
      ? item.amount ?? undefined
      : undefined;
  return {
    item,
    monthKey,
    kind: item.kind,
    title: override?.title ?? item.title,
    amount: amount === undefined ? undefined : Number(amount) || 0,
    note: override?.note ?? item.note ?? "",
  };
}

function PersonalProfitPage({
  company,
  items,
  currentUserId,
  onRefresh,
}: {
  company: CompanyContext;
  items: PersonalFinanceItem[];
  currentUserId: string;
  onRefresh: () => Promise<void>;
}) {
  const [month, setMonth] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const [addingKind, setAddingKind] = useState<PersonalFinanceKind | null>(null);
  const [editing, setEditing] = useState<PersonalFinanceOccurrence | null>(null);
  const monthKey = paymentMonthKey(month);
  const canEdit = company.role === "owner";
  const occurrences = items
    .map((item) => personalFinanceOccurrence(item, monthKey))
    .filter((item): item is PersonalFinanceOccurrence => Boolean(item))
    .sort((a, b) => a.title.localeCompare(b.title));
  const incomes = occurrences.filter((item) => item.kind === "income");
  const expenses = occurrences.filter((item) => item.kind === "expense");
  const incomeTotal = incomes.reduce((sum, item) => sum + (item.amount || 0), 0);
  const expenseTotal = expenses.reduce(
    (sum, item) => sum + (item.amount || 0),
    0,
  );
  const withoutAmount = occurrences.filter(
    (item) => item.amount === undefined,
  ).length;
  const profit = incomeTotal - expenseTotal;
  const profitPercent = incomeTotal
    ? (Math.max(0, profit) / incomeTotal) * 100
    : 0;
  const expensePercent = incomeTotal
    ? (expenseTotal / incomeTotal) * 100
    : expenseTotal
      ? 100
      : 0;
  const profitMargin = incomeTotal ? (profit / incomeTotal) * 100 : 0;
  const chartProfit = Math.max(0, Math.min(100, profitPercent));
  const chartBackground =
    incomeTotal || expenseTotal
      ? `conic-gradient(var(--green) 0 ${chartProfit}%, #ff806c ${chartProfit}% 100%)`
      : "conic-gradient(#26313a 0 100%)";

  const changeMonth = (delta: number) =>
    setMonth(
      (current) =>
        new Date(current.getFullYear(), current.getMonth() + delta, 1),
    );

  const saveBase = async (
    item: PersonalFinanceItem | null,
    values: {
      kind: PersonalFinanceKind;
      title: string;
      amount: number | null;
      note: string;
      repeatMonthly: boolean;
    },
  ) => {
    if (!supabase || !canEdit) return;
    const payload = {
      company_id: company.id,
      kind: values.kind,
      title: values.title,
      amount: values.amount,
      note: values.note || null,
      start_month: item?.start_month || `${monthKey}-01`,
      repeat_monthly: values.repeatMonthly,
      overrides: item?.overrides || {},
      created_by: item?.created_by || currentUserId,
    };
    const result = item
      ? await supabase
          .from("voyager_personal_finance_items")
          .update(payload)
          .eq("company_id", company.id)
          .eq("id", item.id)
      : await supabase.from("voyager_personal_finance_items").insert(payload);
    if (result.error) alert(result.error.message);
    else {
      setAddingKind(null);
      setEditing(null);
      await onRefresh();
    }
  };

  const saveOccurrence = async (
    occurrence: PersonalFinanceOccurrence,
    values: { title: string; amount: number | null; note: string },
  ) => {
    if (!supabase || !canEdit) return;
    const overrides = {
      ...(occurrence.item.overrides || {}),
      [monthKey]: {
        title: values.title,
        amount: values.amount,
        note: values.note,
      },
    };
    const { error } = await supabase
      .from("voyager_personal_finance_items")
      .update({ overrides })
      .eq("company_id", company.id)
      .eq("id", occurrence.item.id);
    if (error) alert(error.message);
    else {
      setEditing(null);
      await onRefresh();
    }
  };

  const deleteSeries = async (item: PersonalFinanceItem) => {
    if (!supabase || !canEdit) return;
    const { error } = await supabase
      .from("voyager_personal_finance_items")
      .delete()
      .eq("company_id", company.id)
      .eq("id", item.id);
    if (error) alert(error.message);
    else {
      setEditing(null);
      await onRefresh();
    }
  };

  const deleteOccurrence = async (occurrence: PersonalFinanceOccurrence) => {
    if (!supabase || !canEdit) return;
    const overrides = {
      ...(occurrence.item.overrides || {}),
      [monthKey]: {
        ...occurrence.item.overrides?.[monthKey],
        cancelled: true,
      },
    };
    const { error } = await supabase
      .from("voyager_personal_finance_items")
      .update({ overrides })
      .eq("company_id", company.id)
      .eq("id", occurrence.item.id);
    if (error) alert(error.message);
    else {
      setEditing(null);
      await onRefresh();
    }
  };

  const renderLedger = (
    kind: PersonalFinanceKind,
    rows: PersonalFinanceOccurrence[],
    total: number,
  ) => (
    <section className={`panel personal-ledger ${kind}`}>
      <div className="personal-ledger-head">
        <div>
          <span>{kind === "income" ? "ДОХОДЫ" : "РАСХОДЫ"}</span>
          <b>{money(total)}</b>
        </div>
        <small>{rows.length} статей</small>
      </div>
      {rows.length ? (
        <div className="personal-ledger-list">
          {rows.map((row) => (
            <article key={row.item.id}>
              <span className="personal-ledger-icon">
                {kind === "income" ? "↗" : "↘"}
              </span>
              <div>
                <b>{row.title}</b>
                <small>
                  {row.item.repeat_monthly ? "Ежемесячно" : "Разовая статья"}
                  {row.note ? ` · ${row.note}` : ""}
                </small>
              </div>
              <strong>
                {row.amount === undefined
                  ? "Сумма не указана"
                  : money(row.amount)}
              </strong>
              {canEdit && (
                <button
                  type="button"
                  className="personal-edit"
                  onClick={() => setEditing(row)}
                >
                  Изменить
                </button>
              )}
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={kind === "income" ? "↗" : "↘"}
          title={kind === "income" ? "Доходов пока нет" : "Расходов пока нет"}
          text={`Добавьте ${kind === "income" ? "доход" : "расход"} за выбранный месяц.`}
        />
      )}
    </section>
  );

  return (
    <PageWrap
      title="Личная прибыль"
      subtitle="Самостоятельный учёт доходов, расходов и чистой прибыли по месяцам."
      actions={
        canEdit ? (
          <div className="personal-profit-actions">
            <button className="secondary" onClick={() => setAddingKind("expense")}>
              ＋ Расход
            </button>
            <button className="primary" onClick={() => setAddingKind("income")}>
              ＋ Доход
            </button>
          </div>
        ) : undefined
      }
    >
      <div className="payment-notice personal-profit-notice">
        <span>i</span>
        Этот раздел ведётся отдельно и не влияет на доходы, расходы, прибыль и
        расчёты с инвесторами в других разделах Voyager.
      </div>
      <div className="personal-month-nav">
        <button onClick={() => changeMonth(-1)} aria-label="Предыдущий месяц">
          ←
        </button>
        <h2>
          {PAYMENT_MONTHS[month.getMonth()]} <span>{month.getFullYear()}</span>
        </h2>
        <button onClick={() => changeMonth(1)} aria-label="Следующий месяц">
          →
        </button>
      </div>
      <div className="personal-profit-top">
        <div className="personal-profit-summary">
          <Stat
            label="Общие доходы за месяц"
            value={money(incomeTotal)}
            note={`${incomes.length} статей дохода${incomes.some((item) => item.amount === undefined) ? " · есть без суммы" : ""}`}
            tone="mint"
          />
          <Stat
            label="Общие расходы за месяц"
            value={money(expenseTotal)}
            note={`${expenses.length} статей расхода${expenses.some((item) => item.amount === undefined) ? " · есть без суммы" : ""}`}
          />
          <Stat
            label={profit >= 0 ? "Чистая прибыль" : "Итого убыток"}
            value={money(profit)}
            note={
              withoutAmount
                ? `${withoutAmount} статей ожидают сумму`
                : `${profitMargin.toFixed(1).replace(".", ",")}% от дохода`
            }
            tone={profit >= 0 ? "mint" : "danger"}
          />
        </div>
        <section className="panel personal-profit-chart">
          <PanelTitle title="Соотношение прибыли и расходов" />
          <div className="personal-chart-content">
            <div
              className="personal-donut"
              style={{ background: chartBackground }}
              role="img"
              aria-label={`Чистая прибыль ${profitPercent.toFixed(1)}%, расходы ${expensePercent.toFixed(1)}%`}
            >
              <div>
                <small>{profit >= 0 ? "ПРИБЫЛЬ" : "УБЫТОК"}</small>
                <b>{money(profit)}</b>
              </div>
            </div>
            <div className="personal-chart-legend">
              <div>
                <span className="profit" />
                <p>
                  <b>Чистая прибыль</b>
                  <small>
                    {profitPercent.toFixed(1).replace(".", ",")}% ·{" "}
                    {money(Math.max(0, profit))}
                  </small>
                </p>
              </div>
              <div>
                <span className="expense" />
                <p>
                  <b>Расходы</b>
                  <small>
                    {expensePercent.toFixed(1).replace(".", ",")}% ·{" "}
                    {money(expenseTotal)}
                  </small>
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
      {!canEdit && (
        <div className="personal-readonly">
          Сотрудникам доступен просмотр. Добавлять и изменять статьи может только
          владелец.
        </div>
      )}
      <div className="personal-ledgers">
        {renderLedger("income", incomes, incomeTotal)}
        {renderLedger("expense", expenses, expenseTotal)}
      </div>
      {(addingKind || editing) && (
        <PersonalFinanceModal
          kind={addingKind || editing?.kind || "income"}
          occurrence={editing || undefined}
          monthKey={monthKey}
          onClose={() => {
            setAddingKind(null);
            setEditing(null);
          }}
          onSaveBase={saveBase}
          onSaveOccurrence={saveOccurrence}
          onDeleteSeries={deleteSeries}
          onDeleteOccurrence={deleteOccurrence}
        />
      )}
    </PageWrap>
  );
}

function PersonalFinanceModal({
  kind,
  occurrence,
  monthKey,
  onClose,
  onSaveBase,
  onSaveOccurrence,
  onDeleteSeries,
  onDeleteOccurrence,
}: {
  kind: PersonalFinanceKind;
  occurrence?: PersonalFinanceOccurrence;
  monthKey: string;
  onClose: () => void;
  onSaveBase: (
    item: PersonalFinanceItem | null,
    values: {
      kind: PersonalFinanceKind;
      title: string;
      amount: number | null;
      note: string;
      repeatMonthly: boolean;
    },
  ) => Promise<void>;
  onSaveOccurrence: (
    occurrence: PersonalFinanceOccurrence,
    values: { title: string; amount: number | null; note: string },
  ) => Promise<void>;
  onDeleteSeries: (item: PersonalFinanceItem) => Promise<void>;
  onDeleteOccurrence: (
    occurrence: PersonalFinanceOccurrence,
  ) => Promise<void>;
}) {
  const [scope, setScope] = useState<"series" | "occurrence">(
    occurrence?.item.repeat_monthly ? "occurrence" : "series",
  );
  const [itemKind, setItemKind] = useState<PersonalFinanceKind>(kind);
  const [title, setTitle] = useState(occurrence?.title || "");
  const [amount, setAmount] = useState(
    occurrence?.amount === undefined ? "" : String(occurrence.amount),
  );
  const [note, setNote] = useState(occurrence?.note || "");
  const [repeatMonthly, setRepeatMonthly] = useState(
    occurrence?.item.repeat_monthly || false,
  );
  const [saving, setSaving] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const values = {
      kind: itemKind,
      title: title.trim(),
      amount: amount.trim() ? Math.max(0, Number(amount) || 0) : null,
      note: note.trim(),
      repeatMonthly,
    };
    setSaving(true);
    if (
      occurrence?.item.repeat_monthly &&
      scope === "occurrence"
    )
      await onSaveOccurrence(occurrence, values);
    else
      await onSaveBase(occurrence?.item || null, {
        ...values,
        amount:
          occurrence?.item.repeat_monthly && scope === "series"
            ? occurrence.item.amount
            : values.amount,
      });
    setSaving(false);
  };
  return (
    <Modal
      title={
        occurrence
          ? "Изменить статью"
          : itemKind === "income"
            ? "Новая статья дохода"
            : "Новая статья расхода"
      }
      onClose={onClose}
    >
      <form className="form-grid personal-finance-form" onSubmit={submit}>
        {occurrence?.item.repeat_monthly && (
          <Field label="Что изменить" wide>
            <select
              value={scope}
              onChange={(event) =>
                setScope(event.target.value as "series" | "occurrence")
              }
            >
              <option value="occurrence">
                Только {PAYMENT_MONTHS[Number(monthKey.slice(5, 7)) - 1].toLowerCase()}
              </option>
              <option value="series">Всю ежемесячную серию</option>
            </select>
          </Field>
        )}
        <Field label="Тип статьи">
          <select
            value={itemKind}
            disabled={scope === "occurrence"}
            onChange={(event) =>
              setItemKind(event.target.value as PersonalFinanceKind)
            }
          >
            <option value="income">Доход</option>
            <option value="expense">Расход</option>
          </select>
        </Field>
        <Field
          label={
            occurrence?.item.repeat_monthly && scope === "series"
              ? "Сумма меняется по месяцам"
              : "Сумма, ₽ (необязательно)"
          }
        >
          <input
            type="number"
            min="0"
            step="0.01"
            disabled={
              Boolean(occurrence?.item.repeat_monthly) && scope === "series"
            }
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder={
              occurrence?.item.repeat_monthly && scope === "series"
                ? "Изменяется отдельно в каждом месяце"
                : "Можно заполнить позже"
            }
          />
        </Field>
        <Field label="Название статьи" wide>
          <input
            required
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Например: аренда, комиссия, кредит"
          />
        </Field>
        <Field label="Повторение">
          <select
            value={repeatMonthly ? "monthly" : "once"}
            disabled={scope === "occurrence"}
            onChange={(event) =>
              setRepeatMonthly(event.target.value === "monthly")
            }
          >
            <option value="once">Только в этом месяце</option>
            <option value="monthly">Каждый месяц</option>
          </select>
        </Field>
        <Field label="Месяц">
          <input
            value={`${PAYMENT_MONTHS[Number(monthKey.slice(5, 7)) - 1]} ${monthKey.slice(0, 4)}`}
            disabled
          />
        </Field>
        <Field label="Комментарий" wide>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Необязательное пояснение"
          />
        </Field>
        <div className="modal-actions wide payment-modal-actions">
          {occurrence && (
            <button
              type="button"
              className="danger-button"
              onClick={async () => {
                const oneMonth =
                  occurrence.item.repeat_monthly && scope === "occurrence";
                if (
                  confirm(
                    oneMonth
                      ? "Убрать эту статью только из выбранного месяца?"
                      : "Удалить статью и все её будущие повторения?",
                  )
                ) {
                  setSaving(true);
                  if (oneMonth) await onDeleteOccurrence(occurrence);
                  else await onDeleteSeries(occurrence.item);
                  setSaving(false);
                }
              }}
            >
              {occurrence.item.repeat_monthly && scope === "occurrence"
                ? "Убрать из месяца"
                : "Удалить"}
            </button>
          )}
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary" disabled={saving}>
            {saving ? "Сохраняем…" : "Сохранить"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

type PaymentOccurrence = {
  payment: PlannedPayment;
  monthKey: string;
  date: string;
  payee: string;
  amount?: number;
  comment: string;
  paid: boolean;
};

const PAYMENT_MONTHS = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

function paymentMonthKey(month: Date) {
  return `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
}

function paymentOccurrence(
  payment: PlannedPayment,
  monthKey: string,
): PaymentOccurrence | null {
  const startMonth = payment.date.slice(0, 7);
  if (
    (!payment.repeatMonthly && startMonth !== monthKey) ||
    (payment.repeatMonthly && monthKey < startMonth)
  )
    return null;
  const override = payment.overrides?.[monthKey];
  if (override?.cancelled) return null;
  const [year, month] = monthKey.split("-").map(Number);
  const anchorDay = Number(payment.date.slice(8, 10)) || 1;
  const lastDay = new Date(year, month, 0).getDate();
  const generatedDate = `${monthKey}-${String(Math.min(anchorDay, lastDay)).padStart(2, "0")}`;
  const date = override?.date || (payment.repeatMonthly ? generatedDate : payment.date);
  const overrideHasAmount = Boolean(
    override && Object.prototype.hasOwnProperty.call(override, "amount"),
  );
  return {
    payment,
    monthKey,
    date,
    payee: override?.payee ?? payment.payee,
    amount: overrideHasAmount ? override?.amount ?? undefined : payment.amount,
    comment: override?.comment ?? payment.comment ?? "",
    paid: payment.paidDates.includes(date),
  };
}

function PaymentCalendar({
  data,
  updateData,
  canEdit,
}: {
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
  canEdit: boolean;
}) {
  const [view, setView] = useState<"list" | "calendar">("list");
  const [month, setMonth] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<PaymentOccurrence | null>(null);
  const monthKey = paymentMonthKey(month);
  const occurrences = data.plannedPayments
    .map((payment) => paymentOccurrence(payment, monthKey))
    .filter((item): item is PaymentOccurrence => Boolean(item))
    .sort((a, b) => a.date.localeCompare(b.date) || a.payee.localeCompare(b.payee));
  const total = occurrences.reduce((sum, item) => sum + (item.amount || 0), 0);
  const paid = occurrences
    .filter((item) => item.paid)
    .reduce((sum, item) => sum + (item.amount || 0), 0);
  const withoutAmount = occurrences.filter(
    (item) => item.amount === undefined,
  ).length;
  const changeMonth = (offset: number) =>
    setMonth(new Date(month.getFullYear(), month.getMonth() + offset, 1));
  const togglePaid = (occurrence: PaymentOccurrence) => {
    updateData((current) => ({
      ...current,
      plannedPayments: current.plannedPayments.map((payment) =>
        payment.id === occurrence.payment.id
          ? {
              ...payment,
              paidDates: occurrence.paid
                ? payment.paidDates.filter((date) => date !== occurrence.date)
                : [...payment.paidDates, occurrence.date],
            }
          : payment,
      ),
    }));
  };
  const saveBase = (payment: PlannedPayment) => {
    updateData((current) => ({
      ...current,
      plannedPayments: current.plannedPayments.some(
        (item) => item.id === payment.id,
      )
        ? current.plannedPayments.map((item) =>
            item.id === payment.id ? payment : item,
          )
        : [...current.plannedPayments, payment],
    }));
    setAdding(false);
    setEditing(null);
  };
  const saveOccurrence = (
    paymentId: string,
    targetMonth: string,
    override: PlannedPaymentOverride,
  ) => {
    updateData((current) => ({
      ...current,
      plannedPayments: current.plannedPayments.map((payment) =>
        payment.id === paymentId
          ? {
              ...payment,
              overrides: {
                ...payment.overrides,
                [targetMonth]: override,
              },
            }
          : payment,
      ),
    }));
    setEditing(null);
  };
  const removeSeries = (paymentId: string) => {
    updateData((current) => ({
      ...current,
      plannedPayments: current.plannedPayments.filter(
        (payment) => payment.id !== paymentId,
      ),
    }));
    setEditing(null);
  };
  const removeOccurrence = (paymentId: string, targetMonth: string) => {
    updateData((current) => ({
      ...current,
      plannedPayments: current.plannedPayments.map((payment) =>
        payment.id === paymentId
          ? {
              ...payment,
              overrides: {
                ...payment.overrides,
                [targetMonth]: {
                  ...payment.overrides?.[targetMonth],
                  cancelled: true,
                },
              },
            }
          : payment,
      ),
    }));
    setEditing(null);
  };

  const firstOffset = (month.getDay() + 6) % 7;
  const cells = Array.from(
    { length: 42 },
    (_, index) =>
      new Date(month.getFullYear(), month.getMonth(), index - firstOffset + 1),
  );

  return (
    <PageWrap
      title="Календарь платежей"
      subtitle="Плановые оплаты и напоминания без влияния на финансовые расчёты."
      actions={
        canEdit ? (
          <button className="primary" onClick={() => setAdding(true)}>
            ＋ Добавить платёж
          </button>
        ) : undefined
      }
    >
      <div className="payment-notice">
        <span>i</span>
        Суммы этого раздела не входят в доходы, расходы, прибыль и расчёты с
        инвесторами.
      </div>
      <div className="payment-summary">
        <Stat
          label="Запланировано"
          value={money(total)}
          note={`${occurrences.length} платежей${withoutAmount ? ` · ${withoutAmount} без суммы` : ""}`}
        />
        <Stat
          label="Оплачено"
          value={money(paid)}
          note={`${occurrences.filter((item) => item.paid).length} выполнено · известные суммы`}
          tone="mint"
        />
        <Stat
          label="Осталось"
          value={money(total - paid)}
          note={
            withoutAmount
              ? `Плюс ${withoutAmount} ${withoutAmount === 1 ? "платёж" : "платежа"} по факту`
              : "Только напоминание"
          }
        />
      </div>
      <section className="panel payment-panel">
        <div className="payment-toolbar">
          <div className="payment-month-nav">
            <button onClick={() => changeMonth(-1)} aria-label="Предыдущий месяц">
              ←
            </button>
            <h2>
              {PAYMENT_MONTHS[month.getMonth()]} <span>{month.getFullYear()}</span>
            </h2>
            <button onClick={() => changeMonth(1)} aria-label="Следующий месяц">
              →
            </button>
          </div>
          <div className="view-switch">
            <button
              className={view === "list" ? "active" : ""}
              onClick={() => setView("list")}
            >
              Список
            </button>
            <button
              className={view === "calendar" ? "active" : ""}
              onClick={() => setView("calendar")}
            >
              Календарь
            </button>
          </div>
        </div>
        {view === "list" ? (
          occurrences.length ? (
            <div className="payment-list">
              {occurrences.map((occurrence) => (
                <article
                  className={`payment-row${occurrence.paid ? " paid" : ""}`}
                  key={`${occurrence.payment.id}-${occurrence.monthKey}`}
                >
                  <time>
                    <b>{occurrence.date.slice(8, 10)}</b>
                    <small>{PAYMENT_MONTHS[month.getMonth()].slice(0, 3)}</small>
                  </time>
                  <div className="payment-main">
                    <b>{occurrence.payee}</b>
                    <small>
                      {occurrence.payment.repeatMonthly
                        ? "Ежемесячный платёж"
                        : "Разовый платёж"}
                      {occurrence.comment ? ` · ${occurrence.comment}` : ""}
                    </small>
                  </div>
                  <strong>
                    {occurrence.amount === undefined
                      ? "Сумма по факту"
                      : money(occurrence.amount)}
                  </strong>
                  <span className={`payment-status ${occurrence.paid ? "done" : ""}`}>
                    {occurrence.paid ? "Оплачено" : "Запланировано"}
                  </span>
                  {canEdit && (
                    <div className="payment-actions">
                      <button
                        className="secondary"
                        onClick={() => togglePaid(occurrence)}
                      >
                        {occurrence.paid ? "Вернуть" : "Оплачено"}
                      </button>
                      <button
                        className="payment-edit"
                        onClick={() => setEditing(occurrence)}
                      >
                        Изменить
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <Empty
              icon="▦"
              title="В этом месяце платежей нет"
              text="Добавьте разовый или ежемесячный плановый платёж."
            />
          )
        ) : (
          <div className="payment-calendar-wrap">
            <div className="calendar-weekdays">
              {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((day) => (
                <b key={day}>{day}</b>
              ))}
            </div>
            <div className="calendar-grid payment-calendar-grid">
              {cells.map((day) => {
                const iso = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
                const items = occurrences.filter((item) => item.date === iso);
                const outside = day.getMonth() !== month.getMonth();
                return (
                  <div
                    className={`calendar-day payment-day${outside ? " outside" : ""}`}
                    key={iso}
                  >
                    <span>{day.getDate()}</span>
                    <div>
                      {items.map((item) => (
                        <button
                          className={`payment-calendar-item${item.paid ? " paid" : ""}`}
                          key={item.payment.id}
                          onClick={() => canEdit && setEditing(item)}
                        >
                          <b>{item.payee}</b>
                          <small>
                            {item.amount === undefined
                              ? "Сумма по факту"
                              : money(item.amount)}
                          </small>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
      {(adding || editing) && (
        <PlannedPaymentModal
          payment={editing?.payment}
          occurrence={editing || undefined}
          monthKey={monthKey}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaveBase={saveBase}
          onSaveOccurrence={saveOccurrence}
          onDeleteSeries={removeSeries}
          onDeleteOccurrence={removeOccurrence}
        />
      )}
    </PageWrap>
  );
}

function PlannedPaymentModal({
  payment,
  occurrence,
  monthKey,
  onClose,
  onSaveBase,
  onSaveOccurrence,
  onDeleteSeries,
  onDeleteOccurrence,
}: {
  payment?: PlannedPayment;
  occurrence?: PaymentOccurrence;
  monthKey: string;
  onClose: () => void;
  onSaveBase: (payment: PlannedPayment) => void;
  onSaveOccurrence: (
    paymentId: string,
    monthKey: string,
    override: PlannedPaymentOverride,
  ) => void;
  onDeleteSeries: (paymentId: string) => void;
  onDeleteOccurrence: (paymentId: string, monthKey: string) => void;
}) {
  const today = new Date();
  const suggestedDay = Math.min(
    today.getDate(),
    new Date(Number(monthKey.slice(0, 4)), Number(monthKey.slice(5, 7)), 0).getDate(),
  );
  const suggestedDate = `${monthKey}-${String(suggestedDay).padStart(2, "0")}`;
  const [scope, setScope] = useState<"series" | "occurrence">(
    payment?.repeatMonthly ? "occurrence" : "series",
  );
  const [payee, setPayee] = useState(occurrence?.payee || payment?.payee || "");
  const [amount, setAmount] = useState(
    String(occurrence?.amount ?? payment?.amount ?? ""),
  );
  const [date, setDate] = useState(
    occurrence?.date || payment?.date || suggestedDate,
  );
  const [comment, setComment] = useState(
    occurrence?.comment || payment?.comment || "",
  );
  const [repeatMonthly, setRepeatMonthly] = useState(
    payment?.repeatMonthly || false,
  );
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (scope === "occurrence" && date.slice(0, 7) !== monthKey) {
      alert("Дата отдельного платежа должна оставаться в выбранном месяце");
      return;
    }
    if (payment && payment.repeatMonthly && scope === "occurrence") {
      onSaveOccurrence(payment.id, monthKey, {
        payee: payee.trim(),
        amount: amount.trim()
          ? Math.max(0, Number(amount) || 0)
          : null,
        date,
        comment: comment.trim(),
      });
      return;
    }
    onSaveBase({
      id: payment?.id || uid(),
      payee: payee.trim(),
      amount: amount.trim() ? Math.max(0, Number(amount) || 0) : undefined,
      date,
      comment: comment.trim() || undefined,
      repeatMonthly,
      paidDates: payment?.paidDates || [],
      overrides: payment?.overrides || {},
      createdAt: payment?.createdAt || new Date().toISOString(),
    });
  };
  return (
    <Modal
      title={payment ? "Изменить плановый платёж" : "Новый плановый платёж"}
      onClose={onClose}
    >
      <form className="form-grid" onSubmit={submit}>
        {payment?.repeatMonthly && (
          <Field label="Что изменить" wide>
            <select
              value={scope}
              onChange={(event) =>
                setScope(event.target.value as "series" | "occurrence")
              }
            >
              <option value="occurrence">Только этот месяц</option>
              <option value="series">Всю ежемесячную серию</option>
            </select>
          </Field>
        )}
        <Field label="Кому оплата">
          <input
            required
            value={payee}
            onChange={(event) => setPayee(event.target.value)}
            placeholder="Арендодатель, банк, поставщик"
          />
        </Field>
        <Field label="Сумма, ₽ (необязательно)">
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="Можно оставить пустым"
          />
        </Field>
        <Field label="Дата оплаты">
          <input
            required
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </Field>
        <Field label="Повторение">
          <select
            value={repeatMonthly ? "monthly" : "once"}
            disabled={scope === "occurrence"}
            onChange={(event) =>
              setRepeatMonthly(event.target.value === "monthly")
            }
          >
            <option value="once">Только один раз</option>
            <option value="monthly">Каждый месяц</option>
          </select>
        </Field>
        <Field label="Комментарий" wide>
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Назначение платежа или важная информация"
          />
        </Field>
        <div className="modal-actions wide payment-modal-actions">
          {payment && (
            <button
              type="button"
              className="danger-button"
              onClick={() => {
                const oneOccurrence =
                  payment.repeatMonthly && scope === "occurrence";
                if (
                  confirm(
                    oneOccurrence
                      ? "Убрать этот платёж только из выбранного месяца?"
                      : "Удалить платёж и его будущие повторения?",
                  )
                ) {
                  if (oneOccurrence)
                    onDeleteOccurrence(payment.id, monthKey);
                  else onDeleteSeries(payment.id);
                }
              }}
            >
              {payment.repeatMonthly && scope === "occurrence"
                ? "Убрать из месяца"
                : "Удалить"}
            </button>
          )}
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary">Сохранить</button>
        </div>
      </form>
    </Modal>
  );
}

type TaskFilter = "all" | "mine" | "today" | "overdue" | "completed";

const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  normal: "Обычная",
  important: "Важная",
  urgent: "Срочная",
};

function localDateKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function currentMileageWeek(now = new Date()) {
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysSinceMonday = (monday.getDay() + 6) % 7;
  monday.setDate(monday.getDate() - daysSinceMonday);
  return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`;
}

function TasksPage({
  company,
  tasks,
  members,
  currentUserId,
  onRefresh,
}: {
  company: CompanyContext;
  tasks: CompanyTask[];
  members: CompanyMember[];
  currentUserId: string;
  onRefresh: () => Promise<void>;
}) {
  const [filter, setFilter] = useState<TaskFilter>("all");
  const [selectedMemberId, setSelectedMemberId] = useState("all");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<CompanyTask | true | null>(null);
  const today = localDateKey();
  const canManage = company.role !== "viewer";
  const activeMembers = members.filter((member) => member.status === "active");
  const open = tasks.filter((task) => task.status === "open");
  const overdue = open.filter(
    (task) => task.due_date && task.due_date < today,
  );
  const todayTasks = open.filter((task) => task.due_date === today);
  const myTasks = open.filter(
    (task) => task.assignee_user_id === currentUserId,
  );
  const scopedTasks =
    selectedMemberId === "all"
      ? tasks
      : tasks.filter((task) => task.assignee_user_id === selectedMemberId);
  const scopedOpen = scopedTasks.filter((task) => task.status === "open");
  const scopedToday = scopedOpen.filter((task) => task.due_date === today);
  const scopedOverdue = scopedOpen.filter(
    (task) => task.due_date && task.due_date < today,
  );
  const selectedMember =
    selectedMemberId === "all"
      ? null
      : members.find((member) => member.user_id === selectedMemberId) || null;
  const selectedMemberName =
    selectedMember?.display_name || selectedMember?.email || "";
  const memberStats = activeMembers.map((member) => {
    const memberTasks = tasks.filter(
      (task) => task.assignee_user_id === member.user_id,
    );
    return {
      member,
      open: memberTasks.filter((task) => task.status === "open").length,
      completed: memberTasks.filter((task) => task.status === "completed")
        .length,
    };
  });
  const list = scopedTasks
    .filter((task) => {
      if (filter === "mine")
        return (
          task.assignee_user_id === currentUserId && task.status === "open"
        );
      if (filter === "today")
        return task.due_date === today && task.status === "open";
      if (filter === "overdue")
        return Boolean(
          task.due_date && task.due_date < today && task.status === "open",
        );
      if (filter === "completed") return task.status === "completed";
      return true;
    })
    .filter((task) =>
      `${task.title} ${task.description || ""}`
        .toLowerCase()
        .includes(query.toLowerCase()),
    )
    .sort((a, b) => {
      if (a.status !== b.status) return a.status === "open" ? -1 : 1;
      const priorityOrder: Record<TaskPriority, number> = {
        urgent: 0,
        important: 1,
        normal: 2,
      };
      if (priorityOrder[a.priority] !== priorityOrder[b.priority])
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      return (a.due_date || "9999-12-31").localeCompare(
        b.due_date || "9999-12-31",
      );
    });
  const memberName = (userId: string | null) => {
    if (!userId) return "Не назначен";
    const member = members.find((item) => item.user_id === userId);
    return member?.display_name || member?.email || "Сотрудник";
  };
  const toggleTask = async (task: CompanyTask) => {
    if (!supabase) return;
    const { error } = await supabase.rpc("voyager_toggle_task", {
      target_company_id: company.id,
      target_task_id: task.id,
      task_completed: task.status !== "completed",
    });
    if (error) alert(error.message);
    else await onRefresh();
  };
  const deleteTask = async (task: CompanyTask) => {
    if (
      !supabase ||
      !confirm(`Удалить задачу «${task.title}»? Восстановить её не получится.`)
    )
      return;
    const { error } = await supabase.rpc("voyager_delete_task", {
      target_company_id: company.id,
      target_task_id: task.id,
    });
    if (error) alert(error.message);
    else await onRefresh();
  };

  const filters: { id: TaskFilter; label: string; count: number }[] = [
    { id: "all", label: "Все", count: scopedTasks.length },
    { id: "mine", label: "Мои", count: myTasks.length },
    { id: "today", label: "Сегодня", count: scopedToday.length },
    { id: "overdue", label: "Просроченные", count: scopedOverdue.length },
    {
      id: "completed",
      label: "Выполненные",
      count: scopedTasks.filter((task) => task.status === "completed").length,
    },
  ];

  return (
    <PageWrap
      title="Задачи"
      subtitle="Общие задачи компании, сроки выполнения и ответственные."
      actions={
        canManage ? (
          <button className="primary" onClick={() => setEditing(true)}>
            ＋ Добавить задачу
          </button>
        ) : undefined
      }
    >
      <div className="task-summary">
        <Stat
          label="В работе"
          value={String(open.length)}
          note={`${myTasks.length} назначено вам`}
          tone="mint"
        />
        <Stat
          label="На сегодня"
          value={String(todayTasks.length)}
          note="Срок сегодня"
        />
        <Stat
          label="Просрочено"
          value={String(overdue.length)}
          note="Требуют внимания"
          tone={overdue.length ? "danger" : ""}
        />
        <Stat
          label="Выполнено"
          value={String(
            tasks.filter((task) => task.status === "completed").length,
          )}
          note="За всё время"
        />
      </div>
      <section className="panel task-team-overview">
        <PanelTitle
          title="По сотрудникам"
          meta={`${activeMembers.length} участников`}
        />
        <div className="task-team-list">
          <button
            type="button"
            className={selectedMemberId === "all" ? "active" : ""}
            aria-pressed={selectedMemberId === "all"}
            onClick={() => {
              setSelectedMemberId("all");
              setFilter("all");
            }}
          >
            <span className="task-team-avatar">Σ</span>
            <span className="task-team-person">
              <b>Все сотрудники</b>
              <small>Общий список задач</small>
            </span>
            <span className="task-team-counts">
              <span>
                В работе <b>{open.length}</b>
              </span>
              <span>
                Выполнено{" "}
                <b>
                  {
                    tasks.filter((task) => task.status === "completed")
                      .length
                  }
                </b>
              </span>
            </span>
          </button>
          {memberStats.map(({ member, open: memberOpen, completed }) => (
            <button
              type="button"
              key={member.user_id}
              className={
                selectedMemberId === member.user_id ? "active" : ""
              }
              aria-pressed={selectedMemberId === member.user_id}
              onClick={() => {
                setSelectedMemberId(member.user_id);
                setFilter("all");
              }}
            >
              <span className="task-team-avatar">
                {(member.display_name || member.email).slice(0, 1).toUpperCase()}
              </span>
              <span className="task-team-person">
                <b>{member.display_name || member.email}</b>
                <small>{ROLE_LABELS[member.role]}</small>
              </span>
              <span className="task-team-counts">
                <span>
                  В работе <b>{memberOpen}</b>
                </span>
                <span>
                  Выполнено <b>{completed}</b>
                </span>
              </span>
            </button>
          ))}
        </div>
      </section>
      <div className="task-layout">
        <aside className="panel task-filters">
          <PanelTitle title="Списки" />
          {filters.map((item) => (
            <button
              key={item.id}
              className={filter === item.id ? "active" : ""}
              onClick={() => {
                setFilter(item.id);
                if (item.id === "mine") setSelectedMemberId(currentUserId);
              }}
            >
              <span>{item.label}</span>
              <b>{item.count}</b>
            </button>
          ))}
          {company.role === "viewer" && (
            <p>
              Вы можете выполнять только задачи, назначенные лично вам.
            </p>
          )}
        </aside>
        <section className="panel task-board">
          <div className="task-board-head">
            <div>
              <h2>
                {filters.find((item) => item.id === filter)?.label}
                {selectedMemberName ? ` · ${selectedMemberName}` : ""}
              </h2>
              <span>{list.length} задач</span>
            </div>
            <label className="task-search">
              ⌕
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Поиск задачи"
              />
            </label>
          </div>
          {list.length ? (
            <div className="task-list">
              {list.map((task) => {
                const isOverdue =
                  task.status === "open" &&
                  Boolean(task.due_date && task.due_date < today);
                const canToggle =
                  canManage || task.assignee_user_id === currentUserId;
                return (
                  <article
                    className={`task-row priority-${task.priority}${task.status === "completed" ? " completed" : ""}`}
                    key={task.id}
                  >
                    <button
                      type="button"
                      className="task-check"
                      disabled={!canToggle}
                      title={
                        canToggle
                          ? task.status === "completed"
                            ? "Вернуть в работу"
                            : "Отметить выполненной"
                          : "Задача назначена другому сотруднику"
                      }
                      onClick={() => void toggleTask(task)}
                    >
                      {task.status === "completed" ? "✓" : ""}
                    </button>
                    <div className="task-content">
                      <b>{task.title}</b>
                      {task.description && <p>{task.description}</p>}
                      <div className="task-meta">
                        <span className={`task-priority ${task.priority}`}>
                          {TASK_PRIORITY_LABELS[task.priority]}
                        </span>
                        <span>Ответственный: {memberName(task.assignee_user_id)}</span>
                        {task.due_date && (
                          <span className={isOverdue ? "task-overdue" : ""}>
                            Срок: {shortDate(task.due_date)}
                            {task.due_time
                              ? ` · ${task.due_time.slice(0, 5)}`
                              : ""}
                          </span>
                        )}
                      </div>
                    </div>
                    {canManage && (
                      <div className="task-actions">
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => setEditing(task)}
                        >
                          Изменить
                        </button>
                        <button
                          type="button"
                          className="task-delete"
                          onClick={() => void deleteTask(task)}
                        >
                          Удалить
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <Empty
              icon="✓"
              title="Задач в этом списке нет"
              text="Выберите другой список или добавьте новую задачу."
            />
          )}
        </section>
      </div>
      {editing && canManage && (
        <TaskModal
          companyId={company.id}
          task={editing === true ? undefined : editing}
          members={members.filter((member) => member.status === "active")}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onRefresh();
          }}
        />
      )}
    </PageWrap>
  );
}

function TaskModal({
  companyId,
  task,
  members,
  onClose,
  onSaved,
}: {
  companyId: string;
  task?: CompanyTask;
  members: CompanyMember[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [title, setTitle] = useState(task?.title || "");
  const [description, setDescription] = useState(task?.description || "");
  const [dueDate, setDueDate] = useState(task?.due_date || "");
  const [dueTime, setDueTime] = useState(task?.due_time?.slice(0, 5) || "");
  const [assignee, setAssignee] = useState(task?.assignee_user_id || "");
  const [priority, setPriority] = useState<TaskPriority>(
    task?.priority || "normal",
  );
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabase) return;
    setBusy(true);
    const params = {
      target_company_id: companyId,
      task_title: title.trim(),
      task_description: description.trim() || null,
      task_due_date: dueDate || null,
      task_due_time: dueDate && dueTime ? dueTime : null,
      task_assignee_user_id: assignee || null,
      task_priority: priority,
    };
    const { error } = task
      ? await supabase.rpc("voyager_update_task", {
          ...params,
          target_task_id: task.id,
        })
      : await supabase.rpc("voyager_create_task", params);
    setBusy(false);
    if (error) {
      alert(error.message);
      return;
    }
    await onSaved();
  };
  return (
    <Modal title={task ? "Изменить задачу" : "Новая задача"} onClose={onClose}>
      <form className="form-grid task-form" onSubmit={submit}>
        <Field label="Название задачи" wide>
          <input
            required
            maxLength={240}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Что нужно сделать?"
          />
        </Field>
        <Field label="Описание" wide>
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Подробности и важная информация"
          />
        </Field>
        <Field label="Срок выполнения">
          <input
            type="date"
            value={dueDate}
            onChange={(event) => {
              setDueDate(event.target.value);
              if (!event.target.value) setDueTime("");
            }}
          />
        </Field>
        <Field label="Время">
          <input
            type="time"
            disabled={!dueDate}
            value={dueTime}
            onChange={(event) => setDueTime(event.target.value)}
          />
        </Field>
        <Field label="Ответственный">
          <select
            value={assignee}
            onChange={(event) => setAssignee(event.target.value)}
          >
            <option value="">Не назначен</option>
            {members.map((member) => (
              <option value={member.user_id} key={member.user_id}>
                {member.display_name || member.email}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Приоритет">
          <select
            value={priority}
            onChange={(event) =>
              setPriority(event.target.value as TaskPriority)
            }
          >
            <option value="normal">Обычная</option>
            <option value="important">Важная</option>
            <option value="urgent">Срочная</option>
          </select>
        </Field>
        <div className="modal-actions wide">
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary" disabled={busy}>
            {busy ? "Сохраняем…" : "Сохранить задачу"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Fleet({
  data,
  onOpenVehicle,
  updateData,
}: {
  data: VoyagerData;
  onOpenVehicle: (id: string) => void;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"Все" | VehicleStatus>("Все");
  const [show, setShow] = useState(false);
  const [showMileage, setShowMileage] = useState(false);
  const list = data.vehicles.filter((v) => {
    const haystack =
      `${v.make} ${v.model} ${v.plate} ${v.vin || ""} ${v.stsSeries || ""} ${v.stsNumber || ""} ${v.driverName || ""} ${v.driverPhone || ""}`.toLowerCase();
    return (
      haystack.includes(query.toLowerCase()) &&
      (status === "Все" || v.status === status)
    );
  });
  return (
    <PageWrap
      title="Таксопарк"
      subtitle={`${data.vehicles.length} автомобилей в едином пространстве.`}
      actions={
        <div className="page-actions">
          <button className="secondary" onClick={() => setShowMileage(true)}>
            ◉ Обновить пробеги
          </button>
          <button className="primary" onClick={() => setShow(true)}>
            ＋ Добавить автомобиль
          </button>
        </div>
      }
    >
      <div className="toolbar">
        <label className="search">
          ⌕
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Госномер, модель, VIN, СТС или водитель"
          />
        </label>
        <div className="filter-pills">
          {(
            ["Все", "Работает", "На ТО", "Ремонт", "Простой", "Архив"] as const
          ).map((item) => (
            <button
              className={status === item ? "active" : ""}
              onClick={() => setStatus(item)}
              key={item}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
      {list.length ? (
        <div className="vehicle-grid">
          {list.map((vehicle) => {
            const f = vehicleFinances(data.events, vehicle.id);
            const investor = data.investors.find(
              (i) => i.id === vehicle.investorId,
            );
            return (
              <button
                className="vehicle-card"
                key={vehicle.id}
                onClick={() => onOpenVehicle(vehicle.id)}
              >
                <div className="vehicle-body">
                  <div className="asset-card-head">
                    <div>
                      <small>{vehicle.plate}</small>
                      <h3>{vehicle.make} {vehicle.model}</h3>
                    </div>
                    <i className={STATUS_COLORS[vehicle.status]}>{vehicle.status}</i>
                  </div>
                  <div className="vehicle-meta">
                    <span>
                      Пробег<b>{vehicle.mileage.toLocaleString("ru-RU")} км</b>
                    </span>
                    <span>
                      Резина<b>{vehicle.tireSeason || "Не указана"}</b>
                    </span>
                    <span>
                      Салонный фильтр
                      <b>{vehicle.cabinFilterReplacementMileage !== undefined ? `${vehicle.cabinFilterReplacementMileage.toLocaleString("ru-RU")} км` : "Не указан"}</b>
                    </span>
                    <span>
                      Воздушный фильтр
                      <b>{vehicle.airFilterReplacementMileage !== undefined ? `${vehicle.airFilterReplacementMileage.toLocaleString("ru-RU")} км` : "Не указан"}</b>
                    </span>
                    <span>
                      Инвестор<b>{investor?.name || "Не указан"}</b>
                    </span>
                    <span>
                      Водитель<b>{vehicle.driverName || "Не указан"}</b>
                    </span>
                    <span>
                      Телефон водителя
                      <b>{vehicle.driverPhone || "Не указан"}</b>
                    </span>
                    <span>
                      Последний осмотр
                      <b>
                        {lastCompletedInspection(data.vehicleInspections, vehicle.id)
                          ? shortDate(lastCompletedInspection(data.vehicleInspections, vehicle.id)!.scheduledAt)
                          : "Не проводился"}
                      </b>
                    </span>
                  </div>
                  <div className="vehicle-profit">
                    <span>Прибыль за месяц</span>
                    <b className={f.profit < 0 ? "negative" : ""}>
                      {money(f.profit)}
                    </b>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <Empty
          icon="◆"
          title="Автомобили не найдены"
          text="Измените фильтры или добавьте первый автомобиль."
        />
      )}
      {show && (
        <VehicleModal
          investors={data.investors}
          onClose={() => setShow(false)}
          onSave={(vehicle) => {
            updateData((d) => ({ ...d, vehicles: [...d.vehicles, vehicle] }));
            setShow(false);
          }}
        />
      )}
      {showMileage && (
        <MileageUpdateModal
          vehicles={data.vehicles.filter(
            (vehicle) => vehicle.status !== "Архив",
          )}
          onClose={() => setShowMileage(false)}
          onSave={(updates) => {
            updateData((d) => ({
              ...d,
              vehicles: d.vehicles.map((vehicle) => {
                const update = updates.get(vehicle.id);
                return update ? { ...vehicle, ...update } : vehicle;
              }),
            }));
            setShowMileage(false);
          }}
        />
      )}
    </PageWrap>
  );
}

function bicycleFinances(
  events: BicycleEvent[],
  bicycleId: string,
  month: string | null = currentMonth(),
) {
  const list = events.filter(
    (e) =>
      e.bicycleId === bicycleId &&
      (month === null || e.date.slice(0, 7) === month),
  );
  const income = list
    .filter((e) => e.type === "income" || e.type === "rental")
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const expense = list
    .filter((e) => ["expense", "repair", "maintenance"].includes(e.type))
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  return { income, expense, profit: income - expense };
}

function assetPayback(purchasePrice: number | undefined, profit: number) {
  if (!purchasePrice || purchasePrice <= 0) return null;
  const paidAmount = Math.max(0, profit);
  const percent = (paidAmount / purchasePrice) * 100;
  return {
    percent,
    progress: Math.min(100, percent),
    remaining: Math.max(0, purchasePrice - paidAmount),
  };
}

function PaybackBlock({
  purchasePrice,
  profit,
}: {
  purchasePrice?: number;
  profit: number;
}) {
  const payback = assetPayback(purchasePrice, profit);
  if (!payback) {
    return (
      <div className="asset-payback asset-payback-empty">
        <span>Стоимость покупки</span>
        <b>Не указана</b>
      </div>
    );
  }
  return (
    <div className="asset-payback">
      <div className="asset-payback-head">
        <span>Стоимость покупки <b>{money(purchasePrice)}</b></span>
        <strong>{payback.percent.toFixed(1).replace(".", ",")}%</strong>
      </div>
      <div className="asset-payback-track" aria-label={`Окупаемость ${payback.percent.toFixed(1)}%`}>
        <i style={{ width: `${payback.progress}%` }} />
      </div>
      <small>
        {payback.remaining > 0
          ? `До полного окупа осталось ${money(payback.remaining)}`
          : "Окупился полностью"}
      </small>
    </div>
  );
}

function calendarDaysUntil(value: string) {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  const today = new Date();
  return Math.round(
    (Date.UTC(year, month - 1, day) -
      Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) /
      86400000,
  );
}

function rentalDaysLabel(days: number) {
  if (days < 0) return `Просрочено на ${Math.abs(days)} сут.`;
  if (days === 0) return "Оплата сегодня";
  return `Осталось ${days} сут.`;
}

function currentBicycleRental(bicycle: Bicycle, events: BicycleEvent[]) {
  const renterKey = (value: string) => value.trim().replace(/\s+/g, " ").toLocaleLowerCase("ru-RU");
  const renter = renterKey(bicycle.currentRenter || "");
  const rentals = renter ? events
    .filter((item) => item.bicycleId === bicycle.id && item.type === "rental" && renterKey(item.renter || "") === renter)
    .sort((left, right) => right.date.localeCompare(left.date) || right.createdAt.localeCompare(left.createdAt)) : [];
  return {
    phone: rentals.find((item) => item.renterPhone?.trim())?.renterPhone || "",
    amount: rentals.find((item) => typeof item.amount === "number")?.amount,
    paymentMethod: rentals.find((item) => item.rentalPaymentMethod)?.rentalPaymentMethod || "Наличные",
  };
}

function addBicycleRentalDays(date: string, days: number) {
  const result = new Date(`${date.slice(0, 10)}T12:00:00Z`);
  result.setUTCDate(result.getUTCDate() + days);
  return result.toISOString().slice(0, 10);
}

function Bicycles({
  data,
  canManagePrices,
  cloudConfirmed,
  onOpenBicycle,
  updateData,
}: {
  data: VoyagerData;
  canManagePrices: boolean;
  cloudConfirmed: boolean;
  onOpenBicycle: (id: string) => void;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
}) {
  const [section, setSection] = useState<BicycleSection>("fleet");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"Все" | BicycleStatus | "Возврат">("Все");
  const [returnPlan, setReturnPlan] = useState<"Все" | BicycleRentalPlan>("Все");
  const [period, setPeriod] = useState(currentMonth());
  const [show, setShow] = useState(false);
  const bicycleSections: { id: BicycleSection; label: string; icon: string }[] = [
    { id: "fleet", label: "Весь парк", icon: "◈" },
    { id: "repairs", label: "Ремонты", icon: "⚒" },
    { id: "prices", label: "Прайс", icon: "₽" },
    { id: "general-expenses", label: "Общие расходы", icon: "↘" },
    { id: "waitlist", label: "Лист ожидания", icon: "☷" },
    { id: "archive", label: "Архив аренд", icon: "▤" },
  ];
  const latestRentalByBicycle = new Map<string, BicycleEvent>();
  data.bicycleEvents
    .filter((event) => event.type === "rental" && Boolean(event.renter))
    .sort((left, right) => right.date.localeCompare(left.date))
    .forEach((event) => {
      if (!latestRentalByBicycle.has(event.bicycleId))
        latestRentalByBicycle.set(event.bicycleId, event);
    });
  const normalizedQuery = query.trim().toLowerCase();
  const list = data.bicycles
    .filter((bicycle) => {
      const latestRental = latestRentalByBicycle.get(bicycle.id);
      const searchable = [
        bicycle.brand,
        bicycle.model,
        bicycle.inventoryNumber,
        bicycle.serialNumber,
        bicycle.batteryNumber,
        bicycle.chargerNumber,
        bicycle.currentRenter,
        bicycle.status,
        bicycle.year,
        bicycle.mileage,
        bicycle.purchasePrice,
        bicycle.rentalUntil,
        bicycle.rentalPlan,
        bicycle.rentalPlanNote,
        latestRental?.renter,
        latestRental?.renterPhone,
        latestRental?.rentalPaymentMethod,
      ].filter((value) => value !== undefined && value !== null).join(" ").toLowerCase();
      const matchesStatus = status === "Возврат"
        ? bicycle.status === "В аренде" &&
          Boolean(bicycle.rentalUntil) &&
          calendarDaysUntil(bicycle.rentalUntil!) <= 3
        : status === "Все" || bicycle.status === status;
      const matchesReturnPlan =
        status !== "Возврат" ||
        returnPlan === "Все" ||
        (bicycle.rentalPlan || "Не уточнено") === returnPlan;
      return searchable.includes(normalizedQuery) && matchesStatus && matchesReturnPlan;
    })
    .sort((left, right) => {
      if (status !== "Возврат") return 0;
      const leftDays = left.rentalUntil ? calendarDaysUntil(left.rentalUntil) : Infinity;
      const rightDays = right.rentalUntil ? calendarDaysUntil(right.rentalUntil) : Infinity;
      const leftOverdue = leftDays < 0;
      const rightOverdue = rightDays < 0;
      if (leftOverdue !== rightOverdue) return leftOverdue ? -1 : 1;
      const planOrder: Record<BicycleRentalPlan, number> = {
        "Не уточнено": 0,
        "Вернёт велосипед": 1,
        "Будет продлевать": 2,
      };
      const planDifference =
        planOrder[left.rentalPlan || "Не уточнено"] -
        planOrder[right.rentalPlan || "Не уточнено"];
      if (planDifference !== 0) return planDifference;
      return (left.rentalUntil || "").localeCompare(right.rentalUntil || "");
    });
  const totalIncome = data.bicycles.reduce(
    (sum, b) => sum + bicycleFinances(data.bicycleEvents, b.id, period).income,
    0,
  );
  const bicycleExpense = data.bicycles.reduce(
    (sum, b) => sum + bicycleFinances(data.bicycleEvents, b.id, period).expense,
    0,
  );
  const generalExpense = data.bicycleGeneralExpenses
    .filter((expense) => monthKey(expense.date) === period)
    .reduce((sum, expense) => sum + expense.amount, 0);
  const totalExpense = bicycleExpense + generalExpense;
  const allTimeTotals = data.bicycles.reduce(
    (sum, bicycle) => {
      const finance = bicycleFinances(data.bicycleEvents, bicycle.id, null);
      return {
        income: sum.income + finance.income,
        expense: sum.expense + finance.expense,
      };
    },
    { income: 0, expense: 0 },
  );
  allTimeTotals.expense += data.bicycleGeneralExpenses.reduce(
    (sum, expense) => sum + expense.amount,
    0,
  );
  return (
    <PageWrap
      title="Электровелосипеды"
      subtitle="Парк, ремонты, очередь арендаторов и история выдач в одном разделе."
      actions={
        section === "fleet" ? (
          <div className="page-actions asset-page-actions">
            <div className="company-income-period">
              <button type="button" aria-label="Предыдущий месяц" onClick={() => setPeriod(shiftMonthKey(period, -1))}>←</button>
              <input type="month" value={period} aria-label="Расчётный месяц" onChange={(event) => setPeriod(event.target.value || currentMonth())} />
              <button type="button" aria-label="Следующий месяц" onClick={() => setPeriod(shiftMonthKey(period, 1))}>→</button>
            </div>
            <button className="primary" onClick={() => setShow(true)}>
              ＋ Добавить велосипед
            </button>
          </div>
        ) : undefined
      }
    >
      <div className="bicycle-sections" role="tablist" aria-label="Подразделы электровелосипедов">
        {bicycleSections.map((item) => (
          <button
            type="button"
            key={item.id}
            role="tab"
            aria-selected={section === item.id}
            className={section === item.id ? "active" : ""}
            onClick={() => setSection(item.id)}
          >
            <span>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>
      {section === "fleet" && <>
        <div className="hero-stats compact bicycle-summary">
        <Stat
          label="Всего"
          value={String(data.bicycles.length)}
          note={`${data.bicycles.filter((b) => b.status === "В аренде").length} в аренде`}
          tone="mint"
        />
        <Stat
          label="Свободно"
          value={String(
            data.bicycles.filter((b) => b.status === "Свободен").length,
          )}
          note="Готовы к выдаче"
        />
        <Stat label="Доход за месяц" value={money(totalIncome)} note={investorReportPeriodLabel(period)} />
        <Stat
          label="Прибыль за месяц"
          value={money(totalIncome - totalExpense)}
          note={investorReportPeriodLabel(period)}
          tone={totalIncome - totalExpense >= 0 ? "mint" : "danger"}
        />
        <Stat label="Доход за всё время" value={money(allTimeTotals.income)} />
        <Stat
          label="Прибыль за всё время"
          value={money(allTimeTotals.income - allTimeTotals.expense)}
          tone={allTimeTotals.income - allTimeTotals.expense >= 0 ? "mint" : "danger"}
        />
        </div>
        <div className="toolbar">
          <label className="search">
            ⌕
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск по номеру, модели, S/N, аккумулятору, З/У или арендатору"
            />
          </label>
          <div className="filter-pills">
            {(
              ["Все", "Свободен", "В аренде", "На ТО", "Ремонт", "Архив", "Возврат"] as const
            ).map((item) => (
              <button
                className={status === item ? "active" : ""}
                onClick={() => {
                  setStatus(item);
                  if (item !== "Возврат") setReturnPlan("Все");
                }}
                key={item}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        {status === "Возврат" && (
          <div className="bicycle-return-plan-filter" aria-label="Планы арендаторов">
            <span>После оплаты:</span>
            <div className="filter-pills">
              {(["Все", ...BICYCLE_RENTAL_PLANS] as const).map((item) => (
                <button
                  type="button"
                  key={item}
                  className={returnPlan === item ? "active" : ""}
                  onClick={() => setReturnPlan(item)}
                >
                  {item === "Все" ? "Все ближайшие" : BICYCLE_RENTAL_PLAN_LABELS[item]}
                </button>
              ))}
            </div>
          </div>
        )}
        {list.length ? (
          <div className="vehicle-grid bicycle-grid">
            {list.map((bicycle) => {
              const f = bicycleFinances(data.bicycleEvents, bicycle.id, period);
              const allTime = bicycleFinances(data.bicycleEvents, bicycle.id, null);
              const rentalDays = bicycle.rentalUntil
                ? calendarDaysUntil(bicycle.rentalUntil)
                : null;
              const rentalPlan = bicycle.rentalPlan || "Не уточнено";
              const rentalPlanClass = rentalPlan === "Будет продлевать"
                ? "extend"
                : rentalPlan === "Вернёт велосипед"
                  ? "return"
                  : "unknown";
              return (
                <button
                  className="vehicle-card bicycle-card"
                  key={bicycle.id}
                  onClick={() => onOpenBicycle(bicycle.id)}
                >
                  <div className="vehicle-body">
                    <div className="asset-card-head">
                      <div>
                        <small>{bicycle.inventoryNumber}</small>
                        <h3>{bicycle.brand} {bicycle.model}</h3>
                      </div>
                      <i className={BICYCLE_STATUS_COLORS[bicycle.status]}>{bicycle.status}</i>
                    </div>
                    <div className="vehicle-meta">
                      <span>
                        Пробег<b>{bicycle.mileage.toLocaleString("ru-RU")} км</b>
                      </span>
                      <span>
                        {bicycle.status === "В аренде" ? "Арендатор" : "Серийный номер"}
                        <b>{bicycle.currentRenter || bicycle.serialNumber || "Не указан"}</b>
                      </span>
                    </div>
                    {(bicycle.batteryNumber || bicycle.chargerNumber) && (
                      <div className="bicycle-equipment-line">
                        <span>АКБ <b>{bicycle.batteryNumber || "—"}</b></span>
                        <span>З/У <b>{bicycle.chargerNumber || "—"}</b></span>
                      </div>
                    )}
                    {bicycle.status === "В аренде" && bicycle.rentalUntil && rentalDays !== null && (
                      <div className="bicycle-rental-brief">
                        <div className={`bicycle-rental-deadline${rentalDays < 0 ? " overdue" : rentalDays <= 3 ? " soon" : ""}`}>
                          <span>Оплачено до<b>{shortDate(bicycle.rentalUntil)}</b></span>
                          <strong>{rentalDaysLabel(rentalDays)}</strong>
                        </div>
                        <div className={`bicycle-rental-plan ${rentalPlanClass}`}>
                          <span>После этой даты</span>
                          <b>{BICYCLE_RENTAL_PLAN_LABELS[rentalPlan]}</b>
                        </div>
                      </div>
                    )}
                    <div className="asset-finance-grid">
                      <span>Прибыль за месяц<b className={f.profit < 0 ? "negative" : ""}>{money(f.profit)}</b></span>
                      <span>Доход за всё время<b>{money(allTime.income)}</b></span>
                      <span>Прибыль за всё время<b className={allTime.profit < 0 ? "negative" : ""}>{money(allTime.profit)}</b></span>
                    </div>
                    <PaybackBlock purchasePrice={bicycle.purchasePrice} profit={allTime.profit} />
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <Empty
            icon="◈"
            title="Велосипеды не найдены"
            text="Измените фильтры или добавьте первый электровелосипед."
          />
        )}
      </>}
      {section === "repairs" && (
        <BicycleRepairsSection data={data} updateData={updateData} />
      )}
      {section === "prices" && <BicyclePriceList data={data} updateData={updateData} canManage={canManagePrices} cloudConfirmed={cloudConfirmed} />}
      {section === "general-expenses" && (
        <BicycleGeneralExpensesSection data={data} updateData={updateData} />
      )}
      {section === "waitlist" && (
        <BicycleWaitlistSection data={data} updateData={updateData} />
      )}
      {section === "archive" && <BicycleRentalArchive data={data} />}
      {show && (
        <BicycleModal
          onClose={() => setShow(false)}
          onSave={(bicycle) => {
            updateData((d) => ({ ...d, bicycles: [...d.bicycles, bicycle] }));
            setShow(false);
          }}
        />
      )}
    </PageWrap>
  );
}

function BicycleGeneralExpensesSection({
  data,
  updateData,
}: {
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
}) {
  const [period, setPeriod] = useState(currentMonth());
  const [category, setCategory] = useState<"Все" | BicycleGeneralExpenseCategory>("Все");
  const [paymentMethod, setPaymentMethod] = useState<"Все" | PaymentMethod>("Все");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<BicycleGeneralExpense | null>(null);
  const [creating, setCreating] = useState(false);
  const periodEntries = data.bicycleGeneralExpenses.filter(
    (expense) => monthKey(expense.date) === period,
  );
  const list = periodEntries
    .filter((expense) => {
      const service = expense.service === "Другой"
        ? expense.customService
        : expense.service;
      const haystack = [
        expense.category,
        expense.notes,
        service,
        expense.paymentMethod,
        expense.amount,
      ].filter(Boolean).join(" ").toLocaleLowerCase("ru");
      return (
        haystack.includes(query.trim().toLocaleLowerCase("ru")) &&
        (category === "Все" || expense.category === category) &&
        (paymentMethod === "Все" || expense.paymentMethod === paymentMethod)
      );
    })
    .sort((left, right) => right.date.localeCompare(left.date));
  const monthTotal = periodEntries.reduce(
    (sum, expense) => sum + expense.amount,
    0,
  );
  const allTimeTotal = data.bicycleGeneralExpenses.reduce(
    (sum, expense) => sum + expense.amount,
    0,
  );
  const filteredTotal = list.reduce((sum, expense) => sum + expense.amount, 0);
  const save = (next: BicycleGeneralExpense) => {
    updateData((current) => ({
      ...current,
      bicycleGeneralExpenses: current.bicycleGeneralExpenses.some(
        (expense) => expense.id === next.id,
      )
        ? current.bicycleGeneralExpenses.map((expense) =>
            expense.id === next.id ? next : expense,
          )
        : [next, ...current.bicycleGeneralExpenses],
    }));
    setCreating(false);
    setEditing(null);
  };
  const remove = (expense: BicycleGeneralExpense) => {
    if (!confirm("Удалить этот общий расход велопарка?")) return;
    updateData((current) => ({
      ...current,
      bicycleGeneralExpenses: current.bicycleGeneralExpenses.filter(
        (item) => item.id !== expense.id,
      ),
    }));
  };

  return (
    <div className="bicycle-subsection">
      <div className="subsection-heading">
        <div>
          <p className="eyebrow">ЭЛЕКТРОВЕЛОСИПЕДЫ</p>
          <h2>Общие расходы</h2>
          <p>Затраты всего велопарка без привязки к конкретному велосипеду.</p>
        </div>
        <button className="primary" onClick={() => setCreating(true)}>
          ＋ Добавить расход
        </button>
      </div>
      <div className="hero-stats compact bicycle-general-expense-summary">
        <Stat
          label="Расходы за месяц"
          value={money(monthTotal)}
          note={investorReportPeriodLabel(period)}
          tone="warm"
        />
        <Stat
          label="Расходы за всё время"
          value={money(allTimeTotal)}
          note={`${data.bicycleGeneralExpenses.length} операций`}
        />
        <Stat
          label="По фильтру"
          value={money(filteredTotal)}
          note={`${list.length} операций`}
        />
        <Stat
          label="Средний расход"
          value={money(periodEntries.length ? monthTotal / periodEntries.length : 0)}
          note="За выбранный месяц"
        />
      </div>
      <div className="toolbar bicycle-workflow-toolbar">
        <div className="company-income-period">
          <button type="button" aria-label="Предыдущий месяц" onClick={() => setPeriod(shiftMonthKey(period, -1))}>←</button>
          <input type="month" value={period} aria-label="Месяц расходов" onChange={(event) => setPeriod(event.target.value || currentMonth())} />
          <button type="button" aria-label="Следующий месяц" onClick={() => setPeriod(shiftMonthKey(period, 1))}>→</button>
        </div>
        <label className="search">⌕<input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Категория, комментарий, сервис или сумма" /></label>
        <select value={category} onChange={(event) => setCategory(event.target.value as "Все" | BicycleGeneralExpenseCategory)}>
          <option value="Все">Все категории</option>
          {BICYCLE_GENERAL_EXPENSE_CATEGORIES.map((item) => <option key={item}>{item}</option>)}
        </select>
        <select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value as "Все" | PaymentMethod)}>
          <option value="Все">Все способы оплаты</option>
          {PAYMENT_METHODS.map((item) => <option key={item}>{item}</option>)}
        </select>
      </div>
      {list.length ? (
        <div className="bicycle-workflow-grid bicycle-general-expense-grid">
          {list.map((expense) => {
            const service = expense.service === "Другой"
              ? expense.customService || "Другой"
              : expense.service;
            return (
              <article className="panel bicycle-repair-card bicycle-general-expense-card" key={expense.id}>
                <header>
                  <div><span className="workflow-badge expense">Общий расход</span></div>
                  <small>{shortDate(expense.date)}</small>
                </header>
                <div className="bicycle-general-expense-title">
                  <div>
                    <h3>{expense.category}</h3>
                    <p className="workflow-subtitle">Не относится к конкретному велосипеду</p>
                  </div>
                  <strong>−{money(expense.amount)}</strong>
                </div>
                <div className="workflow-meta">
                  <span>Дата<b>{shortDate(expense.date)}</b></span>
                  <span>Сервис<b>{service || "Не указан"}</b></span>
                  <span>Оплата<b>{expense.paymentMethod || "Не указана"}</b></span>
                </div>
                {expense.notes && <p className="workflow-comment">{expense.notes}</p>}
                <footer>
                  <button className="secondary" onClick={() => setEditing(expense)}>Изменить</button>
                  <button className="danger-button" onClick={() => remove(expense)}>Удалить</button>
                </footer>
              </article>
            );
          })}
        </div>
      ) : (
        <Empty
          icon="↘"
          title="Общих расходов нет"
          text="Измените фильтры или добавьте первый расход велопарка."
        />
      )}
      {(creating || editing) && (
        <BicycleGeneralExpenseModal
          expense={editing || undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSave={save}
        />
      )}
    </div>
  );
}

function BicycleGeneralExpenseModal({
  expense,
  onClose,
  onSave,
}: {
  expense?: BicycleGeneralExpense;
  onClose: () => void;
  onSave: (expense: BicycleGeneralExpense) => void;
}) {
  const [date, setDate] = useState(
    expense?.date.slice(0, 10) || new Date().toISOString().slice(0, 10),
  );
  const [amount, setAmount] = useState(
    typeof expense?.amount === "number" ? String(expense.amount) : "",
  );
  const [category, setCategory] = useState<BicycleGeneralExpenseCategory>(
    expense?.category || "Запчасти на склад",
  );
  const [service, setService] = useState<"" | BicycleRepairService>(
    expense?.service || "",
  );
  const [customService, setCustomService] = useState(expense?.customService || "");
  const [paymentMethod, setPaymentMethod] = useState<"" | PaymentMethod>(
    expense?.paymentMethod || "",
  );
  const [notes, setNotes] = useState(expense?.notes || "");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      alert("Укажите сумму расхода больше нуля.");
      return;
    }
    const now = new Date().toISOString();
    onSave({
      id: expense?.id || uid(),
      date: new Date(`${date}T12:00:00`).toISOString(),
      amount: numericAmount,
      category,
      service: service || undefined,
      customService: service === "Другой" ? customService.trim() || undefined : undefined,
      paymentMethod: paymentMethod || undefined,
      notes: notes.trim() || undefined,
      createdAt: expense?.createdAt || now,
      updatedAt: now,
    });
  };
  return (
    <Modal title={expense ? "Изменить общий расход" : "Новый общий расход"} onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <Field label="Дата">
          <input required type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </Field>
        <Field label="Сумма, ₽">
          <input required type="number" min="0.01" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} />
        </Field>
        <Field label="Категория">
          <select value={category} onChange={(event) => setCategory(event.target.value as BicycleGeneralExpenseCategory)}>
            {BICYCLE_GENERAL_EXPENSE_CATEGORIES.map((item) => <option key={item}>{item}</option>)}
          </select>
        </Field>
        <Field label="Сервис">
          <select value={service} onChange={(event) => setService(event.target.value as "" | BicycleRepairService)}>
            <option value="">Не указан</option>
            {BICYCLE_REPAIR_SERVICES.map((item) => <option key={item}>{item}</option>)}
          </select>
        </Field>
        {service === "Другой" && (
          <Field label="Название сервиса">
            <input value={customService} onChange={(event) => setCustomService(event.target.value)} placeholder="Укажите сервис" />
          </Field>
        )}
        <Field label="Способ оплаты">
          <select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value as "" | PaymentMethod)}>
            <option value="">Не указан</option>
            {PAYMENT_METHODS.map((item) => <option key={item}>{item}</option>)}
          </select>
        </Field>
        <Field label="Комментарий">
          <textarea rows={4} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Что купили или на что потратили" />
        </Field>
        <div className="modal-actions wide">
          <span />
          <button type="button" className="secondary" onClick={onClose}>Отмена</button>
          <button className="primary">Сохранить</button>
        </div>
      </form>
    </Modal>
  );
}

function BicycleRepairsSection({
  data,
  updateData,
}: {
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
}) {
  const [query, setQuery] = useState("");
  const [urgency, setUrgency] = useState<"Все" | BicycleRepairUrgency>("Все");
  const [status, setStatus] = useState<"Все" | BicycleRepairStatus>("Все");
  const [service, setService] = useState<"Все" | BicycleRepairService>("Все");
  const [payer, setPayer] = useState<"Все" | BicycleRepairPayer>("Все");
  const [editing, setEditing] = useState<BicycleRepairOrder | null>(null);
  const [creating, setCreating] = useState(false);
  const bicycleById = new Map(data.bicycles.map((item) => [item.id, item]));
  const list = data.bicycleRepairOrders
    .filter((item) => {
      const bicycle = bicycleById.get(item.bicycleId);
      const haystack = `${bicycle?.inventoryNumber || ""} ${bicycle?.brand || ""} ${bicycle?.model || ""} ${item.repairs.join(" ")} ${item.notes || ""} ${item.customService || ""}`.toLowerCase();
      return (
        haystack.includes(query.trim().toLowerCase()) &&
        (urgency === "Все" || item.urgency === urgency) &&
        (status === "Все" || item.status === status) &&
        (service === "Все" || item.service === service) &&
        (payer === "Все" || item.payer === payer)
      );
    })
    .sort((left, right) => {
      if (left.status !== right.status)
        return left.status === "Не выполнено" ? -1 : 1;
      if (left.urgency !== right.urgency)
        return left.urgency === "Срочный" ? -1 : 1;
      return right.createdAt.localeCompare(left.createdAt);
    });
  const save = (next: BicycleRepairOrder) => {
    updateData((current) => ({
      ...current,
      bicycleRepairOrders: current.bicycleRepairOrders.some((item) => item.id === next.id)
        ? current.bicycleRepairOrders.map((item) => item.id === next.id ? next : item)
        : [next, ...current.bicycleRepairOrders],
    }));
    setEditing(null);
    setCreating(false);
  };
  const remove = (item: BicycleRepairOrder) => {
    if (!confirm("Удалить эту запись о ремонте?")) return;
    updateData((current) => ({
      ...current,
      bicycleRepairOrders: current.bicycleRepairOrders.filter((entry) => entry.id !== item.id),
    }));
  };
  const toggleStatus = (item: BicycleRepairOrder) => {
    const completed = item.status !== "Выполнено";
    save({
      ...item,
      status: completed ? "Выполнено" : "Не выполнено",
      completedAt: completed ? new Date().toISOString() : undefined,
    });
  };
  return (
    <div className="bicycle-subsection">
      <div className="subsection-heading">
        <div>
          <p className="eyebrow">ЭЛЕКТРОВЕЛОСИПЕДЫ</p>
          <h2>Ремонты</h2>
          <p>Очередь необходимых работ без автоматического влияния на финансы.</p>
        </div>
        <button className="primary" onClick={() => setCreating(true)}>＋ Добавить ремонт</button>
      </div>
      <div className="hero-stats compact bicycle-repair-summary">
        <Stat label="Всего" value={String(data.bicycleRepairOrders.length)} />
        <Stat label="Срочные" value={String(data.bicycleRepairOrders.filter((item) => item.urgency === "Срочный" && item.status === "Не выполнено").length)} tone="danger" />
        <Stat label="В работе" value={String(data.bicycleRepairOrders.filter((item) => item.status === "Не выполнено").length)} tone="warm" />
        <Stat label="Выполнено" value={String(data.bicycleRepairOrders.filter((item) => item.status === "Выполнено").length)} tone="mint" />
      </div>
      <div className="toolbar bicycle-workflow-toolbar">
        <label className="search">⌕<input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Велосипед, работа, сервис или комментарий" /></label>
        <select value={service} onChange={(event) => setService(event.target.value as "Все" | BicycleRepairService)}>
          <option value="Все">Все сервисы</option>
          <option value="Не выбран">Не выбран</option>
          {BICYCLE_REPAIR_SERVICES.map((item) => <option key={item}>{item}</option>)}
        </select>
        <select value={payer} onChange={(event) => setPayer(event.target.value as "Все" | BicycleRepairPayer)}>
          <option value="Все">Любая оплата</option>
          <option value="Не выбран">Не выбран</option>
          <option value="Арендатор">Арендатор</option>
          <option value="Автопарк">Автопарк</option>
        </select>
        <div className="filter-pills">
          {(["Все", "Срочный", "Не срочный"] as const).map((item) => <button key={item} className={urgency === item ? "active" : ""} onClick={() => setUrgency(item)}>{item}</button>)}
        </div>
        <div className="filter-pills">
          {(["Все", "Не выполнено", "Выполнено"] as const).map((item) => <button key={item} className={status === item ? "active" : ""} onClick={() => setStatus(item)}>{item}</button>)}
        </div>
      </div>
      {list.length ? (
        <div className="bicycle-workflow-grid">
          {list.map((item) => {
            const bicycle = bicycleById.get(item.bicycleId);
            return (
              <article key={item.id} className={`panel bicycle-repair-card${item.status === "Выполнено" ? " completed" : ""}`}>
                <header>
                  <div>
                    <span className={`workflow-badge ${item.urgency === "Срочный" ? "urgent" : "regular"}`}>{item.urgency}</span>
                    <span className={`workflow-badge ${item.status === "Выполнено" ? "done" : "open"}`}>{item.status}</span>
                    {item.needsCompletion && <span className="workflow-badge needs-completion">Требует заполнения</span>}
                  </div>
                  <small>{shortDate(item.createdAt)}</small>
                </header>
                <h3>{bicycle?.inventoryNumber || "Велосипед удалён"}</h3>
                <p className="workflow-subtitle">{bicycle ? `${bicycle.brand} ${bicycle.model}` : "История ремонта сохранена"}</p>
                <ol className="repair-task-list">{item.repairs.map((repair, index) => <li key={`${item.id}-${index}`}>{repair}</li>)}</ol>
                <div className="workflow-meta">
                  <span>Сервис<b>{item.service === "Другой" ? item.customService || "Другой" : item.service}</b></span>
                  <span>Работы<b>{item.laborCost !== undefined ? money(item.laborCost) : "Не указано"}</b></span>
                  <span>Оплачивает<b>{item.payer}</b></span>
                </div>
                {item.notes && <p className="workflow-comment">{item.notes}</p>}
                <footer>
                  <button className="secondary small" onClick={() => toggleStatus(item)}>{item.status === "Выполнено" ? "Вернуть в работу" : "Выполнено"}</button>
                  <button className="secondary small" onClick={() => setEditing(item)}>Изменить</button>
                  <button className="danger-button small" onClick={() => remove(item)}>Удалить</button>
                </footer>
              </article>
            );
          })}
        </div>
      ) : <Empty icon="⚒" title="Ремонтов пока нет" text="Создайте первую запись, когда велосипеду потребуются работы." />}
      {(creating || editing) && (
        <BicycleRepairModal
          bicycles={data.bicycles.filter((item) => item.status !== "Архив")}
          item={editing || undefined}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function BicycleRepairModal({
  bicycles,
  item,
  onClose,
  onSave,
}: {
  bicycles: Bicycle[];
  item?: BicycleRepairOrder;
  onClose: () => void;
  onSave: (item: BicycleRepairOrder) => void;
}) {
  const [bicycleId, setBicycleId] = useState(item?.bicycleId || bicycles[0]?.id || "");
  const [urgency, setUrgency] = useState<BicycleRepairUrgency>(item?.urgency || "Не срочный");
  const [repairs, setRepairs] = useState<string[]>(item?.repairs.length ? item.repairs : [""]);
  const [service, setService] = useState<BicycleRepairService>(item?.service || "Наш сервис");
  const [customService, setCustomService] = useState(item?.customService || "");
  const [laborCost, setLaborCost] = useState(item?.laborCost !== undefined ? String(item.laborCost) : "");
  const [payer, setPayer] = useState<BicycleRepairPayer>(item?.payer || "Автопарк");
  const [status, setStatus] = useState<BicycleRepairStatus>(item?.status || "Не выполнено");
  const [notes, setNotes] = useState(item?.notes || "");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const cleanRepairs = repairs.map((value) => value.trim()).filter(Boolean);
    if (!cleanRepairs.length) {
      alert("Добавьте хотя бы одну необходимую работу");
      return;
    }
    const now = new Date().toISOString();
    onSave({
      id: item?.id || uid(),
      bicycleId,
      urgency,
      repairs: cleanRepairs,
      service,
      customService: service === "Другой" ? customService.trim() || undefined : undefined,
      laborCost: laborCost ? Number(laborCost) : undefined,
      payer,
      status,
      notes: notes.trim() || undefined,
      sourceInspectionId: item?.sourceInspectionId,
      needsCompletion: service === "Не выбран" || payer === "Не выбран",
      createdAt: item?.createdAt || now,
      completedAt: status === "Выполнено" ? item?.completedAt || now : undefined,
    });
  };
  return (
    <Modal title={item ? "Изменить ремонт" : "Новый ремонт электровелосипеда"} onClose={onClose} wide>
      <form className="form-grid bicycle-repair-form" onSubmit={submit}>
        <Field label="Электровелосипед" wide>
          <select required value={bicycleId} onChange={(event) => setBicycleId(event.target.value)}>
            {!bicycles.length && <option value="">Нет доступных велосипедов</option>}
            {bicycles.map((bicycle) => <option key={bicycle.id} value={bicycle.id}>{bicycle.inventoryNumber} · {bicycle.brand} {bicycle.model}</option>)}
          </select>
        </Field>
        <Field label="Срочность"><select value={urgency} onChange={(event) => setUrgency(event.target.value as BicycleRepairUrgency)}><option>Срочный</option><option>Не срочный</option></select></Field>
        <Field label="Статус"><select value={status} onChange={(event) => setStatus(event.target.value as BicycleRepairStatus)}><option>Не выполнено</option><option>Выполнено</option></select></Field>
        <div className="wide repair-items-editor">
          <div className="repair-items-head"><b>Необходимые работы</b><button type="button" className="secondary small" onClick={() => setRepairs((current) => [...current, ""])}>＋ Добавить пункт</button></div>
          {repairs.map((repair, index) => (
            <div key={index} className="repair-item-input">
              <span>{index + 1}</span>
              <input required={index === 0} value={repair} onChange={(event) => setRepairs((current) => current.map((value, itemIndex) => itemIndex === index ? event.target.value : value))} placeholder="Что нужно отремонтировать" />
              <button type="button" className="icon-delete" disabled={repairs.length === 1} onClick={() => setRepairs((current) => current.filter((_, itemIndex) => itemIndex !== index))}>×</button>
            </div>
          ))}
        </div>
        <Field label="Сервис"><select value={service} onChange={(event) => setService(event.target.value as BicycleRepairService)}><option>Не выбран</option>{BICYCLE_REPAIR_SERVICES.map((value) => <option key={value}>{value}</option>)}</select></Field>
        {service === "Другой" && <Field label="Название сервиса"><input required value={customService} onChange={(event) => setCustomService(event.target.value)} /></Field>}
        <Field label="Стоимость выполнения работ, ₽"><input type="number" min="0" step="0.01" value={laborCost} onChange={(event) => setLaborCost(event.target.value)} placeholder="Необязательно" /></Field>
        <Field label="Оплата"><select value={payer} onChange={(event) => setPayer(event.target.value as BicycleRepairPayer)}><option>Не выбран</option><option>Арендатор</option><option>Автопарк</option></select></Field>
        <Field label="Комментарий" wide><textarea rows={4} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Дополнительная информация" /></Field>
        <div className="modal-actions wide"><span /><button type="button" className="secondary" onClick={onClose}>Отмена</button><button className="primary" disabled={!bicycleId}>Сохранить</button></div>
      </form>
    </Modal>
  );
}

function BicycleWaitlistSection({
  data,
  updateData,
}: {
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
}) {
  const [model, setModel] = useState<"Все" | BicycleWaitlistModel>("Все");
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BicycleWaitlistEntry | null>(null);
  const list = data.bicycleWaitlist
    .filter((item) => (model === "Все" || item.model === model) && `${item.renterName} ${item.renterPhone} ${item.notes || ""}`.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  const save = (next: BicycleWaitlistEntry) => {
    updateData((current) => ({
      ...current,
      bicycleWaitlist: current.bicycleWaitlist.some((item) => item.id === next.id)
        ? current.bicycleWaitlist.map((item) => item.id === next.id ? next : item)
        : [...current.bicycleWaitlist, next],
    }));
    setCreating(false);
    setEditing(null);
  };
  const remove = (entry: BicycleWaitlistEntry) => {
    if (!confirm(`Удалить ${entry.renterName} из листа ожидания?`)) return;
    updateData((current) => ({ ...current, bicycleWaitlist: current.bicycleWaitlist.filter((item) => item.id !== entry.id) }));
  };
  return (
    <div className="bicycle-subsection">
      <div className="subsection-heading">
        <div><p className="eyebrow">ЭЛЕКТРОВЕЛОСИПЕДЫ</p><h2>Лист ожидания</h2><p>Очередь будущих арендаторов по выбранной модели.</p></div>
        <button className="primary" onClick={() => setCreating(true)}>＋ Добавить человека</button>
      </div>
      <div className="toolbar bicycle-workflow-toolbar">
        <label className="search">⌕<input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="ФИО, телефон или комментарий" /></label>
        <div className="filter-pills">
          {(["Все", ...BICYCLE_WAITLIST_MODELS] as const).map((item) => <button key={item} className={model === item ? "active" : ""} onClick={() => setModel(item)}>{item}</button>)}
        </div>
      </div>
      {list.length ? (
        <div className="waitlist-table-wrap panel">
          <div className="waitlist-table">
            <div className="waitlist-head"><span>№</span><span>Арендатор</span><span>Телефон</span><span>Модель</span><span>Комментарий</span><span /></div>
            {list.map((item) => {
              const position = data.bicycleWaitlist.filter((entry) => entry.model === item.model).sort((left, right) => left.createdAt.localeCompare(right.createdAt)).findIndex((entry) => entry.id === item.id) + 1;
              return <div className="waitlist-row" key={item.id}><strong>{position}</strong><span><b>{item.renterName}</b><small>{shortDate(item.createdAt)}</small></span><span>{item.renterPhone}</span><span><i>{item.model}</i></span><span>{item.notes || "—"}</span><span className="waitlist-actions"><button className="secondary small" onClick={() => setEditing(item)}>Изменить</button><button className="danger-button small" onClick={() => remove(item)}>Удалить</button></span></div>;
            })}
          </div>
        </div>
      ) : <Empty icon="☷" title="Лист ожидания пуст" text="Добавьте первого человека, который ожидает электровелосипед." />}
      {(creating || editing) && <BicycleWaitlistModal item={editing || undefined} onClose={() => { setCreating(false); setEditing(null); }} onSave={save} />}
    </div>
  );
}

function BicycleWaitlistModal({ item, onClose, onSave }: { item?: BicycleWaitlistEntry; onClose: () => void; onSave: (item: BicycleWaitlistEntry) => void }) {
  const [renterName, setRenterName] = useState(item?.renterName || "");
  const [renterPhone, setRenterPhone] = useState(item?.renterPhone || "");
  const [model, setModel] = useState<BicycleWaitlistModel>(item?.model || "Titan 2.0");
  const [notes, setNotes] = useState(item?.notes || "");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSave({ id: item?.id || uid(), renterName: renterName.trim(), renterPhone: renterPhone.trim(), model, notes: notes.trim() || undefined, createdAt: item?.createdAt || new Date().toISOString() });
  };
  return <Modal title={item ? "Изменить запись" : "Добавить в лист ожидания"} onClose={onClose}><form className="form-grid" onSubmit={submit}><Field label="ФИО арендатора" wide><input required value={renterName} onChange={(event) => setRenterName(event.target.value)} /></Field><Field label="Номер телефона" wide><input required type="tel" value={renterPhone} onChange={(event) => setRenterPhone(event.target.value)} /></Field><Field label="Модель" wide><select value={model} onChange={(event) => setModel(event.target.value as BicycleWaitlistModel)}>{BICYCLE_WAITLIST_MODELS.map((value) => <option key={value}>{value}</option>)}</select></Field><Field label="Комментарий" wide><textarea rows={4} value={notes} onChange={(event) => setNotes(event.target.value)} /></Field><div className="modal-actions wide"><span /><button type="button" className="secondary" onClick={onClose}>Отмена</button><button className="primary">Сохранить</button></div></form></Modal>;
}

function BicycleRentalArchive({ data }: { data: VoyagerData }) {
  const [query, setQuery] = useState("");
  const [model, setModel] = useState("Все");
  const [paymentMethod, setPaymentMethod] = useState<"Все" | BicycleRentalPaymentMethod>("Все");
  const [period, setPeriod] = useState("");
  const bicycleById = new Map(data.bicycles.map((item) => [item.id, item]));
  const rentals = data.bicycleEvents
    .filter((event) => event.type === "rental" && Boolean(event.renter))
    .filter((event) => {
      const bicycle = bicycleById.get(event.bicycleId);
      const bicycleModel = event.bicycleModel || bicycle?.model || "";
      const bicycleNumber = event.bicycleInventoryNumber || bicycle?.inventoryNumber || "";
      const haystack = `${event.renter || ""} ${event.renterPhone || ""} ${bicycleModel} ${bicycleNumber} ${event.notes || ""}`.toLowerCase();
      return haystack.includes(query.trim().toLowerCase()) && (model === "Все" || bicycleModel === model) && (paymentMethod === "Все" || event.rentalPaymentMethod === paymentMethod) && (!period || event.date.slice(0, 7) === period);
    })
    .sort((left, right) => right.date.localeCompare(left.date));
  const models = Array.from(new Set(data.bicycleEvents.filter((event) => event.type === "rental" && event.renter).map((event) => event.bicycleModel || bicycleById.get(event.bicycleId)?.model).filter((value): value is string => Boolean(value)))).sort();
  return (
    <div className="bicycle-subsection">
      <div className="subsection-heading"><div><p className="eyebrow">ЭЛЕКТРОВЕЛОСИПЕДЫ</p><h2>Архив аренд</h2><p>Каждая новая выдача автоматически сохраняется здесь.</p></div></div>
      <div className="toolbar bicycle-workflow-toolbar archive-toolbar">
        <label className="search">⌕<input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="ФИО, телефон, модель или номер велосипеда" /></label>
        <select value={model} onChange={(event) => setModel(event.target.value)}><option>Все</option>{models.map((value) => <option key={value}>{value}</option>)}</select>
        <input type="month" value={period} aria-label="Месяц аренды" onChange={(event) => setPeriod(event.target.value)} />
        {period && <button className="secondary" onClick={() => setPeriod("")}>Все месяцы</button>}
        <div className="filter-pills">{(["Все", ...BICYCLE_RENTAL_PAYMENT_METHODS] as const).map((value) => <button key={value} className={paymentMethod === value ? "active" : ""} onClick={() => setPaymentMethod(value)}>{value}</button>)}</div>
      </div>
      {rentals.length ? <div className="rental-archive-list">{rentals.map((event) => {
        const bicycle = bicycleById.get(event.bicycleId);
        return <article className="panel rental-archive-card" key={event.id}><header><div><small>{shortDate(event.date)}</small><h3>{event.renter}</h3></div><strong>{typeof event.amount === "number" ? money(event.amount) : "Сумма не указана"}</strong></header><div className="workflow-meta"><span>Телефон<b>{event.renterPhone || "Не указан"}</b></span><span>Модель<b>{event.bicycleModel || bicycle?.model || "Не указана"}</b></span><span>Номер велосипеда<b>{event.bicycleInventoryNumber || bicycle?.inventoryNumber || "Не указан"}</b></span><span>Оплата<b>{event.rentalPaymentMethod || "Не указана"}</b></span><span>Оплачено до<b>{event.rentalUntil ? shortDate(event.rentalUntil) : "Не указано"}</b></span></div>{event.notes && <p className="workflow-comment">{event.notes}</p>}</article>;
      })}</div> : <Empty icon="▤" title="Архив аренд пуст" text="Первая запись появится автоматически после оформления аренды." />}
    </div>
  );
}

function BicycleDetails({
  bicycle,
  data,
  updateData,
  onBack,
  userId,
}: {
  bicycle: Bicycle;
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
  onBack: () => void;
  userId: string;
}) {
  const [tab, setTab] = useState<BicycleTab>("overview");
  const [eventType, setEventType] = useState<BicycleEventType | null>(null);
  const [editingEvent, setEditingEvent] = useState<BicycleEvent | null>(null);
  const [edit, setEdit] = useState(false);
  const [renewingRental, setRenewingRental] = useState(false);
  const events = data.bicycleEvents
    .filter((e) => e.bicycleId === bicycle.id)
    .sort((a, b) => b.date.localeCompare(a.date));
  const finances = bicycleFinances(data.bicycleEvents, bicycle.id);
  const allTimeFinances = bicycleFinances(data.bicycleEvents, bicycle.id, null);
  const rentalDays = bicycle.rentalUntil
    ? calendarDaysUntil(bicycle.rentalUntil)
    : null;
  const tabs: [BicycleTab, string][] = [
    ["overview", "Обзор"],
    ["finance", "Финансы"],
    ["rentals", "Аренда"],
    ["repair", "Ремонты"],
    ["maintenance", "ТО"],
    ["documents", "Документы"],
    ["history", "История"],
  ];
  const tabTypes: Partial<Record<BicycleTab, BicycleEventType[]>> = {
    finance: ["income", "expense"],
    rentals: ["rental"],
    repair: ["repair"],
    maintenance: ["maintenance"],
    documents: ["document"],
  };
  const visible = tabTypes[tab]
    ? events.filter((e) => tabTypes[tab]!.includes(e.type))
    : events;
  const deleteEvent = (event: BicycleEvent) => {
    if (!confirm(`Отменить запись «${event.title}» и пересчитать финансы?`))
      return;
    void removePrivateFile(event.filePath);
    updateData((d) => ({
      ...d,
      bicycleEvents: d.bicycleEvents.filter((item) => item.id !== event.id),
    }));
  };
  const finishRental = () =>
    updateData((d) => ({
      ...d,
      bicycles: d.bicycles.map((b) =>
        b.id === bicycle.id
          ? {
              ...b,
              status: "Свободен",
              currentRenter: undefined,
              rentalUntil: undefined,
              rentalPlan: undefined,
              rentalPlanNote: undefined,
              rentalPlanUpdatedAt: undefined,
            }
          : b,
      ),
      bicycleEvents: [
        {
          id: uid(),
          bicycleId: bicycle.id,
          type: "rental",
          title: "Аренда завершена",
          date: new Date().toISOString(),
          createdAt: new Date().toISOString(),
        },
        ...d.bicycleEvents,
      ],
    }));
  return (
    <div className="page vehicle-page bicycle-page">
      <button className="back" onClick={onBack}>
        ← Назад к электровелосипедам
      </button>
      <div className="vehicle-header">
        <div className="vehicle-avatar bicycle-avatar">⚡</div>
        <div className="vehicle-heading">
          <div>
            <span
              className={`status-badge ${BICYCLE_STATUS_COLORS[bicycle.status]}`}
            >
              <i />
              {bicycle.status}
            </span>
            <span className="plate">{bicycle.inventoryNumber}</span>
          </div>
          <h1>
            {bicycle.brand} {bicycle.model}
          </h1>
          <p>
            {bicycle.year ? `${bicycle.year} год · ` : ""}
            {bicycle.mileage.toLocaleString("ru-RU")} км
            {bicycle.serialNumber ? ` · S/N ${bicycle.serialNumber}` : ""}
          </p>
        </div>
        <div className="vehicle-header-actions">
          {bicycle.status === "В аренде" && (
            <>
              <button className="secondary" onClick={() => setRenewingRental(true)}>
                Продлить аренду
              </button>
              <button className="secondary" onClick={finishRental}>
                Завершить аренду
              </button>
            </>
          )}
          <button className="secondary" onClick={() => setEdit(true)}>
            Изменить
          </button>
          <button className="primary" onClick={() => setEventType("note")}>
            ＋ Добавить событие
          </button>
        </div>
      </div>
      <div className="tabs">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "overview" ? (
        <>
          <div className="hero-stats compact">
            <Stat label="Доход за месяц" value={money(finances.income)} />
            <Stat
              label="Расходы за месяц"
              value={money(finances.expense)}
              tone="warm"
            />
            <Stat
              label="Прибыль"
              value={money(finances.profit)}
              tone={finances.profit >= 0 ? "mint" : "danger"}
            />
            <Stat label="Доход за всё время" value={money(allTimeFinances.income)} />
            <Stat label="Расходы за всё время" value={money(allTimeFinances.expense)} tone="warm" />
            <Stat
              label="Прибыль за всё время"
              value={money(allTimeFinances.profit)}
              tone={allTimeFinances.profit >= 0 ? "mint" : "danger"}
            />
            <Stat
              label={
                bicycle.status === "В аренде" ? "Арендатор" : "Текущий статус"
              }
              value={bicycle.currentRenter || bicycle.status}
              note={
                bicycle.rentalUntil
                  ? `Оплачено до ${shortDate(bicycle.rentalUntil)}`
                  : undefined
              }
            />
          </div>
          <PaybackBlock purchasePrice={bicycle.purchasePrice} profit={allTimeFinances.profit} />
          <div className="quick-actions bike-actions">
            {(
              [
                "rental",
                "income",
                "expense",
                "repair",
                "maintenance",
                "mileage",
                "document",
              ] as BicycleEventType[]
            ).map((type) => (
              <button key={type} onClick={() => setEventType(type)}>
                <span>{BICYCLE_EVENT_ICONS[type]}</span>
                {BICYCLE_EVENT_LABELS[type]}
              </button>
            ))}
          </div>
          {bicycle.status === "В аренде" &&
            bicycle.rentalUntil &&
            rentalDays !== null && (
              <section
                className={`panel bicycle-rental-panel${rentalDays < 0 ? " overdue" : rentalDays <= 3 ? " soon" : ""}`}
              >
                <PanelTitle
                  title="Текущая аренда"
                  meta={
                    rentalDays < 0
                      ? "Требует внимания"
                      : "Следующая оплата"
                  }
                />
                <div className="bicycle-rental-table">
                  <InfoLine
                    label="Арендатор"
                    value={bicycle.currentRenter || "Не указан"}
                  />
                  <InfoLine
                    label="Оплачено до"
                    value={shortDate(bicycle.rentalUntil)}
                  />
                  <InfoLine
                    label="До следующей оплаты"
                    value={rentalDaysLabel(rentalDays)}
                  />
                  <InfoLine
                    label="После этой даты"
                    value={`${BICYCLE_RENTAL_PLAN_LABELS[bicycle.rentalPlan || "Не уточнено"]}${bicycle.rentalPlanNote ? ` · ${bicycle.rentalPlanNote}` : ""}`}
                  />
                </div>
              </section>
            )}
          <div className="dashboard-grid">
            <section className="panel wide">
              <PanelTitle title="Последние события" meta="История велосипеда" />
              {events.length ? (
                events
                  .slice(0, 6)
                  .map((e) => (
                    <BicycleEventRow
                      key={e.id}
                      event={e}
                      onEdit={() => setEditingEvent(e)}
                      onFile={() => openPrivateFile(e.filePath, userId)}
                      onDelete={
                        [
                          "rental",
                          "income",
                          "expense",
                          "repair",
                          "maintenance",
                          "document",
                        ].includes(e.type)
                          ? () => deleteEvent(e)
                          : undefined
                      }
                    />
                  ))
              ) : (
                <Empty
                  icon="✦"
                  title="Событий пока нет"
                  text="Добавьте аренду, доход, расход или обслуживание."
                />
              )}
            </section>
            <section className="panel">
              <PanelTitle title="Информация" />
              <InfoLine
                label="Инвентарный номер"
                value={bicycle.inventoryNumber}
              />
              <InfoLine
                label="Серийный номер"
                value={bicycle.serialNumber || "Не указан"}
              />
              <InfoLine
                label="Номер аккумулятора"
                value={bicycle.batteryNumber || "Не указан"}
              />
              <InfoLine
                label="Номер З/У"
                value={bicycle.chargerNumber || "Не указан"}
              />
              <InfoLine
                label="Стоимость покупки"
                value={bicycle.purchasePrice ? money(bicycle.purchasePrice) : "Не указана"}
              />
              <InfoLine label="Добавлен" value={shortDate(bicycle.createdAt)} />
            </section>
          </div>
        </>
      ) : (
        <section className="panel tab-panel">
          <PanelTitle
            title={tabs.find(([id]) => id === tab)?.[1] || ""}
            meta={`${visible.length} записей`}
          />
          <div className="tab-action">
            <button
              className="primary small"
              onClick={() =>
                setEventType(
                  tab === "finance"
                    ? "expense"
                    : tab === "rentals"
                      ? "rental"
                      : tab === "repair"
                        ? "repair"
                        : tab === "maintenance"
                          ? "maintenance"
                          : tab === "documents"
                            ? "document"
                            : "note",
                )
              }
            >
              ＋ Добавить запись
            </button>
          </div>
          {visible.length ? (
            visible.map((e) => (
              <BicycleEventRow
                key={e.id}
                event={e}
                onEdit={() => setEditingEvent(e)}
                onFile={() => openPrivateFile(e.filePath, userId)}
                onDelete={
                  [
                    "rental",
                    "income",
                    "expense",
                    "repair",
                    "maintenance",
                    "document",
                  ].includes(e.type)
                    ? () => deleteEvent(e)
                    : undefined
                }
              />
            ))
          ) : (
            <Empty
              icon="◈"
              title="Здесь пока пусто"
              text="Первая запись появится после сохранения."
            />
          )}
        </section>
      )}
      {(eventType || editingEvent || renewingRental) && (
        <BicycleEventModal
          initialType={renewingRental ? "rental" : editingEvent?.type || eventType!}
          event={editingEvent || undefined}
          renewal={renewingRental}
          rentalEvents={data.bicycleEvents}
          bicycle={bicycle}
          userId={userId}
          onClose={() => {
            setEventType(null);
            setEditingEvent(null);
            setRenewingRental(false);
          }}
          onSave={(event, patch) => {
            updateData((d) => ({
              ...d,
              bicycles: d.bicycles.map((b) =>
                b.id === bicycle.id ? { ...b, ...patch } : b,
              ),
              bicycleEvents: d.bicycleEvents.some((item) => item.id === event.id)
                ? d.bicycleEvents.map((item) =>
                    item.id === event.id ? event : item,
                  )
                : [event, ...d.bicycleEvents],
            }));
            setEventType(null);
            setEditingEvent(null);
            setRenewingRental(false);
          }}
        />
      )}
      {edit && (
        <BicycleModal
          bicycle={bicycle}
          onClose={() => setEdit(false)}
          onSave={(updated) => {
            updateData((d) => ({
              ...d,
              bicycles: d.bicycles.map((b) =>
                b.id === updated.id ? updated : b,
              ),
            }));
            setEdit(false);
          }}
          onDelete={() => {
            if (confirm("Удалить велосипед? Архив оформленных аренд сохранится.")) {
              updateData((d) => ({
                ...d,
                bicycles: d.bicycles.filter((b) => b.id !== bicycle.id),
                bicycleEvents: d.bicycleEvents.filter(
                  (e) => e.bicycleId !== bicycle.id || (e.type === "rental" && Boolean(e.renter)),
                ),
              }));
              onBack();
            }
          }}
        />
      )}
    </div>
  );
}

function BicycleEventRow({
  event,
  onEdit,
  onFile,
  onDelete,
}: {
  event: BicycleEvent;
  onEdit?: () => void;
  onFile?: () => void;
  onDelete?: () => void;
}) {
  const negative = ["expense", "repair", "maintenance"].includes(event.type);
  return (
    <div className="event-row">
      <span className={`event-icon event-${event.type}`}>
        {BICYCLE_EVENT_ICONS[event.type]}
      </span>
      <div>
        <b>{event.title}</b>
        <p>
          {shortDate(event.date)}
          {event.renter ? ` · ${event.renter}` : ""}
          {event.renterPhone ? ` · ${event.renterPhone}` : ""}
          {event.notes ? ` · ${event.notes}` : ""}
          {event.paymentMethod ? ` · ${event.paymentMethod}` : ""}
          {event.rentalPaymentMethod ? ` · ${event.rentalPaymentMethod}` : ""}
        </p>
      </div>
      {event.filePath && (
        <button className="file-open" onClick={onFile}>
          Открыть
        </button>
      )}
      {typeof event.amount === "number" && (
        <strong className={negative ? "negative" : "positive"}>
          {negative ? "−" : "+"}
          {money(event.amount)}
        </strong>
      )}
      {event.mileage && !event.amount && (
        <strong>{event.mileage.toLocaleString("ru-RU")} км</strong>
      )}
      {onEdit && (
        <button className="event-edit" onClick={onEdit}>
          Изменить
        </button>
      )}
      {onDelete && (
        <button
          className="event-delete"
          title="Удалить запись"
          onClick={onDelete}
        >
          ×
        </button>
      )}
    </div>
  );
}

function rentalFinances(
  events: RentalEvent[],
  carId: string,
  month: string | null = currentMonth(),
) {
  const list = events.filter(
    (e) =>
      e.rentalCarId === carId &&
      (month === null || e.date.slice(0, 7) === month),
  );
  const income = list
    .filter((e) => e.type === "income")
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  const expense = list
    .filter((e) => ["expense", "repair", "maintenance"].includes(e.type))
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  return { income, expense, profit: income - expense };
}
function rentalDays(start: string, end: string) {
  return Math.max(
    1,
    Math.ceil((new Date(end).getTime() - new Date(start).getTime()) / 86400000),
  );
}
function rentalRate(car: RentalCar, days: number) {
  return (
    [...(car.rateTiers || [])]
      .filter((t) => t.minDays <= days)
      .sort((a, b) => b.minDays - a.minDays)[0]?.dailyRate ?? car.dailyRate
  );
}
function futureRentalBookings(
  bookings: RentalBooking[],
  rentalCarId?: string,
) {
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  return bookings.filter(
    (booking) =>
      booking.status === "Бронь" &&
      booking.endDate.slice(0, 10) >= todayKey &&
      (!rentalCarId || booking.rentalCarId === rentalCarId),
  );
}
function futureBookingAmount(bookings: RentalBooking[]) {
  return bookings.reduce(
    (total, booking) =>
      total +
      booking.dailyRate * rentalDays(booking.startDate, booking.endDate),
    0,
  );
}
function bookingCountLabel(count: number) {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return `${count} броней`;
  if (last === 1) return `${count} бронь`;
  if (last >= 2 && last <= 4) return `${count} брони`;
  return `${count} броней`;
}
function bookingMoment(
  date: string,
  time: string | undefined,
  fallback: string,
) {
  return new Date(`${date.slice(0, 10)}T${time || fallback}:00`).getTime();
}

function RentalCars({
  data,
  onOpenCar,
  updateData,
}: {
  data: VoyagerData;
  onOpenCar: (id: string) => void;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"Все" | RentalCarStatus>("Все");
  const [show, setShow] = useState(false);
  const [view, setView] = useState<"fleet" | "calendar">("fleet");
  const cars = data.rentalCars.filter(
    (c) =>
      `${c.make} ${c.model} ${c.plate} ${c.vin || ""} ${c.stsSeries || ""} ${c.stsNumber || ""}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (status === "Все" || c.status === status),
  );
  const upcoming = data.rentalBookings
    .filter((b) => b.status === "Бронь" || b.status === "Выдан")
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .slice(0, 6);
  const futureBookings = futureRentalBookings(data.rentalBookings);
  const futureAmount = futureBookingAmount(futureBookings);
  const totals = data.rentalCars.reduce(
    (sum, c) => {
      const f = rentalFinances(data.rentalEvents, c.id);
      return {
        income: sum.income + f.income,
        expense: sum.expense + f.expense,
      };
    },
    { income: 0, expense: 0 },
  );
  const allTimeTotals = data.rentalCars.reduce(
    (sum, car) => {
      const finance = rentalFinances(data.rentalEvents, car.id, null);
      return {
        income: sum.income + finance.income,
        expense: sum.expense + finance.expense,
      };
    },
    { income: 0, expense: 0 },
  );
  return (
    <PageWrap
      title="Прокат автомобилей"
      subtitle={`${data.rentalCars.length} автомобилей в прокатном парке.`}
      actions={
        <button className="primary" onClick={() => setShow(true)}>
          ＋ Добавить автомобиль
        </button>
      }
    >
      <div className="hero-stats compact rental-summary">
        <Stat
          label="Автомобилей"
          value={String(data.rentalCars.length)}
          note={`${data.rentalCars.filter((c) => c.status === "Доступен").length} доступны`}
          tone="mint"
        />
        <Stat
          label="Активных аренд"
          value={String(
            data.rentalBookings.filter((b) => b.status === "Выдан").length,
          )}
          note={`${data.rentalBookings.filter((b) => b.status === "Бронь").length} брони`}
        />
        <Stat label="Доход за месяц" value={money(totals.income)} />
        <Stat
          label="Прибыль за месяц"
          value={money(totals.income - totals.expense)}
          tone={totals.income - totals.expense >= 0 ? "mint" : "danger"}
        />
        <Stat label="Доход за всё время" value={money(allTimeTotals.income)} />
        <Stat
          label="Прибыль за всё время"
          value={money(allTimeTotals.income - allTimeTotals.expense)}
          tone={allTimeTotals.income - allTimeTotals.expense >= 0 ? "mint" : "danger"}
        />
        <Stat
          label="Будущие брони"
          value={money(futureAmount)}
          note={`${bookingCountLabel(futureBookings.length)} · без залогов`}
          tone="mint"
        />
      </div>
      <div className="view-switch">
        <button
          className={view === "fleet" ? "active" : ""}
          onClick={() => setView("fleet")}
        >
          Автопарк
        </button>
        <button
          className={view === "calendar" ? "active" : ""}
          onClick={() => setView("calendar")}
        >
          Календарь
        </button>
      </div>
      {view === "fleet" ? (
        <>
          <div className="toolbar">
            <label className="search">
              ⌕
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Госномер, модель, VIN или СТС"
              />
            </label>
            <div className="filter-pills">
              {(
                [
                  "Все",
                  "Доступен",
                  "Забронирован",
                  "В аренде",
                  "Обслуживание",
                  "Архив",
                ] as const
              ).map((s) => (
                <button
                  key={s}
                  className={status === s ? "active" : ""}
                  onClick={() => setStatus(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          {upcoming.length > 0 && (
            <section className="panel rental-calendar">
              <PanelTitle
                title="Ближайшие бронирования"
                meta={`${upcoming.length} записей`}
              />
              <div className="booking-list">
                {upcoming.map((b) => {
                  const car = data.rentalCars.find(
                    (c) => c.id === b.rentalCarId,
                  );
                  return (
                    <button
                      key={b.id}
                      className="booking-row"
                      onClick={() => onOpenCar(b.rentalCarId)}
                    >
                      <span
                        className={`booking-status booking-${b.status.toLowerCase()}`}
                      >
                        {b.status}
                      </span>
                      <div>
                        <b>{b.clientName}</b>
                        <p>
                          {car
                            ? `${car.make} ${car.model} · ${car.plate}`
                            : "Автомобиль"}{" "}
                        </p>
                      </div>
                      <strong>
                        {shortDate(b.startDate)} {b.pickupTime || ""} —{" "}
                        {shortDate(b.endDate)} {b.returnTime || ""}
                      </strong>
                      <em>{money(b.dailyRate)}/сут.</em>
                    </button>
                  );
                })}
              </div>
            </section>
          )}
          {cars.length ? (
            <div className="vehicle-grid rental-grid">
              {cars.map((car) => {
                const f = rentalFinances(data.rentalEvents, car.id);
                const allTime = rentalFinances(data.rentalEvents, car.id, null);
                const active = data.rentalBookings.find(
                  (b) => b.rentalCarId === car.id && b.status === "Выдан",
                );
                return (
                  <button
                    className="vehicle-card rental-card"
                    key={car.id}
                    onClick={() => onOpenCar(car.id)}
                  >
                    <div className="vehicle-body">
                      <div className="asset-card-head">
                        <div>
                          <small>{car.plate}</small>
                          <h3>{car.make} {car.model}</h3>
                        </div>
                        <i className={RENTAL_STATUS_COLORS[car.status]}>{car.status}</i>
                      </div>
                      <div className="vehicle-meta">
                        <span>
                          Тариф от
                          <b>
                            {money(
                              Math.min(
                                car.dailyRate,
                                ...(car.rateTiers || []).map(
                                  (t) => t.dailyRate,
                                ),
                              ),
                            )}
                            /сут.
                          </b>
                        </span>
                        <span>
                          {active ? "Клиент" : "Залог"}
                          <b>
                            {active?.clientName || money(car.defaultDeposit)}
                          </b>
                        </span>
                      </div>
                      <div className="asset-finance-grid">
                        <span>Прибыль за месяц<b className={f.profit < 0 ? "negative" : ""}>{money(f.profit)}</b></span>
                        <span>Доход за всё время<b>{money(allTime.income)}</b></span>
                        <span>Прибыль за всё время<b className={allTime.profit < 0 ? "negative" : ""}>{money(allTime.profit)}</b></span>
                      </div>
                      <PaybackBlock purchasePrice={car.purchasePrice} profit={allTime.profit} />
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <Empty
              icon="◇"
              title="Автомобили не найдены"
              text="Добавьте первый автомобиль проката или измените фильтры."
            />
          )}
        </>
      ) : (
        <RentalMonthCalendar
          cars={data.rentalCars}
          bookings={data.rentalBookings}
          onOpenCar={onOpenCar}
        />
      )}
      {show && (
        <RentalCarModal
          onClose={() => setShow(false)}
          onSave={(car) => {
            updateData((d) => ({ ...d, rentalCars: [...d.rentalCars, car] }));
            setShow(false);
          }}
        />
      )}
    </PageWrap>
  );
}

function RentalMonthCalendar({
  cars,
  bookings,
  onOpenCar,
}: {
  cars: RentalCar[];
  bookings: RentalBooking[];
  onOpenCar: (id: string) => void;
}) {
  const [month, setMonth] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const [carId, setCarId] = useState("all");
  const names = [
    "Январь",
    "Февраль",
    "Март",
    "Апрель",
    "Май",
    "Июнь",
    "Июль",
    "Август",
    "Сентябрь",
    "Октябрь",
    "Ноябрь",
    "Декабрь",
  ];
  const firstOffset = (month.getDay() + 6) % 7;
  const daysInMonth = new Date(
    month.getFullYear(),
    month.getMonth() + 1,
    0,
  ).getDate();
  const cellCount = firstOffset + daysInMonth > 35 ? 42 : 35;
  const cells = Array.from(
    { length: cellCount },
    (_, index) =>
      new Date(month.getFullYear(), month.getMonth(), index - firstOffset + 1),
  );
  const key = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const visible = bookings.filter(
    (b) =>
      b.status !== "Отменён" && (carId === "all" || b.rentalCarId === carId),
  );
  return (
    <section className="panel month-calendar">
      <div className="calendar-toolbar">
        <div>
          <button
            className="secondary tiny"
            onClick={() =>
              setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
            }
          >
            ←
          </button>
          <h2>
            {names[month.getMonth()]} <span>{month.getFullYear()}</span>
          </h2>
          <button
            className="secondary tiny"
            onClick={() =>
              setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
            }
          >
            →
          </button>
        </div>
        <select value={carId} onChange={(e) => setCarId(e.target.value)}>
          <option value="all">Все автомобили</option>
          {cars.map((car) => (
            <option key={car.id} value={car.id}>
              {car.make} {car.model} · {car.plate}
            </option>
          ))}
        </select>
      </div>
      <div className="calendar-weekdays">
        {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((day) => (
          <b key={day}>{day}</b>
        ))}
      </div>
      <div className="calendar-grid">
        {cells.map((day) => {
          const dateKey = key(day);
          const dayBookings = visible.filter(
            (b) =>
              b.startDate.slice(0, 10) <= dateKey &&
              b.endDate.slice(0, 10) >= dateKey,
          );
          const outside = day.getMonth() !== month.getMonth();
          return (
            <div
              className={`calendar-day${outside ? " outside" : ""}${dayBookings.length ? " busy" : ""}`}
              key={dateKey}
            >
              <span>{day.getDate()}</span>
              <div>
                {dayBookings.slice(0, 3).map((b) => {
                  const car = cars.find((c) => c.id === b.rentalCarId);
                  return (
                    <button
                      key={b.id}
                      className={`calendar-booking calendar-${b.status.toLowerCase()}`}
                      onClick={() => onOpenCar(b.rentalCarId)}
                    >
                      <b>{b.clientName}</b>
                      <small>
                        {car?.plate || "Автомобиль"}
                        {b.startDate.slice(0, 10) === dateKey && b.pickupTime
                          ? ` · ${b.pickupTime}`
                          : ""}
                      </small>
                    </button>
                  );
                })}
                {dayBookings.length > 3 && (
                  <em>＋ ещё {dayBookings.length - 3}</em>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="calendar-legend">
        <span>
          <i className="legend-booked" />
          Бронь
        </span>
        <span>
          <i className="legend-issued" />В аренде
        </span>
        <span>
          <i className="legend-complete" />
          Завершена
        </span>
      </div>
    </section>
  );
}

function RentalCarDetails({
  car,
  data,
  updateData,
  onBack,
  userId,
}: {
  car: RentalCar;
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
  onBack: () => void;
  userId: string;
}) {
  const [tab, setTab] = useState<RentalCarTab>("overview");
  const [financeMonth, setFinanceMonth] = useState(currentMonth);
  const [financeKind, setFinanceKind] = useState<"all" | "income" | "expense">("all");
  const [edit, setEdit] = useState(false);
  const [booking, setBooking] = useState<RentalBooking | true | null>(null);
  const [eventType, setEventType] = useState<RentalEventType | null>(null);
  const [editingEvent, setEditingEvent] = useState<RentalEvent | null>(null);
  const events = data.rentalEvents
    .filter((e) => e.rentalCarId === car.id)
    .sort((a, b) => b.date.localeCompare(a.date));
  const bookings = data.rentalBookings
    .filter((b) => b.rentalCarId === car.id)
    .sort((a, b) => b.startDate.localeCompare(a.startDate));
  const active = bookings.find((b) => b.status === "Выдан");
  const futureBookings = futureRentalBookings(data.rentalBookings, car.id);
  const futureAmount = futureBookingAmount(futureBookings);
  const finances = rentalFinances(data.rentalEvents, car.id);
  const monthFinances = rentalFinances(data.rentalEvents, car.id, financeMonth);
  const allTimeFinances = rentalFinances(data.rentalEvents, car.id, null);
  const tabs: [RentalCarTab, string][] = [
    ["overview", "Обзор"],
    ["bookings", "Бронирования"],
    ["finance", "Финансы"],
    ["repair", "Ремонты"],
    ["documents", "Документы"],
    ["damages", "Повреждения"],
    ["history", "История"],
  ];
  const tabTypes: Partial<Record<RentalCarTab, RentalEventType[]>> = {
    finance: ["income", "expense", "repair", "maintenance"],
    repair: ["repair", "maintenance"],
    documents: ["document"],
    damages: ["damage"],
  };
  const visible = events.filter((event) => {
    if (tabTypes[tab] && !tabTypes[tab]!.includes(event.type)) return false;
    if (tab !== "finance") return true;
    if (monthKey(event.date) !== financeMonth) return false;
    return financeKind === "all" ||
      (financeKind === "income" ? event.type === "income" : event.type !== "income");
  });
  const deleteRentalEvent = (event: RentalEvent) => {
    if (!confirm(`Отменить запись «${event.title}» и пересчитать финансы?`))
      return;
    void removePrivateFile(event.filePath);
    updateData((d) => ({
      ...d,
      rentalEvents: d.rentalEvents.filter((item) => item.id !== event.id),
    }));
  };
  const issue = (b: RentalBooking) =>
    updateData((d) => ({
      ...d,
      rentalCars: d.rentalCars.map((c) =>
        c.id === car.id ? { ...c, status: "В аренде" } : c,
      ),
      rentalBookings: d.rentalBookings.map((x) =>
        x.id === b.id ? { ...x, status: "Выдан" } : x,
      ),
    }));
  const finish = (b: RentalBooking) => {
    const amount = rentalDays(b.startDate, b.endDate) * b.dailyRate;
    updateData((d) => ({
      ...d,
      rentalCars: d.rentalCars.map((c) =>
        c.id === car.id ? { ...c, status: "Доступен" } : c,
      ),
      rentalBookings: d.rentalBookings.map((x) =>
        x.id === b.id ? { ...x, status: "Завершён" } : x,
      ),
      rentalEvents: [
        {
          id: uid(),
          rentalCarId: car.id,
          bookingId: b.id,
          type: "income",
          title: `Аренда · ${b.clientName}`,
          amount,
          date: new Date().toISOString(),
          notes: `${rentalDays(b.startDate, b.endDate)} сут.`,
          createdAt: new Date().toISOString(),
        },
        ...d.rentalEvents,
      ],
    }));
  };
  return (
    <div className="page vehicle-page rental-page">
      <button className="back" onClick={onBack}>
        ← Назад к прокату
      </button>
      <div className="vehicle-header">
        <div className="vehicle-avatar rental-avatar">R</div>
        <div className="vehicle-heading">
          <div>
            <span
              className={`status-badge ${RENTAL_STATUS_COLORS[car.status]}`}
            >
              <i />
              {car.status}
            </span>
            <span className="plate">{car.plate}</span>
          </div>
          <h1>
            {car.make} {car.model}
          </h1>
          <p>
            {car.year ? `${car.year} год · ` : ""}
            {car.mileage.toLocaleString("ru-RU")} км
            {car.vin ? ` · VIN ${car.vin}` : ""}
          </p>
        </div>
        <div className="vehicle-header-actions">
          {active && (
            <button className="secondary" onClick={() => finish(active)}>
              Оформить возврат
            </button>
          )}
          <button className="secondary" onClick={() => setEdit(true)}>
            Изменить
          </button>
          <button className="primary" onClick={() => setBooking(true)}>
            ＋ Бронирование
          </button>
        </div>
      </div>
      <div className="tabs">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "overview" ? (
        <>
          <div className="hero-stats compact rental-summary">
            <Stat label="Доход за месяц" value={money(finances.income)} />
            <Stat
              label="Расходы за месяц"
              value={money(finances.expense)}
              tone="warm"
            />
            <Stat
              label="Прибыль"
              value={money(finances.profit)}
              tone={finances.profit >= 0 ? "mint" : "danger"}
            />
            <Stat label="Доход за всё время" value={money(allTimeFinances.income)} />
            <Stat label="Расходы за всё время" value={money(allTimeFinances.expense)} tone="warm" />
            <Stat
              label="Прибыль за всё время"
              value={money(allTimeFinances.profit)}
              tone={allTimeFinances.profit >= 0 ? "mint" : "danger"}
            />
            <Stat
              label={active ? "Текущий клиент" : "Тариф"}
              value={active?.clientName || `${money(car.dailyRate)}/сут.`}
              note={
                active
                  ? `До ${shortDate(active.endDate)}`
                  : `Залог ${money(car.defaultDeposit)}`
              }
            />
            <Stat
              label="Будущие брони"
              value={money(futureAmount)}
              note={`${bookingCountLabel(futureBookings.length)} · потенциально`}
              tone="mint"
            />
          </div>
          <PaybackBlock purchasePrice={car.purchasePrice} profit={allTimeFinances.profit} />
          <div className="quick-actions rental-actions">
            <button onClick={() => setBooking(true)}>
              <span>▣</span>Бронь
            </button>
            {(
              [
                "income",
                "expense",
                "repair",
                "maintenance",
                "mileage",
                "document",
                "damage",
              ] as RentalEventType[]
            ).map((t) => (
              <button key={t} onClick={() => setEventType(t)}>
                <span>{RENTAL_EVENT_ICONS[t]}</span>
                {RENTAL_EVENT_LABELS[t]}
              </button>
            ))}
          </div>
          <div className="dashboard-grid">
            <section className="panel wide">
              <PanelTitle
                title="Ближайшие бронирования"
                meta={`${bookings.length} записей`}
              />
              {bookings.length ? (
                bookings
                  .slice(0, 5)
                  .map((b) => (
                    <RentalBookingRow
                      key={b.id}
                      booking={b}
                      onEdit={() => setBooking(b)}
                      onIssue={
                        b.status === "Бронь" ? () => issue(b) : undefined
                      }
                      onFinish={
                        b.status === "Выдан" ? () => finish(b) : undefined
                      }
                    />
                  ))
              ) : (
                <Empty
                  icon="▣"
                  title="Бронирований пока нет"
                  text="Добавьте первую бронь для этого автомобиля."
                />
              )}
            </section>
            <section className="panel">
              <PanelTitle title="Информация" />
              <InfoLine label="Тариф" value={`${money(car.dailyRate)}/сут.`} />
              <InfoLine label="Залог" value={money(car.defaultDeposit)} />
              <InfoLine label="VIN" value={car.vin || "Не указан"} />
              <InfoLine label="СТС" value={[car.stsSeries, car.stsNumber].filter(Boolean).join(" ") || "Не указано"} />
              <InfoLine label="Стоимость покупки" value={car.purchasePrice ? money(car.purchasePrice) : "Не указана"} />
              <InfoLine
                label="Страховая компания"
                value={car.insuranceCompany || "Не указана"}
              />
              <InfoLine
                label="Страховой полис"
                value={car.insurancePolicyNumber || "Не указан"}
              />
              <InfoLine
                label="Страховка действует до"
                value={
                  car.insuranceExpiresAt
                    ? shortDate(car.insuranceExpiresAt)
                    : "Не указано"
                }
              />
              <InfoLine label="Добавлен" value={shortDate(car.createdAt)} />
            </section>
          </div>
        </>
      ) : tab === "bookings" ? (
        <section className="panel tab-panel">
          <PanelTitle
            title="Бронирования"
            meta={`${bookings.length} записей`}
          />
          <div className="tab-action">
            <button className="primary small" onClick={() => setBooking(true)}>
              ＋ Добавить бронь
            </button>
          </div>
          {bookings.length ? (
            bookings.map((b) => (
              <RentalBookingRow
                key={b.id}
                booking={b}
                onEdit={() => setBooking(b)}
                onIssue={b.status === "Бронь" ? () => issue(b) : undefined}
                onFinish={b.status === "Выдан" ? () => finish(b) : undefined}
              />
            ))
          ) : (
            <Empty
              icon="▣"
              title="Бронирований пока нет"
              text="Создайте первую бронь."
            />
          )}
        </section>
      ) : (
        <section className="panel tab-panel">
          <PanelTitle
            title={tabs.find(([id]) => id === tab)?.[1] || ""}
            meta={`${visible.length} записей`}
          />
          <div className={tab === "finance" ? "rental-finance-actions" : "tab-action"}>
            {tab === "finance" && (
              <button
                className="secondary small"
                onClick={() => setEventType("income")}
              >
                ＋ Добавить доход
              </button>
            )}
            <button
              className="primary small"
              onClick={() =>
                setEventType(
                  tab === "finance"
                    ? "expense"
                    : tab === "repair"
                      ? "repair"
                      : tab === "documents"
                        ? "document"
                        : tab === "damages"
                          ? "damage"
                          : "note",
                )
              }
            >
              {tab === "finance" ? "− Добавить расход" : "＋ Добавить запись"}
            </button>
          </div>
          {tab === "finance" && (
            <>
              <div className="toolbar rental-finance-toolbar">
                <div className="company-income-period">
                  <button type="button" aria-label="Предыдущий месяц" onClick={() => setFinanceMonth(shiftMonthKey(financeMonth, -1))}>←</button>
                  <input
                    type="month"
                    value={financeMonth}
                    aria-label="Месяц финансов автомобиля"
                    onChange={(event) => setFinanceMonth(event.target.value || currentMonth())}
                  />
                  <button type="button" aria-label="Следующий месяц" onClick={() => setFinanceMonth(shiftMonthKey(financeMonth, 1))}>→</button>
                </div>
                <div className="filter-pills" role="group" aria-label="Тип финансовых операций">
                  {([
                    ["all", "Все операции"],
                    ["income", "Доходы"],
                    ["expense", "Расходы"],
                  ] as const).map(([kind, label]) => (
                    <button
                      type="button"
                      key={kind}
                      className={financeKind === kind ? "active" : ""}
                      aria-pressed={financeKind === kind}
                      onClick={() => setFinanceKind(kind)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="hero-stats compact rental-finance-summary">
                <Stat label="Доход за месяц" value={money(monthFinances.income)} note={investorReportPeriodLabel(financeMonth)} tone="mint" />
                <Stat label="Расходы за месяц" value={money(monthFinances.expense)} note="Включая ремонты и ТО" tone="warm" />
                <Stat label="Прибыль за месяц" value={money(monthFinances.profit)} tone={monthFinances.profit >= 0 ? "mint" : "danger"} />
              </div>
            </>
          )}
          {visible.length ? (
            visible.map((e) => (
              <RentalEventRow
                key={e.id}
                event={e}
                onEdit={() => setEditingEvent(e)}
                onFile={() => openPrivateFile(e.filePath, userId)}
                onDelete={
                  [
                    "income",
                    "expense",
                    "repair",
                    "maintenance",
                    "document",
                  ].includes(e.type)
                    ? () => deleteRentalEvent(e)
                    : undefined
                }
              />
            ))
          ) : (
            <Empty
              icon="◇"
              title={tab === "finance"
                ? financeKind === "income" ? "Доходов за этот месяц нет" : financeKind === "expense" ? "Расходов за этот месяц нет" : "Операций за этот месяц нет"
                : "Здесь пока пусто"}
              text={tab === "finance" ? "Выберите другой месяц или добавьте новую операцию." : "Первая запись появится после сохранения."}
            />
          )}
        </section>
      )}
      {booking && (
        <RentalBookingModal
          booking={booking === true ? undefined : booking}
          bookings={data.rentalBookings}
          car={car}
          onClose={() => setBooking(null)}
          onSave={(item) => {
            updateData((d) => ({
              ...d,
              rentalBookings: d.rentalBookings.some((b) => b.id === item.id)
                ? d.rentalBookings.map((b) => (b.id === item.id ? item : b))
                : [...d.rentalBookings, item],
              rentalCars: d.rentalCars.map((c) =>
                c.id === car.id &&
                c.status === "Доступен" &&
                item.status === "Бронь"
                  ? { ...c, status: "Забронирован" }
                  : c,
              ),
            }));
            setBooking(null);
          }}
          onDelete={
            booking !== true
              ? () => {
                  const id = booking.id;
                  updateData((d) => ({
                    ...d,
                    rentalBookings: d.rentalBookings.filter((b) => b.id !== id),
                  }));
                  setBooking(null);
                }
              : undefined
          }
        />
      )}
      {(eventType || editingEvent) && (
        <RentalEventModal
          initialType={editingEvent?.type || eventType!}
          event={editingEvent || undefined}
          car={car}
          userId={userId}
          onClose={() => {
            setEventType(null);
            setEditingEvent(null);
          }}
          onSave={(event, patch) => {
            updateData((d) => ({
              ...d,
              rentalCars: d.rentalCars.map((c) =>
                c.id === car.id ? { ...c, ...patch } : c,
              ),
              rentalEvents: d.rentalEvents.some((item) => item.id === event.id)
                ? d.rentalEvents.map((item) =>
                    item.id === event.id ? event : item,
                  )
                : [event, ...d.rentalEvents],
            }));
            setEventType(null);
            setEditingEvent(null);
          }}
        />
      )}
      {edit && (
        <RentalCarModal
          car={car}
          onClose={() => setEdit(false)}
          onSave={(updated) => {
            updateData((d) => ({
              ...d,
              rentalCars: d.rentalCars.map((c) =>
                c.id === updated.id ? updated : c,
              ),
            }));
            setEdit(false);
          }}
          onDelete={() => {
            if (confirm("Удалить автомобиль проката и его историю?")) {
              updateData((d) => ({
                ...d,
                rentalCars: d.rentalCars.filter((c) => c.id !== car.id),
                rentalBookings: d.rentalBookings.filter(
                  (b) => b.rentalCarId !== car.id,
                ),
                rentalEvents: d.rentalEvents.filter(
                  (e) => e.rentalCarId !== car.id,
                ),
              }));
              onBack();
            }
          }}
        />
      )}
    </div>
  );
}

function RentalBookingRow({
  booking,
  onEdit,
  onIssue,
  onFinish,
}: {
  booking: RentalBooking;
  onEdit: () => void;
  onIssue?: () => void;
  onFinish?: () => void;
}) {
  return (
    <div className="booking-row">
      <span
        className={`booking-status booking-${booking.status.toLowerCase()}`}
      >
        {booking.status}
      </span>
      <button className="booking-client" onClick={onEdit}>
        <b>{booking.clientName}</b>
        <p>
          {booking.clientPhone || "Телефон не указан"} · залог{" "}
          {money(booking.deposit)}
        </p>
      </button>
      <strong>
        {shortDate(booking.startDate)} {booking.pickupTime || ""} —{" "}
        {shortDate(booking.endDate)} {booking.returnTime || ""}
      </strong>
      <em>
        {money(
          booking.dailyRate * rentalDays(booking.startDate, booking.endDate),
        )}
      </em>
      {onIssue && (
        <button className="secondary tiny" onClick={onIssue}>
          Выдать
        </button>
      )}
      {onFinish && (
        <button className="secondary tiny" onClick={onFinish}>
          Вернуть
        </button>
      )}
    </div>
  );
}
function RentalEventRow({
  event,
  onEdit,
  onFile,
  onDelete,
}: {
  event: RentalEvent;
  onEdit?: () => void;
  onFile?: () => void;
  onDelete?: () => void;
}) {
  const negative = ["expense", "repair", "maintenance"].includes(event.type);
  return (
    <div className="event-row">
      <span className={`event-icon event-${event.type}`}>
        {RENTAL_EVENT_ICONS[event.type]}
      </span>
      <div>
        <b>{event.title}</b>
        <p>
          {shortDate(event.date)}
          {event.notes ? ` · ${event.notes}` : ""}
          {event.paymentMethod ? ` · ${event.paymentMethod}` : ""}
        </p>
      </div>
      {event.filePath && (
        <button className="file-open" onClick={onFile}>
          Открыть
        </button>
      )}
      {typeof event.amount === "number" && (
        <strong className={negative ? "negative" : "positive"}>
          {negative ? "−" : "+"}
          {money(event.amount)}
        </strong>
      )}
      {event.mileage && !event.amount && (
        <strong>{event.mileage.toLocaleString("ru-RU")} км</strong>
      )}
      {onEdit && (
        <button className="event-edit" onClick={onEdit}>
          Изменить
        </button>
      )}
      {onDelete && (
        <button
          className="event-delete"
          title="Удалить запись"
          onClick={onDelete}
        >
          ×
        </button>
      )}
    </div>
  );
}

function VehicleDetails({
  vehicle,
  data,
  updateData,
  onBack,
  userId,
  onOpenRecommendations,
}: {
  vehicle: Vehicle;
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
  onBack: () => void;
  userId: string;
  onOpenRecommendations: () => void;
}) {
  const [tab, setTab] = useState<VehicleTab>("overview");
  const [eventType, setEventType] = useState<EventType | null>(null);
  const [editingEvent, setEditingEvent] = useState<VoyagerEvent | null>(null);
  const [edit, setEdit] = useState(false);
  const f = vehicleFinances(data.events, vehicle.id);
  const events = [...data.events]
    .filter((e) => e.vehicleId === vehicle.id)
    .sort((a, b) => b.date.localeCompare(a.date));
  const investor = data.investors.find((i) => i.id === vehicle.investorId);
  const latestInspection = lastCompletedInspection(
    data.vehicleInspections,
    vehicle.id,
  );
  const tabTypes: Partial<Record<VehicleTab, EventType[]>> = {
    finance: ["income", "expense"],
    repair: ["repair"],
    maintenance: ["maintenance"],
    documents: ["document"],
    photos: ["photo"],
  };
  const visibleEvents = tabTypes[tab]
    ? events.filter((e) => tabTypes[tab]!.includes(e.type))
    : events;
  const vehicleRecommendations = [...data.repairRecommendations]
    .filter((item) => item.vehicleId === vehicle.id)
    .sort((left, right) => {
      if (left.status !== right.status)
        return left.status === "Не выполнено" ? -1 : 1;
      return right.date.localeCompare(left.date);
    });
  const tabs: [VehicleTab, string][] = [
    ["overview", "Обзор"],
    ["finance", "Финансы"],
    ["repair", "Ремонты"],
    ["maintenance", "ТО"],
    ["documents", "Документы"],
    ["photos", "Фото"],
    ["history", "История"],
  ];
  const deleteEvent = (event: VoyagerEvent) => {
    if (!confirm(`Удалить запись «${event.title}»?`)) return;
    void removePrivateFile(event.filePath);
    updateData((d) => ({
      ...d,
      events: d.events.filter((item) => item.id !== event.id),
      repairRecommendations: d.repairRecommendations.map((item) =>
        item.repairEventId === event.id
          ? {
              ...item,
              status: "Не выполнено",
              actualRepairDate: undefined,
              repairEventId: undefined,
              updatedAt: new Date().toISOString(),
            }
          : item,
      ),
    }));
  };
  return (
    <div className="page vehicle-page">
      <button className="back" onClick={onBack}>
        ← Назад к автопарку
      </button>
      <div className="vehicle-header">
        <div className="vehicle-avatar">{vehicle.make[0] || "V"}</div>
        <div className="vehicle-heading">
          <div>
            <span className={`status-badge ${STATUS_COLORS[vehicle.status]}`}>
              <i />
              {vehicle.status}
            </span>
            <span className="plate">{vehicle.plate}</span>
          </div>
          <h1>
            {vehicle.make} {vehicle.model}
          </h1>
          <p>
            {vehicle.year ? `${vehicle.year} год · ` : ""}
            {vehicle.mileage.toLocaleString("ru-RU")} км{" "}
            {vehicle.vin ? `· VIN ${vehicle.vin}` : ""}
          </p>
        </div>
        <div className="vehicle-header-actions">
          <button className="secondary" onClick={() => setEdit(true)}>
            Изменить
          </button>
          <button className="primary" onClick={() => setEventType("note")}>
            ＋ Добавить событие
          </button>
        </div>
      </div>
      <div className="tabs">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "overview" ? (
        <>
          <div className="hero-stats compact">
            <Stat label="Доход за месяц" value={money(f.income)} />
            <Stat
              label="Расходы за месяц"
              value={money(f.expense)}
              tone="warm"
            />
            <Stat
              label="Прибыль"
              value={money(f.profit)}
              tone={f.profit >= 0 ? "mint" : "danger"}
            />
            <Stat
              label="Следующее ТО"
              value={
                vehicle.nextMaintenanceMileage
                  ? `${vehicle.nextMaintenanceMileage.toLocaleString("ru-RU")} км`
                  : "Не задано"
              }
              note={
                vehicle.nextMaintenanceMileage
                  ? `${vehicle.nextMaintenanceMileage - vehicle.mileage} км осталось`
                  : ""
              }
            />
          </div>
          <div className="quick-actions">
            {(
              [
                "income",
                "expense",
                "repair",
                "maintenance",
                "mileage",
                "document",
              ] as EventType[]
            ).map((type) => (
              <button key={type} onClick={() => setEventType(type)}>
                <span>{EVENT_ICONS[type]}</span>
                {EVENT_LABELS[type]}
              </button>
            ))}
          </div>
          <div className="dashboard-grid">
            <section className="panel wide">
              <PanelTitle title="Последние события" meta="История автомобиля" />
              {events.slice(0, 6).length ? (
                events.slice(0, 6).map((e) => (
                  <EventRow
                    key={e.id}
                    event={e}
                    onEdit={
                      e.source === "vehicle_inspection"
                        ? undefined
                        : () => setEditingEvent(e)
                    }
                    onDelete={
                      ["expense", "repair", "maintenance", "document"].includes(
                        e.type,
                      )
                        ? () => deleteEvent(e)
                        : undefined
                    }
                  />
                ))
              ) : (
                <Empty
                  icon="✦"
                  title="Событий пока нет"
                  text="Добавьте доход, расход, пробег или обслуживание."
                />
              )}
            </section>
            <section className="panel">
              <PanelTitle title="Информация" />
              <InfoLine
                label="Инвестор"
                value={investor?.name || "Не указан"}
              />
              <InfoLine
                label="Водитель"
                value={vehicle.driverName || "Не указан"}
              />
              <InfoLine
                label="Телефон водителя"
                value={vehicle.driverPhone || "Не указан"}
              />
              <InfoLine
                label="Последний осмотр"
                value={
                  latestInspection
                    ? inspectionDateTime(latestInspection.scheduledAt)
                    : "Не проводился"
                }
              />
              <InfoLine label="VIN" value={vehicle.vin || "Не указан"} />
              <InfoLine label="СТС" value={[vehicle.stsSeries, vehicle.stsNumber].filter(Boolean).join(" ") || "Не указано"} />
              <InfoLine
                label="Пробег замены салонного фильтра"
                value={vehicle.cabinFilterReplacementMileage !== undefined ? `${vehicle.cabinFilterReplacementMileage.toLocaleString("ru-RU")} км` : "Не указан"}
              />
              <InfoLine
                label="Пробег замены воздушного фильтра"
                value={vehicle.airFilterReplacementMileage !== undefined ? `${vehicle.airFilterReplacementMileage.toLocaleString("ru-RU")} км` : "Не указан"}
              />
              <InfoLine label="Резина" value={vehicle.tireSeason || "Не указана"} />
              <InfoLine
                label="Страховая компания"
                value={vehicle.insuranceCompany || "Не указана"}
              />
              <InfoLine
                label="Полис"
                value={vehicle.insurancePolicyNumber || "Не указан"}
              />
              <InfoLine
                label="Страховка действует до"
                value={
                  vehicle.insuranceExpiresAt
                    ? shortDate(vehicle.insuranceExpiresAt)
                    : "Не указано"
                }
              />
              <InfoLine label="Статус" value={vehicle.status} />
              <InfoLine label="Добавлен" value={shortDate(vehicle.createdAt)} />
            </section>
          </div>
        </>
      ) : tab === "photos" ? (
        <PhotoGrid
          events={visibleEvents}
          userId={userId}
          onAdd={() => setEventType("photo")}
          onEdit={(event) => setEditingEvent(event)}
        />
      ) : (
        <section className="panel tab-panel">
          <PanelTitle
            title={tabs.find(([id]) => id === tab)?.[1] || ""}
            meta={`${visibleEvents.length} записей`}
          />
          <div className="tab-action">
            <button
              className="primary small"
              onClick={() =>
                setEventType(
                  tab === "finance"
                    ? "expense"
                    : tab === "repair"
                      ? "repair"
                      : tab === "maintenance"
                        ? "maintenance"
                        : tab === "documents"
                          ? "document"
                          : "note",
                )
              }
            >
              ＋ Добавить запись
            </button>
          </div>
          {tab === "repair" && (
            <div className="vehicle-recommendations-block">
              <div className="vehicle-recommendations-head">
                <div>
                  <b>Рекомендации по ремонту</b>
                  <span>
                    {
                      vehicleRecommendations.filter(
                        (item) => item.status === "Не выполнено",
                      ).length
                    } активных
                  </span>
                </div>
                <button className="secondary small" onClick={onOpenRecommendations}>
                  Открыть раздел
                </button>
              </div>
              {vehicleRecommendations
                .filter((item) => item.status === "Не выполнено")
                .slice(0, 4)
                .map((item) => (
                  <div className="vehicle-recommendation-row" key={item.id}>
                    <span className={`repair-priority priority-${item.priority.toLowerCase()}`}>
                      {item.priority}
                    </span>
                    <div>
                      <b>{item.recommendation}</b>
                      <small>
                        {item.parts.length
                          ? item.parts.map((part) => part.name).join(", ")
                          : "Запчасти не указаны"}
                      </small>
                    </div>
                    <time>
                      {item.plannedRepairDate
                        ? shortDate(item.plannedRepairDate)
                        : "Без даты"}
                    </time>
                  </div>
                ))}
              {!vehicleRecommendations.some(
                (item) => item.status === "Не выполнено",
              ) && (
                <p className="vehicle-recommendations-empty">
                  Активных рекомендаций нет. Выполненные работы находятся ниже в журнале.
                </p>
              )}
            </div>
          )}
          {visibleEvents.length ? (
            visibleEvents.map((e) => (
              <EventRow
                key={e.id}
                event={e}
                onEdit={
                  e.source === "vehicle_inspection"
                    ? undefined
                    : () => setEditingEvent(e)
                }
                onFile={() => openPrivateFile(e.filePath)}
                onDelete={
                  ["expense", "repair", "maintenance", "document"].includes(
                    e.type,
                  )
                    ? () => deleteEvent(e)
                    : undefined
                }
              />
            ))
          ) : (
            <Empty
              icon={
                EVENT_ICONS[
                  tab === "repair"
                    ? "repair"
                    : tab === "maintenance"
                      ? "maintenance"
                      : tab === "documents"
                        ? "document"
                        : "note"
                ]
              }
              title="Здесь пока пусто"
              text="Первая запись появится сразу после сохранения."
            />
          )}
        </section>
      )}
      {(eventType || editingEvent) && (
        <EventModal
          initialType={editingEvent?.type || eventType!}
          event={editingEvent || undefined}
          vehicle={vehicle}
          investor={investor}
          userId={userId}
          onClose={() => {
            setEventType(null);
            setEditingEvent(null);
          }}
          onSave={(event, vehiclePatch) => {
            updateData((d) => ({
              ...d,
              vehicles: d.vehicles.map((v) =>
                v.id === vehicle.id ? { ...v, ...vehiclePatch } : v,
              ),
              events: d.events.some((item) => item.id === event.id)
                ? d.events.map((item) =>
                    item.id === event.id ? event : item,
                  )
                : [event, ...d.events],
            }));
            setEventType(null);
            setEditingEvent(null);
          }}
        />
      )}
      {edit && (
        <VehicleModal
          vehicle={vehicle}
          investors={data.investors}
          onClose={() => setEdit(false)}
          onSave={(updated) => {
            updateData((d) => ({
              ...d,
              vehicles: d.vehicles.map((v) =>
                v.id === updated.id ? updated : v,
              ),
            }));
            setEdit(false);
          }}
          onDelete={() => {
            if (confirm("Переместить автомобиль и его историю в удалённые?")) {
              const inspectionPhotos = data.vehicleInspections
                .filter((inspection) => inspection.vehicleId === vehicle.id)
                .flatMap((inspection) => inspection.photos);
              void Promise.all(
                inspectionPhotos.map((photo) =>
                  removePrivateFile(photo.filePath),
                ),
              );
              updateData((d) => ({
                ...d,
                vehicles: d.vehicles.filter((v) => v.id !== vehicle.id),
                events: d.events.filter((e) => e.vehicleId !== vehicle.id),
                vehicleInspections: d.vehicleInspections.filter(
                  (inspection) => inspection.vehicleId !== vehicle.id,
                ),
              }));
              onBack();
            }
          }}
        />
      )}
    </div>
  );
}

function previousCalendarMonthKey() {
  const now = new Date();
  const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return paymentMonthKey(previous);
}

function shiftMonthKey(monthKey: string, offset: number) {
  const year = Number(monthKey.slice(0, 4));
  const month = Number(monthKey.slice(5, 7));
  const next = new Date(year, month - 1 + offset, 1, 12);
  return paymentMonthKey(next);
}

function InvestorMonthPicker({ period, onChange, label = "Расчётный месяц" }: {
  period: string;
  onChange: (period: string) => void;
  label?: string;
}) {
  return <div className="investor-month-toolbar">
    <span>{label}</span>
    <div className="company-income-period" role="group" aria-label={label}>
      <button type="button" aria-label="Предыдущий месяц" onClick={() => onChange(shiftMonthKey(period, -1))}>←</button>
      <input type="month" value={period} aria-label={label} onChange={event => {
        if (/^\d{4}-(0[1-9]|1[0-2])$/.test(event.target.value)) onChange(event.target.value);
      }} />
      <button type="button" aria-label="Следующий месяц" onClick={() => onChange(shiftMonthKey(period, 1))}>→</button>
    </div>
  </div>;
}

function investorReportPeriodLabel(periodMonth: string) {
  const monthKey = periodMonth.slice(0, 7);
  const year = Number(monthKey.slice(0, 4));
  const month = Number(monthKey.slice(5, 7));
  return `${PAYMENT_MONTHS[month - 1]} ${year}`;
}

function investorReportPeriodRange(periodMonth: string) {
  const monthKey = periodMonth.slice(0, 7);
  const year = Number(monthKey.slice(0, 4));
  const month = Number(monthKey.slice(5, 7));
  const lastDay = new Date(year, month, 0).getDate();
  return `01.${String(month).padStart(2, "0")}.${year} — ${String(lastDay).padStart(2, "0")}.${String(month).padStart(2, "0")}.${year}`;
}

function investorReportPercentages(report: InvestorReport) {
  const month = Number(report.period_month.slice(5, 7));
  const year = Number(report.period_month.slice(0, 4));
  const daysInMonth = new Date(year, month, 0).getDate();
  const possibleDays = Math.max(0, report.vehicle_count * daysInMonth);
  return {
    possibleDays,
    utilization: possibleDays
      ? Math.min(100, (report.rental_days / possibleDays) * 100)
      : 0,
    idle: possibleDays
      ? Math.min(100, (report.idle_days / possibleDays) * 100)
      : 0,
  };
}

function investorReportText(report: InvestorReport) {
  const percentages = investorReportPercentages(report);
  return [
    "ОТЧЁТ ИНВЕСТОРУ",
    `Инвестор: ${report.investor_name}`,
    `Период: ${investorReportPeriodLabel(report.period_month)}`,
    `Количество автомобилей: ${report.vehicle_count} шт.`,
    `Выручка за период: ${money(report.revenue)}`,
    `Операционные расходы: ${money(report.operating_expenses)}`,
    `Личные расходы инвестора: ${money(report.personal_expenses)}`,
    `Количество авто-дней в аренде: ${report.rental_days}`,
    `Количество авто-дней в простое: ${report.idle_days}`,
    `Сумма незаработанных денег: ${money(report.lost_income)}`,
    `Процент простоя: ${percentages.idle.toFixed(2).replace(".", ",")}%`,
    `Процент загруженности: ${percentages.utilization.toFixed(2).replace(".", ",")}%`,
    `Начислено инвестору: ${money(report.accrued)}`,
    `Уже выплачено за период: ${money(report.payments)}`,
    `ИТОГО К ВЫПЛАТЕ: ${money(report.payout)}`,
    report.comment ? `Комментарий: ${report.comment}` : "",
  ]
    .filter(Boolean)
    .join("\n");
}

function InvestorReportsPage({
  company,
  data,
  reports,
  currentUserId,
  onRefresh,
}: {
  company: CompanyContext;
  data: VoyagerData;
  reports: InvestorReport[];
  currentUserId: string;
  onRefresh: () => Promise<void>;
}) {
  const [investorId, setInvestorId] = useState(data.investors[0]?.id || "");
  const [monthKey, setMonthKey] = useState(previousCalendarMonthKey);
  const [selected, setSelected] = useState<InvestorReport | null>(null);
  const canEdit = company.role === "owner";
  const selectedInvestor = data.investors.find((item) => item.id === investorId);
  const existing = reports.find(
    (report) =>
      report.investor_id === investorId &&
      report.period_month.slice(0, 7) === monthKey,
  );
  const periodReports = reports.filter(report => report.period_month.slice(0, 7) === monthKey);
  const readyCount = periodReports.filter(report => report.status === "ready").length;

  const generateReport = () => {
    if (!selectedInvestor) {
      alert("Сначала выберите инвестора");
      return;
    }
    if (existing) {
      setSelected(existing);
      return;
    }
    const finance = investorFinances(data, selectedInvestor, monthKey);
    const vehicles = data.vehicles.filter(
      (vehicle) => vehicle.investorId === selectedInvestor.id,
    );
    const now = new Date().toISOString();
    setSelected({
      id: uid(),
      company_id: company.id,
      investor_id: selectedInvestor.id,
      investor_name: selectedInvestor.name,
      period_month: `${monthKey}-01`,
      vehicle_count: vehicles.length,
      revenue: finance.income,
      operating_expenses: finance.operatingExpenses,
      personal_expenses: finance.personalExpenses,
      accrued: finance.accrued,
      payments: finance.payments,
      payout: finance.payable,
      rental_days: 0,
      idle_days: 0,
      lost_income: 0,
      comment: null,
      status: "draft",
      created_by: currentUserId,
      created_at: now,
      updated_at: now,
    });
  };

  const refreshFromVoyager = (report: InvestorReport) => {
    const investor = data.investors.find(
      (item) => item.id === report.investor_id,
    );
    if (!investor) return report;
    const period = report.period_month.slice(0, 7);
    const finance = investorFinances(data, investor, period);
    return {
      ...report,
      investor_name: investor.name,
      vehicle_count: data.vehicles.filter(
        (vehicle) => vehicle.investorId === investor.id,
      ).length,
      revenue: finance.income,
      operating_expenses: finance.operatingExpenses,
      personal_expenses: finance.personalExpenses,
      accrued: finance.accrued,
      payments: finance.payments,
      payout: finance.payable,
    };
  };

  const saveReport = async (report: InvestorReport) => {
    if (!supabase || !canEdit) return null;
    const payload = {
      id: report.id,
      company_id: company.id,
      investor_id: report.investor_id,
      investor_name: report.investor_name.trim(),
      period_month: report.period_month,
      vehicle_count: Math.max(0, report.vehicle_count),
      revenue: report.revenue,
      operating_expenses: report.operating_expenses,
      personal_expenses: report.personal_expenses,
      accrued: report.accrued,
      payments: report.payments,
      payout: report.payout,
      rental_days: Math.max(0, report.rental_days),
      idle_days: Math.max(0, report.idle_days),
      lost_income: Math.max(0, report.lost_income),
      comment: report.comment?.trim() || null,
      status: report.status,
      created_by: report.created_by || currentUserId,
    };
    const { data: saved, error } = await supabase
      .from("voyager_investor_reports")
      .upsert(payload, {
        onConflict: "company_id,investor_id,period_month",
      })
      .select("*")
      .single();
    if (error) {
      alert(error.message);
      return null;
    }
    await onRefresh();
    setSelected(saved as InvestorReport);
    return saved as InvestorReport;
  };

  const deleteReport = async (report: InvestorReport) => {
    if (!supabase || !canEdit) return;
    const { error } = await supabase
      .from("voyager_investor_reports")
      .delete()
      .eq("company_id", company.id)
      .eq("id", report.id);
    if (error) alert(error.message);
    else {
      setSelected(null);
      await onRefresh();
    }
  };

  return (
    <PageWrap
      title="Отчёты инвесторам"
      subtitle="Ежемесячные отчёты, готовые для отправки инвесторам."
    >
      <div className="payment-notice investor-report-notice">
        <span>i</span>
        Новый отчёт создаётся как отдельный снимок. Его ручное изменение не
        влияет на карточку инвестора и финансовые расчёты Voyager.
      </div>
      <InvestorMonthPicker period={monthKey} onChange={setMonthKey} label="Отчётный месяц" />
      <div className="investor-report-summary">
        <Stat label="Всего отчётов" value={String(reports.length)} note="В архиве" />
        <Stat
          label="Готовы за выбранный месяц"
          value={String(readyCount)}
          note={investorReportPeriodLabel(monthKey)}
          tone="mint"
        />
        <Stat
          label="Отчётов за выбранный месяц"
          value={String(periodReports.length)}
          note={investorReportPeriodLabel(monthKey)}
        />
      </div>
      <section className="panel investor-report-create">
        <PanelTitle
          title={canEdit ? "Сформировать отчёт" : "Открыть отчёт"}
          meta={investorReportPeriodLabel(monthKey)}
        />
        <div className="investor-report-create-form investor-report-period-filtered">
          <label>
            <span>ИНВЕСТОР</span>
            <select
              value={investorId}
              onChange={(event) => setInvestorId(event.target.value)}
            >
              {!data.investors.length && (
                <option value="">Инвесторы не добавлены</option>
              )}
              {data.investors.map((investor) => (
                <option key={investor.id} value={investor.id}>
                  {investor.name}
                </option>
              ))}
            </select>
          </label>
          <button
            className="primary"
            disabled={!investorId || (!canEdit && !existing)}
            onClick={generateReport}
          >
            {existing
              ? "Открыть готовый отчёт"
              : canEdit
                ? "Сформировать отчёт"
                : "Отчёт ещё не создан"}
          </button>
        </div>
      </section>
      <section className="panel investor-report-archive">
        <PanelTitle title="Архив отчётов" meta={`${investorReportPeriodLabel(monthKey)} · ${periodReports.length} документов`} />
        {periodReports.length ? (
          <div className="investor-report-list">
            {periodReports.map((report) => (
              <button key={report.id} onClick={() => setSelected(report)}>
                <span className="investor-report-avatar">
                  {report.investor_name.slice(0, 1).toUpperCase()}
                </span>
                <div>
                  <b>{report.investor_name}</b>
                  <small>{investorReportPeriodLabel(report.period_month)}</small>
                </div>
                <span className={`report-status ${report.status}`}>
                  {report.status === "ready" ? "Готов" : "Черновик"}
                </span>
                <strong>{money(report.payout)}</strong>
                <em>Открыть →</em>
              </button>
            ))}
          </div>
        ) : (
          <Empty
            icon="▧"
            title="За выбранный месяц отчётов пока нет"
            text={canEdit ? "Выберите другой месяц или сформируйте отчёт за этот период." : "Выберите другой месяц или дождитесь создания отчёта владельцем."}
          />
        )}
      </section>
      {selected && (
        <InvestorReportModal
          report={selected}
          canEdit={canEdit}
          isNew={!reports.some((item) => item.id === selected.id)}
          onClose={() => setSelected(null)}
          onSave={saveReport}
          onDelete={deleteReport}
          onRefreshFromVoyager={refreshFromVoyager}
        />
      )}
    </PageWrap>
  );
}

function InvestorReportModal({
  report,
  canEdit,
  isNew,
  onClose,
  onSave,
  onDelete,
  onRefreshFromVoyager,
}: {
  report: InvestorReport;
  canEdit: boolean;
  isNew: boolean;
  onClose: () => void;
  onSave: (report: InvestorReport) => Promise<InvestorReport | null>;
  onDelete: (report: InvestorReport) => Promise<void>;
  onRefreshFromVoyager: (report: InvestorReport) => InvestorReport;
}) {
  const [draft, setDraft] = useState(report);
  const [editing, setEditing] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const percentages = investorReportPercentages(draft);
  const daysWarning =
    percentages.possibleDays > 0 &&
    draft.rental_days + draft.idle_days !== percentages.possibleDays;
  const setNumber = (field: keyof InvestorReport, value: string) =>
    setDraft((current) => ({
      ...current,
      [field]: Number(value) || 0,
    }));
  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(investorReportText(draft));
      alert("Текст отчёта скопирован");
    } catch {
      alert("Не удалось скопировать текст. Попробуйте ещё раз.");
    }
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const saved = await onSave(draft);
    if (saved) {
      setDraft(saved);
      setEditing(false);
    }
    setSaving(false);
  };

  return (
    <Modal title="Отчёт инвестору" onClose={onClose} wide>
      <div className="investor-report-modal-actions no-print">
        <button className="secondary" onClick={() => void copyReport()}>
          Скопировать текст
        </button>
        <button className="secondary" onClick={() => window.print()}>
          Печать / сохранить PDF
        </button>
        {canEdit && !editing && (
          <button className="primary" onClick={() => setEditing(true)}>
            Изменить отчёт
          </button>
        )}
      </div>
      {editing && canEdit && (
        <form className="investor-report-form no-print" onSubmit={submit}>
          <div className="investor-report-form-head">
            <div>
              <b>{isNew ? "Проверьте новый отчёт" : "Редактирование отчёта"}</b>
              <small>
                Автоматические значения можно исправить — другие разделы не изменятся.
              </small>
            </div>
            <button
              type="button"
              className="secondary"
              onClick={() => setDraft(onRefreshFromVoyager(draft))}
            >
              Обновить из Voyager
            </button>
          </div>
          <div className="form-grid">
            <Field label="Инвестор">
              <input
                required
                value={draft.investor_name}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    investor_name: event.target.value,
                  }))
                }
              />
            </Field>
            <Field label="Период">
              <input value={investorReportPeriodLabel(draft.period_month)} disabled />
            </Field>
            <Field label="Количество автомобилей">
              <input
                type="number"
                min="0"
                value={draft.vehicle_count}
                onChange={(event) => setNumber("vehicle_count", event.target.value)}
              />
            </Field>
            <Field label="Выручка за период, ₽">
              <input
                type="number"
                step="0.01"
                value={draft.revenue}
                onChange={(event) => setNumber("revenue", event.target.value)}
              />
            </Field>
            <Field label="Операционные расходы, ₽">
              <input
                type="number"
                step="0.01"
                value={draft.operating_expenses}
                onChange={(event) =>
                  setNumber("operating_expenses", event.target.value)
                }
              />
            </Field>
            <Field label="Личные расходы инвестора, ₽">
              <input
                type="number"
                step="0.01"
                value={draft.personal_expenses}
                onChange={(event) =>
                  setNumber("personal_expenses", event.target.value)
                }
              />
            </Field>
            <Field label="Авто-дней в аренде">
              <input
                type="number"
                min="0"
                value={draft.rental_days}
                onChange={(event) => setNumber("rental_days", event.target.value)}
              />
            </Field>
            <Field label="Авто-дней в простое">
              <input
                type="number"
                min="0"
                value={draft.idle_days}
                onChange={(event) => setNumber("idle_days", event.target.value)}
              />
            </Field>
            <Field label="Незаработанные деньги, ₽">
              <input
                type="number"
                min="0"
                step="0.01"
                value={draft.lost_income}
                onChange={(event) => setNumber("lost_income", event.target.value)}
              />
            </Field>
            <Field label="Начислено инвестору, ₽">
              <input
                type="number"
                step="0.01"
                value={draft.accrued}
                onChange={(event) => setNumber("accrued", event.target.value)}
              />
            </Field>
            <Field label="Уже выплачено за месяц, ₽">
              <input
                type="number"
                step="0.01"
                value={draft.payments}
                onChange={(event) => setNumber("payments", event.target.value)}
              />
            </Field>
            <Field label="Итого к выплате, ₽">
              <input
                type="number"
                step="0.01"
                value={draft.payout}
                onChange={(event) => setNumber("payout", event.target.value)}
              />
            </Field>
            <Field label="Статус">
              <select
                value={draft.status}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    status: event.target.value as InvestorReportStatus,
                  }))
                }
              >
                <option value="draft">Черновик</option>
                <option value="ready">Готов к отправке</option>
              </select>
            </Field>
            <Field label="Комментарий" wide>
              <textarea
                value={draft.comment || ""}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    comment: event.target.value,
                  }))
                }
                placeholder="Пояснение для инвестора"
              />
            </Field>
          </div>
          <div className={`report-days-check${daysWarning ? " warning" : ""}`}>
            Возможных авто-дней: <b>{percentages.possibleDays}</b> · внесено:{" "}
            <b>{draft.rental_days + draft.idle_days}</b>
            {daysWarning && " — проверьте количество дней"}
          </div>
          <div className="modal-actions report-edit-actions">
            {!isNew && (
              <button
                type="button"
                className="danger-button"
                onClick={() => {
                  if (confirm("Удалить сохранённый отчёт?"))
                    void onDelete(draft);
                }}
              >
                Удалить отчёт
              </button>
            )}
            {!isNew && (
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setDraft(report);
                  setEditing(false);
                }}
              >
                Отмена
              </button>
            )}
            <button className="primary" disabled={saving}>
              {saving ? "Сохраняем…" : "Сохранить отчёт"}
            </button>
          </div>
        </form>
      )}
      <InvestorReportPreview report={draft} />
    </Modal>
  );
}

function InvestorReportPreview({ report }: { report: InvestorReport }) {
  const percentages = investorReportPercentages(report);
  return (
    <article className="investor-report-print-card">
      <header>
        <div className="report-brand-mark">V</div>
        <div>
          <small>VOYAGER OPERATING SYSTEM</small>
          <h2>Отчёт инвестору</h2>
          <p>{investorReportPeriodRange(report.period_month)}</p>
        </div>
        <span className={`report-status ${report.status}`}>
          {report.status === "ready" ? "ГОТОВ" : "ЧЕРНОВИК"}
        </span>
      </header>
      <section className="report-investor-title">
        <span>ИНВЕСТОР</span>
        <h3>{report.investor_name}</h3>
        <p>{report.vehicle_count} автомобилей в отчёте</p>
      </section>
      <div className="report-main-numbers">
        <div>
          <span>ВЫРУЧКА</span>
          <b>{money(report.revenue)}</b>
        </div>
        <div>
          <span>РАСХОДЫ</span>
          <b>{money(report.operating_expenses)}</b>
        </div>
        <div className="payout">
          <span>К ВЫПЛАТЕ</span>
          <b>{money(report.payout)}</b>
        </div>
      </div>
      <div className="report-details-grid">
        <div><span>Авто-дней в аренде</span><b>{report.rental_days}</b></div>
        <div><span>Авто-дней в простое</span><b>{report.idle_days}</b></div>
        <div><span>Загруженность</span><b>{percentages.utilization.toFixed(2).replace(".", ",")}%</b></div>
        <div><span>Простой</span><b>{percentages.idle.toFixed(2).replace(".", ",")}%</b></div>
        <div><span>Незаработанные деньги</span><b>{money(report.lost_income)}</b></div>
        <div><span>Личные расходы инвестора</span><b>{money(report.personal_expenses)}</b></div>
        <div><span>Начислено инвестору</span><b>{money(report.accrued)}</b></div>
        <div><span>Выплачено за период</span><b>{money(report.payments)}</b></div>
      </div>
      {report.comment && (
        <section className="report-comment">
          <span>КОММЕНТАРИЙ</span>
          <p>{report.comment}</p>
        </section>
      )}
      <footer>
        <span>Сформировано в Voyager OS</span>
        <b>{investorReportPeriodLabel(report.period_month)}</b>
      </footer>
    </article>
  );
}

type InvestorPortalPage = "overview" | "vehicles" | "expenses" | "finance";

function InvestorPortalApp({
  portal,
  email,
  onLogout,
}: {
  portal: InvestorPortalContext;
  email: string;
  onLogout: () => void;
}) {
  const [page, setPage] = useState<InvestorPortalPage>("overview");
  const [period, setPeriod] = useState(currentMonth());
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const { data } = portal;
  const investor = data.investors.find((item) => item.id === portal.investorId);

  if (!investor)
    return (
      <WaitingAccess email={email} onLogout={onLogout} />
    );

  const finance = investorFinances(data, investor, period);
  const nav: { id: InvestorPortalPage; icon: string; label: string }[] = [
    { id: "overview", icon: "⌂", label: "Обзор" },
    { id: "vehicles", icon: "◆", label: "Мои автомобили" },
    { id: "expenses", icon: "↘", label: "Расходы" },
    { id: "finance", icon: "₽", label: "Финансы" },
  ];

  return (
    <div className="investor-portal-shell">
      <aside className="investor-portal-sidebar">
        <div className="investor-portal-brand">
          <div className="brand-mark">V</div>
          <div><b>VOYAGER</b><span>КАБИНЕТ ИНВЕСТОРА</span></div>
        </div>
        <div className="investor-portal-person">
          <span>{investor.name[0]}</span>
          <div><b>{investor.name}</b><small>{data.vehicles.length} автомобилей</small></div>
        </div>
        <nav>
          {nav.map((item) => (
            <button
              key={item.id}
              className={page === item.id ? "active" : ""}
              onClick={() => {
                setPage(item.id);
                setSelectedVehicleId(null);
              }}
            >
              <span>{item.icon}</span>{item.label}
            </button>
          ))}
        </nav>
        <div className="investor-portal-sidebar-footer">
          <span>{email}</span>
          <button onClick={onLogout}>Выйти</button>
        </div>
      </aside>

      <main className="investor-portal-main">
        <header className="investor-portal-topbar">
          <div>
            <span>{portal.companyName}</span>
            <small>
              <i className="live-dot" /> Данные обновляются автоматически
            </small>
          </div>
          <div className="investor-portal-period">
            <button aria-label="Предыдущий месяц" onClick={() => setPeriod(shiftMonthKey(period, -1))}>←</button>
            <b>{investorReportPeriodLabel(period)}</b>
            <button aria-label="Следующий месяц" onClick={() => setPeriod(shiftMonthKey(period, 1))}>→</button>
          </div>
          <button className="investor-portal-mobile-logout" onClick={onLogout}>
            Выйти
          </button>
        </header>

        <div className="investor-portal-mobile-nav">
          {nav.map((item) => (
            <button key={item.id} className={page === item.id ? "active" : ""} onClick={() => {
              setPage(item.id);
              setSelectedVehicleId(null);
            }}>
              <span>{item.icon}</span>{item.label}
            </button>
          ))}
        </div>

        {page === "overview" ? (
          <InvestorPortalOverview
            investor={investor}
            data={data}
            period={period}
          />
        ) : page === "vehicles" ? (
          selectedVehicleId ? (
            <InvestorPortalVehicleDetails
              data={data}
              period={period}
              vehicleId={selectedVehicleId}
              onBack={() => setSelectedVehicleId(null)}
            />
          ) : (
            <InvestorPortalVehicles
              data={data}
              period={period}
              onOpenVehicle={setSelectedVehicleId}
            />
          )
        ) : page === "expenses" ? (
          <InvestorPortalExpenses data={data} period={period} />
        ) : (
          <InvestorPortalFinance
            finance={finance}
            period={period}
          />
        )}
      </main>
    </div>
  );
}

function InvestorPortalExpenses({
  data,
  period,
}: {
  data: VoyagerData;
  period: string;
}) {
  const [vehicleId, setVehicleId] = useState("all");
  const expenseTypes: EventType[] = ["expense", "repair", "maintenance"];
  const expenses = data.events
    .filter(
      (event) =>
        expenseTypes.includes(event.type) &&
        monthKey(event.date) === period &&
        (vehicleId === "all" || event.vehicleId === vehicleId),
    )
    .sort((left, right) => right.date.localeCompare(left.date));
  const operatingExpenses = expenses
    .filter((event) => event.expenseScope !== "investor_personal")
    .reduce((sum, event) => sum + (event.amount || 0), 0);
  const personalExpenses = expenses
    .filter(
      (event) =>
        event.type === "expense" &&
        event.expenseScope === "investor_personal",
    )
    .reduce((sum, event) => sum + (event.amount || 0), 0);
  const selectedVehicle = data.vehicles.find((vehicle) => vehicle.id === vehicleId);
  const filterLabel = selectedVehicle?.plate || "Все автомобили";

  return (
    <div className="investor-portal-page">
      <div className="investor-portal-heading">
        <p className="eyebrow">РАСХОДЫ</p>
        <h1>Расходы автомобилей</h1>
        <p>
          Операционные и личные расходы за {investorReportPeriodLabel(period).toLowerCase()}.
        </p>
      </div>
      <div className="hero-stats investor-portal-stats investor-portal-expense-stats">
        <Stat
          label="Операционные расходы"
          value={money(operatingExpenses)}
          note={filterLabel}
          tone="warm"
        />
        <Stat
          label="Личные расходы"
          value={money(personalExpenses)}
          note={filterLabel}
        />
        <Stat
          label="Всего расходов"
          value={money(operatingExpenses + personalExpenses)}
          note={filterLabel}
          tone="warm"
        />
      </div>
      <section className="panel investor-portal-expenses-panel">
        <div className="investor-portal-expenses-toolbar">
          <PanelTitle
            title="Все расходы"
            meta={`${expenses.length} ${expenses.length === 1 ? "запись" : "записей"}`}
          />
          <label>
            <span>Автомобиль</span>
            <select value={vehicleId} onChange={(event) => setVehicleId(event.target.value)}>
              <option value="all">Все автомобили</option>
              {data.vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.plate} · {vehicle.make} {vehicle.model}
                </option>
              ))}
            </select>
          </label>
        </div>
        {expenses.length ? (
          <div className="investor-portal-event-list investor-portal-expense-list">
            {expenses.map((event) => {
              const vehicle = data.vehicles.find((item) => item.id === event.vehicleId);
              const personal = event.expenseScope === "investor_personal";
              return (
                <div className="event-row" key={event.id}>
                  <span className="event-icon event-expense">↘</span>
                  <div>
                    <b>{event.title}</b>
                    <p>
                      {shortDate(event.date)} · {vehicle?.plate || "Без автомобиля"} · {personal ? "Личный расход" : EVENT_LABELS[event.type]}
                    </p>
                  </div>
                  <strong className="negative">−{money(event.amount || 0)}</strong>
                </div>
              );
            })}
          </div>
        ) : (
          <Empty
            icon="↘"
            title="Расходов не найдено"
            text="За выбранный месяц и автомобиль расходов пока нет."
          />
        )}
      </section>
    </div>
  );
}

function InvestorPortalOverview({
  investor,
  data,
  period,
}: {
  investor: Investor;
  data: VoyagerData;
  period: string;
}) {
  const finance = investorFinances(data, investor, period);
  const investorNameParts = investor.name.trim().split(/\s+/).filter(Boolean);
  const greetingName =
    investorNameParts.length >= 3
      ? investorNameParts.slice(1, 3).join(" ")
      : investorNameParts.length === 2
        ? investorNameParts[1]
        : investorNameParts[0] || "Инвестор";
  return (
    <div className="investor-portal-page">
      <div className="investor-portal-heading">
        <p className="eyebrow">ЛИЧНЫЙ КАБИНЕТ</p>
        <h1>Здравствуйте, {greetingName}</h1>
        <p>Здесь отображаются только ваши автомобили и финансовые показатели.</p>
      </div>
      <div className="hero-stats investor-portal-stats">
        <Stat label="Доход" value={money(finance.income)} note={investorReportPeriodLabel(period)} tone="mint" />
        <Stat label="Расходы" value={money(finance.operatingExpenses + finance.personalExpenses)} note="Операционные и личные" />
        <Stat label="Начислено" value={money(finance.accrued)} note={`${investor.commissionPercent}% от ${investor.commissionBase === "profit" ? "прибыли" : "выручки"}`} />
        <Stat label="К выплате" value={money(finance.payable)} note={`Выплачено: ${money(finance.payments)}`} tone={finance.payable < 0 ? "danger" : "mint"} />
      </div>
      <section className="panel">
        <PanelTitle title="Мои автомобили" meta={`${data.vehicles.length} шт.`} />
        <div className="investor-portal-mini-vehicles">
          {data.vehicles.map((vehicle) => {
            const vehicleFinance = vehicleFinances(data.events, vehicle.id, period);
            return (
              <div key={vehicle.id}>
                <span className={`status-dot ${vehicle.status === "Работает" ? "green" : "yellow"}`} />
                <div><b>{vehicle.plate}</b><small>{vehicle.make} {vehicle.model}</small></div>
                <strong className="negative">Расходы: {money(vehicleFinance.expense)}</strong>
              </div>
            );
          })}
          {!data.vehicles.length && <p className="muted">Автомобили пока не назначены.</p>}
        </div>
      </section>
    </div>
  );
}

function InvestorPortalVehicles({
  data,
  period,
  onOpenVehicle,
}: {
  data: VoyagerData;
  period: string;
  onOpenVehicle: (vehicleId: string) => void;
}) {
  return (
    <div className="investor-portal-page">
      <div className="investor-portal-heading">
        <p className="eyebrow">АВТОПАРК</p>
        <h1>Мои автомобили</h1>
        <p>Актуальный статус, пробег и расходы каждого автомобиля.</p>
      </div>
      <div className="investor-portal-vehicle-grid">
        {data.vehicles.map((vehicle) => {
          const finance = vehicleFinances(data.events, vehicle.id, period);
          return (
            <button
              type="button"
              className="panel investor-portal-vehicle-card"
              key={vehicle.id}
              onClick={() => onOpenVehicle(vehicle.id)}
              aria-label={`Открыть расходы автомобиля ${vehicle.plate}`}
            >
              <header>
                <div className="vehicle-avatar">{vehicle.make[0]}</div>
                <div><h2>{vehicle.plate}</h2><p>{vehicle.make} {vehicle.model}{vehicle.year ? ` · ${vehicle.year}` : ""}</p></div>
                <span className={`status-pill ${vehicle.status === "Работает" ? "green" : "yellow"}`}>{vehicle.status}</span>
              </header>
              <div className="investor-portal-vehicle-meta expenses-only"><span>Пробег<b>{vehicle.mileage.toLocaleString("ru-RU")} км</b></span><span>Расходы за месяц<b>{money(finance.expense)}</b></span></div>
              <span className="investor-portal-vehicle-open-hint">Открыть расходы →</span>
            </button>
          );
        })}
      </div>
      {!data.vehicles.length && <section className="panel"><Empty icon="◆" title="Автомобили пока не назначены" text="После назначения автомобиля владельцем он появится здесь автоматически." /></section>}
    </div>
  );
}

function InvestorPortalVehicleDetails({
  data,
  period,
  vehicleId,
  onBack,
}: {
  data: VoyagerData;
  period: string;
  vehicleId: string;
  onBack: () => void;
}) {
  const vehicle = data.vehicles.find((item) => item.id === vehicleId);
  if (!vehicle) {
    return (
      <div className="investor-portal-page">
        <button type="button" className="back-link" onClick={onBack}>← Назад к автомобилям</button>
        <section className="panel"><Empty icon="◆" title="Автомобиль не найден" text="Возможно, владелец изменил назначение автомобиля." /></section>
      </div>
    );
  }

  const finance = vehicleFinances(data.events, vehicle.id, period);
  const expenseTypes: EventType[] = ["expense", "repair", "maintenance"];
  const events = data.events
    .filter(
      (event) =>
        event.vehicleId === vehicle.id &&
        monthKey(event.date) === period &&
        expenseTypes.includes(event.type),
    )
    .sort((left, right) => right.date.localeCompare(left.date));

  return (
    <div className="investor-portal-page investor-portal-vehicle-details">
      <button type="button" className="back-link investor-portal-vehicle-back" onClick={onBack}>← Назад к автомобилям</button>
      <div className="investor-portal-vehicle-detail-heading">
        <div className="vehicle-avatar">{vehicle.make[0]}</div>
        <div>
          <p className="eyebrow">АВТОМОБИЛЬ</p>
          <h1>{vehicle.plate}</h1>
          <p>{vehicle.make} {vehicle.model}{vehicle.year ? ` · ${vehicle.year}` : ""} · {vehicle.mileage.toLocaleString("ru-RU")} км</p>
        </div>
        <span className={`status-pill ${vehicle.status === "Работает" ? "green" : "yellow"}`}>{vehicle.status}</span>
      </div>
      <div className="hero-stats investor-portal-vehicle-stats expenses-only">
        <Stat label="Расходы" value={money(finance.expense)} note={investorReportPeriodLabel(period)} />
      </div>
      <section className="panel investor-portal-vehicle-operations">
        <PanelTitle title="Расходы автомобиля" meta={`${events.length} записей · ${investorReportPeriodLabel(period)}`} />
        <InvestorPortalEventList events={events} vehicles={[vehicle]} />
      </section>
    </div>
  );
}

function InvestorPortalFinance({
  finance,
  period,
}: {
  finance: ReturnType<typeof investorFinances>;
  period: string;
}) {
  return (
    <div className="investor-portal-page">
      <div className="investor-portal-heading">
        <p className="eyebrow">ФИНАНСЫ</p>
        <h1>Доходы и расходы</h1>
        <p>Финансовый расчёт за {investorReportPeriodLabel(period).toLowerCase()}.</p>
      </div>
      <div className="investor-portal-finance-summary">
        <InfoLine label="Доход автомобилей" value={money(finance.income)} />
        <InfoLine label="Операционные расходы" value={money(finance.operatingExpenses)} />
        <InfoLine label="Личные расходы инвестора" value={money(finance.personalExpenses)} />
        <InfoLine label="Начислено инвестору" value={money(finance.accrued)} />
        <InfoLine label="Выплачено" value={money(finance.payments)} />
        <InfoLine label="Итого к выплате" value={money(finance.payable)} />
      </div>
    </div>
  );
}

function InvestorPortalEventList({ events, vehicles }: { events: VoyagerEvent[]; vehicles: Vehicle[] }) {
  if (!events.length) return <p className="muted investor-portal-empty-line">Операций за этот период пока нет.</p>;
  return (
    <div className="investor-portal-event-list">
      {events.map((event) => {
        const vehicle = vehicles.find((item) => item.id === event.vehicleId);
        const income = event.type === "income";
        return <div className="event-row" key={event.id}><span className={`event-icon event-${income ? "income" : "expense"}`}>{income ? "↗" : "↘"}</span><div><b>{event.title}</b><p>{shortDate(event.date)} · {vehicle?.plate || "Личный расход"}</p></div><strong className={income ? "positive" : "negative"}>{income ? "+" : "−"}{money(event.amount || 0)}</strong></div>;
      })}
    </div>
  );
}

function Investors({
  data,
  period,
  onPeriodChange,
  updateData,
  onOpenInvestor,
}: {
  data: VoyagerData;
  period: string;
  onPeriodChange: (period: string) => void;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
  onOpenInvestor: (id: string) => void;
}) {
  const [show, setShow] = useState(false);
  const previousPeriod = shiftMonthKey(period, -1);
  const previousMonthPayable = data.investors.reduce(
    (sum, investor) =>
      sum + Math.max(0, investorFinances(data, investor, previousPeriod).payable),
    0,
  );
  const currentMonthPayable = data.investors.reduce(
    (sum, investor) =>
      sum + Math.max(0, investorFinances(data, investor, period).payable),
    0,
  );
  return (
    <PageWrap
      title="Инвесторы"
      subtitle="Показатели рассчитаны за выбранный месяц. Условия применяются ко всем автомобилям инвестора."
      actions={
        <button className="primary" onClick={() => setShow(true)}>
          ＋ Добавить инвестора
        </button>
      }
    >
      <InvestorMonthPicker period={period} onChange={onPeriodChange} />
      <div className="hero-stats compact investor-overview-summary">
        <Stat
          label="Осталось выплатить за предыдущий месяц"
          value={money(previousMonthPayable)}
          note={`${investorReportPeriodLabel(previousPeriod)} · после учтённых выплат`}
          tone={previousMonthPayable > 0 ? "warm" : "mint"}
        />
        <Stat
          label="К выплате за выбранный месяц"
          value={money(currentMonthPayable)}
          note={`${investorReportPeriodLabel(period)} · после учтённых выплат`}
          tone="mint"
        />
      </div>
      {data.investors.length ? (
        <div className="investor-grid">
          {data.investors.map((investor) => {
            const vehicles = data.vehicles.filter(
              (v) => v.investorId === investor.id,
            );
            const finance = investorFinances(data, investor, period);
            return (
              <button
                className="investor-card"
                key={investor.id}
                onClick={() => onOpenInvestor(investor.id)}
              >
                <div className="investor-head">
                  <span>
                    {investor.name
                      .split(" ")
                      .map((n) => n[0])
                      .slice(0, 2)
                      .join("")}
                  </span>
                  <div>
                    <h3>{investor.name}</h3>
                    <p>{vehicles.length} автомобилей</p>
                  </div>
                </div>
                <div className="investor-numbers">
                  <span>
                    Начислено за месяц<b>{money(finance.accrued)}</b>
                  </span>
                  <span>
                    К выплате за месяц
                    <b className={finance.payable < 0 ? "negative" : ""}>
                      {money(finance.payable)}
                    </b>
                  </span>
                </div>
                <div className="investor-terms">
                  {investor.commissionPercent}% от{" "}
                  {investor.commissionBase === "profit" ? "прибыли" : "выручки"}
                  <em>→</em>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <Empty
          icon="◌"
          title="Инвесторов пока нет"
          text="Добавьте инвестора и назначьте ему автомобили."
        />
      )}
      {show && (
        <InvestorModal
          onClose={() => setShow(false)}
          onSave={(item) => {
            updateData((d) => ({ ...d, investors: [...d.investors, item] }));
            setShow(false);
          }}
        />
      )}
    </PageWrap>
  );
}

function InvestorDetails({
  investor,
  data,
  period,
  onPeriodChange: setPeriod,
  updateData,
  onBack,
  onOpenVehicle,
  company,
  portalAccount,
  onPortalAccessChange,
}: {
  investor: Investor;
  data: VoyagerData;
  period: string;
  onPeriodChange: (period: string) => void;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
  onBack: () => void;
  onOpenVehicle: (id: string) => void;
  company: CompanyContext;
  portalAccount?: InvestorPortalAccount;
  onPortalAccessChange: () => void;
}) {
  const [edit, setEdit] = useState(false);
  const [paymentEditor, setPaymentEditor] = useState<
    InvestorPayment | "new" | null
  >(null);
  const finance = investorFinances(data, investor, period);
  const previousPeriod = shiftMonthKey(period, -1);
  const previousFinance = investorFinances(data, investor, previousPeriod);
  const vehicles = data.vehicles.filter((v) => v.investorId === investor.id);
  const payments = data.investorPayments
    .filter((p) => p.investorId === investor.id)
    .sort((a, b) => b.date.localeCompare(a.date));
  return (
  <div className="page investor-detail-page">
    <button className="back-link investor-back-link" onClick={onBack}>
      ← Назад к инвесторам
    </button>
    <div className="detail-header investor-detail-header">
      <div className="vehicle-avatar investor-avatar">{investor.name[0]}</div>
      <div className="investor-heading">
          <span className="status-pill green">ИНВЕСТОР</span>
          <h1>{investor.name}</h1>
          <p>
            {investor.commissionPercent}% от{" "}
            {investor.commissionBase === "profit" ? "прибыли" : "выручки"} ·{" "}
            {vehicles.length} автомобилей
          </p>
        </div>
      <div className="detail-actions investor-detail-actions">
          <button className="secondary" onClick={() => setEdit(true)}>
            Изменить условия
          </button>
          <button className="primary" onClick={() => setPaymentEditor("new")}>
            ＋ Записать выплату
          </button>
        </div>
      </div>
      <div className="investor-period-bar">
        <div className="investor-period-nav">
          <button
            type="button"
            aria-label="Предыдущий месяц"
            onClick={() => setPeriod(shiftMonthKey(period, -1))}
          >
            ←
          </button>
          <div>
            <span>РАСЧЁТНЫЙ ПЕРИОД</span>
            <b>{investorReportPeriodLabel(period)}</b>
          </div>
          <button
            type="button"
            aria-label="Следующий месяц"
            onClick={() => setPeriod(shiftMonthKey(period, 1))}
          >
            →
          </button>
        </div>
        <div className="investor-previous-payout">
          <span>К выплате за {investorReportPeriodLabel(previousPeriod)}</span>
          <b className={previousFinance.payable < 0 ? "negative" : ""}>
            {money(previousFinance.payable)}
          </b>
          <small>После расходов и выплат за этот период</small>
        </div>
      </div>
      <div className="hero-stats">
        <Stat
          label="Выручка"
          value={money(finance.income)}
          note={investorReportPeriodLabel(period)}
        />
        <Stat
          label="Прибыль машин"
          value={money(finance.profit)}
          note={investorReportPeriodLabel(period)}
          tone={finance.profit >= 0 ? "mint" : "danger"}
        />
        <Stat
          label="Начислено инвестору"
          value={money(finance.accrued)}
          note={investorReportPeriodLabel(period)}
        />
        <Stat
          label="К выплате"
          value={money(finance.payable)}
          note={
            finance.payable < 0
              ? "Долг переносится дальше"
              : "С учётом расходов и выплат"
          }
          tone={finance.payable < 0 ? "danger" : "mint"}
        />
      </div>
      <div className="dashboard-grid">
        <section className="panel">
          <PanelTitle title="Расчёт" />
          <InfoLine
            label="Операционные расходы"
            value={money(finance.operatingExpenses)}
          />
          <InfoLine
            label="Личные расходы инвестора"
            value={money(finance.personalExpenses)}
          />
          <InfoLine label="Уже выплачено" value={money(finance.payments)} />
          <InfoLine
            label="Доход компании"
            value={money(finance.companyShare)}
          />
        </section>
        <section className="panel">
          <PanelTitle title="Контакты" />
          <InfoLine label="Телефон" value={investor.phone || "Не указан"} />
          <InfoLine label="Email" value={investor.email || "Не указан"} />
          <InfoLine
            label="База расчёта"
            value={
              investor.commissionBase === "profit"
                ? "Прибыль после расходов"
                : "Выручка"
            }
          />
        </section>
        {company.role === "owner" && (
          <InvestorPortalAccessPanel
            companyId={company.id}
            investor={investor}
            account={portalAccount}
            onChange={onPortalAccessChange}
          />
        )}
        <section className="panel wide">
          <PanelTitle
            title="Автомобили инвестора"
            meta={`${vehicles.length} шт.`}
          />
          {vehicles.length ? (
            vehicles.map((v) => (
              <button
                className="attention-row"
                key={v.id}
                onClick={() => onOpenVehicle(v.id)}
              >
                <span className="attention-icon">{v.make[0]}</span>
                <div>
                  <b>
                    {v.make} {v.model}
                  </b>
                  <p>
                    {v.plate} · прибыль{" "}
                    {money(vehicleFinances(data.events, v.id, period).profit)}
                  </p>
                </div>
                <em>→</em>
              </button>
            ))
          ) : (
            <Empty
              icon="◆"
              title="Автомобили не назначены"
              text="Назначьте инвестора в карточке автомобиля."
            />
          )}
        </section>
        <section className="panel wide">
          <PanelTitle
            title="История выплат"
            meta={`${payments.length} записей`}
          />
          {payments.length ? (
            payments.map((p) => (
              <div className="event-row" key={p.id}>
                <span className="event-icon event-income">↗</span>
                <div>
                  <b>Выплата инвестору</b>
                  <p>
                    {shortDate(p.date)}
                    {` · за ${investorReportPeriodLabel(
                      p.periodMonth || p.date.slice(0, 7),
                    )}`}
                    {p.notes ? ` · ${p.notes}` : ""}
                  </p>
                </div>
                <strong className="positive">{money(p.amount)}</strong>
                <button
                  type="button"
                  className="secondary tiny"
                  onClick={() => setPaymentEditor(p)}
                >
                  Изменить
                </button>
              </div>
            ))
          ) : (
            <Empty
              icon="₽"
              title="Выплат пока нет"
              text="Первая выплата появится после сохранения."
            />
          )}
        </section>
      </div>
      {edit && (
        <InvestorModal
          investor={investor}
          onClose={() => setEdit(false)}
          onSave={(item) => {
            updateData((d) => ({
              ...d,
              investors: d.investors.map((i) => (i.id === item.id ? item : i)),
            }));
            setEdit(false);
          }}
          onDelete={() => {
            updateData((d) => ({
              ...d,
              investors: d.investors.filter((i) => i.id !== investor.id),
              vehicles: d.vehicles.map((v) =>
                v.investorId === investor.id
                  ? { ...v, investorId: undefined }
                  : v,
              ),
              investorPayments: d.investorPayments.filter(
                (p) => p.investorId !== investor.id,
              ),
            }));
            onBack();
          }}
        />
      )}
      {paymentEditor && (
        <InvestorPaymentModal
          investorId={investor.id}
          payment={paymentEditor === "new" ? undefined : paymentEditor}
          onClose={() => setPaymentEditor(null)}
          onSave={(item) => {
            updateData((d) => ({
              ...d,
              investorPayments: d.investorPayments.some(
                (payment) => payment.id === item.id,
              )
                ? d.investorPayments.map((payment) =>
                    payment.id === item.id ? item : payment,
                  )
                : [item, ...d.investorPayments],
            }));
            setPaymentEditor(null);
          }}
          onDelete={
            paymentEditor === "new"
              ? undefined
              : () => {
                  if (!confirm("Удалить эту выплату инвестору?")) return;
                  updateData((d) => ({
                    ...d,
                    investorPayments: d.investorPayments.filter(
                      (payment) => payment.id !== paymentEditor.id,
                    ),
                  }));
                  setPaymentEditor(null);
                }
          }
        />
      )}
    </div>
  );
}

function InvestorPortalAccessPanel({
  companyId,
  investor,
  account,
  onChange,
}: {
  companyId: string;
  investor: Investor;
  account?: InvestorPortalAccount;
  onChange: () => void;
}) {
  const [email, setEmail] = useState(account?.email || investor.email || "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setEmail(account?.email || investor.email || "");
  }, [account?.email, investor.email]);

  const saveAccess = async (enabled: boolean) => {
    if (!supabase || !email.trim()) return;
    if (
      !enabled &&
      !confirm(`Отключить личный кабинет для ${investor.name}?`)
    )
      return;
    setBusy(true);
    setError("");
    setMessage("");
    const { error: requestError } = await supabase.rpc(
      "voyager_set_investor_portal_access",
      {
        target_company_id: companyId,
        target_investor_id: investor.id,
        investor_email: email.trim(),
        access_enabled: enabled,
      },
    );
    setBusy(false);
    if (requestError) {
      setError(requestError.message);
      return;
    }
    setMessage(enabled ? "Доступ к личному кабинету включён" : "Доступ отключён");
    onChange();
  };

  const active = account?.status === "active";
  return (
    <section className="panel wide investor-portal-access-panel">
      <PanelTitle
        title="Личный кабинет"
        meta={active ? "Доступ включён" : "Доступ не выдан"}
      />
      <p className="investor-portal-access-note">
        Инвестор сначала регистрируется на странице входа Voyager. После этого
        укажите тот же email и включите доступ.
      </p>
      <Field label="Email для входа">
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="investor@example.com"
        />
      </Field>
      {error && <div className="form-error">{error}</div>}
      {message && <div className="form-success">{message}</div>}
      <div className="investor-portal-access-actions">
        {active && (
          <button
            type="button"
            className="danger-button"
            disabled={busy}
            onClick={() => void saveAccess(false)}
          >
            Отключить доступ
          </button>
        )}
        <button
          type="button"
          className="primary"
          disabled={busy || !email.trim()}
          onClick={() => void saveAccess(true)}
        >
          {busy ? "Сохраняем…" : active ? "Обновить доступ" : "Выдать доступ"}
        </button>
      </div>
    </section>
  );
}

function Documents({
  data,
  onOpenVehicle,
  userId,
  updateData,
}: {
  data: VoyagerData;
  onOpenVehicle: (id: string) => void;
  userId: string;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
}) {
  const [editingDocument, setEditingDocument] =
    useState<VoyagerEvent | null>(null);
  const documents = data.events
    .filter((e) => e.type === "document")
    .sort((a, b) => b.date.localeCompare(a.date));
  return (
    <PageWrap
      title="Документы"
      subtitle="Все документы автопарка и сроки действия в одном месте."
    >
      <section className="panel table-panel">
        <div className="doc-table">
          <div className="doc-row doc-head">
            <span>Документ</span>
            <span>Автомобиль</span>
            <span>Добавлен</span>
            <span>Действует до</span>
            <span>Действия</span>
          </div>
          {documents.map((doc) => {
            const vehicle = data.vehicles.find((v) => v.id === doc.vehicleId);
            const overdue =
              doc.documentExpiresAt &&
              new Date(doc.documentExpiresAt) < new Date();
            return (
              <div className="doc-row" key={doc.id}>
                <span>
                  <i>▤</i>
                  <b>{doc.title}</b>
                  <small>{doc.fileName}</small>
                </span>
                <button onClick={() => onOpenVehicle(doc.vehicleId)}>
                  {vehicle
                    ? `${vehicle.make} ${vehicle.model} · ${vehicle.plate}`
                    : "—"}
                </button>
                <span>{shortDate(doc.date)}</span>
                <span className={overdue ? "overdue" : ""}>
                  {doc.documentExpiresAt
                    ? shortDate(doc.documentExpiresAt)
                    : "Без срока"}
                </span>
                <span className="row-actions">
                  <button
                    className="icon-button"
                    title="Открыть документ"
                    onClick={() => openPrivateFile(doc.filePath, userId)}
                  >
                    ↗
                  </button>
                  <button
                    className="icon-button document-edit"
                    title="Изменить документ"
                    onClick={() => setEditingDocument(doc)}
                  >
                    ✎
                  </button>
                  <button
                    className="icon-button delete"
                    title="Удалить документ"
                    onClick={() => {
                      if (!confirm(`Удалить документ «${doc.title}»?`)) return;
                      void removePrivateFile(doc.filePath);
                      updateData((d) => ({
                        ...d,
                        events: d.events.filter((event) => event.id !== doc.id),
                      }));
                    }}
                  >
                    ×
                  </button>
                </span>
              </div>
            );
          })}
        </div>
        {!documents.length && (
          <Empty
            icon="▤"
            title="Документов пока нет"
            text="Загрузите документ из карточки автомобиля."
          />
        )}
      </section>
      {editingDocument &&
        (() => {
          const vehicle = data.vehicles.find(
            (item) => item.id === editingDocument.vehicleId,
          );
          if (!vehicle) return null;
          const investor = data.investors.find(
            (item) => item.id === vehicle.investorId,
          );
          return (
            <EventModal
              initialType="document"
              event={editingDocument}
              vehicle={vehicle}
              investor={investor}
              userId={userId}
              onClose={() => setEditingDocument(null)}
              onSave={(event, vehiclePatch) => {
                updateData((d) => ({
                  ...d,
                  vehicles: d.vehicles.map((item) =>
                    item.id === vehicle.id
                      ? { ...item, ...vehiclePatch }
                      : item,
                  ),
                  events: d.events.map((item) =>
                    item.id === event.id ? event : item,
                  ),
                }));
                setEditingDocument(null);
              }}
            />
          );
        })()}
    </PageWrap>
  );
}

function ReferenceDirectory({
  data,
  updateData,
}: {
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
}) {
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<"all" | ReferenceItem["kind"]>("all");
  const [editing, setEditing] = useState<ReferenceItem | true | null>(null);
  const items = data.references
    .filter(
      (item) =>
        `${item.title} ${item.category || ""} ${item.description || ""}`
          .toLowerCase()
          .includes(query.toLowerCase()) &&
        (kind === "all" || item.kind === kind),
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const kindLabel = (value: ReferenceItem["kind"]) =>
    value === "table" ? "Таблица" : value === "link" ? "Ссылка" : "Заметка";
  const kindIcon = (value: ReferenceItem["kind"]) =>
    value === "table" ? "▦" : value === "link" ? "↗" : "✎";
  const openItem = (item: ReferenceItem) => {
    if (!item.url) {
      setEditing(item);
      return;
    }
    window.open(
      /^https?:\/\//i.test(item.url) ? item.url : `https://${item.url}`,
      "_blank",
      "noopener,noreferrer",
    );
  };
  return (
    <PageWrap
      title="Справочник"
      subtitle="Рабочие таблицы, полезные ссылки и важная информация в одном месте."
      actions={
        <button className="primary" onClick={() => setEditing(true)}>
          ＋ Добавить запись
        </button>
      }
    >
      <div className="toolbar">
        <label className="search">
          ⌕
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Найти в справочнике"
          />
        </label>
        <div className="filter-pills">
          {(
            [
              ["all", "Все"],
              ["table", "Таблицы"],
              ["link", "Ссылки"],
              ["note", "Заметки"],
            ] as const
          ).map(([value, label]) => (
            <button
              className={kind === value ? "active" : ""}
              onClick={() => setKind(value)}
              key={value}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {items.length ? (
        <div className="reference-grid">
          {items.map((item) => (
            <article
              className={`reference-card ref-${item.kind}`}
              key={item.id}
            >
              <button className="reference-open" onClick={() => openItem(item)}>
                <span className="reference-icon">{kindIcon(item.kind)}</span>
                <div className="reference-copy">
                  <div>
                    <small>{item.category || kindLabel(item.kind)}</small>
                    <em>{kindLabel(item.kind)}</em>
                  </div>
                  <h3>{item.title}</h3>
                  <p>
                    {item.description ||
                      (item.url
                        ? item.url.replace(/^https?:\/\//, "")
                        : "Важная заметка")}
                  </p>
                </div>
                <strong>{item.url ? "↗" : "→"}</strong>
              </button>
              <button
                className="reference-edit"
                onClick={() => setEditing(item)}
              >
                Изменить
              </button>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon="⌘"
          title="Справочник пока пуст"
          text="Добавьте первую таблицу, ссылку или важную заметку."
        />
      )}
      {editing && (
        <ReferenceModal
          item={editing === true ? undefined : editing}
          onClose={() => setEditing(null)}
          onSave={(item) => {
            updateData((d) => ({
              ...d,
              references: d.references.some((r) => r.id === item.id)
                ? d.references.map((r) => (r.id === item.id ? item : r))
                : [...d.references, item],
            }));
            setEditing(null);
          }}
          onDelete={
            editing !== true
              ? () => {
                  const id = editing.id;
                  updateData((d) => ({
                    ...d,
                    references: d.references.filter((r) => r.id !== id),
                  }));
                  setEditing(null);
                }
              : undefined
          }
        />
      )}
    </PageWrap>
  );
}

function Settings({
  data,
  updateData,
  email,
  currentUserName,
  company,
  members,
  sync,
  syncError,
  hasPendingChanges,
  lastCloudSyncAt,
  cloudLoadMs,
  cacheShownMs,
  snapshotRevision,
  onRefreshMembers,
}: {
  data: VoyagerData;
  updateData: (fn: (d: VoyagerData) => VoyagerData) => void;
  email: string;
  currentUserName: string;
  company: CompanyContext;
  members: CompanyMember[];
  sync: SyncState;
  syncError: string;
  hasPendingChanges: boolean;
  lastCloudSyncAt: string | null;
  cloudLoadMs: number | null;
  cacheShownMs: number | null;
  snapshotRevision: number | null;
  onRefreshMembers: () => Promise<void>;
}) {
  const [monthlyExpenseLimit, setMonthlyExpenseLimit] = useState(
    String(data.settings.monthlyVehicleExpenseLimit),
  );
  const [insuranceAlertDays, setInsuranceAlertDays] = useState(
    String(data.settings.insuranceAlertDays),
  );
  const [memberEmail, setMemberEmail] = useState("");
  const [memberRole, setMemberRole] = useState<Exclude<CompanyRole, "owner">>(
    "viewer",
  );
  const [memberBusy, setMemberBusy] = useState(false);
  const [editingMemberName, setEditingMemberName] = useState<CompanyMember | null>(
    null,
  );
  const [memberNameDraft, setMemberNameDraft] = useState("");
  const [memberNameBusy, setMemberNameBusy] = useState(false);
  const [backups, setBackups] = useState<SnapshotBackup[]>([]);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupFeatureMessage, setBackupFeatureMessage] = useState("");
  const [connectionResult, setConnectionResult] = useState("");
  const snapshotSize = new Blob([JSON.stringify(data)]).size;
  const formatBytes = (bytes: number) =>
    bytes >= 1024 * 1024
      ? `${(bytes / 1024 / 1024).toFixed(1)} МБ`
      : `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  const syncLabel: Record<SyncState, string> = {
    loading: "Проверяем облако",
    saving: "Сохраняем в облако",
    saved: "Все изменения сохранены",
    offline: "Сохранено на устройстве, ожидается сеть",
    error: "Ошибка облачной синхронизации",
  };
  const backupReasonLabel: Record<SnapshotBackup["reason"], string> = {
    daily: "Ежедневная",
    manual: "Ручная",
    before_restore: "Перед восстановлением",
  };

  const loadBackups = async () => {
    if (!supabase) return;
    const { data: result, error } = await supabase
      .from("voyager_company_snapshot_backups")
      .select("id, company_id, revision, reason, created_at, created_by")
      .eq("company_id", company.id)
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) {
      setBackupFeatureMessage(
        error.code === "42P01" || error.code === "PGRST205"
          ? "Для облачной истории выполните миграцию 008_snapshot_backups.sql в Supabase."
          : error.message,
      );
      return;
    }
    setBackupFeatureMessage("");
    setBackups((result || []) as SnapshotBackup[]);
  };

  useEffect(() => {
    void loadBackups();
  }, [company.id]);

  const createCloudBackup = async () => {
    if (!supabase || backupBusy) return;
    setBackupBusy(true);
    const { error } = await supabase.rpc("voyager_create_snapshot_backup", {
      target_company_id: company.id,
    });
    setBackupBusy(false);
    if (error) {
      alert(error.message);
      return;
    }
    await loadBackups();
  };

  const restoreCloudBackup = async (backup: SnapshotBackup) => {
    if (
      !supabase ||
      backupBusy ||
      !confirm(
        `Восстановить данные на ${new Date(backup.created_at).toLocaleString("ru-RU")}? Текущее состояние будет автоматически сохранено отдельной копией.`,
      )
    )
      return;
    setBackupBusy(true);
    const { error } = await supabase.rpc("voyager_restore_snapshot_backup", {
      backup_id: backup.id,
    });
    setBackupBusy(false);
    if (error) {
      alert(error.message);
      return;
    }
    location.reload();
  };

  const deleteCloudBackup = async (backup: SnapshotBackup) => {
    if (
      !supabase ||
      backupBusy ||
      backups.length <= 1 ||
      !confirm(
        `Удалить резервную копию от ${new Date(backup.created_at).toLocaleString("ru-RU")}? Это действие нельзя отменить.`,
      )
    )
      return;
    setBackupBusy(true);
    const { error } = await supabase.rpc("voyager_delete_snapshot_backup", {
      backup_id: backup.id,
    });
    setBackupBusy(false);
    if (error) {
      alert(
        error.code === "PGRST202"
          ? "Выполните миграцию 009_snapshot_backup_retention.sql в Supabase."
          : error.message,
      );
      return;
    }
    await loadBackups();
  };

  const testCloudConnection = async () => {
    if (!supabase) return;
    const startedAt = performance.now();
    setConnectionResult("Проверяем…");
    const { data: result, error } = await supabase
      .from("voyager_company_snapshots")
      .select("revision, updated_at")
      .eq("company_id", company.id)
      .maybeSingle();
    const duration = Math.round(performance.now() - startedAt);
    setConnectionResult(
      error
        ? `Ошибка за ${duration} мс: ${error.message}`
        : `Связь работает · ${duration} мс · ревизия ${result?.revision ?? "—"}`,
    );
  };
  const addMember = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabase || !memberEmail.trim()) return;
    setMemberBusy(true);
    const { error } = await supabase.rpc("voyager_add_company_member", {
      target_company_id: company.id,
      member_email: memberEmail.trim(),
      member_role: memberRole,
    });
    setMemberBusy(false);
    if (error) {
      alert(error.message);
      return;
    }
    setMemberEmail("");
    await onRefreshMembers();
  };
  const updateMember = async (
    member: CompanyMember,
    role: Exclude<CompanyRole, "owner">,
  ) => {
    if (!supabase) return;
    const { error } = await supabase.rpc("voyager_update_company_member", {
      target_company_id: company.id,
      target_user_id: member.user_id,
      member_role: role,
      member_status: member.status,
    });
    if (error) alert(error.message);
    else await onRefreshMembers();
  };
  const removeMember = async (member: CompanyMember) => {
    if (
      !supabase ||
      !confirm(`Удалить доступ для ${member.email}? Данные компании сохранятся.`)
    )
      return;
    const { error } = await supabase.rpc("voyager_remove_company_member", {
      target_company_id: company.id,
      target_user_id: member.user_id,
    });
    if (error) alert(error.message);
    else await onRefreshMembers();
  };
  const openMemberNameEditor = (member: CompanyMember) => {
    setMemberNameDraft(member.display_name || "");
    setEditingMemberName(member);
  };
  const saveMemberName = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabase || !editingMemberName || memberNameBusy) return;
    setMemberNameBusy(true);
    const { error } = await supabase.rpc(
      "voyager_set_company_member_display_name",
      {
        target_company_id: company.id,
        target_user_id: editingMemberName.user_id,
        member_display_name: memberNameDraft.trim() || null,
      },
    );
    setMemberNameBusy(false);
    if (error) {
      alert(error.message);
      return;
    }
    setEditingMemberName(null);
    setMemberNameDraft("");
    await onRefreshMembers();
  };
  const exportData = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `voyager-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const importData = (file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        updateData(() => normaliseData(JSON.parse(String(reader.result))));
        alert("Резервная копия загружена");
      } catch {
        alert("Не удалось прочитать файл");
      }
    };
    reader.readAsText(file);
  };
  return (
    <PageWrap
      title="Настройки"
      subtitle="Управление аккаунтом и резервными копиями."
    >
      <div className="settings-grid">
        <section className="panel">
          <PanelTitle title="Компания и аккаунт" />
          <InfoLine label="Компания" value={company.name} />
          <InfoLine label="Отображаемое имя" value={currentUserName} />
          <InfoLine label="Email" value={email} />
          <InfoLine label="Роль" value={ROLE_LABELS[company.role]} />
          <InfoLine label="Хранилище" value="Supabase PostgreSQL" />
          <InfoLine label="Синхронизация" value="В реальном времени" />
        </section>
        <section className="panel company-team-panel">
          <PanelTitle
            title="Сотрудники"
            meta={`${members.length} ${members.length === 1 ? "участник" : "участников"}`}
          />
          {company.role === "owner" ? (
            <>
              <p className="settings-copy">
                Сначала сотрудник регистрируется на сайте Voyager. Затем добавьте
                сюда точный email и назначьте уровень доступа.
              </p>
              <form className="member-add" onSubmit={addMember}>
                <Field label="Email сотрудника">
                  <input
                    type="email"
                    required
                    placeholder="employee@example.com"
                    value={memberEmail}
                    onChange={(event) => setMemberEmail(event.target.value)}
                  />
                </Field>
                <Field label="Уровень доступа">
                  <select
                    value={memberRole}
                    onChange={(event) =>
                      setMemberRole(
                        event.target.value as Exclude<CompanyRole, "owner">,
                      )
                    }
                  >
                    <option value="viewer">Только просмотр</option>
                    <option value="editor">Редактор</option>
                    <option value="admin">Администратор</option>
                  </select>
                </Field>
                <button className="primary" disabled={memberBusy}>
                  {memberBusy ? "Добавляем…" : "Добавить сотрудника"}
                </button>
              </form>
            </>
          ) : (
            <p className="settings-copy">
              Управлять составом команды может только владелец компании.
            </p>
          )}
          <div className="member-list">
            {members.map((member) => (
              <div className="member-row" key={member.user_id}>
                <span className="member-avatar">
                  {(member.display_name || member.email)[0]?.toUpperCase()}
                </span>
                <div className="member-identity">
                  <b>{member.display_name || member.email}</b>
                  <small>
                    {member.display_name
                      ? member.email
                      : "ФИО ещё не указано"}
                  </small>
                </div>
                {company.role === "owner" ? (
                  <>
                    <div className="member-actions">
                      <button
                        type="button"
                        className="secondary tiny"
                        onClick={() => openMemberNameEditor(member)}
                      >
                        ФИО
                      </button>
                      {member.role !== "owner" && (
                        <button
                          type="button"
                          className="member-remove"
                          onClick={() => void removeMember(member)}
                        >
                          Удалить
                        </button>
                      )}
                    </div>
                    {member.role !== "owner" ? (
                      <select
                        aria-label={`Роль ${member.email}`}
                        value={member.role}
                        onChange={(event) =>
                          void updateMember(
                            member,
                            event.target.value as Exclude<CompanyRole, "owner">,
                          )
                        }
                      >
                        <option value="viewer">Только просмотр</option>
                        <option value="editor">Редактор</option>
                        <option value="admin">Администратор</option>
                      </select>
                    ) : (
                      <span className="role-badge">{ROLE_LABELS[member.role]}</span>
                    )}
                  </>
                ) : (
                  <span className="role-badge">{ROLE_LABELS[member.role]}</span>
                )}
              </div>
            ))}
          </div>
        </section>
        <section className="panel diagnostics-panel">
          <PanelTitle title="Диагностика сохранения" />
          <InfoLine
            label="Интернет"
            value={navigator.onLine ? "Подключён" : "Нет соединения"}
          />
          <InfoLine label="Состояние" value={syncLabel[sync]} />
          <InfoLine
            label="Последнее подтверждение облака"
            value={
              lastCloudSyncAt
                ? new Date(lastCloudSyncAt).toLocaleString("ru-RU")
                : "Ещё не получено"
            }
          />
          <InfoLine
            label="Ревизия данных"
            value={snapshotRevision === null ? "—" : String(snapshotRevision)}
          />
          <InfoLine label="Размер данных" value={formatBytes(snapshotSize)} />
          <InfoLine
            label="Загрузка из облака"
            value={cloudLoadMs === null ? "Ожидается" : `${cloudLoadMs} мс`}
          />
          <InfoLine
            label="Локальная копия показана"
            value={cacheShownMs === null ? "Не использовалась" : `${cacheShownMs} мс`}
          />
          {hasPendingChanges && (
            <p className="settings-warning">
              Есть изменения, сохранённые на этом устройстве и ожидающие
              подтверждения облака. Не выходите из аккаунта до завершения.
            </p>
          )}
          {syncError && <p className="settings-error">{syncError}</p>}
          <div className="settings-actions">
            <button
              type="button"
              className="secondary"
              onClick={() => void testCloudConnection()}
            >
              Проверить соединение
            </button>
            {connectionResult && <small>{connectionResult}</small>}
          </div>
        </section>
        <section className="panel cloud-backup-panel">
          <PanelTitle
            title="История в облаке"
            meta={backups.length ? `${backups.length} копий` : undefined}
          />
          <p className="settings-copy">
            Voyager сохраняет состояние до первого изменения каждого дня.
            Ежедневные копии хранятся 30 дней, копии перед восстановлением — 90
            дней. Ручные копии хранятся без срока.
          </p>
          {backupFeatureMessage && (
            <p className="settings-warning">{backupFeatureMessage}</p>
          )}
          {company.role !== "viewer" && !backupFeatureMessage && (
            <div className="settings-actions">
              <button
                type="button"
                className="secondary"
                disabled={backupBusy}
                onClick={() => void createCloudBackup()}
              >
                {backupBusy ? "Подождите…" : "Создать копию сейчас"}
              </button>
              <button
                type="button"
                className="secondary"
                disabled={backupBusy}
                onClick={() => void loadBackups()}
              >
                Обновить список
              </button>
            </div>
          )}
          <div className="backup-list">
            {backups.map((backup) => (
              <div className="backup-row" key={backup.id}>
                <div>
                  <b>{backupReasonLabel[backup.reason]}</b>
                  <small>
                    {new Date(backup.created_at).toLocaleString("ru-RU")} ·
                    ревизия {backup.revision}
                  </small>
                </div>
                {(company.role === "owner" || company.role === "admin") && (
                  <div className="backup-actions">
                    <button
                      type="button"
                      className="secondary"
                      disabled={backupBusy || hasPendingChanges}
                      title={
                        hasPendingChanges
                          ? "Сначала дождитесь сохранения текущих изменений"
                          : undefined
                      }
                      onClick={() => void restoreCloudBackup(backup)}
                    >
                      Восстановить
                    </button>
                    <button
                      type="button"
                      className="danger-button"
                      disabled={backupBusy || backups.length <= 1}
                      title={
                        backups.length <= 1
                          ? "Последнюю резервную копию удалить нельзя"
                          : undefined
                      }
                      onClick={() => void deleteCloudBackup(backup)}
                    >
                      Удалить
                    </button>
                  </div>
                )}
              </div>
            ))}
            {!backupFeatureMessage && backups.length === 0 && (
              <p className="settings-copy">Копий пока нет.</p>
            )}
          </div>
        </section>
        <section className="panel">
          <PanelTitle title="Резервная копия" />
          <p className="settings-copy">
            Экспортируйте независимую копию таксопарка, проката автомобилей,
            велосипедов, событий, инвесторов и справочника.
          </p>
          <div className="settings-actions">
            <button className="secondary" onClick={exportData}>
              Скачать копию
            </button>
            <label className="secondary file-label">
              Загрузить копию
              <input
                type="file"
                accept="application/json"
                onChange={(e) => importData(e.target.files?.[0])}
              />
            </label>
          </div>
        </section>
        <section className="panel">
          <PanelTitle title="Контроль расходов и сроков" />
          <div className="settings-controls">
            <Field label="Лимит расходов на автомобиль в месяц, ₽">
              <input
                type="number"
                min="0"
                step="100"
                value={monthlyExpenseLimit}
                onChange={(e) => setMonthlyExpenseLimit(e.target.value)}
              />
            </Field>
            <Field label="Предупреждать о страховке за, дней">
              <input
                type="number"
                min="0"
                step="1"
                value={insuranceAlertDays}
                onChange={(e) => setInsuranceAlertDays(e.target.value)}
              />
            </Field>
            <button
              type="button"
              className="primary"
              onClick={() => {
                updateData((current) => ({
                  ...current,
                  settings: {
                    monthlyVehicleExpenseLimit:
                      Number(monthlyExpenseLimit) || 17000,
                    insuranceAlertDays: Math.max(
                      0,
                      Number(insuranceAlertDays) || 0,
                    ),
                  },
                }));
              }}
            >
              Сохранить настройки
            </button>
          </div>
        </section>
      </div>
      {editingMemberName && (
        <Modal
          title="ФИО сотрудника"
          onClose={() => {
            if (!memberNameBusy) setEditingMemberName(null);
          }}
        >
          <form className="modal-form" onSubmit={saveMemberName}>
            <p className="settings-copy">
              Укажите имя, которое будет видно в задачах, списках и верхней
              панели. Email и права доступа не изменятся.
            </p>
            <Field label="ФИО">
              <input
                autoFocus
                maxLength={160}
                placeholder="Например, Иванов Иван Иванович"
                value={memberNameDraft}
                onChange={(event) => setMemberNameDraft(event.target.value)}
              />
            </Field>
            <small className="member-name-email">
              Аккаунт: {editingMemberName.email}
            </small>
            <div className="modal-actions">
              <button
                type="button"
                className="secondary"
                disabled={memberNameBusy}
                onClick={() => setEditingMemberName(null)}
              >
                Отмена
              </button>
              <button className="primary" disabled={memberNameBusy}>
                {memberNameBusy ? "Сохраняем…" : "Сохранить ФИО"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </PageWrap>
  );
}

function MileageUpdateModal({
  vehicles,
  onClose,
  onSave,
}: {
  vehicles: Vehicle[];
  onClose: () => void;
  onSave: (
    updates: Map<
      string,
      Pick<
        Vehicle,
        "mileage" | "nextMaintenanceMileage" | "mileageUpdatedWeek"
      >
    >,
  ) => void;
}) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(
      vehicles.map((vehicle) => [
        vehicle.id,
        {
          mileage: String(vehicle.mileage),
          nextMaintenanceMileage: vehicle.nextMaintenanceMileage
            ? String(vehicle.nextMaintenanceMileage)
            : "",
        },
      ]),
    ),
  );
  const [selectedIds, setSelectedIds] = useState(
    () => new Set<string>(),
  );
  const toggleVehicle = (vehicleId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(vehicleId)) next.delete(vehicleId);
      else next.add(vehicleId);
      return next;
    });
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const updates = new Map<
      string,
      Pick<
        Vehicle,
        "mileage" | "nextMaintenanceMileage" | "mileageUpdatedWeek"
      >
    >();
    vehicles.forEach((vehicle) => {
      if (!selectedIds.has(vehicle.id)) return;
      const value = values[vehicle.id];
      updates.set(vehicle.id, {
        mileage: Math.max(0, Number(value.mileage) || 0),
        nextMaintenanceMileage: value.nextMaintenanceMileage
          ? Math.max(0, Number(value.nextMaintenanceMileage) || 0)
          : undefined,
        mileageUpdatedWeek: currentMileageWeek(),
      });
    });
    onSave(updates);
  };
  return (
    <Modal title="Обновить пробеги и ТО" onClose={onClose}>
      <form onSubmit={submit} className="mileage-form">
        <div className="mileage-head">
          <span>Обновить</span>
          <span>Автомобиль</span>
          <span>Пробег сейчас</span>
          <span>Следующее ТО</span>
        </div>
        <div className="mileage-list">
          {vehicles.map((vehicle) => (
            <div className="mileage-row" key={vehicle.id}>
              <label className="mileage-select">
                <input
                  type="checkbox"
                  checked={selectedIds.has(vehicle.id)}
                  onChange={() => toggleVehicle(vehicle.id)}
                  aria-label={`Подтвердить пробег ${vehicle.plate}`}
                />
              </label>
              <span>
                <b>
                  {vehicle.make} {vehicle.model}
                </b>
                <small>{vehicle.plate}</small>
              </span>
              <input
                required
                type="number"
                min="0"
                aria-label={`Пробег ${vehicle.plate}`}
                value={values[vehicle.id].mileage}
                onChange={(event) => {
                  setSelectedIds((current) =>
                    new Set(current).add(vehicle.id),
                  );
                  setValues((current) => ({
                    ...current,
                    [vehicle.id]: {
                      ...current[vehicle.id],
                      mileage: event.target.value,
                    },
                  }));
                }}
              />
              <input
                type="number"
                min="0"
                aria-label={`Следующее ТО ${vehicle.plate}`}
                value={values[vehicle.id].nextMaintenanceMileage}
                onChange={(event) => {
                  setSelectedIds((current) =>
                    new Set(current).add(vehicle.id),
                  );
                  setValues((current) => ({
                    ...current,
                    [vehicle.id]: {
                      ...current[vehicle.id],
                      nextMaintenanceMileage: event.target.value,
                    },
                  }));
                }}
              />
            </div>
          ))}
        </div>
        {!vehicles.length && (
          <Empty
            icon="◆"
            title="Автомобилей пока нет"
            text="Сначала добавьте автомобиль в таксопарк."
          />
        )}
        <div className="modal-actions">
          <button
            type="button"
            className="secondary"
            onClick={() =>
              setSelectedIds(
                selectedIds.size === vehicles.length
                  ? new Set()
                  : new Set(vehicles.map((vehicle) => vehicle.id)),
              )
            }
          >
            {selectedIds.size === vehicles.length
              ? "Снять отметки"
              : "Отметить все"}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary" disabled={!selectedIds.size}>
            Сохранить выбранные ({selectedIds.size})
          </button>
        </div>
      </form>
    </Modal>
  );
}

function LedgerEntryModal({
  mode,
  entry,
  vehicles,
  investors,
  onClose,
  onSave,
}: {
  mode: "expenses" | "income";
  entry?: VoyagerEvent;
  vehicles: Vehicle[];
  investors: Investor[];
  onClose: () => void;
  onSave: (event: VoyagerEvent) => void;
}) {
  const [target, setTarget] = useState<"vehicle" | "investor">(
    entry?.expenseScope === "investor_personal" ? "investor" : "vehicle",
  );
  const [vehicleId, setVehicleId] = useState(
    entry?.vehicleId || vehicles[0]?.id || "",
  );
  const [investorId, setInvestorId] = useState(
    entry?.investorId || investors[0]?.id || "",
  );
  const [title, setTitle] = useState(entry?.title || "");
  const [amount, setAmount] = useState(
    typeof entry?.amount === "number" ? String(entry.amount) : "",
  );
  const [date, setDate] = useState(
    entry?.date.slice(0, 10) || new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState(entry?.notes || "");
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>(entry?.paymentMethod || "СБЕР");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const selectedVehicle = vehicles.find((vehicle) => vehicle.id === vehicleId);
    const directInvestor = mode === "expenses" && target === "investor";
    onSave({
      ...entry,
      id: entry?.id || uid(),
      vehicleId: directInvestor ? "" : vehicleId,
      investorId: directInvestor ? investorId : selectedVehicle?.investorId,
      type: entry?.type || (mode === "expenses" ? "expense" : "income"),
      title: title.trim() || (mode === "expenses" ? "Расход" : "Доход"),
      amount: Number(amount) || 0,
      expenseScope:
        mode === "expenses"
          ? directInvestor
            ? "investor_personal"
            : "vehicle"
          : undefined,
      paymentMethod: mode === "expenses" ? paymentMethod : undefined,
      date: new Date(`${date}T12:00:00`).toISOString(),
      notes: notes.trim() || undefined,
      paid: true,
      createdAt: entry?.createdAt || new Date().toISOString(),
    });
  };
  return (
    <Modal
      title={
        entry
          ? mode === "expenses"
            ? "Изменить расход"
            : "Изменить доход"
          : mode === "expenses"
            ? "Новый расход"
            : "Новый доход"
      }
      onClose={onClose}
    >
      <form onSubmit={submit} className="event-form">
        {mode === "expenses" && (
          <Field label="Кому относится">
            <select
              value={target}
              onChange={(event) =>
                setTarget(event.target.value as "vehicle" | "investor")
              }
            >
              <option value="vehicle">Автомобилю</option>
              <option value="investor">Инвестору без автомобиля</option>
            </select>
          </Field>
        )}
        {target === "vehicle" || mode === "income" ? (
          <Field label="Автомобиль">
            <select
              required
              value={vehicleId}
              onChange={(event) => setVehicleId(event.target.value)}
            >
              {!vehicles.length && <option value="">Нет автомобилей</option>}
              {vehicles.map((vehicle) => (
                <option value={vehicle.id} key={vehicle.id}>
                  {vehicle.make} {vehicle.model} · {vehicle.plate}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="Инвестор">
            <select
              required
              value={investorId}
              onChange={(event) => setInvestorId(event.target.value)}
            >
              {!investors.length && <option value="">Нет инвесторов</option>}
              {investors.map((investor) => (
                <option value={investor.id} key={investor.id}>
                  {investor.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Статья">
          <input
            required
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={
              mode === "expenses" ? "Топливо, мойка, комиссия" : "Смена, аренда"
            }
          />
        </Field>
        <Field label="Сумма, ₽">
          <input
            required
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>
        <Field label="Дата">
          <input
            required
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </Field>
        {mode === "expenses" && (
          <Field label="Способ оплаты">
            <select
              required
              value={paymentMethod}
              onChange={(event) =>
                setPaymentMethod(event.target.value as PaymentMethod)
              }
            >
              {PAYMENT_METHODS.map((method) => (
                <option key={method}>{method}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Комментарий" wide>
          <textarea
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Необязательно"
          />
        </Field>
        <div className="modal-actions wide">
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button
            className="primary"
            disabled={
              (target === "vehicle" || mode === "income")
                ? !vehicleId
                : !investorId
            }
          >
            Сохранить
          </button>
        </div>
      </form>
    </Modal>
  );
}

function VehicleModal({
  vehicle,
  investors,
  onClose,
  onSave,
  onDelete,
}: {
  vehicle?: Vehicle;
  investors: Investor[];
  onClose: () => void;
  onSave: (v: Vehicle) => void;
  onDelete?: () => void;
}) {
  const [form, setForm] = useState({
    make: vehicle?.make || "",
    model: vehicle?.model || "",
    plate: vehicle?.plate || "",
    vin: vehicle?.vin || "",
    stsSeries: vehicle?.stsSeries || "",
    stsNumber: vehicle?.stsNumber || "",
    year: vehicle?.year?.toString() || "",
    mileage: vehicle?.mileage?.toString() || "0",
    nextMaintenanceMileage: vehicle?.nextMaintenanceMileage?.toString() || "",
    cabinFilterReplacementMileage: vehicle?.cabinFilterReplacementMileage?.toString() || "",
    airFilterReplacementMileage: vehicle?.airFilterReplacementMileage?.toString() || "",
    tireSeason: vehicle?.tireSeason || "",
    status: vehicle?.status || ("Работает" as VehicleStatus),
    investorId: vehicle?.investorId || "",
    insuranceCompany: vehicle?.insuranceCompany || "",
    insurancePolicyNumber: vehicle?.insurancePolicyNumber || "",
    insuranceExpiresAt: vehicle?.insuranceExpiresAt?.slice(0, 10) || "",
    driverName: vehicle?.driverName || "",
    driverPhone: vehicle?.driverPhone || "",
  });
  const save = (e: FormEvent) => {
    e.preventDefault();
    onSave({
      id: vehicle?.id || uid(),
      make: form.make.trim(),
      model: form.model.trim(),
      plate: form.plate.trim().toUpperCase(),
      vin: form.vin.trim().toUpperCase() || undefined,
      stsSeries: form.stsSeries.trim().toUpperCase() || undefined,
      stsNumber: form.stsNumber.trim().toUpperCase() || undefined,
      year: form.year ? Number(form.year) : undefined,
      mileage: Number(form.mileage) || 0,
      nextMaintenanceMileage: form.nextMaintenanceMileage
        ? Number(form.nextMaintenanceMileage)
        : undefined,
      cabinFilterReplacementMileage: form.cabinFilterReplacementMileage
        ? Number(form.cabinFilterReplacementMileage)
        : undefined,
      airFilterReplacementMileage: form.airFilterReplacementMileage
        ? Number(form.airFilterReplacementMileage)
        : undefined,
      tireSeason: form.tireSeason ? (form.tireSeason as TireSeason) : undefined,
      status: form.status,
      investorId: form.investorId || undefined,
      insuranceCompany: form.insuranceCompany.trim() || undefined,
      insurancePolicyNumber: form.insurancePolicyNumber.trim() || undefined,
      insuranceExpiresAt: form.insuranceExpiresAt || undefined,
      driverName: form.driverName.trim() || undefined,
      driverPhone: form.driverPhone.trim() || undefined,
      photoUrl: vehicle?.photoUrl,
      createdAt: vehicle?.createdAt || new Date().toISOString(),
    });
  };
  return (
    <Modal
      title={vehicle ? "Изменить автомобиль" : "Новый автомобиль"}
      onClose={onClose}
    >
      <form onSubmit={save} className="form-grid">
        <Field label="Марка">
          <input
            required
            value={form.make}
            onChange={(e) => setForm({ ...form, make: e.target.value })}
            placeholder="Skoda"
          />
        </Field>
        <Field label="Модель">
          <input
            required
            value={form.model}
            onChange={(e) => setForm({ ...form, model: e.target.value })}
            placeholder="Rapid"
          />
        </Field>
        <Field label="Госномер">
          <input
            required
            value={form.plate}
            onChange={(e) => setForm({ ...form, plate: e.target.value })}
            placeholder="А111АА39"
          />
        </Field>
        <Field label="Год">
          <input
            type="number"
            value={form.year}
            onChange={(e) => setForm({ ...form, year: e.target.value })}
            placeholder="2022"
          />
        </Field>
        <Field label="Пробег, км">
          <input
            required
            type="number"
            min="0"
            value={form.mileage}
            onChange={(e) => setForm({ ...form, mileage: e.target.value })}
          />
        </Field>
        <Field label="Следующее ТО, км">
          <input
            type="number"
            min="0"
            value={form.nextMaintenanceMileage}
            onChange={(e) =>
              setForm({ ...form, nextMaintenanceMileage: e.target.value })
            }
            placeholder="150000"
          />
        </Field>
        <Field label="Замена салонного фильтра, км">
          <input
            type="number"
            min="0"
            value={form.cabinFilterReplacementMileage}
            onChange={(e) => setForm({ ...form, cabinFilterReplacementMileage: e.target.value })}
            placeholder="Пробег последней замены"
          />
        </Field>
        <Field label="Замена воздушного фильтра, км">
          <input
            type="number"
            min="0"
            value={form.airFilterReplacementMileage}
            onChange={(e) => setForm({ ...form, airFilterReplacementMileage: e.target.value })}
            placeholder="Пробег последней замены"
          />
        </Field>
        <Field label="Установленная резина">
          <select
            value={form.tireSeason}
            onChange={(e) => setForm({ ...form, tireSeason: e.target.value })}
          >
            <option value="">Не указана</option>
            <option value="Летняя">Летняя</option>
            <option value="Зимняя">Зимняя</option>
          </select>
        </Field>
        <Field label="VIN" wide>
          <input
            value={form.vin}
            onChange={(e) => setForm({ ...form, vin: e.target.value })}
            placeholder="17 символов"
          />
        </Field>
        <Field label="Серия СТС">
          <input
            value={form.stsSeries}
            onChange={(e) => setForm({ ...form, stsSeries: e.target.value })}
            placeholder="39 00"
          />
        </Field>
        <Field label="Номер СТС">
          <input
            value={form.stsNumber}
            onChange={(e) => setForm({ ...form, stsNumber: e.target.value })}
            placeholder="123456"
          />
        </Field>
        <Field label="Текущий водитель">
          <input
            value={form.driverName}
            onChange={(e) => setForm({ ...form, driverName: e.target.value })}
            placeholder="ФИО водителя"
          />
        </Field>
        <Field label="Телефон водителя">
          <input
            type="tel"
            value={form.driverPhone}
            onChange={(e) => setForm({ ...form, driverPhone: e.target.value })}
            placeholder="+7 900 000-00-00"
          />
        </Field>
        <Field label="Страховая компания">
          <input
            value={form.insuranceCompany}
            onChange={(e) =>
              setForm({ ...form, insuranceCompany: e.target.value })
            }
            placeholder="Название компании"
          />
        </Field>
        <Field label="Номер страхового полиса">
          <input
            value={form.insurancePolicyNumber}
            onChange={(e) =>
              setForm({ ...form, insurancePolicyNumber: e.target.value })
            }
            placeholder="Серия и номер"
          />
        </Field>
        <Field label="Страховой полис действует до" wide>
          <input
            type="date"
            value={form.insuranceExpiresAt}
            onChange={(e) =>
              setForm({ ...form, insuranceExpiresAt: e.target.value })
            }
          />
        </Field>
        <Field label="Статус">
          <select
            value={form.status}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value as VehicleStatus })
            }
          >
            {(
              [
                "Работает",
                "На ТО",
                "Ремонт",
                "Простой",
                "Архив",
              ] as VehicleStatus[]
            ).map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="Инвестор">
          <select
            value={form.investorId}
            onChange={(e) => setForm({ ...form, investorId: e.target.value })}
          >
            <option value="">Не указан</option>
            {investors.map((i) => (
              <option value={i.id} key={i.id}>
                {i.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="modal-actions wide">
          {onDelete && (
            <button type="button" className="danger-button" onClick={onDelete}>
              Удалить
            </button>
          )}
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary">Сохранить</button>
        </div>
      </form>
    </Modal>
  );
}

function BicycleModal({
  bicycle,
  onClose,
  onSave,
  onDelete,
}: {
  bicycle?: Bicycle;
  onClose: () => void;
  onSave: (b: Bicycle) => void;
  onDelete?: () => void;
}) {
  const [form, setForm] = useState({
    brand: bicycle?.brand || "",
    model: bicycle?.model || "",
    inventoryNumber: bicycle?.inventoryNumber || "",
    serialNumber: bicycle?.serialNumber || "",
    batteryNumber: bicycle?.batteryNumber || "",
    chargerNumber: bicycle?.chargerNumber || "",
    year: bicycle?.year?.toString() || "",
    mileage: bicycle?.mileage?.toString() || "0",
    purchasePrice: bicycle?.purchasePrice?.toString() || "",
    status: bicycle?.status || ("Свободен" as BicycleStatus),
    currentRenter: bicycle?.currentRenter || "",
    rentalUntil: bicycle?.rentalUntil?.slice(0, 10) || "",
    rentalPlan: bicycle?.rentalPlan || ("Не уточнено" as BicycleRentalPlan),
    rentalPlanNote: bicycle?.rentalPlanNote || "",
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const previousRentalUntil = bicycle?.rentalUntil?.slice(0, 10) || "";
    const previousRentalPlan = bicycle?.rentalPlan || "Не уточнено";
    const rentalDateChanged = Boolean(bicycle) && form.rentalUntil !== previousRentalUntil;
    const rentalPlanChanged = form.rentalPlan !== previousRentalPlan;
    const shouldResetPlan = rentalDateChanged && !rentalPlanChanged;
    const nextRentalPlan = shouldResetPlan ? "Не уточнено" : form.rentalPlan;
    const now = new Date().toISOString();
    onSave({
      id: bicycle?.id || uid(),
      brand: form.brand.trim(),
      model: form.model.trim(),
      inventoryNumber: form.inventoryNumber.trim().toUpperCase(),
      serialNumber: form.serialNumber.trim() || undefined,
      batteryNumber: form.batteryNumber.trim() || undefined,
      chargerNumber: form.chargerNumber.trim() || undefined,
      year: form.year ? Number(form.year) : undefined,
      mileage: Number(form.mileage) || 0,
      purchasePrice: form.purchasePrice ? Number(form.purchasePrice) : undefined,
      status: form.status,
      currentRenter:
        form.status === "В аренде"
          ? form.currentRenter.trim() || undefined
          : undefined,
      rentalUntil:
        form.status === "В аренде" && form.rentalUntil
          ? new Date(`${form.rentalUntil}T12:00:00`).toISOString()
          : undefined,
      rentalPlan:
        form.status === "В аренде" ? nextRentalPlan : undefined,
      rentalPlanNote:
        form.status === "В аренде" && !shouldResetPlan
          ? form.rentalPlanNote.trim() || undefined
          : undefined,
      rentalPlanUpdatedAt:
        form.status === "В аренде" && (rentalPlanChanged || shouldResetPlan)
          ? now
          : form.status === "В аренде"
            ? bicycle?.rentalPlanUpdatedAt
            : undefined,
      createdAt: bicycle?.createdAt || now,
    });
  };
  return (
    <Modal
      title={bicycle ? "Изменить электровелосипед" : "Новый электровелосипед"}
      onClose={onClose}
    >
      <form onSubmit={submit} className="form-grid">
        <Field label="Марка">
          <input
            required
            value={form.brand}
            onChange={(e) => setForm({ ...form, brand: e.target.value })}
            placeholder="Kugoo"
          />
        </Field>
        <Field label="Модель">
          <input
            required
            value={form.model}
            onChange={(e) => setForm({ ...form, model: e.target.value })}
            placeholder="V1"
          />
        </Field>
        <Field label="Инвентарный номер">
          <input
            required
            value={form.inventoryNumber}
            onChange={(e) =>
              setForm({ ...form, inventoryNumber: e.target.value })
            }
            placeholder="EB-001"
          />
        </Field>
        <Field label="Год">
          <input
            type="number"
            value={form.year}
            onChange={(e) => setForm({ ...form, year: e.target.value })}
            placeholder="2026"
          />
        </Field>
        <Field label="Серийный номер">
          <input
            value={form.serialNumber}
            onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
          />
        </Field>
        <Field label="Номер аккумулятора">
          <input
            value={form.batteryNumber}
            onChange={(e) => setForm({ ...form, batteryNumber: e.target.value })}
            placeholder="Необязательно"
          />
        </Field>
        <Field label="Номер З/У">
          <input
            value={form.chargerNumber}
            onChange={(e) => setForm({ ...form, chargerNumber: e.target.value })}
            placeholder="Необязательно"
          />
        </Field>
        <Field label="Пробег, км">
          <input
            required
            type="number"
            min="0"
            value={form.mileage}
            onChange={(e) => setForm({ ...form, mileage: e.target.value })}
          />
        </Field>
        <Field label="Стоимость покупки, ₽">
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.purchasePrice}
            onChange={(e) => setForm({ ...form, purchasePrice: e.target.value })}
            placeholder="Необязательно"
          />
        </Field>
        <Field label="Статус">
          <select
            value={form.status}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value as BicycleStatus })
            }
          >
            {(
              [
                "Свободен",
                "В аренде",
                "На ТО",
                "Ремонт",
                "Архив",
              ] as BicycleStatus[]
            ).map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Field>
        {form.status === "В аренде" && (
          <>
            <Field label="Арендатор">
              <input
                required
                value={form.currentRenter}
                onChange={(e) =>
                  setForm({ ...form, currentRenter: e.target.value })
                }
              />
            </Field>
            <Field label="Оплачено до">
              <input
                type="date"
                value={form.rentalUntil}
                onChange={(e) =>
                  setForm({ ...form, rentalUntil: e.target.value })
                }
              />
            </Field>
            <Field label="После этой даты">
              <select
                value={form.rentalPlan}
                onChange={(e) =>
                  setForm({
                    ...form,
                    rentalPlan: e.target.value as BicycleRentalPlan,
                  })
                }
              >
                {BICYCLE_RENTAL_PLANS.map((plan) => (
                  <option key={plan}>{plan}</option>
                ))}
              </select>
            </Field>
            <Field label="Комментарий по планам">
              <input
                value={form.rentalPlanNote}
                onChange={(e) =>
                  setForm({ ...form, rentalPlanNote: e.target.value })
                }
                placeholder="Например: подтвердит 15 октября"
              />
            </Field>
          </>
        )}
        <div className="modal-actions wide">
          {onDelete && (
            <button type="button" className="danger-button" onClick={onDelete}>
              Удалить
            </button>
          )}
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary">Сохранить</button>
        </div>
      </form>
    </Modal>
  );
}

function RentalCarModal({
  car,
  onClose,
  onSave,
  onDelete,
}: {
  car?: RentalCar;
  onClose: () => void;
  onSave: (car: RentalCar) => void;
  onDelete?: () => void;
}) {
  const [form, setForm] = useState({
    make: car?.make || "",
    model: car?.model || "",
    plate: car?.plate || "",
    vin: car?.vin || "",
    stsSeries: car?.stsSeries || "",
    stsNumber: car?.stsNumber || "",
    year: car?.year?.toString() || "",
    mileage: car?.mileage?.toString() || "0",
    purchasePrice: car?.purchasePrice?.toString() || "",
    status: car?.status || ("Доступен" as RentalCarStatus),
    dailyRate: car?.dailyRate?.toString() || "",
    defaultDeposit: car?.defaultDeposit?.toString() || "",
    insuranceCompany: car?.insuranceCompany || "",
    insurancePolicyNumber: car?.insurancePolicyNumber || "",
    insuranceExpiresAt: car?.insuranceExpiresAt?.slice(0, 10) || "",
  });
  const [tiers, setTiers] = useState<RentalRateTier[]>(car?.rateTiers || []);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave({
      id: car?.id || uid(),
      make: form.make.trim(),
      model: form.model.trim(),
      plate: form.plate.trim().toUpperCase(),
      vin: form.vin.trim().toUpperCase() || undefined,
      stsSeries: form.stsSeries.trim().toUpperCase() || undefined,
      stsNumber: form.stsNumber.trim().toUpperCase() || undefined,
      year: form.year ? Number(form.year) : undefined,
      mileage: Number(form.mileage) || 0,
      purchasePrice: form.purchasePrice ? Number(form.purchasePrice) : undefined,
      status: form.status,
      dailyRate: Number(form.dailyRate) || 0,
      defaultDeposit: Number(form.defaultDeposit) || 0,
      insuranceCompany: form.insuranceCompany.trim() || undefined,
      insurancePolicyNumber: form.insurancePolicyNumber.trim() || undefined,
      insuranceExpiresAt: form.insuranceExpiresAt || undefined,
      rateTiers: tiers
        .filter((t) => t.minDays > 1 && t.dailyRate >= 0)
        .sort((a, b) => a.minDays - b.minDays),
      createdAt: car?.createdAt || new Date().toISOString(),
    });
  };
  return (
    <Modal
      title={car ? "Автомобиль проката" : "Новый автомобиль проката"}
      onClose={onClose}
    >
      <form onSubmit={submit} className="form-grid">
        <Field label="Марка">
          <input
            required
            value={form.make}
            onChange={(e) => setForm({ ...form, make: e.target.value })}
            placeholder="Hyundai"
          />
        </Field>
        <Field label="Модель">
          <input
            required
            value={form.model}
            onChange={(e) => setForm({ ...form, model: e.target.value })}
            placeholder="Solaris"
          />
        </Field>
        <Field label="Госномер">
          <input
            required
            value={form.plate}
            onChange={(e) => setForm({ ...form, plate: e.target.value })}
            placeholder="А111АА39"
          />
        </Field>
        <Field label="Год">
          <input
            type="number"
            min="1900"
            max="2100"
            value={form.year}
            onChange={(e) => setForm({ ...form, year: e.target.value })}
            placeholder="2024"
          />
        </Field>
        <Field label="Пробег, км">
          <input
            required
            type="number"
            min="0"
            value={form.mileage}
            onChange={(e) => setForm({ ...form, mileage: e.target.value })}
          />
        </Field>
        <Field label="Стоимость покупки, ₽">
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.purchasePrice}
            onChange={(e) => setForm({ ...form, purchasePrice: e.target.value })}
            placeholder="Необязательно"
          />
        </Field>
        <Field label="Статус">
          <select
            value={form.status}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value as RentalCarStatus })
            }
          >
            {(
              [
                "Доступен",
                "Забронирован",
                "В аренде",
                "Обслуживание",
                "Архив",
              ] as RentalCarStatus[]
            ).map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="Цена за 1 сутки, ₽">
          <input
            required
            type="number"
            min="0"
            step="0.01"
            value={form.dailyRate}
            onChange={(e) => setForm({ ...form, dailyRate: e.target.value })}
            placeholder="3000"
          />
        </Field>
        <Field label="Залог, ₽">
          <input
            required
            type="number"
            min="0"
            step="0.01"
            value={form.defaultDeposit}
            onChange={(e) =>
              setForm({ ...form, defaultDeposit: e.target.value })
            }
            placeholder="10000"
          />
        </Field>
        <Field label="VIN" wide>
          <input
            value={form.vin}
            onChange={(e) => setForm({ ...form, vin: e.target.value })}
            placeholder="17 символов"
          />
        </Field>
        <Field label="Серия СТС">
          <input
            value={form.stsSeries}
            onChange={(e) => setForm({ ...form, stsSeries: e.target.value })}
            placeholder="39 00"
          />
        </Field>
        <Field label="Номер СТС">
          <input
            value={form.stsNumber}
            onChange={(e) => setForm({ ...form, stsNumber: e.target.value })}
            placeholder="123456"
          />
        </Field>
        <Field label="Страховая компания">
          <input
            value={form.insuranceCompany}
            onChange={(e) =>
              setForm({ ...form, insuranceCompany: e.target.value })
            }
            placeholder="Название компании"
          />
        </Field>
        <Field label="Номер страхового полиса">
          <input
            value={form.insurancePolicyNumber}
            onChange={(e) =>
              setForm({ ...form, insurancePolicyNumber: e.target.value })
            }
            placeholder="Серия и номер"
          />
        </Field>
        <Field label="Страховой полис действует до" wide>
          <input
            type="date"
            value={form.insuranceExpiresAt}
            onChange={(e) =>
              setForm({ ...form, insuranceExpiresAt: e.target.value })
            }
          />
        </Field>
        <div className="rate-editor wide">
          <div className="rate-editor-head">
            <div>
              <b>Градация стоимости</b>
              <p>
                Цена за сутки автоматически меняется в зависимости от срока
                аренды.
              </p>
            </div>
            <button
              type="button"
              className="secondary tiny"
              onClick={() =>
                setTiers([
                  ...tiers,
                  {
                    id: uid(),
                    minDays: tiers.length
                      ? Math.max(...tiers.map((t) => t.minDays)) + 1
                      : 3,
                    dailyRate: Number(form.dailyRate) || 0,
                  },
                ])
              }
            >
              ＋ Добавить
            </button>
          </div>
          <div className="rate-base">
            <span>От 1 суток</span>
            <b>{money(Number(form.dailyRate) || 0)}/сут.</b>
          </div>
          {tiers.map((tier, index) => (
            <div className="rate-tier" key={tier.id}>
              <label>
                От{" "}
                <input
                  type="number"
                  min="2"
                  value={tier.minDays}
                  onChange={(e) =>
                    setTiers(
                      tiers.map((t, i) =>
                        i === index
                          ? { ...t, minDays: Number(e.target.value) || 2 }
                          : t,
                      ),
                    )
                  }
                />{" "}
                суток
              </label>
              <label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tier.dailyRate}
                  onChange={(e) =>
                    setTiers(
                      tiers.map((t, i) =>
                        i === index
                          ? { ...t, dailyRate: Number(e.target.value) || 0 }
                          : t,
                      ),
                    )
                  }
                />{" "}
                ₽/сут.
              </label>
              <button
                type="button"
                onClick={() => setTiers(tiers.filter((_, i) => i !== index))}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <div className="modal-actions wide">
          {onDelete && (
            <button type="button" className="danger-button" onClick={onDelete}>
              Удалить
            </button>
          )}
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary">Сохранить</button>
        </div>
      </form>
    </Modal>
  );
}

function RentalBookingModal({
  booking,
  bookings,
  car,
  onClose,
  onSave,
  onDelete,
}: {
  booking?: RentalBooking;
  bookings: RentalBooking[];
  car: RentalCar;
  onClose: () => void;
  onSave: (booking: RentalBooking) => void;
  onDelete?: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    clientName: booking?.clientName || "",
    clientPhone: booking?.clientPhone || "",
    startDate: booking?.startDate.slice(0, 10) || today,
    endDate: booking?.endDate.slice(0, 10) || tomorrow,
    pickupTime: booking?.pickupTime || "10:00",
    returnTime: booking?.returnTime || "10:00",
    dailyRate: String(booking?.dailyRate ?? rentalRate(car, 1)),
    deposit: String(booking?.deposit ?? car.defaultDeposit),
    status: booking?.status || ("Бронь" as RentalBookingStatus),
    notes: booking?.notes || "",
  });
  const days = rentalDays(form.startDate, form.endDate);
  const setDates = (patch: Partial<typeof form>) => {
    const next = { ...form, ...patch };
    const nextDays = rentalDays(next.startDate, next.endDate);
    setForm({ ...next, dailyRate: String(rentalRate(car, nextDays)) });
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const start = bookingMoment(form.startDate, form.pickupTime, "10:00");
    const end = bookingMoment(form.endDate, form.returnTime, "10:00");
    if (end <= start) {
      alert("Возврат должен быть позже выдачи");
      return;
    }
    const conflict = bookings.find(
      (b) =>
        b.rentalCarId === car.id &&
        b.id !== booking?.id &&
        !["Отменён", "Завершён"].includes(b.status) &&
        start < bookingMoment(b.endDate, b.returnTime, "23:59") &&
        end > bookingMoment(b.startDate, b.pickupTime, "00:00"),
    );
    if (conflict) {
      alert(`Эти даты пересекаются с бронью клиента «${conflict.clientName}»`);
      return;
    }
    onSave({
      id: booking?.id || uid(),
      rentalCarId: car.id,
      clientName: form.clientName.trim(),
      clientPhone: form.clientPhone.trim() || undefined,
      startDate: new Date(`${form.startDate}T12:00:00`).toISOString(),
      endDate: new Date(`${form.endDate}T12:00:00`).toISOString(),
      pickupTime: form.pickupTime,
      returnTime: form.returnTime,
      dailyRate: Number(form.dailyRate) || 0,
      deposit: Number(form.deposit) || 0,
      status: form.status,
      notes: form.notes.trim() || undefined,
      createdAt: booking?.createdAt || new Date().toISOString(),
    });
  };
  return (
    <Modal
      title={booking ? "Бронирование" : "Новое бронирование"}
      onClose={onClose}
    >
      <form onSubmit={submit} className="form-grid">
        <Field label="Клиент" wide>
          <input
            required
            value={form.clientName}
            onChange={(e) => setForm({ ...form, clientName: e.target.value })}
            placeholder="Имя клиента"
          />
        </Field>
        <Field label="Телефон">
          <input
            value={form.clientPhone}
            onChange={(e) => setForm({ ...form, clientPhone: e.target.value })}
            placeholder="+7 900 000-00-00"
          />
        </Field>
        <Field label="Статус">
          <select
            value={form.status}
            onChange={(e) =>
              setForm({
                ...form,
                status: e.target.value as RentalBookingStatus,
              })
            }
          >
            {(
              ["Бронь", "Выдан", "Завершён", "Отменён"] as RentalBookingStatus[]
            ).map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="Дата выдачи">
          <input
            required
            type="date"
            value={form.startDate}
            onChange={(e) => setDates({ startDate: e.target.value })}
          />
        </Field>
        <Field label="Время выдачи">
          <input
            required
            type="time"
            value={form.pickupTime}
            onChange={(e) => setForm({ ...form, pickupTime: e.target.value })}
          />
        </Field>
        <Field label="Дата возврата">
          <input
            required
            type="date"
            min={form.startDate}
            value={form.endDate}
            onChange={(e) => setDates({ endDate: e.target.value })}
          />
        </Field>
        <Field label="Время возврата">
          <input
            required
            type="time"
            value={form.returnTime}
            onChange={(e) => setForm({ ...form, returnTime: e.target.value })}
          />
        </Field>
        <Field label="Тариф, ₽/сутки">
          <input
            required
            type="number"
            min="0"
            step="0.01"
            value={form.dailyRate}
            onChange={(e) => setForm({ ...form, dailyRate: e.target.value })}
          />
        </Field>
        <Field label="Залог, ₽">
          <input
            required
            type="number"
            min="0"
            step="0.01"
            value={form.deposit}
            onChange={(e) => setForm({ ...form, deposit: e.target.value })}
          />
        </Field>
        <Field label="Комментарий" wide>
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Условия, пожелания, детали выдачи"
          />
        </Field>
        <div className="booking-total wide">
          <span>{days} сут.</span>
          <strong>Итого: {money(days * (Number(form.dailyRate) || 0))}</strong>
          <em>
            {money(Number(form.dailyRate) || 0)}/сут. · залог{" "}
            {money(Number(form.deposit) || 0)}
          </em>
        </div>
        <div className="modal-actions wide">
          {onDelete && (
            <button
              type="button"
              className="danger-button"
              onClick={() => confirm("Удалить бронирование?") && onDelete()}
            >
              Удалить
            </button>
          )}
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary">Сохранить</button>
        </div>
      </form>
    </Modal>
  );
}

function RentalEventModal({
  initialType,
  event,
  car,
  userId,
  onClose,
  onSave,
}: {
  initialType: RentalEventType;
  event?: RentalEvent;
  car: RentalCar;
  userId: string;
  onClose: () => void;
  onSave: (event: RentalEvent, patch: Partial<RentalCar>) => void;
}) {
  const [type, setType] = useState<RentalEventType>(event?.type || initialType);
  const [title, setTitle] = useState(
    event?.title || (initialType === "mileage"
      ? "Обновление пробега"
      : RENTAL_EVENT_LABELS[initialType]),
  );
  const [amount, setAmount] = useState(
    typeof event?.amount === "number" ? String(event.amount) : "",
  );
  const [mileage, setMileage] = useState(
    String(event?.mileage ?? car.mileage),
  );
  const [date, setDate] = useState(
    event?.date.slice(0, 10) || new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState(event?.notes || "");
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>(event?.paymentMethod || "СБЕР");
  const [expires, setExpires] = useState(
    event?.documentExpiresAt?.slice(0, 10) || "",
  );
  const [file, setFile] = useState<File | undefined>();
  const [uploading, setUploading] = useState(false);
  const moneyType = ["income", "expense", "repair", "maintenance"].includes(
    type,
  );
  const expenseType = ["expense", "repair", "maintenance"].includes(type);
  const fileType = ["document", "damage"].includes(type);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setUploading(true);
    let filePath = event?.filePath;
    let fileName = event?.fileName;
    if (file && supabase) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      filePath = `${userId}/rentals/${car.id}/${uid()}-${safeName}`;
      const { error } = await supabase.storage
        .from("voyager-files")
        .upload(filePath, file, { contentType: file.type, upsert: false });
      if (error) {
        alert(`Не удалось загрузить файл: ${error.message}`);
        setUploading(false);
        return;
      }
      fileName = file.name;
      if (event?.filePath && event.filePath !== filePath)
        void removePrivateFile(event.filePath);
    }
    const savedEvent: RentalEvent = {
      ...event,
      id: event?.id || uid(),
      rentalCarId: car.id,
      type,
      title: title.trim() || RENTAL_EVENT_LABELS[type],
      amount: moneyType ? Number(amount) || 0 : undefined,
      paymentMethod: expenseType ? paymentMethod : undefined,
      mileage: ["mileage", "maintenance"].includes(type)
        ? Number(mileage) || car.mileage
        : undefined,
      date: new Date(`${date}T12:00:00`).toISOString(),
      notes: notes.trim() || undefined,
      fileName,
      filePath,
      documentExpiresAt:
        type === "document" && expires
          ? new Date(`${expires}T12:00:00`).toISOString()
          : undefined,
      createdAt: event?.createdAt || new Date().toISOString(),
    };
    const patch: Partial<RentalCar> = {};
    if (type === "mileage" || type === "maintenance")
      patch.mileage = Number(mileage) || car.mileage;
    if (type === "maintenance") patch.status = "Доступен";
    if (type === "repair") patch.status = "Обслуживание";
    onSave(savedEvent, patch);
  };
  return (
    <Modal
      title={event ? "Изменить событие автомобиля проката" : "Событие автомобиля проката"}
      onClose={onClose}
    >
      <form onSubmit={submit} className="event-form">
        <div className="event-types">
          {(
            [
              "income",
              "expense",
              "repair",
              "maintenance",
              "mileage",
              "document",
              "damage",
              "note",
            ] as RentalEventType[]
          ).map((item) => (
            <button
              type="button"
              key={item}
              disabled={Boolean(event)}
              className={type === item ? "active" : ""}
              onClick={() => {
                setType(item);
                setTitle(RENTAL_EVENT_LABELS[item]);
              }}
            >
              <span>{RENTAL_EVENT_ICONS[item]}</span>
              {RENTAL_EVENT_LABELS[item]}
            </button>
          ))}
        </div>
        <Field label="Название">
          <input
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        {moneyType && (
          <Field label="Сумма, ₽">
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field>
        )}
        {expenseType && (
          <Field label="Способ оплаты">
            <select
              required
              value={paymentMethod}
              onChange={(e) =>
                setPaymentMethod(e.target.value as PaymentMethod)
              }
            >
              {PAYMENT_METHODS.map((method) => (
                <option key={method}>{method}</option>
              ))}
            </select>
          </Field>
        )}
        {["mileage", "maintenance"].includes(type) && (
          <Field label="Пробег, км">
            <input
              required
              type="number"
              min={event ? 0 : car.mileage}
              value={mileage}
              onChange={(e) => setMileage(e.target.value)}
            />
          </Field>
        )}
        <Field label="Дата">
          <input
            required
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        {type === "document" && (
          <Field label="Действует до">
            <input
              type="date"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
            />
          </Field>
        )}
        {fileType && (
          <Field
            label={type === "damage" ? "Фото повреждения" : "Файл документа"}
          >
            <input
              required={!event?.filePath}
              type="file"
              accept={
                type === "damage"
                  ? "image/*"
                  : ".pdf,.doc,.docx,.jpg,.jpeg,.png"
              }
              onChange={(e) => setFile(e.target.files?.[0])}
            />
          </Field>
        )}
        <Field label="Комментарий">
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Необязательно"
          />
        </Field>
        <div className="modal-actions">
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary" disabled={uploading}>
            {uploading ? "Загрузка…" : "Сохранить"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function BicycleEventModal({
  initialType,
  event,
  renewal = false,
  rentalEvents,
  bicycle,
  userId,
  onClose,
  onSave,
}: {
  initialType: BicycleEventType;
  event?: BicycleEvent;
  renewal?: boolean;
  rentalEvents: BicycleEvent[];
  bicycle: Bicycle;
  userId: string;
  onClose: () => void;
  onSave: (event: BicycleEvent, patch: Partial<Bicycle>) => void;
}) {
  const previousRental = currentBicycleRental(bicycle, rentalEvents);
  const continuingRental = !event && bicycle.status === "В аренде";
  const renewalFrom = bicycle.rentalUntil?.slice(0, 10) || new Date().toISOString().slice(0, 10);
  const [type, setType] = useState<BicycleEventType>(event?.type || initialType);
  const [title, setTitle] = useState(
    event?.title || (renewal ? "Продление аренды" : initialType === "mileage"
      ? "Обновление пробега"
      : BICYCLE_EVENT_LABELS[initialType]),
  );
  const [amount, setAmount] = useState(
    typeof event?.amount === "number" ? String(event.amount)
      : renewal && typeof previousRental.amount === "number" ? String(previousRental.amount) : "",
  );
  const [mileage, setMileage] = useState(
    String(event?.mileage ?? bicycle.mileage),
  );
  const [date, setDate] = useState(
    event?.date.slice(0, 10) || new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState(event?.notes || "");
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>(event?.paymentMethod || "СБЕР");
  const [renter, setRenter] = useState(
    event?.renter || bicycle.currentRenter || "",
  );
  const [renterPhone, setRenterPhone] = useState(event?.renterPhone ?? (continuingRental ? previousRental.phone : ""));
  const [rentalPaymentMethod, setRentalPaymentMethod] =
    useState<BicycleRentalPaymentMethod>(event?.rentalPaymentMethod || (continuingRental ? previousRental.paymentMethod : "Наличные"));
  const [renewalDays, setRenewalDays] = useState("7");
  const [rentalUntil, setRentalUntil] = useState(
    renewal ? addBicycleRentalDays(renewalFrom, 7)
      : event?.rentalUntil?.slice(0, 10) || bicycle.rentalUntil?.slice(0, 10) || "",
  );
  const [expires, setExpires] = useState(
    event?.documentExpiresAt?.slice(0, 10) || "",
  );
  const [file, setFile] = useState<File | undefined>();
  const [uploading, setUploading] = useState(false);
  const savingRentalEvent = useRef(false);
  const moneyType = [
    "rental",
    "income",
    "expense",
    "repair",
    "maintenance",
  ].includes(type);
  const expenseType = ["expense", "repair", "maintenance"].includes(type);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (uploading || savingRentalEvent.current) return;
    if (renewal && (!Number.isInteger(Number(renewalDays)) || Number(renewalDays) < 1 || Number(renewalDays) > 3650 || !rentalUntil || rentalUntil <= renewalFrom)) {
      alert("Укажите срок продления не менее одного дня. Новая дата должна быть позже предыдущей даты оплаты.");
      return;
    }
    savingRentalEvent.current = true;
    setUploading(true);
    let filePath = event?.filePath;
    let fileName = event?.fileName;
    if (file && supabase) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      filePath = `${userId}/bicycles/${bicycle.id}/${uid()}-${safeName}`;
      const { error } = await supabase.storage
        .from("voyager-files")
        .upload(filePath, file, { contentType: file.type, upsert: false });
      if (error) {
        alert(`Не удалось загрузить файл: ${error.message}`);
        setUploading(false);
        savingRentalEvent.current = false;
        return;
      }
      fileName = file.name;
      if (event?.filePath && event.filePath !== filePath)
        void removePrivateFile(event.filePath);
    }
    const savedEvent: BicycleEvent = {
      ...event,
      id: event?.id || uid(),
      bicycleId: bicycle.id,
      type,
      title: title.trim() || BICYCLE_EVENT_LABELS[type],
      amount: moneyType ? Number(amount) || 0 : undefined,
      paymentMethod: expenseType ? paymentMethod : undefined,
      mileage: ["mileage", "maintenance"].includes(type)
        ? Number(mileage) || bicycle.mileage
        : undefined,
      date: new Date(`${date}T12:00:00`).toISOString(),
      notes: notes.trim() || undefined,
      renter: type === "rental" ? renter.trim() : undefined,
      renterPhone: type === "rental" ? renterPhone.trim() || undefined : undefined,
      rentalUntil:
        type === "rental" && rentalUntil
          ? new Date(`${rentalUntil}T12:00:00`).toISOString()
          : undefined,
      rentalPaymentMethod: type === "rental" ? rentalPaymentMethod : undefined,
      bicycleModel:
        type === "rental" ? event?.bicycleModel || bicycle.model : undefined,
      bicycleInventoryNumber:
        type === "rental"
          ? event?.bicycleInventoryNumber || bicycle.inventoryNumber
          : undefined,
      fileName,
      filePath,
      documentExpiresAt:
        type === "document" && expires
          ? new Date(`${expires}T12:00:00`).toISOString()
          : undefined,
      createdAt: event?.createdAt || new Date().toISOString(),
    };
    const patch: Partial<Bicycle> = {};
    if (type === "mileage" || type === "maintenance")
      patch.mileage = Number(mileage) || bicycle.mileage;
    if (type === "maintenance") patch.status = "Свободен";
    if (type === "repair") patch.status = "Ремонт";
    if (type === "rental") {
      patch.status = "В аренде";
      patch.currentRenter = renter.trim();
      patch.rentalUntil = savedEvent.rentalUntil;
      if (!event || savedEvent.rentalUntil !== bicycle.rentalUntil) {
        patch.rentalPlan = "Не уточнено";
        patch.rentalPlanNote = undefined;
        patch.rentalPlanUpdatedAt = new Date().toISOString();
      }
    }
    onSave(savedEvent, patch);
  };
  return (
    <Modal
      title={renewal ? `Продлить аренду · ${bicycle.inventoryNumber}` : event ? "Изменить событие электровелосипеда" : "Событие электровелосипеда"}
      onClose={onClose}
    >
      <form onSubmit={submit} className="event-form">
        {!renewal && <div className="event-types">
          {(
            [
              "rental",
              "income",
              "expense",
              "repair",
              "maintenance",
              "mileage",
              "document",
              "note",
            ] as BicycleEventType[]
          ).map((item) => (
            <button
              type="button"
              key={item}
              disabled={Boolean(event)}
              className={type === item ? "active" : ""}
              onClick={() => {
                setType(item);
                setTitle(BICYCLE_EVENT_LABELS[item]);
              }}
            >
              <span>{BICYCLE_EVENT_ICONS[item]}</span>
              {BICYCLE_EVENT_LABELS[item]}
            </button>
          ))}
        </div>}
        {renewal && <p className="bicycle-renewal-note">
          Продление от {shortDate(renewalFrom)}. Проверьте сумму и срок:
          сумма указывается за весь новый период. При сохранении будет добавлена
          новая оплата, а дата «Оплачено до» обновится.
        </p>}
        {!renewal && <Field label="Название">
          <input
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>}
        {moneyType && (
          <Field label="Сумма, ₽">
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field>
        )}
        {expenseType && (
          <Field label="Способ оплаты">
            <select
              required
              value={paymentMethod}
              onChange={(e) =>
                setPaymentMethod(e.target.value as PaymentMethod)
              }
            >
              {PAYMENT_METHODS.map((method) => (
                <option key={method}>{method}</option>
              ))}
            </select>
          </Field>
        )}
        {type === "rental" && (
          <>
            <Field label="Арендатор">
              <input
                required
                value={renter}
                onChange={(e) => {
                  setRenter(e.target.value);
                  if (e.target.value.trim().replace(/\s+/g, " ").toLocaleLowerCase("ru-RU") !== renter.trim().replace(/\s+/g, " ").toLocaleLowerCase("ru-RU")) {
                    setRenterPhone("");
                  }
                }}
              />
            </Field>
            <Field label="Номер телефона">
              <input
                required
                type="tel"
                value={renterPhone}
                onChange={(e) => setRenterPhone(e.target.value)}
              />
            </Field>
            <Field label="Способ оплаты">
              <select
                required
                value={rentalPaymentMethod}
                onChange={(e) =>
                  setRentalPaymentMethod(e.target.value as BicycleRentalPaymentMethod)
                }
              >
                {BICYCLE_RENTAL_PAYMENT_METHODS.map((method) => (
                  <option key={method}>{method}</option>
                ))}
              </select>
            </Field>
            {renewal && <Field label="Продлить на, дней">
              <input
                required
                type="number"
                min="1"
                max="3650"
                step="1"
                value={renewalDays}
                onChange={(e) => {
                  const days = Number(e.target.value);
                  setRenewalDays(e.target.value);
                  if (Number.isInteger(days) && days >= 1 && days <= 3650) {
                    setRentalUntil(addBicycleRentalDays(renewalFrom, days));
                  }
                }}
              />
            </Field>}
            <Field label="Оплачено до">
              <input
                required={renewal}
                type="date"
                min={renewal ? addBicycleRentalDays(renewalFrom, 1) : undefined}
                value={rentalUntil}
                onChange={(e) => {
                  const nextDate = e.target.value;
                  setRentalUntil(nextDate);
                  if (renewal) {
                    const days = Math.round((Date.parse(`${nextDate}T12:00:00Z`) - Date.parse(`${renewalFrom}T12:00:00Z`)) / 86400000);
                    setRenewalDays(Number.isFinite(days) ? String(days) : "");
                  }
                }}
              />
            </Field>
          </>
        )}
        {["mileage", "maintenance"].includes(type) && (
          <Field label="Пробег, км">
            <input
              required
              type="number"
              min={event ? 0 : bicycle.mileage}
              value={mileage}
              onChange={(e) => setMileage(e.target.value)}
            />
          </Field>
        )}
        <Field label={renewal ? "Дата оплаты" : "Дата"}>
          <input
            required
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        {type === "document" && (
          <>
            <Field label="Действует до">
              <input
                type="date"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
              />
            </Field>
            <Field label="Файл">
              <input
                required={!event?.filePath}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files?.[0])}
              />
            </Field>
          </>
        )}
        <Field label="Комментарий">
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        <div className="modal-actions">
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary" disabled={uploading}>
            {uploading ? "Сохраняем…" : renewal ? "Сохранить и продлить" : "Сохранить"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EventModal({
  initialType,
  event,
  vehicle,
  investor,
  userId,
  onClose,
  onSave,
}: {
  initialType: EventType;
  event?: VoyagerEvent;
  vehicle: Vehicle;
  investor?: Investor;
  userId: string;
  onClose: () => void;
  onSave: (event: VoyagerEvent, patch: Partial<Vehicle>) => void;
}) {
  const [type, setType] = useState<EventType>(event?.type || initialType);
  const [title, setTitle] = useState(
    event?.title || (initialType === "mileage" ? "Обновление пробега" : ""),
  );
  const [amount, setAmount] = useState(
    typeof event?.amount === "number" ? String(event.amount) : "",
  );
  const [mileage, setMileage] = useState(
    String(event?.mileage ?? vehicle.mileage),
  );
  const [date, setDate] = useState(
    event?.date.slice(0, 10) || new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState(event?.notes || "");
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>(event?.paymentMethod || "СБЕР");
  const [expenseScope, setExpenseScope] = useState<
    "vehicle" | "investor_personal"
  >(event?.expenseScope || "vehicle");
  const [expires, setExpires] = useState(
    event?.documentExpiresAt?.slice(0, 10) || "",
  );
  const [file, setFile] = useState<File | undefined>();
  const [uploading, setUploading] = useState(false);
  const moneyType = ["income", "expense", "repair", "maintenance"].includes(
    type,
  );
  const expenseType = ["expense", "repair", "maintenance"].includes(type);
  const fileType = ["document", "photo"].includes(type);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setUploading(true);
    let filePath = event?.filePath;
    let fileName = event?.fileName;
    if (file && supabase) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      filePath = `${userId}/${vehicle.id}/${uid()}-${safeName}`;
      const { error } = await supabase.storage
        .from("voyager-files")
        .upload(filePath, file, { contentType: file.type, upsert: false });
      if (error) {
        alert(`Не удалось загрузить файл: ${error.message}`);
        setUploading(false);
        return;
      }
      fileName = file.name;
      if (event?.filePath && event.filePath !== filePath)
        void removePrivateFile(event.filePath);
    }
    const savedEvent: VoyagerEvent = {
      ...event,
      id: event?.id || uid(),
      vehicleId: vehicle.id,
      type,
      title: title.trim() || EVENT_LABELS[type],
      amount: moneyType ? Number(amount) || 0 : undefined,
      expenseScope: type === "expense" ? expenseScope : undefined,
      investorId:
        type === "expense" && expenseScope === "investor_personal"
          ? investor?.id
          : undefined,
      paymentMethod: expenseType ? paymentMethod : undefined,
      mileage: ["mileage", "maintenance"].includes(type)
        ? Number(mileage) || vehicle.mileage
        : undefined,
      date: new Date(`${date}T12:00:00`).toISOString(),
      notes: notes.trim() || undefined,
      fileName,
      filePath,
      documentExpiresAt:
        type === "document" && expires
          ? new Date(`${expires}T12:00:00`).toISOString()
          : undefined,
      paid: true,
      createdAt: event?.createdAt || new Date().toISOString(),
    };
    const patch: Partial<Vehicle> = {};
    if (type === "mileage") patch.mileage = Number(mileage) || vehicle.mileage;
    if (type === "maintenance") {
      patch.mileage = Number(mileage) || vehicle.mileage;
      patch.nextMaintenanceMileage =
        (Number(mileage) || vehicle.mileage) + 10000;
      patch.status = "Работает";
    }
    onSave(savedEvent, patch);
  };
  return (
    <Modal title={event ? "Изменить событие" : "Добавить событие"} onClose={onClose}>
      <form onSubmit={submit} className="event-form">
        <div className="event-types">
          {(
            [
              "income",
              "expense",
              "repair",
              "maintenance",
              "mileage",
              "document",
              "photo",
              "note",
            ] as EventType[]
          ).map((item) => (
            <button
              type="button"
              key={item}
              disabled={Boolean(event)}
              className={type === item ? "active" : ""}
              onClick={() => {
                setType(item);
                if (!title || title === EVENT_LABELS[type])
                  setTitle(EVENT_LABELS[item]);
              }}
            >
              <span>{EVENT_ICONS[item]}</span>
              {EVENT_LABELS[item]}
            </button>
          ))}
        </div>
        <Field label="Название">
          <input
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={
              type === "repair"
                ? "Замена стойки"
                : type === "maintenance"
                  ? "Плановое ТО"
                  : EVENT_LABELS[type]
            }
          />
        </Field>
        {moneyType && (
          <Field label="Сумма, ₽">
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
            />
          </Field>
        )}
        {expenseType && (
          <Field label="Способ оплаты">
            <select
              required
              value={paymentMethod}
              onChange={(e) =>
                setPaymentMethod(e.target.value as PaymentMethod)
              }
            >
              {PAYMENT_METHODS.map((method) => (
                <option key={method}>{method}</option>
              ))}
            </select>
          </Field>
        )}
        {type === "expense" && investor && (
          <Field label="Тип расхода">
            <select
              value={expenseScope}
              onChange={(e) =>
                setExpenseScope(
                  e.target.value as "vehicle" | "investor_personal",
                )
              }
            >
              <option value="vehicle">Расход автомобиля</option>
              <option value="investor_personal">
                Личный расход инвестора — {investor.name}
              </option>
            </select>
          </Field>
        )}
        {["mileage", "maintenance"].includes(type) && (
          <Field label="Пробег, км">
            <input
              required
              type="number"
              min={event ? 0 : vehicle.mileage}
              value={mileage}
              onChange={(e) => setMileage(e.target.value)}
            />
          </Field>
        )}
        <Field label="Дата">
          <input
            required
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        {type === "document" && (
          <Field label="Действует до">
            <input
              type="date"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
            />
          </Field>
        )}
        {fileType && (
          <Field label={type === "photo" ? "Фотография" : "Файл документа"}>
            <input
              required={!event?.filePath}
              type="file"
              accept={
                type === "photo" ? "image/*" : ".pdf,.doc,.docx,.jpg,.jpeg,.png"
              }
              onChange={(e) => setFile(e.target.files?.[0])}
            />
          </Field>
        )}
        <Field label="Комментарий">
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Необязательно"
          />
        </Field>
        <div className="modal-actions">
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary" disabled={uploading}>
            {uploading ? "Загрузка…" : "Сохранить"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InvestorModal({
  investor,
  onClose,
  onSave,
  onDelete,
}: {
  investor?: Investor;
  onClose: () => void;
  onSave: (i: Investor) => void;
  onDelete?: () => void;
}) {
  const [form, setForm] = useState({
    name: investor?.name || "",
    phone: investor?.phone || "",
    email: investor?.email || "",
    commissionPercent: String(investor?.commissionPercent ?? 50),
    commissionBase:
      investor?.commissionBase || ("profit" as "profit" | "revenue"),
    personalExpenses: String(investor?.personalExpenses || 0),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave({
      id: investor?.id || uid(),
      name: form.name.trim(),
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      commissionPercent: Number(form.commissionPercent) || 0,
      commissionBase: form.commissionBase,
      personalExpenses: Number(form.personalExpenses) || 0,
      createdAt: investor?.createdAt || new Date().toISOString(),
    });
  };
  return (
    <Modal
      title={investor ? "Условия инвестора" : "Новый инвестор"}
      onClose={onClose}
    >
      <form onSubmit={submit} className="form-grid">
        <Field label="Имя" wide>
          <input
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Иван Петров"
          />
        </Field>
        <Field label="Телефон">
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label="Доля инвестора, %">
          <input
            type="number"
            min="0"
            max="100"
            value={form.commissionPercent}
            onChange={(e) =>
              setForm({ ...form, commissionPercent: e.target.value })
            }
          />
        </Field>
        <Field label="База расчёта">
          <select
            value={form.commissionBase}
            onChange={(e) =>
              setForm({
                ...form,
                commissionBase: e.target.value as "profit" | "revenue",
              })
            }
          >
            <option value="profit">Прибыль после расходов</option>
            <option value="revenue">Выручка</option>
          </select>
        </Field>
        <Field label="Начальные личные расходы, ₽" wide>
          <input
            type="number"
            min="0"
            value={form.personalExpenses}
            onChange={(e) =>
              setForm({ ...form, personalExpenses: e.target.value })
            }
          />
        </Field>
        <div className="modal-actions wide">
          {onDelete && (
            <button
              type="button"
              className="danger-button"
              onClick={() => confirm("Удалить инвестора?") && onDelete()}
            >
              Удалить
            </button>
          )}
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary">Сохранить</button>
        </div>
      </form>
    </Modal>
  );
}

function InvestorPaymentModal({
  investorId,
  payment,
  onClose,
  onSave,
  onDelete,
}: {
  investorId: string;
  payment?: InvestorPayment;
  onClose: () => void;
  onSave: (payment: InvestorPayment) => void;
  onDelete?: () => void;
}) {
  const [amount, setAmount] = useState(
    payment ? String(payment.amount) : "",
  );
  const [date, setDate] = useState(
    payment?.date.slice(0, 10) || new Date().toISOString().slice(0, 10),
  );
  const [periodMonth, setPeriodMonth] = useState(
    payment?.periodMonth || payment?.date.slice(0, 7) || previousCalendarMonthKey(),
  );
  const [notes, setNotes] = useState(payment?.notes || "");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave({
      id: payment?.id || uid(),
      investorId,
      amount: Number(amount) || 0,
      date: new Date(`${date}T12:00:00`).toISOString(),
      periodMonth,
      notes: notes.trim() || undefined,
      createdAt: payment?.createdAt || new Date().toISOString(),
    });
  };
  return (
    <Modal
      title={payment ? "Изменить выплату инвестору" : "Записать выплату инвестору"}
      onClose={onClose}
    >
      <form onSubmit={submit} className="form-grid">
        <Field label="Сумма, ₽">
          <input
            required
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field label="Дата">
          <input
            required
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field label="За расчётный месяц">
          <input
            required
            type="month"
            value={periodMonth}
            onChange={(e) => setPeriodMonth(e.target.value)}
          />
        </Field>
        <Field label="Комментарий" wide>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Например, перевод за июль"
          />
        </Field>
        <div className="modal-actions wide">
          {onDelete ? (
            <button type="button" className="danger-button" onClick={onDelete}>
              Удалить выплату
            </button>
          ) : (
            <span />
          )}
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary">
            {payment ? "Сохранить изменения" : "Сохранить выплату"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ReferenceModal({
  item,
  onClose,
  onSave,
  onDelete,
}: {
  item?: ReferenceItem;
  onClose: () => void;
  onSave: (item: ReferenceItem) => void;
  onDelete?: () => void;
}) {
  const [form, setForm] = useState({
    kind: item?.kind || ("table" as ReferenceItem["kind"]),
    title: item?.title || "",
    category: item?.category || "",
    url: item?.url || "",
    description: item?.description || "",
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave({
      id: item?.id || uid(),
      kind: form.kind,
      title: form.title.trim(),
      category: form.category.trim() || undefined,
      url: form.kind !== "note" ? form.url.trim() || undefined : undefined,
      description: form.description.trim() || undefined,
      createdAt: item?.createdAt || new Date().toISOString(),
    });
  };
  return (
    <Modal
      title={item ? "Изменить запись" : "Новая запись в справочнике"}
      onClose={onClose}
    >
      <form onSubmit={submit} className="form-grid">
        <Field label="Тип">
          <select
            value={form.kind}
            onChange={(e) =>
              setForm({
                ...form,
                kind: e.target.value as ReferenceItem["kind"],
              })
            }
          >
            <option value="table">Таблица</option>
            <option value="link">Ссылка</option>
            <option value="note">Заметка</option>
          </select>
        </Field>
        <Field label="Категория">
          <input
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            placeholder="Финансы, отчёты…"
          />
        </Field>
        <Field label="Название" wide>
          <input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Платёжный календарь"
          />
        </Field>
        {form.kind !== "note" && (
          <Field label="Ссылка" wide>
            <input
              required
              type="text"
              inputMode="url"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://docs.google.com/spreadsheets/…"
            />
          </Field>
        )}
        <Field label={form.kind === "note" ? "Текст заметки" : "Описание"} wide>
          <textarea
            required={form.kind === "note"}
            rows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder={
              form.kind === "note"
                ? "Важная информация…"
                : "Для чего используется эта ссылка"
            }
          />
        </Field>
        <div className="modal-actions wide">
          {onDelete && (
            <button
              type="button"
              className="danger-button"
              onClick={() =>
                confirm("Удалить запись из справочника?") && onDelete()
              }
            >
              Удалить
            </button>
          )}
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="primary">Сохранить</button>
        </div>
      </form>
    </Modal>
  );
}

function AuthScreen() {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    const result =
      mode === "signin"
        ? await supabase!.auth.signInWithPassword({ email, password })
        : await supabase!.auth.signUp({ email, password });
    setBusy(false);
    if (result.error)
      setError(
        result.error.message === "Invalid login credentials"
          ? "Неверный email или пароль"
          : result.error.message,
      );
    else if (mode === "signup" && !result.data.session)
      setMessage("Проверьте почту и подтвердите регистрацию.");
  };
  return (
    <div className="auth-page">
      <div className="auth-brand">
        <div className="brand-mark large">V</div>
        <b>VOYAGER OS</b>
        <span>NEXT · CLOUD</span>
      </div>
      <div className="auth-card">
        <p className="eyebrow">ЗАЩИЩЁННЫЙ ДОСТУП</p>
        <h1>{mode === "signin" ? "С возвращением" : "Создать аккаунт"}</h1>
        <p>Ваш автопарк доступен на любом устройстве.</p>
        <form onSubmit={submit}>
          <Field label="Email">
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </Field>
          <Field label="Пароль">
            <input
              required
              minLength={6}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={
                mode === "signin" ? "current-password" : "new-password"
              }
            />
          </Field>
          {error && <div className="form-error">{error}</div>}
          {message && <div className="form-success">{message}</div>}
          <button className="primary auth-submit" disabled={busy}>
            {busy
              ? "Подождите…"
              : mode === "signin"
                ? "Войти в Voyager"
                : "Зарегистрироваться"}
          </button>
        </form>
        <button
          className="auth-switch"
          onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
        >
          {mode === "signin"
            ? "Нет аккаунта? Создать"
            : "Уже есть аккаунт? Войти"}
        </button>
      </div>
      <small className="auth-note">
        Данные защищены политиками доступа Supabase
      </small>
    </div>
  );
}

function SetupScreen() {
  return (
    <div className="auth-page">
      <div className="auth-brand">
        <div className="brand-mark large">V</div>
        <b>VOYAGER OS</b>
        <span>NEXT · CLOUD</span>
      </div>
      <div className="auth-card setup-card">
        <p className="eyebrow">НУЖНА НАСТРОЙКА</p>
        <h1>Подключите Supabase</h1>
        <p>
          Добавьте две переменные окружения из файла <code>.env.example</code>,
          затем перезапустите проект.
        </p>
        <div className="code-box">
          VITE_SUPABASE_URL
          <br />
          VITE_SUPABASE_PUBLISHABLE_KEY
        </div>
        <p className="muted">Полная инструкция находится в README.md.</p>
      </div>
    </div>
  );
}
function FullLoader({ label }: { label: string }) {
  return (
    <div className="full-loader">
      <div className="brand-mark large">V</div>
      <span className="spinner" />
      <p>{label}</p>
    </div>
  );
}
function PageWrap({
  className = "",
  title,
  subtitle,
  actions,
  children,
}: {
  className?: string;
  title: string;
  subtitle: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={`page ${className}`.trim()}>
      <div className="page-title">
        <div>
          <p className="eyebrow">VOYAGER OS · CLOUD</p>
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}
function Stat({
  label,
  value,
  note,
  tone = "",
}: {
  label: string;
  value: string;
  note?: string;
  tone?: string;
}) {
  return (
    <div className={`stat-card ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note || "Текущий месяц"}</small>
    </div>
  );
}
function PanelTitle({ title, meta }: { title: string; meta?: string }) {
  return (
    <div className="panel-title">
      <h2>{title}</h2>
      {meta && <span>{meta}</span>}
    </div>
  );
}
function Empty({
  icon,
  title,
  text,
}: {
  icon: string;
  title: string;
  text: string;
}) {
  return (
    <div className="empty">
      <span>{icon}</span>
      <b>{title}</b>
      <p>{text}</p>
    </div>
  );
}
function InfoLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="info-line">
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}
function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <label className={`field ${wide ? "wide" : ""}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}
function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const fn = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", fn);
    return () => removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`modal${wide ? " modal-wide" : ""}`}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EventRow({
  event,
  vehicle,
  onClick,
  onEdit,
  onFile,
  onDelete,
}: {
  event: VoyagerEvent;
  vehicle?: Vehicle;
  onClick?: () => void;
  onEdit?: () => void;
  onFile?: () => void;
  onDelete?: () => void;
}) {
  const negative = ["expense", "repair", "maintenance"].includes(event.type);
  return (
    <div className="event-row" onClick={onClick}>
      <span className={`event-icon event-${event.type}`}>
        {EVENT_ICONS[event.type]}
      </span>
      <div>
        <b>{event.title}</b>
        <p>
          {vehicle
            ? `${vehicle.make} ${vehicle.model} · ${vehicle.plate} · `
            : ""}
          {shortDate(event.date)}
          {event.notes ? ` · ${event.notes}` : ""}
          {event.paymentMethod ? ` · ${event.paymentMethod}` : ""}
        </p>
      </div>
      {event.filePath && (
        <button
          className="file-open"
          onClick={(e) => {
            e.stopPropagation();
            onFile?.();
          }}
        >
          Открыть
        </button>
      )}
      {typeof event.amount === "number" && (
        <strong className={negative ? "negative" : "positive"}>
          {negative ? "−" : "+"}
          {money(event.amount)}
        </strong>
      )}
      {event.mileage && !event.amount && (
        <strong>{event.mileage.toLocaleString("ru-RU")} км</strong>
      )}
      {onEdit && (
        <button
          className="event-edit"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          Изменить
        </button>
      )}
      {onDelete && (
        <button
          className="event-delete"
          title="Удалить запись"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          ×
        </button>
      )}
      <em>{onClick ? "→" : ""}</em>
    </div>
  );
}

function PhotoGrid({
  events,
  userId,
  onAdd,
  onEdit,
}: {
  events: VoyagerEvent[];
  userId: string;
  onAdd: () => void;
  onEdit: (event: VoyagerEvent) => void;
}) {
  return (
    <section className="panel tab-panel">
      <PanelTitle title="Фото" meta={`${events.length} файлов`} />
      <div className="photo-grid">
        <button className="photo-add" onClick={onAdd}>
          <span>＋</span>Добавить фото
        </button>
        {events.map((e) => (
          <div className="photo-item" key={e.id}>
            <button
              className="photo-open"
              onClick={() => openPrivateFile(e.filePath, userId)}
            >
              <span>▧</span>
              <b>{e.title}</b>
              <small>{shortDate(e.date)}</small>
            </button>
            <button className="event-edit" onClick={() => onEdit(e)}>
              Изменить
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

async function openPrivateFile(path?: string, _userId?: string) {
  if (!path || !supabase) return;
  const { data, error } = await supabase.storage
    .from("voyager-files")
    .createSignedUrl(path, 60);
  if (error) alert(`Не удалось открыть файл: ${error.message}`);
  else window.open(data.signedUrl, "_blank", "noopener,noreferrer");
}

async function removePrivateFile(path?: string) {
  if (!path || !supabase) return;
  await supabase.storage.from("voyager-files").remove([path]);
}

function groupAttention(items: AttentionItem[]) {
  return ATTENTION_CATEGORIES.map((category) => ({
    ...category,
    items: items.filter((item) => item.category === category.id),
  }));
}

function attentionCountLabel(count: number) {
  const lastTwo = count % 100;
  const last = count % 10;
  const noun =
    lastTwo >= 11 && lastTwo <= 14
      ? "событий"
      : last === 1
        ? "событие"
        : last >= 2 && last <= 4
          ? "события"
          : "событий";
  return `${count} ${noun}`;
}

function getAttention(data: VoyagerData, tasks: CompanyTask[] = []) {
  const now = Date.now();
  const items: AttentionItem[] = [];
  const insuranceAlertDays = data.settings.insuranceAlertDays || 30;
  const expenseLimit = data.settings.monthlyVehicleExpenseLimit || 17000;
  const mileageWeek = currentMileageWeek();
  for (const v of data.vehicles) {
    if (v.status !== "Архив" && v.mileageUpdatedWeek !== mileageWeek)
      items.push({
        key: `weekly-mileage-${mileageWeek}-${v.id}`,
        category: "fleet",
        vehicleId: v.id,
        fleetMileage: true,
        icon: "◉",
        tone: "warn",
        title: "Требуется обновить пробег",
        text: `${v.make} ${v.model} · ${v.plate} · пробег не подтверждён на этой неделе`,
      });
    const monthExpense = vehicleFinances(
      data.events,
      v.id,
      currentMonth(),
    ).expense;
    if (monthExpense > expenseLimit)
      items.push({
        key: `expense-limit-${v.id}`,
        category: "fleet",
        vehicleId: v.id,
        icon: "₽",
        tone: "warn",
        title: "Превышен лимит расходов",
        text: `${v.make} ${v.model} · ${v.plate} · ${money(monthExpense)}, превышение ${money(monthExpense - expenseLimit)}`,
      });
    if (v.insuranceExpiresAt) {
      const days = Math.ceil(
        (new Date(v.insuranceExpiresAt).getTime() - now) / 86400000,
      );
      if (days <= insuranceAlertDays)
        items.push({
          key: `insurance-${v.id}`,
          category: "fleet",
          vehicleId: v.id,
          icon: "▤",
          tone: days < 0 ? "danger" : "warn",
          title: days < 0 ? "Страховой полис просрочен" : "Заканчивается страховка",
          text: `${v.make} ${v.model} · ${v.plate} · ${
            days < 0 ? `${Math.abs(days)} дн. назад` : `через ${days} дн.`
          }`,
        });
    }
    if (v.nextMaintenanceMileage) {
      const left = v.nextMaintenanceMileage - v.mileage;
      if (left <= 1000)
        items.push({
          key: `to-${v.id}`,
          category: "fleet",
          vehicleId: v.id,
          icon: "!",
          tone: left < 0 ? "danger" : "warn",
          title: left < 0 ? "ТО просрочено" : "Скоро плановое ТО",
          text: `${v.plate} · ${left < 0 ? `${Math.abs(left)} км просрочки` : `${left} км осталось`}`,
        });
    }
    const incomes = data.events
      .filter((e) => e.vehicleId === v.id && e.type === "income")
      .sort((a, b) => b.date.localeCompare(a.date));
    if (
      v.status === "Работает" &&
      incomes.length &&
      now - new Date(incomes[0].date).getTime() > 4 * 86400000
    )
      items.push({
        key: `income-${v.id}`,
        category: "fleet",
        vehicleId: v.id,
        icon: "↘",
        tone: "warn",
        title: "Нет свежих доходов",
        text: `${v.plate} · последняя запись ${shortDate(incomes[0].date)}`,
      });
    data.events
      .filter(
        (e) =>
          e.vehicleId === v.id && e.type === "document" && e.documentExpiresAt,
      )
      .forEach((e) => {
        const days = Math.ceil(
          (new Date(e.documentExpiresAt!).getTime() - now) / 86400000,
        );
        if (days <= 30)
          items.push({
            key: `doc-${e.id}`,
            category: "fleet",
            vehicleId: v.id,
            icon: "▤",
            tone: days < 0 ? "danger" : "warn",
            title: days < 0 ? "Документ просрочен" : "Заканчивается документ",
            text: `${e.title} · ${days < 0 ? `${Math.abs(days)} дн. назад` : `через ${days} дн.`}`,
          });
      });
  }
  for (const bicycle of data.bicycles) {
    if (bicycle.status !== "В аренде" || !bicycle.rentalUntil) continue;
    const days = calendarDaysUntil(bicycle.rentalUntil);
    const rentalPlan = bicycle.rentalPlan || "Не уточнено";
    if (days <= 3) {
      const title = days < 0
        ? "Дата оплаты аренды прошла"
        : rentalPlan === "Вернёт велосипед"
          ? "Ожидается возврат велосипеда"
          : rentalPlan === "Будет продлевать"
            ? "Ожидается продление аренды"
            : "Нужно уточнить планы арендатора";
      items.push({
        key: `bicycle-rental-${bicycle.id}`,
        category: "bicycles",
        bicycleId: bicycle.id,
        icon: "⚡",
        tone: days < 0
          ? "danger"
          : rentalPlan === "Вернёт велосипед"
            ? "info"
            : rentalPlan === "Будет продлевать"
              ? ""
              : "warn",
        title,
        text: `${bicycle.brand} ${bicycle.model} · ${bicycle.inventoryNumber} · оплачено до ${shortDate(bicycle.rentalUntil)} · ${BICYCLE_RENTAL_PLAN_LABELS[rentalPlan]}${bicycle.rentalPlanNote ? ` · ${bicycle.rentalPlanNote}` : ""}`,
      });
    }
  }
  for (const car of data.rentalCars) {
    if (!car.insuranceExpiresAt) continue;
    const days = Math.ceil(
      (new Date(car.insuranceExpiresAt).getTime() - now) / 86400000,
    );
    if (days <= insuranceAlertDays)
      items.push({
        key: `rental-insurance-${car.id}`,
        category: "rentals",
        rentalCarId: car.id,
        icon: "▤",
        tone: days < 0 ? "danger" : "warn",
        title:
          days < 0
            ? "Страховка прокатного авто просрочена"
            : "Заканчивается страховка прокатного авто",
        text: `${car.make} ${car.model} · ${car.plate} · ${
          days < 0 ? `${Math.abs(days)} дн. назад` : `через ${days} дн.`
        }`,
      });
  }
  for (const task of tasks) {
    if (task.status !== "open" || !task.due_date) continue;
    const days = calendarDaysUntil(task.due_date);
    if (days <= 1)
      items.push({
        key: `task-${task.id}`,
        category: "tasks",
        taskId: task.id,
        icon: "✓",
        tone: days < 0 ? "danger" : "warn",
        title:
          days < 0
            ? "Задача просрочена"
            : days === 0
              ? "Задача на сегодня"
              : "Скоро срок задачи",
        text: `${task.title} · ${
          days < 0
            ? `${Math.abs(days)} дн. просрочки`
            : days === 0
              ? `сегодня${task.due_time ? ` в ${task.due_time.slice(0, 5)}` : ""}`
              : "завтра"
        }`,
      });
  }
  return items;
}

export default App;
